import CategorizationProgress from '../components/CategorizationProgress';
import usePhase3Valence, { phase3SuggestionFor } from '../hooks/usePhase3Valence';
import usePhase4Categories, { phase4SuggestionFor } from '../hooks/usePhase4Categories';
import { drawReportEmphasis } from '../utils/reportEmphasisPdf';
import { reportSourceResponses } from '../utils/reportEmphasis';
import { reportFieldPdfRuns, preserveReportListMarkersHtml } from '../utils/reportFieldFormatting';
import { splitPoint11Content, Point11Findings } from '../utils/point11Content';
import PremiumClientSynthesis from '../components/PremiumClientSynthesis';
import {loadSynthesisBranding} from '../components/ClientSynthesisBranding';
import { documentHeaders } from '../utils/browserPresence';
import { readClientSynthesisStream } from '../utils/clientSynthesisStream';
import { analysisDisplayText } from '../utils/analysisDisplayText';
import { pointAnalysisDisplay, replacePointAnalysis, phase5DataQualityContent } from '../utils/phase5DataQuality';
import { getClientPhaseProgress } from '../utils/clientPhaseProgress';
import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Brain, ArrowLeft, Clock, ChevronRight, ChevronDown, Loader2, CheckCircle, FileText, Trash2, Eye, EyeOff, Plus, Download, AlertCircle, Info, Volume2, VolumeX, Pause, Play, StopCircle, Edit, Save, Lock, BookOpen, MessageSquare, RefreshCw, Send, X, Sparkles } from 'lucide-react';
import NotificationModal from '../components/NotificationModal';
import { useNotification } from '../hooks/useNotification';
import { toast } from 'sonner';
import {
  BooksConfirmModal, BooksSuccessModal,
  MoviesConfirmModal, MoviesSuccessModal
} from '../components/SimplePhase5Modals';
import GenerationTimer from '../components/GenerationTimer';
import GenerationProgressOverlay from '../components/GenerationProgressOverlay';
import WhyNextProgressDialog from '../components/WhyNextProgressDialog';
import { phase5JobFailure } from '../utils/phase5JobError';
import Phase5RegenerationDialog from '../components/Phase5RegenerationDialog';
import { CollapsibleSection, CollapsibleNarrative, SectionGroup, ExpandAllControls } from '../components/CollapsibleSection';
import RichReportRenderer from '../components/RichReportRenderer';
import FormattedReport from '../components/FormattedReport';
import { Phase5ReportTitle, ReportFontSelector, ReportTextSizeSelector, phase5Tone } from '../components/Phase5ReportSystem';
import Phase5ReportView from '../components/Phase5ReportView';
import RecommendationReportRenderer from '../components/RecommendationReportRenderer';
import CustomReportHistory from '../components/CustomReportHistory';
import { normalizeNexusMarkupForPdf } from '../utils/nexusMarkup';
import { nextStepsDisplayHtml, nextStepsText } from '../utils/nextStepsSections';
import { createNextStepsState } from '../utils/nextStepsState';
import ProcessIntro from '../components/ProcessIntro';
import ExplanationVoiceInput from '../components/ExplanationVoiceInput';
import VoiceQuestion, { VoicePicker, NarrateButton, MicInput, useSpeak } from '../components/VoiceQuestion';
import VoicePrivacyNotice from '../components/VoicePrivacyNotice';
import { useAutoSave, AutoSaveIndicator } from '../hooks/useAutoSave';
import './SessionPhases.sqis.css';
import './SessionPhases.premium.css';

import { withDoctorTitle } from '../utils/doctorTitle';
import { isPhaseTabDisabled, isPsychologistUser } from '../utils/phaseNavigation';
import { displayResponseStatus, canonicalTimeoutCategory, phase4ExpiredCategories, SYSTEM_TIME_EXPIRED, SYSTEM_TIME_EXPIRED_DISPLAY, RESERVED_TIMEOUT_MESSAGE, isReservedClientTimeoutText, isSystemTimeExpired, isPhase3SlotTimedOut, getPhase3TimedOutSlots, getPhase3ClassificationCounts, createPhase4TimeoutSlots, isPhase4FieldTimedOut, isPhase4DescriptorTimedOut, getPhase4CopingAggregateCategory, getPhase4ThematicAggregateCategory, getPhase4SummaryCounts } from '../utils/timeoutState';
import { getClientTextCorrectionDiagnostic, getTextCorrectionSuggestion, isClientTextCorrectionEnabled } from '../utils/textCorrection';
import { CLIENT_TEXT_GIBBERISH_MESSAGE, validateClientTextInput } from '../utils/clientTextValidation';
import { getDurablePhase4Items, getPhase4ReviewerRenderState, isPhase4DurablyComplete } from '../utils/phase4Completion';
import { getPhase4CategoryOptions, resolvePhase4Category } from '../utils/phase4CategoryOptions';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { sqisGenerationErrorMessage } from '../utils/sqisGenerationError';
import { visiblePhase5ReportTabs, recommendationDependenciesReady, normalizePhase5ReportTab, indexedAiReportsWithoutClientSummary, findClientSummaryReport, actualCustomReports, researchReportsForType } from '../utils/phase5ReportTabs';
import { readPhase5ReportUi, updatePhase5ReportUi } from '../utils/phase5ReportUi';
import Phase5Loading from '../components/Phase5Loading';
import { normalizeSynthesisPoints } from '../utils/synthesisReport';
import { recommendationGeneratedBy } from '../utils/recommendationMetadata';
import { StructuredReportContent } from '../utils/phase5BodyStructure';
import { subscribeInsightInvalidation } from '../utils/insightInvalidation';
import ClientSummaryEditor from '../components/ClientSummaryEditor';
import { clientSummaryHtmlToText, plainTextToClientSummaryHtml, sanitizeClientSummaryHtml } from '../utils/clientSummaryRichText';
import { CLIENT_SYNTHESIS_DISPLAY_NAME, getClientSynthesisPdfFilename } from '../utils/clientSynthesisName';
import {
  PHASE1_SKIPPED_KEY,
  clearPhase1QuestionSkipped,
  firstIncompletePhase1QuestionIndex,
  isPhase1QuestionSkipped,
  markPhase1QuestionSkipped,
  withoutPhase1DraftMetadata,
} from '../utils/phase1OptionalSkip';
// Display-only title overrides for the 14 Points Structured Qualitative Interpretative Summary.
// Underlying stored titles remain unchanged to preserve backend prompts,
// LLM parsing, and persisted data integrity.
const POINT_TITLE_DISPLAY_OVERRIDES = {
  2: "Client's Dominating Orientation Analysis",
  3: "Client's Preferred Areas of Development and Interest Analysis",
  4: "Client's Learning and Coping Mechanisms Analysis",
  5: "Client's Influential Past Experiences Analysis",
  6: "Client's Thinking Perspectives Analysis",
  7: "Client's Outlook Towards Life Analysis",
  8: "Client's Most UnMet Aspirations or Desires Analysis",
  9: "Client's Distribution of Positive, Negative, and Neutral Responses Analysis",
  10: "Time Taken for Responding Indicates",
  11: "Client's Views Needs and Presses",
  12: "Client's Consistent response patterns under emotional elicitation Analysis",
  13: "Client's Moral Values Analysis",
  14: 'Client Response Word Connection Analysis',
};

const getDisplayTitle = (pointNumber, defaultTitle) => {
  return POINT_TITLE_DISPLAY_OVERRIDES[pointNumber] || defaultTitle;
};

export const isDeepDiveReport = (report) => Boolean(report) && (
  report.report_type === 'report3' || report.report_name === 'Deep Dive into Inner World'
);

const RecommendationText = ({ children, list = false }) => (
  <StructuredReportContent list={list}>{children}</StructuredReportContent>
);

const BookReport = ({ report, sessionId }) => {
  const sections = Array.isArray(report?.sections) ? report.sections : null;
  if (!sections) return null;
  const byTitle = Object.fromEntries(sections.map((section) => [section.title, section.content]));
  const techniqueTitles = [1, 2, 3, 4, 5].map((number) => `THERAPEUTIC TECHNIQUE ${number}`);
  const proseSections = [
    'RECOMMENDED BOOK', 'INTERPRETATION OF THEMES', 'WHY THIS BOOK',
    'THERAPEUTIC RELEVANCE', 'CONNECTION TO WHY', 'CONNECTION TO NEXT STEPS',
  ];
  const displaySections = [...proseSections, ...techniqueTitles, 'REFLECTION QUESTIONS', 'CAUTION']
    .map((title) => ({ title, body: <RecommendationText list={title === 'REFLECTION QUESTIONS'}>{byTitle[title]}</RecommendationText> }));
  return <RecommendationReportRenderer sessionId={sessionId} reportKey="book_recommendations"
    reportName="Book Reflections" sections={displaySections} testId="structured-book-report" />;
};

const MovieReport = ({ report, sessionId }) => {
  const sections = Array.isArray(report?.sections) ? report.sections : null;
  if (!sections) return null;
  const byTitle = Object.fromEntries(sections.map((section) => [section.title, section.content]));
  const learningTitles = [1, 2, 3, 4, 5].map((number) => `KEY THEME / LEARNING ${number}`);
  const proseSections = [
    'RECOMMENDED MOVIE', 'INTERPRETATION OF THEMES', 'WHY THIS MOVIE',
    'THERAPEUTIC RELEVANCE', 'CONNECTION TO WHY', 'CONNECTION TO NEXT STEPS',
  ];
  const displaySections = [...proseSections, ...learningTitles, 'REFLECTION QUESTIONS', 'CAUTION / VIEWING NOTE']
    .map((title) => ({ title, body: <RecommendationText list={title === 'REFLECTION QUESTIONS'}>{byTitle[title]}</RecommendationText> }));
  return <RecommendationReportRenderer sessionId={sessionId} reportKey="movie_recommendations"
    reportName="Movie Reflections" sections={displaySections} testId="structured-movie-report" />;
};

const MusicReport = ({ report, sessionId }) => {
  const sections = [{ title: 'DISCLAIMER', body: <p>{report?.disclaimer || 'Suggested for reflection — not a clinical prescription.'}</p> }]
    .concat((report?.music_recommendations || []).map((item, index) => ({
      id: `music-recommendation-${index + 1}`,
      title: `MUSIC RECOMMENDATION ${index + 1}`,
      body: <>
        <dl className="phase5-recommendation-fields">
          <div><dt>Title:</dt><dd>{item.title}</dd></div>
          <div><dt>Artist:</dt><dd>{item.artist}</dd></div>
          {item.album_year && <div><dt>Album/year:</dt><dd>{item.album_year}</dd></div>}
        </dl>
        <h6 className="phase5-subheading">WHY THIS MUSIC</h6><RecommendationText>{item.why_relevant}</RecommendationText>
        <h6 className="phase5-subheading">LISTENING FOCUS</h6><RecommendationText>{item.listening_focus}</RecommendationText>
        <h6 className="phase5-subheading">REFLECTION QUESTIONS</h6>
        <ul className="phase5-question-list phase5-semantic-list">{(item.reflection_questions || []).map((q, i) => <li key={i}>{q}</li>)}</ul>
      </>,
    })));
  return <RecommendationReportRenderer sessionId={sessionId} reportKey="music_recommendations"
    reportName="Music Reflections" sections={sections} testId="music-recommendations-report" />;
};

const LegacyRecommendationReport = ({ report, type, sessionId, testId }) => {
  const isBook = type === 'book';
  const rows = report?.[isBook ? 'book_recommendations' : 'movie_recommendations'] || [];
  const sections = rows.map((item, index) => ({
    id: `${isBook ? 'recommended-book' : 'recommended-movie'}-${index + 1}`,
    title: `${isBook ? 'RECOMMENDED BOOK' : 'RECOMMENDED MOVIE'} ${index + 1}`,
    body: <>
      <dl className="phase5-recommendation-fields">
        <div><dt>Title:</dt><dd>{item.title || `${isBook ? 'Book' : 'Movie'} ${index + 1}`}</dd></div>
        {isBook && item.author && <div><dt>Author:</dt><dd>{item.author}</dd></div>}
        {!isBook && item.director && <div><dt>Director:</dt><dd>{item.director}</dd></div>}
        {!isBook && item.year && <div><dt>Year:</dt><dd>{item.year}</dd></div>}
      </dl>
      {(item.why_recommended || item.therapeutic_value) && <><h6 className="phase5-subheading">{isBook ? 'WHY THIS BOOK' : 'WHY THIS MOVIE'}</h6><RecommendationText>{item.why_recommended || item.therapeutic_value}</RecommendationText></>}
    </>,
  }));
  return <RecommendationReportRenderer sessionId={sessionId} reportKey={`${type}_recommendations`}
    reportName={`${isBook ? 'Book' : 'Movie'} Reflections`} sections={sections}
    testId={testId} />;
};

const recommendationReportPdfContent = (report) => {
  if (Array.isArray(report?.sections)) {
    return report.sections.map((section) => `${section.title || 'SECTION'}\n\n${section.content || ''}`).join('\n\n');
  }
  const rows = report?.book_recommendations || report?.movie_recommendations || report?.music_recommendations || [];
  return rows.map((item, index) => {
    const fields = Object.entries(item || {}).map(([key, value]) => {
      const label = key.replace(/_/g, ' ').toUpperCase();
      const text = Array.isArray(value) ? value.join('\n') : (typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : value);
      return `${label}\n\n${text ?? ''}`;
    });
    return `RECOMMENDATION ${index + 1}\n\n${fields.join('\n\n')}`;
  }).join('\n\n');
};

// Client validation "Suggest Change" inline form component
const FlaggingForm = ({ initialContent, initialFeedback, onCancel, onSave, pointNumber }) => {
  const [content, setContent] = React.useState(initialContent || '');
  const [feedback, setFeedback] = React.useState(initialFeedback || '');
  const canSave = content.trim().length > 0;
  return (
    <div className="p-3 rounded bg-amber-50 border border-amber-300 space-y-3" data-testid={`client-flagging-form-${pointNumber}`}>
      <div>
        <label className="block text-xs font-semibold text-amber-900 mb-1">Suggested Correction / Your Version:</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 text-sm border border-amber-300 rounded focus:ring-2 focus:ring-amber-500"
          placeholder="Edit this point to reflect what feels correct to you..."
          data-testid={`client-flag-content-${pointNumber}`}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-amber-900 mb-1">Note to your psychologist (optional):</label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-amber-300 rounded focus:ring-2 focus:ring-amber-500"
          placeholder="Anything else you want to add..."
          data-testid={`client-flag-feedback-${pointNumber}`}
        />
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          data-testid={`client-flag-cancel-${pointNumber}`}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => onSave(content.trim(), feedback.trim())}
          className="px-3 py-1.5 text-sm rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`client-flag-save-${pointNumber}`}
        >
          Save Suggestion
        </button>
      </div>
    </div>
  );
};



// ---------------------------------------------------------------------------
// Markdown table parser used by all 14 Points Structured Qualitative Interpretative Summary renderers (UI + PDF).
// Detects standard markdown tables: a header row, separator row of dashes,
// and one or more data rows, all using the `|` pipe character.
// Returns a list of blocks: { type: 'text', text } | { type: 'table', headers, rows }.
// Surrounding non-table text is preserved as text blocks.
// ---------------------------------------------------------------------------
const parseMarkdownBlocks = (content) => {
  if (!content || typeof content !== 'string') return [];
  const lines = content.split('\n');
  const isPipeRow = (l) => /^\s*\|.*\|\s*$/.test(l);
  const isSeparatorRow = (l) => /^\s*\|?\s*[:\-\s|]+\s*\|?\s*$/.test(l) && /-/.test(l) && /\|/.test(l);
  const splitCells = (l) => l.trim().replace(/^\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());

  const blocks = [];
  let textBuf = [];
  const flushText = () => {
    if (textBuf.length) {
      const t = textBuf.join('\n').replace(/^\n+|\n+$/g, '');
      if (t.trim()) blocks.push({ type: 'text', text: t });
      textBuf = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isPipeRow(line) && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      flushText();
      const headers = splitCells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && isPipeRow(lines[i])) {
        rows.push(splitCells(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }
    textBuf.push(line);
    i += 1;
  }
  flushText();
  return blocks;
};

// Standardized disclaimer shown at the bottom of every 14-Point Structured
// Qualitative Profile (UI + PDF), per requirement.
const SYNTHESIS_DISCLAIMER_NOTE = "Note: All numerical summaries in this profile are organizational heuristics reflecting within-session response distributions from a single administration. They are not standardized, norm-referenced, or psychometrically validated instances. They serve to support, not replace, clinical judgment. ";

// React component that renders mixed text + markdown-table content for the UI.
const PointContentRenderer = ({ content }) => {
  const blocks = parseMarkdownBlocks(content || '');

  // Helper: turn **foo** into <strong>foo</strong> while keeping rest of the text intact.
  const renderInlineBold = (text) => {
    const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      const m = part.match(/^\*\*(.+)\*\*$/);
        if (m) return <strong key={i} className="sqis-content-label font-semibold text-gray-900">{m[1]}</strong>;
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  if (blocks.length === 0 || (blocks.length === 1 && blocks[0].type === 'text')) {
    return <p className="sqis-content-paragraph text-gray-700 whitespace-pre-line">{renderInlineBold(content)}</p>;
  }
  return (
    <div className="space-y-3">
      {blocks.map((b, idx) => {
        if (b.type === 'text') {
          return (
            <p key={idx} className="sqis-content-paragraph text-gray-700 whitespace-pre-line">
              {renderInlineBold(b.text)}
            </p>
          );
        }
        return (
          <div key={idx} className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead className="bg-blue-50">
                <tr>
                  {b.headers.map((h, hi) => (
                    <th key={hi} className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 text-sm">
                      {h.replace(/\*\*/g, '')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r, ri) => (
                  <tr key={ri} className="hover:bg-gray-50">
                    {r.map((c, ci) => (
                      <td key={ci} className="border border-gray-300 px-3 py-2 text-sm text-gray-800 align-top whitespace-pre-line">
                        {renderInlineBold(c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

// Validation helper functions
const validateTextOnly = (value) => {
  // Allow letters, spaces, hyphens, apostrophes
  const regex = /^[a-zA-Z\s'-]*$/;
  return regex.test(value);
};

const validateNumberOnly = (value) => {
  // Allow only digits
  const regex = /^[0-9]*$/;
  return regex.test(value);
};

const validateAlphanumeric = (value) => {
  // Allow letters, numbers, and common punctuation
  const regex = /^[a-zA-Z0-9\s.,;:!?'"()-]*$/;
  return regex.test(value);
};

const validateLettersOnly = (value) => {
  // Allow only letters and spaces - NO numbers, NO special characters
  const regex = /^[a-zA-Z\s]*$/;
  return regex.test(value);
};

const validateNumeric = (value) => {
  // Allow only digits
  const regex = /^[0-9]*$/;
  return regex.test(value);
};

// Module-level notification handler - defined outside component to avoid scope issues
let globalNotificationHook = null;

const setNotificationHook = (hook) => {
  globalNotificationHook = hook;
};

window.showModalNotification = (type, message) => {
  console.log('🔔 [GLOBAL MODAL NOTIFICATION] Called:', { type, message });
  if (!globalNotificationHook) {
    console.error('❌ Notification hook not initialized yet');
    return;
  }
  try {
    if (type === 'success') {
      globalNotificationHook.showSuccess(message);
    } else if (type === 'error') {
      globalNotificationHook.showError(message);
    } else {
      globalNotificationHook.showInfo(message);
    }
  } catch (err) {
    console.error('[GLOBAL NOTIFICATION ERROR]:', err);
  }
};

const SessionPhases = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const isDomainExpert = isPsychologistUser(user);
  const navigate = useNavigate();
  
  // Check if viewing reports only mode
  const viewReportsOnly = searchParams.get('viewReportsOnly') === 'true';
  const [activePhase, setActivePhase] = useState(viewReportsOnly ? 5 : 1);
  useEffect(() => {
    const activeTab = document.querySelector(`[data-testid="phase-tab-${activePhase}"]`);
    activeTab?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activePhase]);
  const clientSummaryNavigationGuardRef = useRef(null);
  const registerClientSummaryNavigationGuard = useCallback((guard) => {
    clientSummaryNavigationGuardRef.current = guard;
  }, []);

  const requestApplicationNavigation = (action) => {
    if (activePhase === 5 && clientSummaryNavigationGuardRef.current) {
      clientSummaryNavigationGuardRef.current(action);
      return;
    }
    action();
  };

  const handlePhaseTabChange = (value) => {
    if (viewReportsOnly && !isDomainExpert) return;
    requestApplicationNavigation(() => {
      const phase = parseInt(value.replace('phase', ''), 10);
      if (isDomainExpert) {
        try { sessionStorage.setItem(`expert-phase-${sessionId}`, String(phase)); } catch (_) { /* optional navigation preference */ }
      }
      setActivePhase(phase);
    });
  };
  const notificationHook = useNotification();
  const { notification, showSuccess, showError, showInfo, closeNotification } = notificationHook;
  
  // Initialize global notification hook
  React.useEffect(() => {
    setNotificationHook(notificationHook);
  }, [notificationHook]);
  
  // Use global notification function
  const showModalNotification = window.showModalNotification;
  const closeModalNotification = () => {
    if (notificationHook) {
      notificationHook.closeNotification();
    }
    // Hide timer when user dismisses notification
    setShowIndividualTimer(false);
  };
  
  // Debug logging for notifications
  
  // Track notification state changes
  useEffect(() => {
    if (notification.isOpen) {
    }
  }, [notification]);
  
  const [session, setSession] = useState(null);
  const initializedSessionRef = useRef(null);
  const clientProgress = getClientPhaseProgress(session);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPhaseCompletionModal, setShowPhaseCompletionModal] = useState(false);
  const [completedPhaseNumber, setCompletedPhaseNumber] = useState(0);
  
  // Track if ANY report generation is happening (lifted from Phase5)
  const [isAnyReportGenerating, setIsAnyReportGenerating] = useState(false);

  useEffect(() => {
    fetchSession();
    fetchSettings();
    const refreshSettings = () => fetchSettings();
    const refreshWhenVisible = () => { if (document.visibilityState === 'visible') refreshSettings(); };
    window.addEventListener('focus', refreshSettings);
    window.addEventListener('storage', refreshSettings);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    const settingsRefreshInterval = window.setInterval(refreshSettings, 10000);
    return () => {
      window.removeEventListener('focus', refreshSettings);
      window.removeEventListener('storage', refreshSettings);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.clearInterval(settingsRefreshInterval);
    };
  }, [sessionId]);

  useEffect(() => subscribeInsightInvalidation((change) => {
    if (change.sessionId && change.sessionId !== String(sessionId)) return;
    if (change.type === 'session_data_changed' || change.type === 'phase5_report_changed' ||
        change.type === 'report_review_changed' || change.type === 'report_share_changed' ||
        change.type === 'client_feedback_changed') fetchSession();
  }), [sessionId]);

  const fetchSession = async () => {
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}`, { params: viewReportsOnly ? { reports_only: true } : {} });
      console.log('📡 [FETCH SESSION] Response received:', res.data);
      console.log('📡 [FETCH SESSION] phase5_synthesis_complete:', res.data?.phase5_synthesis_complete);
      setSession(res.data);
      
      // Don't update activePhase if viewing reports only (historical session)
      if (!viewReportsOnly && (!isDomainExpert || initializedSessionRef.current !== sessionId)) {
        // Cap activePhase at 5 for psychologists, 4 for clients
        const maxPhase = user?.role === 'client' ? 4 : 5;
        let nextPhase = res.data.current_phase + 1 || 1;
        if (isDomainExpert) {
          try {
            const selected = Number(sessionStorage.getItem(`expert-phase-${sessionId}`));
            if (selected >= 1 && selected <= 5 && !isPhaseTabDisabled({ user, phase: selected,
              currentPhase: res.data.current_phase, hasSynthesis: res.data.has_synthesis })) nextPhase = selected;
          } catch (_) { /* use server navigation default */ }
        }
        // Refresh saved state without pulling an expert out of Phase 4 review.
        // The Phase 5 tab unlocks from the validated backend milestone.
        setActivePhase(previous => {
          // Phase 3 completion (including mutation-triggered refreshes) updates
          // access without taking the client past the completion popup.
          if (previous === 3 && user?.role === 'client' && res.data.current_phase === 3) return 3;
          return previous === 4 && isDomainExpert ? 4 : Math.min(nextPhase, maxPhase);
        });
      }
      initializedSessionRef.current = sessionId;
    } catch (error) {
      const detail = error.response?.data?.detail;
      showError(detail?.message || (typeof detail === 'string' ? detail : 'Failed to load session'));
      if (viewReportsOnly && error.response?.status === 409) navigate('/psychologist-dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Reused assessments are view-only for outside psychologists, except for the
  // psychologist currently assigned to this session (the existing backend
  // authorization path permits that reviewer to edit/review the SQIS).
  const isViewOnlyMode = isDomainExpert &&
    session?.original_psychologist_id &&
    session?.original_psychologist_id !== user?.id &&
    session?.psychologist_id !== user?.id;


  const handleSetPhase = async (newPhase) => {
    try {
      await axios.patch(`${API}/sessions/${sessionId}/set-phase?phase=${newPhase}`);
      showSuccess(`Phase manually set to ${newPhase}`);
      fetchSession();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to set phase');
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`, { params: { _fresh: Date.now() }, headers: { 'Cache-Control': 'no-cache' } });
      console.log('⚙️ [CLIENT] Settings fetched:', {
        phase2_timer: res.data.phase2_timer,
        phase3_timer_per_word: res.data.phase3_timer_per_word,
        phase4_timer_total: res.data.phase4_timer_total
      });
      setSettings(res.data);
      if (process.env.NODE_ENV !== 'production') {
        window.__INSIGHT_CLIENT_TEXT_CORRECTION__ = getClientTextCorrectionDiagnostic(res.data);
      }
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="session-phases-page min-h-screen bg-white" data-testid="session-phases">
      <nav className="px-8 py-6 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => requestApplicationNavigation(() => navigate(`/${user.role}-dashboard`))}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <img src="/insight-logo.png" alt="INSIGHT Logo" className="h-14" />
        </div>
      </nav>

      {/* View-Only Mode Banner for Non-Original Psychologists */}
      {isViewOnlyMode && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-4 border-blue-400">
          <div className="max-w-5xl mx-auto px-8 py-4">
            <div className="flex items-start gap-4">
              <Eye className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-blue-900 mb-2">
                  📋 View-Only Mode: Analysis from Previous Psychologist
                </h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>
                    <strong>This analysis is valid:</strong>{' '}
                    {session?.analysis_always_valid
                      ? 'indefinitely'
                      : `until ${new Date(session?.assessment_validity_expires_at).toLocaleDateString()}`}
                    {!session?.analysis_always_valid && (
                      <span className="ml-2 px-2 py-1 bg-blue-200 text-blue-900 rounded text-xs font-medium">
                        {session?.assessment_days_remaining} days remaining
                      </span>
                    )}
                  </p>
                  <p><strong>You can:</strong> ✅ View all phases • ✅ Download Analysis • ✅ Add custom interpretations</p>
                  <p><strong>You cannot:</strong> ❌ Recategorize Phase 3 & 4 responses • ❌ Regenerate 14-point Analysis</p>
                  <p className="text-xs text-blue-700 mt-2 italic">
                    The original analysis and categorization remain locked to maintain consistency
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="w-full px-8 py-12">
        {isDomainExpert && !clientProgress.allComplete && (
          <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="incomplete-phase-status">
            <strong>Client has not completed all assessment phases yet.</strong>{' '}
            Existing responses and status remain available through every tab.
          </div>
        )}
            <div className="mb-8">
              <div className="phase-progress-summary flex justify-between items-center mb-4">
                <h2 className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {user?.role === 'client' 
                    ? `Phase ${Math.min(activePhase, 4)} of 4`
                    : `Phase ${activePhase} of 5`
                  }
                </h2>
                <div className="flex items-center gap-4">
                  {/* Manual Override hidden per user request */}
                  <span className="phase-progress-label text-sm text-gray-600">
                    <span className="phase-progress-step">Phase {activePhase} of {user?.role === 'client' ? 4 : 5}</span>
                    <span>Client Progress: {clientProgress.count}/4 Phases Completed</span>
                    <span style={{ display: 'none' }}>Session Progress: {user?.role === 'client' ? ((session?.current_phase > 4 ? 4 : session?.current_phase) / 4 * 100).toFixed(0) : ((session?.current_phase || 0) / 5 * 100).toFixed(0)}%</span>
                  </span>
                </div>
              </div>
              <Progress value={user?.role === 'client' ? ((session?.current_phase > 4 ? 4 : session?.current_phase) / 4 * 100) : ((session?.current_phase || 0) / 5 * 100)} />
            </div>

            <Tabs value={viewReportsOnly && !isDomainExpert ? 'phase5' : `phase${activePhase}`} onValueChange={handlePhaseTabChange} className="w-full">
              <TabsList className="phase-navigation-list" aria-label="Session phases">
                {[1, 2, 3, 4, 5].map(p => {
                  // Client phases retain sequential access. Experts may review
                  // Phases 1–5 freely; generation retains its own prerequisites.
                  // Historical Reports Library access stays available.
                  const disabledLogic = isPhaseTabDisabled({ user, phase: p, currentPhase: session?.current_phase, reportsOnly: viewReportsOnly, hasSynthesis: session?.has_synthesis });

                  return (
                    <TabsTrigger
                      key={p}
                      value={`phase${p}`}
                      disabled={disabledLogic}
                      className={disabledLogic ? undefined : 'cursor-pointer hover:bg-accent hover:text-accent-foreground'}
                      data-testid={`phase-tab-${p}`}
                      title={disabledLogic ? `Complete Phase ${p - 1} first to unlock this tab` : undefined}
                    >
                      {(p <= 4 ? clientProgress.completed[p - 1] : session?.has_synthesis) ? <CheckCircle className="w-4 h-4 mr-1" /> : null}
                      {(() => {
                        if (user?.role === 'client' && p === 5) return 'Analysis';
                        if (p === 1) return 'Client Contextual Demographics';
                        if (p === 2) return 'Client Moral Reflections';
                        if (p === 3) return 'Client Views';
                        if (p === 4) return 'Client Aspirations';
                        if (p === 5) return 'Analysis';
                        return `Phase ${p}`;
                      })()}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              <TabsContent value="phase1"><Phase1 sessionId={sessionId} settings={settings} onComplete={fetchSession} showCompletionModal={(phaseNum) => { setCompletedPhaseNumber(phaseNum); setShowPhaseCompletionModal(true); }} isViewOnlyMode={isViewOnlyMode} /></TabsContent>
              <TabsContent value="phase2"><Phase2 sessionId={sessionId} settings={settings} onComplete={fetchSession} userRole={user?.role} showCompletionModal={(phaseNum) => { setCompletedPhaseNumber(phaseNum); setShowPhaseCompletionModal(true); }} isViewOnlyMode={isViewOnlyMode} /></TabsContent>
              <TabsContent value="phase3"><Phase3 key={sessionId} sessionId={sessionId} settings={settings} onComplete={fetchSession} userRole={user?.role} showCompletionModal={(phaseNum) => { setCompletedPhaseNumber(phaseNum); setShowPhaseCompletionModal(true); }} isViewOnlyMode={isViewOnlyMode} /></TabsContent>
              <TabsContent value="phase4"><Phase4 key={sessionId} sessionId={sessionId} settings={settings} onComplete={fetchSession} userRole={user?.role} showCompletionModal={(phaseNum) => { setCompletedPhaseNumber(phaseNum); setShowPhaseCompletionModal(true); }} isViewOnlyMode={isViewOnlyMode} /></TabsContent>
              <TabsContent value="phase5"><Phase5 key={sessionId} sessionId={sessionId} onComplete={fetchSession} refreshSession={fetchSession} userRole={user?.role} currentUser={user} showCompletionModal={(phaseNum) => { setCompletedPhaseNumber(phaseNum); setShowPhaseCompletionModal(true); }} isViewOnlyMode={isViewOnlyMode || (viewReportsOnly && !isDomainExpert)} canManageHelpfulResources={user?.role === 'psychologist' && !isViewOnlyMode} session={session} setIsAnyReportGenerating={setIsAnyReportGenerating} registerClientSummaryNavigationGuard={registerClientSummaryNavigationGuard} /></TabsContent>
            </Tabs>
      </div>
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
      
      {/* Phase Completion Modal */}
      <Dialog open={showPhaseCompletionModal} onOpenChange={setShowPhaseCompletionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-green-600">
              🎉 Phase {completedPhaseNumber} {completedPhaseNumber === 3 ? 'Complete!' : 'Completed!'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center">
              <div className="rounded-full bg-green-100 p-6">
                <svg className="h-16 w-16 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <p className="text-center text-gray-600">
              {completedPhaseNumber === 1 && "Your personal information has been recorded successfully."}
              {completedPhaseNumber === 2 && "Your responses to moral questions have been saved."}
              {completedPhaseNumber === 3 && "Response association exercise completed successfully."}
              {completedPhaseNumber === 4 && "Your favorite things have been recorded and categorized."}
              {completedPhaseNumber === 5 && "AI-powered Analysis has been generated successfully!"}
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                {completedPhaseNumber < 5 ? (
                  <>
                    <strong>Next Step:</strong> Proceed to Phase {completedPhaseNumber + 1} when you're ready.
                  </>
                ) : (
                  <>
                    <strong>Session Complete!</strong> All phases have been completed. You can review your Analysis anytime.
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => {
              setShowPhaseCompletionModal(false);
              // Auto-navigate to Phase 5 after Phase 4 completion (for clients)
              if (completedPhaseNumber === 4 && user?.role === 'client') {
                setActivePhase(5);
              }
            }}>
              {completedPhaseNumber === 4 && user?.role === 'client' ? 'Proceed to Phase 5' : 'Close'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Phase 1 Voice Interview Questions — narrative fields that benefit from spoken responses.
// Structured fields (dropdowns, dates, checkboxes) remain in the regular form UI after the voice interview.
// NOTE: Language sub-questions (name, read, write, speak) are injected dynamically
// once the client answers `language_count` — see `buildPhase1VoiceQuestions` below.
const PHASE1_VOICE_QUESTIONS_BASE = [
  { field: 'name',                              question: "Let's begin. What is your full name?" },
  { field: 'date_of_birth',                     question: 'What is your date of birth? Please say the day, month, and year. Your age will be calculated automatically.' },
  { field: 'gender',                            question: 'Please state your gender. You can say Male, Female, or Transgender.' },
  { field: 'nationality',                       question: 'What is your nationality?' },
  { field: 'state',                             question: 'Which state or region do you live in?' },
  { field: 'religion',                          question: 'What is your religion, if any?' },
  { field: 'marital_status',                    question: 'What is your current marital status? Please say Single, Married, Divorced, or Widowed.' },
  { field: 'number_of_children',                question: 'How many children do you have? If none, say zero.', helperText: 'Example: Say it in a statement that "I have two children." The system will pick up the number 2.' },
  { field: 'siblings_brothers',                 question: 'How many brothers do you have? If none, say zero.', helperText: 'Example: Say it in a statement only that "I have two brothers." The system will pick up the number 2.' },
  { field: 'siblings_sisters',                  question: 'How many sisters do you have? If none, say zero.', helperText: 'Example: Say it in a statement only that "I have two sisters." The system will pick up the number 2.' },
  { field: 'friends_in_touch',                  question: 'Approximately how many close friends do you keep in regular touch with?', helperText: 'Example: Say it in a statement only that "I have two friends." The system will pick up the number 2.' },
  { field: 'education',                         question: 'What is the highest level of education you have completed?' },
  { field: 'working_status',                    question: 'What is your current working status? Please say Working, Presently Not Working, or Retired.' },
  { field: 'occupation',                        question: 'What is your current occupation or profession?' },
  { field: 'language_count',                    question: 'How many languages can you read, write, or speak? Please say a number.', helperText: 'Example: Say it in a statement only that i know 2 languages, and the system will pick the number 2.' },
  { field: 'disability',                        question: 'Do you have any disability? Please say yes or no.' },
  { field: 'disability_description',            question: 'Please briefly describe the disability you mentioned.', dependsOn: { field: 'disability', yes: true } },
  { field: 'medical_issues',                    question: 'Do you have any ongoing medical issues? Please say yes or no.' },
  { field: 'medical_issues_description',        question: 'Please describe your medical issues, ongoing health concerns, or medications.', dependsOn: { field: 'medical_issues', yes: true } },
  { field: 'substance_abuse_history',           question: 'Do you have any history of substance use? Please say yes or no.' },
  { field: 'substance_abuse_description',       question: 'Please describe your history of substance use.', dependsOn: { field: 'substance_abuse_history', yes: true } },
  { field: 'dependent',                         question: 'Are you financially or emotionally dependent on anyone? Please say yes or no.' },
  { field: 'dependent_explanation',             question: 'Please describe who is dependent on you and in what way.', dependsOn: { field: 'dependent', yes: true } },
  { field: 'family_members_description',        question: 'Please describe the important members of your family and your relationship with each of them.' },
  { field: 'life_goal_1',                       question: 'What is your first and most important life goal?' },
  { field: 'life_goal_2',                       question: 'This is optional. What is your second life goal?', optional: true },
  { field: 'life_goal_3',                       question: 'This is optional. What is your third life goal?', optional: true },
  { field: 'life_goal_4',                       question: 'This is optional. What is your fourth life goal?', optional: true },
  { field: 'life_goal_5',                       question: 'This is optional. What is your fifth life goal?', optional: true },
  { field: 'life_goal_6',                       question: 'This is optional. What is your sixth life goal?', optional: true },
  { field: 'life_goal_7',                       question: 'This is optional. What is your seventh life goal?', optional: true },
  { field: 'life_goal_8',                       question: 'This is optional. What is your eighth life goal?', optional: true },
  { field: 'life_goal_9',                       question: 'This is optional. What is your ninth life goal?', optional: true },
  { field: 'life_goal_10',                      question: 'This is optional. What is your tenth life goal?', optional: true },
  { field: 'obstacles_to_goals',                question: 'What are the main obstacles that are preventing you from reaching these goals?' },
  { field: 'is_irritated',                      question: 'Do you sometimes feel irritated by things or people? Please say yes or no.' },
  { field: 'what_irritates_you',                question: 'What kinds of things or situations irritate or bother you the most?', dependsOn: { field: 'is_irritated', yes: true } },
  { field: 'disobeyed_social_norm',             question: 'Have you ever gone against a social norm or expectation? Please say yes or no.' },
  { field: 'disobeyed_social_norm_description', question: 'Please describe the social norm or expectation you went against.', dependsOn: { field: 'disobeyed_social_norm', yes: true } },
  { field: 'has_dreams',                        question: 'This is optional. Do you remember or experience different types of dreams? Please say yes or no.', optional: true },
  { field: 'reason_for_visiting',               question: 'Finally, what is the main reason you are visiting a therapist today, and what do you hope to gain from these sessions?' },
];

// Dream categories mirrored from the standard form so the voice flow asks
// the same options in the same order and writes the same `dream_types` values.
const DREAM_TYPES = [
  { value: 'recurring',  label: 'recurring dreams',          description: 'the same dream or similar dreams that repeat on different nights' },
  { value: 'continuing', label: 'continuing dreams',         description: 'dreams that pick up where they left off after you wake up and go back to sleep' },
  { value: 'nightmares', label: 'nightmares',                description: 'scary or disturbing dreams that may wake you up' },
  { value: 'lucid',      label: 'lucid dreams',              description: "dreams where you know you're dreaming while it's happening" },
  { value: 'anxiety',    label: 'anxiety or stress dreams',  description: 'dreams about worries, deadlines, exams, or stressful situations' },
  { value: 'flying',     label: 'flying dreams',             description: "dreams where you're flying or floating" },
  { value: 'falling',    label: 'falling dreams',            description: "dreams where you're falling" },
  { value: 'chased',     label: 'being-chased dreams',       description: 'dreams where someone or something is chasing you' },
  { value: 'people',     label: 'dreams about people you know', description: 'dreams featuring family, friends, or people from your life' },
  { value: 'abstract',   label: 'abstract or surreal dreams',description: "strange, unusual dreams that don't make logical sense" },
  { value: 'no_recall',  label: 'not remembering your dreams', description: 'you rarely or never recall your dreams' },
  { value: 'other',      label: 'other kinds of dreams',     description: 'something different you would like to describe' },
];

// Ordinal helper for prompt wording ("first language", "second language", ...).
const _ORDINALS = ['first','second','third','fourth','fifth','sixth','seventh','eighth','ninth','tenth',
                   'eleventh','twelfth','thirteenth','fourteenth','fifteenth','sixteenth','seventeenth',
                   'eighteenth','nineteenth','twentieth'];
const _ordinal = (i) => _ORDINALS[i] || `${i + 1}th`;

// Build the full Phase 1 voice question list, expanding language sub-questions
// (name / read / write / speak) for each language the client says they know,
// and filtering out any question whose `dependsOn` prerequisite isn't met
// (e.g. we don't ask "describe your disability" if the client said "No").
const buildPhase1VoiceQuestions = (formData) => {
  const count = Math.max(0, Math.min(20, parseInt(formData?.language_count) || 0));
  const isYes = (v) => String(v || '').trim().toLowerCase() === 'yes';
  const dependencyMet = (q) => {
    if (!q.dependsOn) return true;
    const parent = formData?.[q.dependsOn.field];
    // The dependency is expressed as `{ field, yes: true }` — the follow-up is
    // only asked when the parent Yes/No answer is literally "Yes".
    return q.dependsOn.yes ? isYes(parent) : !isYes(parent);
  };
  const out = [];
  for (const q of PHASE1_VOICE_QUESTIONS_BASE) {
    if (!dependencyMet(q)) continue;
    out.push(q);
    if (q.field === 'language_count' && count > 0) {
      for (let i = 0; i < count; i++) {
        const langName = (formData?.languages?.[i]?.name || '').trim();
        const label = langName ? `the language "${langName}"` : `the ${_ordinal(i)} language`;
        out.push({
          field: `language_${i}_name`,
          question: `What is the name of the ${_ordinal(i)} language you can read, write, or speak?`,
          languageIndex: i,
          languageSubField: 'name',
        });
        out.push({
          field: `language_${i}_can_read`,
          question: `Can you read ${label}? Please say yes or no.`,
          languageIndex: i,
          languageSubField: 'can_read',
        });
        out.push({
          field: `language_${i}_can_write`,
          question: `Can you write ${label}? Please say yes or no.`,
          languageIndex: i,
          languageSubField: 'can_write',
        });
        out.push({
          field: `language_${i}_can_speak`,
          question: `Can you speak ${label}? Please say yes or no.`,
          languageIndex: i,
          languageSubField: 'can_speak',
        });
      }
    }
    // After the `has_dreams` gate, expand into per-dream check + description questions.
    if (q.field === 'has_dreams' && String(formData?.has_dreams || '').toLowerCase() === 'yes') {
      const selected = new Set((formData?.dream_types || []));
      for (const dt of DREAM_TYPES) {
        out.push({
          field: `_dream_check_${dt.value}`,
          question: `Do you have ${dt.label} — ${dt.description}? Please say yes or no.`,
          dreamValue: dt.value,
          dreamCheck: true,
          optional: true,
        });
        // Only ask for a description if the client checked this dream. Also skip
        // description for `no_recall` (nothing to describe) and `other`
        // (handled by the shared `dream_other_description` textarea in the form).
        if (selected.has(dt.value) && dt.value !== 'no_recall' && dt.value !== 'other') {
          out.push({
            field: `_dream_desc_${dt.value}`,
            question: `Please describe your ${dt.label} — what happens, how they make you feel, how often, and anything else you want to share.`,
            dreamValue: dt.value,
            dreamDesc: true,
            optional: true,
          });
        }
        if (selected.has(dt.value) && dt.value === 'other') {
          out.push({
            field: '_dream_other_description',
            question: 'Please describe the other kinds of dreams you have.',
            dreamValue: 'other',
            dreamOther: true,
            optional: true,
          });
        }
      }
    }
  }
  return out;
};

// ---------------------------------------------------------------------------
// Dropdown fields in Phase 1 that must match one of a fixed set of values.
// For each field we list the canonical value (as stored in formData / the
// form's <option value=...>) mapped to the natural-language synonyms the
// client is likely to speak. Matching is case-insensitive and uses word
// boundaries so "no" doesn't match inside "know".
// ---------------------------------------------------------------------------
const PHASE1_OPTIONS_MAP = {
  gender: {
    'Male':        ['male', 'man', 'boy', 'm'],
    'Female':      ['female', 'woman', 'girl', 'f'],
    'Transgender': ['transgender', 'trans', 'non binary', 'nonbinary', 'non-binary', 'other'],
  },
  marital_status: {
    'Single':   ['single', 'unmarried', 'never married', 'not married', 'bachelor', 'spinster'],
    'Married':  ['married', 'wed', 'wedded', 'in a marriage'],
    'Divorced': ['divorced', 'divorce', 'separated', 'seperated'],
    'Widowed':  ['widowed', 'widow', 'widower', 'lost my spouse'],
  },
  working_status: {
    'working':                   ['working', 'employed', 'self employed', 'self-employed', 'freelance', 'freelancer', 'business', 'businessman', 'businesswoman', 'student', 'studying', 'yes', 'currently working'],
    'presently not working':     ['presently not working', 'not working', 'unemployed', 'jobless', 'no', 'between jobs', 'on break', 'career break', 'looking for a job', 'homemaker', 'housewife'],
    'retired':                   ['retired', 'retirement', 'pensioner'],
  },
  disability:                { 'Yes': ['yes','yeah','yep','yup','affirmative','true','correct'], 'No': ['no','nope','nah','negative','false','none'] },
  medical_issues:            { 'Yes': ['yes','yeah','yep','yup','affirmative','true','correct'], 'No': ['no','nope','nah','negative','false','none'] },
  substance_abuse_history:   { 'Yes': ['yes','yeah','yep','yup','affirmative','true','correct'], 'No': ['no','nope','nah','negative','false','none'] },
  dependent:                 { 'Yes': ['yes','yeah','yep','yup','affirmative','true','correct'], 'No': ['no','nope','nah','negative','false','none'] },
  is_irritated:              { 'Yes': ['yes','yeah','yep','yup','affirmative','true','correct'], 'No': ['no','nope','nah','negative','false','none'] },
  disobeyed_social_norm:     { 'Yes': ['yes','yeah','yep','yup','affirmative','true','correct'], 'No': ['no','nope','nah','negative','false','none'] },
  has_dreams:                { 'Yes': ['yes','yeah','yep','yup','affirmative','true','correct','i do','sometimes','often'], 'No': ['no','nope','nah','negative','false','none','never','rarely'] },
};

// Return the canonical dropdown value for a spoken answer, or null if none matched.
const matchPhase1DropdownValue = (field, spoken) => {
  const map = PHASE1_OPTIONS_MAP[field];
  if (!map || spoken === null || spoken === undefined) return null;
  const lc = String(spoken).toLowerCase().trim();
  if (!lc) return null;
  // Prefer longer synonyms first so "self employed" beats "employed" when both apply.
  const entries = Object.entries(map).flatMap(([canon, syns]) => syns.map((s) => [canon, s]));
  entries.sort((a, b) => b[1].length - a[1].length);
  for (const [canon, syn] of entries) {
    const pattern = new RegExp(`\\b${syn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (pattern.test(lc)) return canon;
  }
  return null;
};

// Phase 1: Demographics
const Phase1 = ({ sessionId, settings, onComplete, showCompletionModal, isViewOnlyMode }) => {
  const { notification, showSuccess, showError, closeNotification } = useNotification();
  const [formData, setFormData] = useState({
    date_of_test: new Date().toISOString().split('T')[0],
    name: '',
    nationality: '',
    gender: '',
    disability: '',
    disability_description: '',
    date_of_birth: '',
    state: '',
    age: '',
    religion: '',
    language_count: 0,
    languages: [],
    education: '',
    occupation: '',
    working_status: '',
    marital_status: '',
    number_of_children: 0,
    medical_issues: '',
    medical_issues_description: '',
    substance_abuse_history: '',
    substance_abuse_description: '',
    dependent: '',
    dependent_explanation: '',
    siblings_sisters: 0,
    siblings_brothers: 0,
    friends_in_touch: 0,
    family_members_description: '',
    life_goal_1: '',
    life_goal_2: '',
    life_goal_3: '',
    life_goal_4: '',
    life_goal_5: '',
    life_goal_6: '',
    life_goal_7: '',
    life_goal_8: '',
    life_goal_9: '',
    life_goal_10: '',
    obstacles_to_goals: '',
    is_irritated: '',
    what_irritates_you: '',
    disobeyed_social_norm: '',
    disobeyed_social_norm_description: '',
    reason_for_visiting: '',
    dream_types: [],
    dream_other_description: '',
    dream_descriptions: {},
    has_dreams: '',
    [PHASE1_SKIPPED_KEY]: {},
  });
  const [loading, setLoading] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [errors, setErrors] = useState({});
  const [phase1Correction, setPhase1Correction] = useState(null);
  const phase1FormDataRef = useRef(formData);
  useEffect(() => { phase1FormDataRef.current = formData; }, [formData]);
  useEffect(() => {
    if (!isClientTextCorrectionEnabled(settings)) setPhase1Correction(null);
  }, [settings?.client_text_correction_enabled]);
  const phase1CorrectionProtected = new Set([
    'name', 'nationality', 'state', 'religion', 'date_of_test', 'date_of_birth', 'age',
    'language_count', 'number_of_children', 'siblings_sisters', 'siblings_brothers', 'friends_in_touch',
    'gender', 'disability', 'marital_status', 'working_status', 'medical_issues',
    'substance_abuse_history', 'dependent', 'is_irritated', 'disobeyed_social_norm', 'has_dreams',
  ]);
  const offerPhase1Correction = (event) => {
    if (!isClientTextCorrectionEnabled(settings)) return;
    const value = event?.target?.value;
    if (typeof value !== 'string' || !value.trim()) return;
    // Child controls update formData in their own onChange handlers. Resolve
    // against the post-render ref on the next task so the parent form can
    // offer a suggestion while typing without rewriting the original value.
    window.setTimeout(() => {
      const matches = Object.entries(phase1FormDataRef.current).filter(([key, fieldValue]) =>
        !phase1CorrectionProtected.has(key) && typeof fieldValue === 'string' && fieldValue === value
      );
      if (matches.length !== 1) return;
      const [field] = matches[0];
      const suggestion = getTextCorrectionSuggestion(value);
      setPhase1Correction(suggestion ? { field, ...suggestion } : null);
    }, 0);
  };

  // Phase 1 voice interview state
  const [phase1Started, setPhase1Started] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  // Set to true when the client finishes the voice interview and lands on the
  // text form for a final cross-check. Drives the amber "Review & Submit" banner.
  const [phase1ReviewMode, setPhase1ReviewMode] = useState(false);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(
    typeof window !== 'undefined' ? window.localStorage.getItem('phase1_voice_uri') || '' : ''
  );
  const [voiceQIdx, setVoiceQIdx] = useState(0);
  // When set, the voice flow only walks through the skipped questions
  // (used after clicking Submit with gaps, or "Answer now" in the review).
  const [voiceReviewOnlyMissing, setVoiceReviewOnlyMissing] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem('phase1_voice_uri', selectedVoiceURI || '');
  }, [selectedVoiceURI]);

  useEffect(() => {
    fetchExisting();
  }, []);

  const fetchExisting = async () => {
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/phase1`);
      const phase1 = res.data && typeof res.data === 'object' ? res.data : {};
      if (phase1.name || phase1.age) {
        // Historical records predate draft-skip and correction metadata. Keep
        // every persisted value verbatim while supplying safe render shapes.
        const hydrated = {
          ...formData,
          ...phase1,
          languages: Array.isArray(phase1.languages) ? phase1.languages : [],
          dream_types: Array.isArray(phase1.dream_types) ? phase1.dream_types : [],
          dream_descriptions: phase1.dream_descriptions && typeof phase1.dream_descriptions === 'object'
            ? phase1.dream_descriptions : {},
          correction_metadata: phase1.correction_metadata && typeof phase1.correction_metadata === 'object'
            ? phase1.correction_metadata : {},
          [PHASE1_SKIPPED_KEY]: phase1[PHASE1_SKIPPED_KEY] && typeof phase1[PHASE1_SKIPPED_KEY] === 'object'
            ? phase1[PHASE1_SKIPPED_KEY] : {},
        };
        setExistingData(hydrated);
        setFormData(hydrated);
      }
    } catch (error) {}
  };

  // Auto-save integration — only auto-save while the form is in-progress
  // (not after final submission or when viewing a completed phase).
  const autosaveEnabled = !existingData && !isViewOnlyMode;
  const { status: autoSaveStatus, savedAt: autoSavedAt, loadDraft, clearDraft } = useAutoSave({
    sessionId,
    phaseNumber: 1,
    data: formData,
    enabled: autosaveEnabled,
  });
  const [draftLoaded, setDraftLoaded] = useState(false);
  useEffect(() => {
    if (draftLoaded || existingData) return;
    (async () => {
      const draft = await loadDraft();
      if (draft && Object.keys(draft).length > 0) {
        setFormData((prev) => ({
          ...prev,
          ...draft,
          languages: Array.isArray(draft.languages) ? draft.languages : prev.languages,
          dream_types: Array.isArray(draft.dream_types) ? draft.dream_types : prev.dream_types,
          dream_descriptions: draft.dream_descriptions && typeof draft.dream_descriptions === 'object'
            ? draft.dream_descriptions : prev.dream_descriptions,
          correction_metadata: draft.correction_metadata && typeof draft.correction_metadata === 'object'
            ? draft.correction_metadata : (prev.correction_metadata || {}),
          [PHASE1_SKIPPED_KEY]: draft[PHASE1_SKIPPED_KEY] && typeof draft[PHASE1_SKIPPED_KEY] === 'object'
            ? draft[PHASE1_SKIPPED_KEY] : (prev[PHASE1_SKIPPED_KEY] || {}),
        }));
      }
      setDraftLoaded(true);
    })();
    // eslint-disable-next-line
  }, [sessionId]);
  // Auto-calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Handle date of birth change with auto age calculation
  const handleDOBChange = (e) => {
    const dob = e.target.value;
    const calculatedAge = calculateAge(dob);
    setFormData({ 
      ...formData, 
      date_of_birth: dob, 
      age: calculatedAge.toString() 
    });
    
    // Validate future date or present date
    const selectedDate = new Date(dob);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare only dates
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate >= today) {
      setErrors({ ...errors, date_of_birth: 'Date of birth must be in the past (cannot be present or future date)' });
    } else if (calculatedAge < 15) {
      // Validate minimum age requirement
      setErrors({ ...errors, date_of_birth: '⚠️ Minimum age should be 15 years for taking this analysis' });
      showError('⚠️ Minimum age should be 15 years for taking this analysis');
    } else {
      const newErrors = { ...errors };
      delete newErrors.date_of_birth;
      setErrors(newErrors);
    }
  };

  // Validate name (only alphabets and spaces)
  const handleNameChange = (e) => {
    const value = e.target.value;
    const nameRegex = /^[a-zA-Z\s]*$/;
    if (nameRegex.test(value)) {
      setFormData({ ...formData, name: value });
      const newErrors = { ...errors };
      delete newErrors.name;
      setErrors(newErrors);
    } else {
      setErrors({ ...errors, name: 'Name can only contain alphabets and spaces' });
    }
  };

  // Validate reason for visiting (minimum 20 characters)
  const handleReasonChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, reason_for_visiting: value });
    if (value.length > 0 && value.length < 20) {
      setErrors({ ...errors, reason_for_visiting: 'Reason must be at least 20 characters' });
    } else {
      const newErrors = { ...errors };
      delete newErrors.reason_for_visiting;
      setErrors(newErrors);
    }
  };

  // Handle dream type checkbox changes
  const handleDreamTypeChange = (dreamType) => {
    const currentDreamTypes = formData.dream_types || [];
    if (currentDreamTypes.includes(dreamType)) {
      // Remove if already selected
      setFormData({ 
        ...formData, 
        dream_types: currentDreamTypes.filter(type => type !== dreamType)
      });
    } else {
      // Add if not selected
      setFormData({ 
        ...formData, 
        dream_types: [...currentDreamTypes, dreamType]
      });
    }
  };

  // Handle language count change - dynamically create language fields
  const handleLanguageCountChange = (count) => {
    const numCount = parseInt(count) || 0;
    setFormData(prev => {
      const newLanguages = [];
      for (let i = 0; i < numCount; i++) {
        newLanguages.push(prev.languages[i] || {
          name: '',
          can_read: '',
          can_write: '',
          can_speak: ''
        });
      }
      return {
        ...prev,
        language_count: numCount,
        languages: newLanguages
      };
    });
  };

  // Handle individual language field changes
  const handleLanguageFieldChange = (index, field, value) => {
    setFormData(prev => {
      const newLanguages = [...prev.languages];
      newLanguages[index] = {
        ...newLanguages[index],
        [field]: value
      };
      return {
        ...prev,
        languages: newLanguages
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Comprehensive validation - ALL fields required except Goals 2-10
    const newErrors = {};

    const validatePhase1ClientText = (data, targetErrors) => {
      const fields = [
        'name', 'nationality', 'state', 'religion', 'education', 'occupation',
        'disability_description', 'medical_issues_description', 'substance_abuse_description',
        'dependent_explanation', 'family_members_description', 'life_goal_1', 'life_goal_2',
        'life_goal_3', 'life_goal_4', 'life_goal_5', 'life_goal_6', 'life_goal_7',
        'life_goal_8', 'life_goal_9', 'life_goal_10', 'obstacles_to_goals',
        'what_irritates_you', 'disobeyed_social_norm_description', 'reason_for_visiting',
        'dream_other_description',
      ];
      fields.forEach((field) => {
        if (String(data[field] ?? '').trim() && !validateClientTextInput(data[field]).valid) {
          targetErrors[field] = CLIENT_TEXT_GIBBERISH_MESSAGE;
        }
      });
      (data.languages || []).forEach((language, index) => {
        if (String(language?.name ?? '').trim() && !validateClientTextInput(language.name).valid) {
          targetErrors[`language_${index}_name`] = CLIENT_TEXT_GIBBERISH_MESSAGE;
        }
      });
      Object.entries(data.dream_descriptions || {}).forEach(([key, value]) => {
        if (String(value ?? '').trim() && !validateClientTextInput(value).valid) {
          targetErrors[`dream_desc_${key}`] = CLIENT_TEXT_GIBBERISH_MESSAGE;
        }
      });
    };

    // Name validation
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }
    
    // Gender validation
    if (!formData.gender || formData.gender.trim() === '') {
      newErrors.gender = 'Gender is required';
    }
    
    // Date of Birth validation
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    } else {
      const selectedDate = new Date(formData.date_of_birth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate >= today) {
        newErrors.date_of_birth = 'Date of birth must be in the past (cannot be present or future date)';
      }
    }
    
    // Age validation - minimum 15 years
    const calculatedAge = calculateAge(formData.date_of_birth);
    if (calculatedAge < 15) {
      newErrors.date_of_birth = '⚠️ Minimum age should be 15 years for taking this analysis';
    }
    
    // Nationality validation
    if (!formData.nationality || formData.nationality.trim() === '') {
      newErrors.nationality = 'Nationality is required';
    }
    
    // State validation
    if (!formData.state || formData.state.trim() === '') {
      newErrors.state = 'State is required';
    }
    
    // Religion validation
    if (!formData.religion || formData.religion.trim() === '') {
      newErrors.religion = 'Religion is required';
    }
    
    // Language Proficiency validation
    if (formData.language_count > 0) {
      formData.languages.forEach((lang, index) => {
        if (!lang.name || lang.name.trim() === '') {
          newErrors[`language_${index}_name`] = `Language ${index + 1} name is required`;
        }
        if (!lang.can_read) {
          newErrors[`language_${index}_read`] = `Please select if you can read Language ${index + 1}`;
        }
        if (!lang.can_write) {
          newErrors[`language_${index}_write`] = `Please select if you can write Language ${index + 1}`;
        }
        if (!lang.can_speak) {
          newErrors[`language_${index}_speak`] = `Please select if you can speak Language ${index + 1}`;
        }
      });
    }
    
    // Education validation
    if (!formData.education || formData.education.trim() === '') {
      newErrors.education = 'Education is required';
    }
    
    // Occupation validation
    if (!formData.occupation || formData.occupation.trim() === '') {
      newErrors.occupation = 'Occupation is required';
    }
    
    // Working Status validation
    if (!formData.working_status || formData.working_status.trim() === '') {
      newErrors.working_status = 'Working status is required';
    }
    
    // Disability validation
    if (!formData.disability || formData.disability.trim() === '') {
      newErrors.disability = 'Disability field is required';
    }
    
    // Conditional: If disability is "Yes", description is required
    if (formData.disability === 'Yes' && (!formData.disability_description || formData.disability_description.trim() === '')) {
      newErrors.disability_description = 'Disability description is required when disability is selected as Yes';
    }
    
    // Marriage validation
    if (!formData.marital_status || formData.marital_status.trim() === '') {
      newErrors.marital_status = 'Marital status is required';
    }

    // Number of children (0 is valid, must be provided)
    if (formData.number_of_children === null || formData.number_of_children === undefined || String(formData.number_of_children).trim() === '') {
      newErrors.number_of_children = 'Please enter number of children (0 if none)';
    }
    // Siblings
    if (formData.siblings_brothers === null || formData.siblings_brothers === undefined || String(formData.siblings_brothers).trim() === '') {
      newErrors.siblings_brothers = 'Please enter number of brothers (0 if none)';
    }
    if (formData.siblings_sisters === null || formData.siblings_sisters === undefined || String(formData.siblings_sisters).trim() === '') {
      newErrors.siblings_sisters = 'Please enter number of sisters (0 if none)';
    }
    // Friends in touch
    if (formData.friends_in_touch === null || formData.friends_in_touch === undefined || String(formData.friends_in_touch).trim() === '') {
      newErrors.friends_in_touch = 'Please enter number of close friends (0 if none)';
    }
    // Has-dreams gate — dreams are OPTIONAL, so no validation error is raised.
    
    // Medical Issues validation
    if (!formData.medical_issues || formData.medical_issues.trim() === '') {
      newErrors.medical_issues = 'Medical issues field is required';
    }
    
    // Conditional: If medical issues is "Yes", description is required
    if (formData.medical_issues === 'Yes' && (!formData.medical_issues_description || formData.medical_issues_description.trim() === '')) {
      newErrors.medical_issues_description = 'Medical issues description is required when medical issues is selected as Yes';
    }
    
    // Substance Abuse History validation
    if (!formData.substance_abuse_history || formData.substance_abuse_history.trim() === '') {
      newErrors.substance_abuse_history = 'Substance abuse history field is required';
    }
    
    // Conditional: If substance abuse is "Yes", description is required
    if (formData.substance_abuse_history === 'Yes' && (!formData.substance_abuse_description || formData.substance_abuse_description.trim() === '')) {
      newErrors.substance_abuse_description = 'Substance abuse description is required when history is selected as Yes';
    }
    
    // Financially Dependent validation
    if (!formData.dependent || formData.dependent.trim() === '') {
      newErrors.dependent = 'Financial dependency field is required';
    }
    
    // Conditional: If dependent is "Yes", explanation is required
    if (formData.dependent === 'Yes' && (!formData.dependent_explanation || formData.dependent_explanation.trim() === '')) {
      newErrors.dependent_explanation = 'Explanation is required when financially dependent is selected as Yes';
    }
    
    // Family Members Description validation (required)
    if (!formData.family_members_description || formData.family_members_description.trim() === '') {
      newErrors.family_members_description = 'Family members description is required';
    }
    
    // Career Goal 1 validation (only Goal 1 is mandatory)
    if (!formData.life_goal_1 || formData.life_goal_1.trim() === '') {
      newErrors.life_goal_1 = 'At least one goal is required';
    }
    
    // Obstacles to Goals validation (required)
    if (!formData.obstacles_to_goals || formData.obstacles_to_goals.trim() === '') {
      newErrors.obstacles_to_goals = 'Please describe what is stopping you from achieving your goals';
    }
    
    // Disobeyed Social Norm validation
    if (!formData.disobeyed_social_norm || formData.disobeyed_social_norm.trim() === '') {
      newErrors.disobeyed_social_norm = 'Please select an option';
    }
    
    // Conditional: If disobeyed social norm is "Yes", description is required
    if (formData.disobeyed_social_norm === 'Yes' && (!formData.disobeyed_social_norm_description || formData.disobeyed_social_norm_description.trim() === '')) {
      newErrors.disobeyed_social_norm_description = 'Description is required when you select Yes';
    }
    
    // Irritation validation: Yes/No must be selected
    if (!formData.is_irritated || formData.is_irritated.trim() === '') {
      newErrors.is_irritated = 'Please select an option';
    }
    // Conditional: If irritated is "Yes", description is required
    if (formData.is_irritated === 'Yes' && (!formData.what_irritates_you || formData.what_irritates_you.trim() === '')) {
      newErrors.what_irritates_you = 'Description is required when you select Yes';
    }
    
    // Reason for Consultation validation
    if (!formData.reason_for_visiting || formData.reason_for_visiting.length < 20) {
      newErrors.reason_for_visiting = 'Reason for consultation must be at least 20 characters';
    }
    
    // Dream Descriptions validation — every checked dream (except 'other') requires a description
    const dreamLabelMap = {
      recurring: 'Recurring dreams',
      continuing: 'Continuing dreams',
      nightmares: 'Nightmares',
      lucid: 'Lucid dreams',
      anxiety: 'Anxiety or stress dreams',
      flying: 'Flying dreams',
      falling: 'Falling dreams',
      chased: 'Being chased',
      people: 'Dreams about people I know',
      abstract: 'Abstract or surreal dreams',
      no_recall: "I don't remember my dreams",
      other: 'Other'
    };
    (formData.dream_types || []).forEach((dt) => {
      if (dt === 'other') return;
      if (formData[PHASE1_SKIPPED_KEY]?.[`_dream_desc_${dt}`]) return;
      const desc = (formData.dream_descriptions || {})[dt];
      if (!desc || !desc.trim()) {
        newErrors[`dream_desc_${dt}`] = `Please describe your ${(dreamLabelMap[dt] || dt).toLowerCase()}`;
      }
    });

    // Run after required/format checks so an entered gibberish value always
    // receives the neutral inline message instead of being treated as missing.
    validatePhase1ClientText(formData, newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Compute the missing answers inline (walking the same voice question list
      // the render section builds) so we don't rely on any later-defined helper
      // being in scope for this event handler.
      const missing = [];
      const voiceList = buildPhase1VoiceQuestions(formData);
      const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';
      voiceList.forEach((vq, i) => {
        let value;
        if (vq.languageIndex !== undefined && vq.languageSubField) {
          value = (formData.languages || [])[vq.languageIndex]?.[vq.languageSubField];
        } else if (vq.dreamCheck) {
          return; // dream_check answers are captured via dream_types; nothing to prompt for here
        } else if (vq.dreamDesc) {
          value = (formData.dream_descriptions || {})[vq.dreamValue];
        } else if (vq.dreamOther) {
          value = formData.dream_other_description;
        } else {
          value = formData[vq.field];
        }
        if (vq.field === 'reason_for_visiting' && !isBlank(value) && String(value).trim().length < 20) {
          missing.push({ field: vq.field, question: vq.question, jumpIndex: i });
          return;
        }
        // Optional questions (goals 2-10, etc.) never block submission.
        if (vq.optional) return;
        if (isBlank(value)) missing.push({ field: vq.field, question: vq.question, jumpIndex: i });
      });
      // Debug trace so we can confirm the exact question the flow is jumping to.
      // eslint-disable-next-line no-console
      console.log('[Phase1] Submit blocked — missing answers:', missing.map(m => ({ field: m.field, idx: m.jumpIndex })));
      if (missing.length > 0 && typeof missing[0].jumpIndex === 'number') {
        setPhase1Started(true);
        setVoiceReviewOnlyMissing(true);
        setVoiceMode(true);
        setVoiceQIdx(missing[0].jumpIndex);
        showError(`${missing.length} question${missing.length > 1 ? 's' : ''} still need${missing.length === 1 ? 's' : ''} an answer — walking you through only those now.`);
      } else {
        showError(`Please fix ${Object.keys(newErrors).length} validation error(s) before submitting`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    
    setLoading(true);
    try {
      // Safety net: always recompute age from DOB so voice-mode and draft-loaded
      // flows never send an empty/NaN age to the backend.
      const safeAge = calculateAge(formData.date_of_birth);
      await axios.post(`${API}/sessions/${sessionId}/phase1`, {
        ...withoutPhase1DraftMetadata(formData),
        age: parseInt(safeAge || formData.age),
      });
      clearDraft();
      showCompletionModal(1);
      onComplete();
    } catch (error) {
      showError('Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  // Show completed view only if data exists AND it's complete (has been submitted)
  // Check if at least some key required fields are filled
  if (existingData && existingData.name && existingData.age) {
    return (
      <Card className="mt-6" data-testid="phase1-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Phase 1 Complete - Client Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Personal Identification & Demographics */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#2563EB' }}>Personal Identification & Demographics</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Name</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.name || 'Undisclosed'}</p></div>
                <div><Label>Date of Birth</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.date_of_birth || 'Undisclosed'}</p></div>
                <div><Label>Age</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.age || 'Undisclosed'}</p></div>
                <div><Label>Gender</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.gender || 'Undisclosed'}</p></div>
                <div><Label>Nationality</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.nationality || 'Undisclosed'}</p></div>
                <div><Label>State</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.state || 'Undisclosed'}</p></div>
                <div><Label>Religion</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.religion || 'Undisclosed'}</p></div>
              </div>
            </div>

            {/* Language Proficiency */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#0D9488' }}>Language Proficiency</h3>
              <div>
                <Label>Number of Languages</Label>
                <p className="text-sm text-gray-600">Client Response:</p>
                <p className="text-gray-700">{existingData.language_count || 0} language(s)</p>
                
                {existingData.languages && existingData.languages.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {existingData.languages.map((lang, index) => (
                      <div key={index} className="p-3 bg-teal-50 rounded border border-teal-200">
                        <h4 className="font-semibold text-teal-900 mb-2">Language {index + 1}: {lang.name || 'N/A'}</h4>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Read:</span>{' '}
                            <span className={lang.can_read === 'Yes' ? 'text-green-600' : 'text-red-600'}>
                              {lang.can_read || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Write:</span>{' '}
                            <span className={lang.can_write === 'Yes' ? 'text-green-600' : 'text-red-600'}>
                              {lang.can_write || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Speak:</span>{' '}
                            <span className={lang.can_speak === 'Yes' ? 'text-green-600' : 'text-red-600'}>
                              {lang.can_speak || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Education & Career */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#2563eb' }}>Education & Career</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Education</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.education || 'Undisclosed'}</p></div>
                <div><Label>Occupation</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.occupation || 'Undisclosed'}</p></div>
                <div><Label>Are you still working?</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.working_status || 'Undisclosed'}</p></div>
              </div>
              
              {/* Career Goals Section */}
              <div className="mt-4">
                <Label className="text-base font-semibold">Goals (10 Goals)</Label>
                <div className="mt-2 space-y-2">
                  {existingData.life_goal_1 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 1:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_1}</p>
                    </div>
                  )}
                  {existingData.life_goal_2 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 2:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_2}</p>
                    </div>
                  )}
                  {existingData.life_goal_3 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 3:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_3}</p>
                    </div>
                  )}
                  {existingData.life_goal_4 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 4:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_4}</p>
                    </div>
                  )}
                  {existingData.life_goal_5 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 5:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_5}</p>
                    </div>
                  )}
                  {existingData.life_goal_6 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 6:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_6}</p>
                    </div>
                  )}
                  {existingData.life_goal_7 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 7:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_7}</p>
                    </div>
                  )}
                  {existingData.life_goal_8 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 8:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_8}</p>
                    </div>
                  )}
                  {existingData.life_goal_9 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 9:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_9}</p>
                    </div>
                  )}
                  {existingData.life_goal_10 && (
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-xs font-semibold text-green-800">Goal 10:</p>
                      <p className="text-sm text-gray-700">{existingData.life_goal_10}</p>
                    </div>
                  )}
                  {!existingData.life_goal_1 && (
                    <p className="text-sm text-gray-600 italic">No goals provided</p>
                  )}
                </div>
              </div>
            </div>

            {/* Obstacles to Goals */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#2563eb' }}>Obstacles to Goals</h3>
              <div><Label>What do you think is stopping you to achieve your goals?</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700 whitespace-pre-line">{existingData.obstacles_to_goals || 'Undisclosed'}</p></div>
            </div>

            {/* What Irritates You */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#2563eb' }}>What Irritates You</h3>
              {existingData.is_irritated === 'Yes' ? (
                <div>
                  <Label>What irritates you?</Label>
                  <p className="text-sm text-gray-600">Client Response:</p>
                  <p className="text-gray-700 whitespace-pre-line" data-testid="what-irritates-you-readonly">
                    {existingData.what_irritates_you || 'Undisclosed'}
                  </p>
                </div>
              ) : (
                <p className="text-gray-700 italic" data-testid="what-irritates-you-readonly">
                  The client is not irritated by anything.
                </p>
              )}
            </div>

            {/* Social Norm */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#9333EA' }}>Social Norms</h3>
              <div className="space-y-3">
                <div><Label>Have you ever disobeyed any social norm?</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.disobeyed_social_norm || 'Undisclosed'}</p></div>
                {existingData.disobeyed_social_norm === 'Yes' && existingData.disobeyed_social_norm_description && (
                  <div><Label>What is it you disobeyed?</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700 whitespace-pre-line">{existingData.disobeyed_social_norm_description}</p></div>
                )}
              </div>
            </div>

            {/* Marital & Family */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#2563EB' }}>Marital & Family Structure</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Marital Status</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.marital_status || 'Undisclosed'}</p></div>
                <div><Label>Number of Children</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.number_of_children !== undefined && existingData.number_of_children !== null ? existingData.number_of_children : 'Undisclosed'}</p></div>
                <div><Label>Brothers</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.siblings_brothers !== undefined && existingData.siblings_brothers !== null ? existingData.siblings_brothers : 'Undisclosed'}</p></div>
                <div><Label>Sisters</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.siblings_sisters !== undefined && existingData.siblings_sisters !== null ? existingData.siblings_sisters : 'Undisclosed'}</p></div>
              </div>
            </div>

            {/* Health & Dependency */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#EF4444' }}>Health & Dependency</h3>
              
              {/* Disability */}
              <div className="mb-4">
                <Label>Disability</Label>
                <p className="text-sm text-gray-600">Client Response:</p>
                <p className="text-gray-700">{existingData.disability || 'Undisclosed'}</p>
                {existingData.disability === 'Yes' && existingData.disability_description && (
                  <div className="mt-2 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-semibold text-gray-700">Description:</p>
                    <p className="text-sm text-gray-600">{existingData.disability_description}</p>
                  </div>
                )}
              </div>

              {/* Medical Issues */}
              <div className="mb-4">
                <Label>Medical Issues</Label>
                <p className="text-sm text-gray-600">Client Response:</p>
                <p className="text-gray-700">{existingData.medical_issues || 'Undisclosed'}</p>
                {existingData.medical_issues === 'Yes' && existingData.medical_issues_description && (
                  <div className="mt-2 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-semibold text-gray-700">Description:</p>
                    <p className="text-sm text-gray-600">{existingData.medical_issues_description}</p>
                  </div>
                )}
              </div>

              {/* Substance Abuse */}
              <div className="mb-4">
                <Label>Substance Abuse History</Label>
                <p className="text-sm text-gray-600">Client Response:</p>
                <p className="text-gray-700">{existingData.substance_abuse_history || 'Undisclosed'}</p>
                {existingData.substance_abuse_history === 'Yes' && existingData.substance_abuse_description && (
                  <div className="mt-2 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-semibold text-gray-700">Description:</p>
                    <p className="text-sm text-gray-600">{existingData.substance_abuse_description}</p>
                  </div>
                )}
              </div>

              {/* Financial Dependency */}
              <div className="mb-4">
                <Label>Financially Dependent</Label>
                <p className="text-sm text-gray-600">Client Response:</p>
                <p className="text-gray-700">{existingData.dependent || 'Undisclosed'}</p>
                {existingData.dependent === 'Yes' && existingData.dependent_explanation && (
                  <div className="mt-2 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-semibold text-gray-700">Explanation:</p>
                    <p className="text-sm text-gray-600">{existingData.dependent_explanation}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Social & Session Details */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#2563EB' }}>Social Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Number of Friends in Touch</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.friends_in_touch !== undefined && existingData.friends_in_touch !== null ? existingData.friends_in_touch : 'Undisclosed'}</p></div>
              </div>
            </div>

            {/* Family Members Description */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#2563EB' }}>Family Information</h3>
              <div><Label>Tell me about your family members</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700 whitespace-pre-line">{existingData.family_members_description || 'Undisclosed'}</p></div>
            </div>

            {/* Presenting Issue */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#2563EB' }}>Presenting Issue</h3>
              <div><Label>Reason For Visiting</Label><p className="text-sm text-gray-600">Client Response:</p><p className="text-gray-700">{existingData.reason_for_visiting || 'Undisclosed'}</p></div>
            </div>

            {/* Dream Experiences */}
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ color: '#4F46E5' }}>Dream Experiences</h3>
              <div>
                <Label>Types of dreams typically experienced</Label>
                <p className="text-sm text-gray-600">Client Response:</p>
                {existingData.dream_types && existingData.dream_types.length > 0 ? (
                  <div className="space-y-2 mt-2">
                    {existingData.dream_types.map((dreamType, idx) => {
                      const dreamLabels = {
                        'recurring': 'Recurring dreams',
                        'continuing': 'Continuing dreams',
                        'nightmares': 'Nightmares',
                        'lucid': 'Lucid dreams',
                        'anxiety': 'Anxiety or stress dreams',
                        'flying': 'Flying dreams',
                        'falling': 'Falling dreams',
                        'chased': 'Being chased',
                        'people': 'Dreams about people I know',
                        'abstract': 'Abstract or surreal dreams',
                        'no_recall': 'I don\'t remember my dreams',
                        'other': 'Other'
                      };
                      return (
                        <div key={idx}>
                          <div className="flex items-center space-x-2">
                            <span className="text-indigo-600">✓</span>
                            <span className="text-gray-700 font-medium">{dreamLabels[dreamType] || dreamType}</span>
                          </div>
                          {existingData.dream_descriptions && existingData.dream_descriptions[dreamType] && dreamType !== 'other' && (
                            <div className="ml-6 mt-1 p-2 bg-indigo-50 rounded text-sm">
                              <p className="text-gray-700 whitespace-pre-line">{existingData.dream_descriptions[dreamType]}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {existingData.dream_other_description && (
                      <div className="mt-3 p-3 bg-indigo-50 rounded">
                        <Label className="text-sm">Additional dream details:</Label>
                        <p className="text-gray-700 mt-1 whitespace-pre-line">{existingData.dream_other_description}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-700">No dream information provided</p>
                )}
              </div>
            </div>

            <p className="text-green-600 font-semibold mt-4" data-testid="phase1-completed-message">✓ Phase 1 completed</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getQuestionValueForResume = (vq, data) => {
    if (vq.languageIndex !== undefined && vq.languageSubField) {
      return (data.languages || [])[vq.languageIndex]?.[vq.languageSubField];
    }
    if (vq.dreamCheck) return (data.dream_types || []).includes(vq.dreamValue) ? 'Yes' : '';
    if (vq.dreamDesc) return (data.dream_descriptions || {})[vq.dreamValue];
    if (vq.dreamOther) return data.dream_other_description;
    return data[vq.field];
  };

  const resumeVoiceInterview = () => {
    const questions = buildPhase1VoiceQuestions(formData);
    const nextIndex = firstIncompletePhase1QuestionIndex(questions, formData, getQuestionValueForResume);
    setVoiceReviewOnlyMissing(false);
    setVoiceQIdx(nextIndex);
    setVoiceMode(true);
    setPhase1Started(true);
  };

  // ------------------------------------------------------------------
  // INTRO SCREEN — shown before the client starts the demographics form
  // ------------------------------------------------------------------
  if (!phase1Started && !existingData) {
    return (
      <Card className="mt-6" data-testid="phase1-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Client Demographics & Background</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-10" data-testid="phase1-intro">
            <FileText className="w-16 h-16 mx-auto mb-4 text-blue-600" />
            <h3 className="text-xl font-semibold mb-4">Ready to begin your background interview?</h3>

            <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="phase1-numeric-summary">
              <div className="p-4 rounded-lg bg-blue-50 border-2 border-blue-200">
                <p className="text-3xl font-bold text-blue-700">{PHASE1_VOICE_QUESTIONS_BASE.length}+</p>
                <p className="text-xs uppercase tracking-wide text-blue-800 mt-1">Voice questions (voice mode)</p>
              </div>
              <div className="p-4 rounded-lg bg-indigo-50 border-2 border-indigo-200">
                <p className="text-3xl font-bold text-indigo-700">All</p>
                <p className="text-xs uppercase tracking-wide text-indigo-800 mt-1">Fields optional to skip</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 border-2 border-amber-200">
                <p className="text-3xl font-bold text-amber-700">Self-Paced</p>
                <p className="text-xs uppercase tracking-wide text-amber-800 mt-1">No time limit</p>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left inline-block max-w-2xl">
              <p className="font-semibold text-blue-900 mb-2">How this phase works</p>
              <ul className="text-sm text-gray-700 ml-4 mb-3 space-y-1">
                <li>• Choose <strong>Voice interview</strong> to have each question read to you and answer by speaking</li>
                <li>• Or choose <strong>Text form</strong> to fill in everything by typing</li>
                <li>• In voice mode, you can tap <strong>&quot;Type instead&quot;</strong> for any question at any time</li>
                <li>• Structured questions (age, dropdowns, dates) always use their normal input</li>
              </ul>
              <p className="text-sm font-semibold text-blue-900">🎤 Microphone permission may be requested by your browser.</p>
            </div>

            <div className="mb-4 flex items-center justify-center gap-2 flex-wrap">
              <NarrateButton
                text={`Phase 1: Basic information. Choose voice interview to have each question read aloud and answer by speaking, or choose text form to type your answers. In voice mode, you can tap Type Instead for any question at any time. Structured questions like age, dates, and dropdowns always use their normal input. Your microphone permission may be requested by your browser.`}
                label="🎧 Narrate the rules"
                selectedVoiceURI={selectedVoiceURI}
                testId="phase1-intro-narrate-rules-btn"
              />
              <VoicePicker selectedVoiceURI={selectedVoiceURI} onChange={setSelectedVoiceURI} />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                onClick={resumeVoiceInterview}
                className="px-8 py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
                data-testid="phase1-start-voice-btn"
              >
                <Volume2 className="w-5 h-5 mr-2" />
                Start Voice Interview
              </Button>
              <Button
                onClick={() => { setVoiceMode(false); setPhase1Started(true); }}
                variant="outline"
                className="px-8 py-6 text-lg"
                data-testid="phase1-start-text-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Start Text Form
              </Button>
            </div>
            <VoicePrivacyNotice className="mt-6" testId="phase1-voice-privacy-notice" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // ------------------------------------------------------------------
  // VOICE INTERVIEW SCREEN — walks through dynamically-built questions one by one.
  // The list expands automatically once `language_count` is captured so each
  // language gets its own name / read / write / speak questions.
  // ------------------------------------------------------------------
  const phase1VoiceQuestions = buildPhase1VoiceQuestions(formData);

  // Determine which mandatory Phase 1 answers are still missing.
  // Walks the actual voice-interview list — so the review lists exactly the
  // questions the client saw and skipped, and nothing else. Follow-ups whose
  // parent Yes/No is "No" (or blank) are already excluded because the builder
  // itself filters them out via `dependsOn`.
  const computeMissingPhase1Answers = () => {
    const missing = [];
    const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';
    phase1VoiceQuestions.forEach((vq, i) => {
      let value;
      if (vq.languageIndex !== undefined && vq.languageSubField) {
        value = (formData.languages || [])[vq.languageIndex]?.[vq.languageSubField];
      } else if (vq.dreamCheck) {
        // A dream check is considered answered as soon as the client has decided
        // Yes (in dream_types) *or* No (has_dreams is Yes but this dream isn't
        // ticked). We can't tell "No" from "never asked", so require the client
        // to have completed the whole flow. Once they finish the interview and
        // reach this review screen, any dream_check without an entry means it
        // was truly skipped.
        value = (formData.dream_types || []).includes(vq.dreamValue) ? 'Yes' : '';
        // Skip flagging dream_checks entirely — a "No" answer looks identical
        // to an unanswered one at this layer, and the important gate is
        // has_dreams itself + description of any dream that was ticked.
        return;
      } else if (vq.dreamDesc) {
        value = (formData.dream_descriptions || {})[vq.dreamValue];
      } else if (vq.dreamOther) {
        value = formData.dream_other_description;
      } else {
        value = formData[vq.field];
      }
      // Special case: reason_for_visiting also enforces a 20-char minimum.
      if (vq.field === 'reason_for_visiting' && !isBlank(value) && String(value).trim().length < 20) {
        missing.push({ field: vq.field, question: vq.question, jumpIndex: i, hint: 'At least 20 characters required' });
        return;
      }
      // Optional questions (e.g. life_goal_2 through life_goal_10) are never flagged as missing.
      if (vq.optional || isPhase1QuestionSkipped(formData, vq)) return;
      if (isBlank(value)) {
        missing.push({ field: vq.field, question: vq.question, jumpIndex: i });
      }
    });
    return missing;
  };

  if (voiceMode && phase1Started && voiceQIdx < phase1VoiceQuestions.length) {
    const q = phase1VoiceQuestions[voiceQIdx];
    const total = phase1VoiceQuestions.length;

    // Advance to the next voice question. When the client skips an OPTIONAL
    // life-goal question (goals 2-10), jump past ALL remaining consecutive
    // life-goal questions to the next non-goal question — the client has
    // signalled they don't have more goals to list, no point in asking again.
    const advanceVoiceIdx = (skippedBlank) => {
      const cur = phase1VoiceQuestions[voiceQIdx];
      const isOptionalGoal = cur && cur.optional && typeof cur.field === 'string' && cur.field.startsWith('life_goal_');
      if (skippedBlank && isOptionalGoal) {
        // Walk forward until we leave the life_goal_* block.
        let j = voiceQIdx + 1;
        while (j < phase1VoiceQuestions.length && (phase1VoiceQuestions[j].field || '').startsWith('life_goal_')) {
          j += 1;
        }
        setVoiceQIdx(j);
      } else {
        setVoiceQIdx((i) => i + 1);
      }
    };
    // For language sub-questions, resolve the current value from the languages array.
    // For dream sub-questions, derive it from dream_types / dream_descriptions.
    // Always coerce to string — the VoiceQuestion component calls `.trim()` on the
    // value, which crashes if `formData[field]` is a number (e.g. language_count).
    let rawValue;
    if (q.languageIndex !== undefined && q.languageSubField) {
      rawValue = formData?.languages?.[q.languageIndex]?.[q.languageSubField] ?? '';
    } else if (q.dreamCheck) {
      // Prefer the raw transcript scratch (so free-text like "yeah maybe" or the
      // in-progress dictation is visible while typing) and fall back to the
      // canonical Yes if this dream is already in dream_types.
      const scratch = formData?.[`_dc_scratch_${q.dreamValue}`];
      if (scratch && String(scratch).trim() !== '') {
        rawValue = scratch;
      } else {
        rawValue = (formData?.dream_types || []).includes(q.dreamValue) ? 'Yes' : '';
      }
    } else if (q.dreamDesc) {
      rawValue = (formData?.dream_descriptions || {})[q.dreamValue] || '';
    } else if (q.dreamOther) {
      rawValue = formData?.dream_other_description || '';
    } else {
      rawValue = formData[q.field] ?? '';
    }
    const currentValue = rawValue === null || rawValue === undefined ? '' : String(rawValue);
    return (
      <Card className="mt-6" data-testid="phase1-voice-interview">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Voice Interview</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Question <span className="font-semibold" data-testid="voice-q-index">{voiceQIdx + 1}</span> of {total}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <AutoSaveIndicator status={autoSaveStatus} savedAt={autoSavedAt} />
              <VoicePicker
                selectedVoiceURI={selectedVoiceURI}
                onChange={setSelectedVoiceURI}
              />
            </div>
          </div>
          {/* Progress bar */}
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mt-3">
            <div
              className="h-full bg-indigo-600 transition-all"
              style={{ width: `${((voiceQIdx) / total) * 100}%` }}
              data-testid="voice-q-progress"
            />
          </div>
        </CardHeader>
        <CardContent>
          <VoiceQuestion
            key={q.field}
            question={q.question}
            helperText={q.helperText}
            value={currentValue}
            displayValue={
              q.field === 'date_of_birth' && /^\d{4}-\d{2}-\d{2}$/.test((currentValue || '').trim())
                ? (() => {
                    const [y, m, d] = currentValue.trim().split('-');
                    return `${d}-${m}-${y}`;
                  })()
                : undefined
            }
            onChange={(newVal) => {
              // Providing an answer after navigating back converts an intentional
              // optional skip into a normal answer and persists that distinction.
              setFormData((prev) => clearPhase1QuestionSkipped(prev, q));
              // Field-specific normalisation so structured answers land in the
              // right format for the text form below (dates → YYYY-MM-DD,
              // numeric fields → digit string).
              let value = newVal;
              try {
                if (q.field === 'date_of_birth' && typeof newVal === 'string') {
                  // Extract the FIRST valid date pattern from the transcript — Chrome
                  // STT occasionally delivers the same phrase twice ("March 10 1983 March 10 1983")
                  // when TTS echo bounces back. We want a single clean YYYY-MM-DD.
                  const trimmed = newVal.trim();
                  const parseCandidate = (raw) => {
                    if (!raw) return null;
                    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
                    const cleaned = raw
                      .replace(/(\d+)(st|nd|rd|th)/gi, '$1')
                      .replace(/\b(of)\b/gi, ' ')
                      .replace(/\s+/g, ' ')
                      .trim();
                    const d = new Date(cleaned);
                    if (!isNaN(d.getTime()) && d.getFullYear() > 1900 && d.getFullYear() < 2200) {
                      const y = d.getFullYear();
                      const m = String(d.getMonth() + 1).padStart(2, '0');
                      const dd = String(d.getDate()).padStart(2, '0');
                      return `${y}-${m}-${dd}`;
                    }
                    return null;
                  };
                  // Try the whole string first, then progressively shorter prefixes.
                  // This absorbs repeated phrases like "10 March 1983 10 March 1983".
                  const iso =
                    parseCandidate(trimmed) ||
                    parseCandidate(trimmed.split(/\s+/).slice(0, 5).join(' ')) ||
                    parseCandidate(trimmed.split(/\s+/).slice(0, 4).join(' ')) ||
                    parseCandidate(trimmed.split(/\s+/).slice(0, 3).join(' '));
                  if (iso) value = iso;
                } else if (
                  ['number_of_children','siblings_brothers','siblings_sisters','friends_in_touch','language_count'].includes(q.field)
                  && typeof newVal === 'string'
                ) {
                  // Extract the LAST number (digit OR word) in the spoken text.
                  // We use LAST because the mic seeds the transcript buffer
                  // with the field's current value — e.g. a default "0" would
                  // otherwise be grabbed as the first digit even after the
                  // user has spoken a new number ("0 five" → we want 5, not 0).
                  const wordMap = { zero:0, one:1, two:2, three:3, four:4, five:5, six:6, seven:7, eight:8, nine:9, ten:10, eleven:11, twelve:12 };
                  const lc = newVal.toLowerCase();
                  const re = /(\d+)|\b(zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\b/g;
                  let lastMatch = null;
                  let m;
                  while ((m = re.exec(lc)) !== null) {
                    if (m[1] !== undefined) lastMatch = m[1];
                    else if (m[2] !== undefined) lastMatch = String(wordMap[m[2]]);
                  }
                  if (lastMatch !== null) value = lastMatch;
                } else if (q.languageSubField && ['can_read','can_write','can_speak'].includes(q.languageSubField) && typeof newVal === 'string') {
                  // Normalise yes/no spoken answers to the exact "Yes"/"No" tokens the form expects.
                  const lc = newVal.toLowerCase();
                  if (/\b(yes|yeah|yep|yup|correct|true|affirmative)\b/.test(lc)) value = 'Yes';
                  else if (/\b(no|nope|nah|negative|false)\b/.test(lc)) value = 'No';
                } else if (q.languageSubField === 'name' && typeof newVal === 'string') {
                  // Capitalise the first letter and trim so "hindi" → "Hindi".
                  const trimmed = newVal.trim();
                  value = trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : trimmed;
                } else if (PHASE1_OPTIONS_MAP[q.field] && typeof newVal === 'string') {
                  // Dropdown-restricted field: map the spoken text to the canonical option
                  // (e.g. "single" → "Single", "self employed" → "working"). If no synonym
                  // matches, keep the raw transcript so the user can see what was captured
                  // — confirm() will then block and re-ask.
                  const canon = matchPhase1DropdownValue(q.field, newVal);
                  if (canon) value = canon;
                } else if (q.dreamCheck && typeof newVal === 'string') {
                  // Per-dream Yes/No — normalise to strict "Yes"/"No".
                  const lc = newVal.toLowerCase();
                  if (/\b(yes|yeah|yep|yup|correct|true|affirmative|i do|sometimes|often)\b/.test(lc)) value = 'Yes';
                  else if (/\b(no|nope|nah|negative|false|none|never|rarely)\b/.test(lc)) value = 'No';
                }
              } catch (e) { /* fall through — keep raw */ }
              // Dream check field — always keep a scratch of the raw text so the
              // transcript can echo what the client typed/said, and update
              // dream_types (add or remove) whenever the value cleanly parses
              // to Yes / No. Free-text intermediate strings are preserved.
              if (q.dreamCheck) {
                setFormData((prev) => {
                  const set = new Set(prev.dream_types || []);
                  if (value === 'Yes') set.add(q.dreamValue);
                  else if (value === 'No') set.delete(q.dreamValue);
                  return {
                    ...prev,
                    dream_types: Array.from(set),
                    [`_dc_scratch_${q.dreamValue}`]: value,
                  };
                });
                return;
              }
              // Dream description field — write to dream_descriptions[value].
              if (q.dreamDesc) {
                setFormData((prev) => ({
                  ...prev,
                  dream_descriptions: { ...(prev.dream_descriptions || {}), [q.dreamValue]: value },
                }));
                return;
              }
              if (q.dreamOther) {
                setFormData((prev) => ({ ...prev, dream_other_description: value }));
                return;
              }
              // Language sub-field write path — update the specific slot in formData.languages.
              if (q.languageIndex !== undefined && q.languageSubField) {
                const idx = q.languageIndex;
                setFormData((prev) => {
                  const next = [...(prev.languages || [])];
                  while (next.length <= idx) next.push({ name: '', can_read: '', can_write: '', can_speak: '' });
                  next[idx] = { ...next[idx], [q.languageSubField]: value };
                  return { ...prev, languages: next };
                });
                return;
              }
              // language_count captured → also initialise the languages array so
              // form + validation see the correct slots. Keep the value as a string
              // so the VoiceQuestion component (which calls `.trim()`) stays happy.
              if (q.field === 'language_count') {
                const numCount = Math.max(0, Math.min(20, parseInt(value) || 0));
                setFormData((prev) => {
                  const existing = prev.languages || [];
                  const newLanguages = [];
                  for (let i = 0; i < numCount; i++) {
                    newLanguages.push(existing[i] || { name: '', can_read: '', can_write: '', can_speak: '' });
                  }
                  return { ...prev, language_count: String(numCount), languages: newLanguages };
                });
                return;
              }
              // If DOB was captured, auto-calculate the age silently (no TTS announcement).
              if (q.field === 'date_of_birth' && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                const calcAge = calculateAge(value);
                setFormData((prev) => ({ ...prev, date_of_birth: value, age: calcAge ? String(calcAge) : '' }));
                return;
              }
              setFormData((prev) => ({ ...prev, [q.field]: value }));
            }}
            onConfirm={() => {
              // For dropdown-restricted fields, validate before advancing.
              // If the current stored value isn't one of the allowed options
              // (i.e. no synonym matched), re-narrate the question and
              // display a hint so the client can try again.
              const optMap = PHASE1_OPTIONS_MAP[q.field];
              // Yes/No language sub-fields share the same yes/no option set.
              const langYesNoField = q.languageSubField && ['can_read','can_write','can_speak'].includes(q.languageSubField);
              // Per-dream check questions also need a strict Yes/No answer.
              const dreamCheckField = Boolean(q.dreamCheck);
              if (optMap || langYesNoField || dreamCheckField) {
                const allowed = optMap ? Object.keys(optMap) : ['Yes','No'];
                const cur = String(currentValue || '').trim();
                // Try to reconcile once more (covers manually-typed answers).
                let effective = cur;
                if (optMap && !allowed.includes(cur)) {
                  const canon = matchPhase1DropdownValue(q.field, cur);
                  if (canon) effective = canon;
                }
                if ((langYesNoField || dreamCheckField) && !allowed.includes(cur)) {
                  const lc = cur.toLowerCase();
                  if (/\b(yes|yeah|yep|yup|correct|true|affirmative|i do|sometimes|often)\b/.test(lc)) effective = 'Yes';
                  else if (/\b(no|nope|nah|negative|false|none|never|rarely)\b/.test(lc)) effective = 'No';
                }
                if (!allowed.includes(effective)) {
                  const opts = allowed.join(', ');
                  const hint = `Sorry, I didn't catch a valid option. Please answer with one of: ${opts}. ${q.question}`;
                  try {
                    window.speechSynthesis?.cancel();
                    const u = new SpeechSynthesisUtterance(hint);
                    if (selectedVoiceURI) {
                      const v = window.speechSynthesis.getVoices().find((x) => x.voiceURI === selectedVoiceURI);
                      if (v) u.voice = v;
                    }
                    u.rate = 1.0;
                    window.speechSynthesis?.speak(u);
                  } catch (_) { /* ignore */ }
                  showError(`Please answer with one of: ${opts}`);
                  return;
                }
                // Persist the reconciled canonical value before advancing.
                if (langYesNoField) {
                  const idx = q.languageIndex;
                  setFormData((prev) => {
                    const next = [...(prev.languages || [])];
                    while (next.length <= idx) next.push({ name: '', can_read: '', can_write: '', can_speak: '' });
                    next[idx] = { ...next[idx], [q.languageSubField]: effective };
                    return { ...prev, languages: next };
                  });
                } else if (dreamCheckField) {
                  // Ensure dream_types reflects the reconciled Yes/No answer.
                  // Also clear the raw-text scratch so the next render shows the
                  // canonical 'Yes' / '' (empty) without lingering free text.
                  setFormData((prev) => {
                    const set = new Set(prev.dream_types || []);
                    if (effective === 'Yes') set.add(q.dreamValue);
                    else set.delete(q.dreamValue);
                    return {
                      ...prev,
                      dream_types: Array.from(set),
                      [`_dc_scratch_${q.dreamValue}`]: '',
                    };
                  });
                } else if (effective !== cur) {
                  setFormData((prev) => ({ ...prev, [q.field]: effective }));
                }
              }
              // In "review only missing" mode, hop straight to the next question
              // that still needs an answer instead of walking sequentially.
              if (voiceReviewOnlyMissing) {
                // Rebuild "missing" inline so we do not depend on a possibly-stale
                // closure. We also *exclude* the field just answered (q.field)
                // because React has not yet flushed the setFormData above.
                const isBlank2 = (v) => v === null || v === undefined || String(v).trim() === '';
                const stillMissing = [];
                phase1VoiceQuestions.forEach((vq, i) => {
                  if (vq.field === q.field) return; // just answered
                  let value;
                  if (vq.languageIndex !== undefined && vq.languageSubField) {
                    value = (formData.languages || [])[vq.languageIndex]?.[vq.languageSubField];
                  } else if (vq.dreamCheck) {
                    return;
                  } else if (vq.dreamDesc) {
                    value = (formData.dream_descriptions || {})[vq.dreamValue];
                  } else if (vq.dreamOther) {
                    value = formData.dream_other_description;
                  } else {
                    value = formData[vq.field];
                  }
                  if (vq.field === 'reason_for_visiting' && !isBlank2(value) && String(value).trim().length < 20) {
                    stillMissing.push({ field: vq.field, jumpIndex: i });
                    return;
                  }
                  if (!vq.optional && !isPhase1QuestionSkipped(formData, vq) && isBlank2(value)) {
                    stillMissing.push({ field: vq.field, jumpIndex: i });
                  }
                });
                // eslint-disable-next-line no-console
                console.log('[Phase1] Hop → next missing:', stillMissing.map(m => ({ field: m.field, idx: m.jumpIndex })));
                if (stillMissing.length > 0 && typeof stillMissing[0].jumpIndex === 'number') {
                  setVoiceQIdx(stillMissing[0].jumpIndex);
                } else {
                  setVoiceReviewOnlyMissing(false);
                  setVoiceQIdx(phase1VoiceQuestions.length);
                }
                return;
              }
              // Determine if the answer we just captured is effectively blank so
              // chain-skip logic can fire for optional life-goal questions.
              const confirmedBlank = (() => {
                const v = formData[q.field];
                return v === null || v === undefined || String(v).trim() === '';
              })();
              advanceVoiceIdx(confirmedBlank);
            }}
            onSkip={q.optional ? () => {
              // Store intentional omission in the same opaque Phase 1 draft as
              // answers. This survives auto-save, refresh, and session resume.
              setFormData((prev) => {
                let next = markPhase1QuestionSkipped(prev, q);
                // Preserve the established life-goal chain-skip, but explicitly
                // mark every optional goal crossed by that jump.
                if (typeof q.field === 'string' && q.field.startsWith('life_goal_')) {
                  let j = voiceQIdx + 1;
                  while (j < phase1VoiceQuestions.length && phase1VoiceQuestions[j].optional &&
                         (phase1VoiceQuestions[j].field || '').startsWith('life_goal_')) {
                    next = markPhase1QuestionSkipped(next, phase1VoiceQuestions[j]);
                    j += 1;
                  }
                }
                return next;
              });
              if (voiceReviewOnlyMissing) {
                // In review-only mode, Skip means "leave it blank" but keep moving.
                // Compute next missing while excluding the current field so the
                // flow does not re-ask the same question in a tight loop when
                // there are multiple missing answers remaining.
                const isBlank2 = (v) => v === null || v === undefined || String(v).trim() === '';
                const stillMissing = [];
                phase1VoiceQuestions.forEach((vq, i) => {
                  if (i === voiceQIdx) return; // skipping current question — don't re-pick it immediately
                  let value;
                  if (vq.languageIndex !== undefined && vq.languageSubField) {
                    value = (formData.languages || [])[vq.languageIndex]?.[vq.languageSubField];
                  } else if (vq.dreamCheck) {
                    return;
                  } else if (vq.dreamDesc) {
                    value = (formData.dream_descriptions || {})[vq.dreamValue];
                  } else if (vq.dreamOther) {
                    value = formData.dream_other_description;
                  } else {
                    value = formData[vq.field];
                  }
                  if (vq.field === 'reason_for_visiting' && !isBlank2(value) && String(value).trim().length < 20) {
                    stillMissing.push({ field: vq.field, jumpIndex: i });
                    return;
                  }
                  if (!vq.optional && !isPhase1QuestionSkipped(formData, vq) && isBlank2(value)) {
                    stillMissing.push({ field: vq.field, jumpIndex: i });
                  }
                });
                if (stillMissing.length > 0 && typeof stillMissing[0].jumpIndex === 'number') {
                  setVoiceQIdx(stillMissing[0].jumpIndex);
                } else {
                  setVoiceReviewOnlyMissing(false);
                  setVoiceQIdx(phase1VoiceQuestions.length);
                }
                return;
              }
              // Skip = blank answer. Chain-skip through remaining optional life-goals.
              advanceVoiceIdx(true);
            } : undefined}
            selectedVoiceURI={selectedVoiceURI}
            autoPlay
            placeholder="Tap the mic to record your answer, or 'Type instead' to type it."
            testId={`voice-q-${q.field}`}
          />

          <div className="mt-4 flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setVoiceQIdx((i) => Math.max(0, i - 1))}
              disabled={voiceQIdx === 0}
              data-testid="voice-q-prev-btn"
            >
              ← Previous
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setVoiceMode(false); }}
              data-testid="voice-q-switch-to-form-btn"
            >
              Switch to text form →
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ------------------------------------------------------------------
  // POST-VOICE REVIEW SCREEN — after the client walks through every voice
  // question, list any answers they skipped so they can jump back and
  // complete them. The Submit button in the standard text form remains
  // disabled (via validation on submit) until every mandatory field is
  // filled, so this screen mainly gives them a quick, guided way to
  // resolve gaps without hunting through the long form.
  // ------------------------------------------------------------------
  if (voiceMode && phase1Started && voiceQIdx >= phase1VoiceQuestions.length) {
    const missing = computeMissingPhase1Answers();
    if (missing.length > 0) {
      return (
        <Card className="mt-6" data-testid="phase1-voice-review">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Almost done — {missing.length} question{missing.length > 1 ? 's' : ''} still need{missing.length === 1 ? 's' : ''} an answer
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  All Phase 1 questions are mandatory. Please complete the items below before submitting.
                </p>
              </div>
              <AutoSaveIndicator status={autoSaveStatus} savedAt={autoSavedAt} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 mb-5" data-testid="phase1-missing-list">
              <p className="text-sm font-semibold text-amber-900 mb-3">Skipped or incomplete answers</p>
              <ul className="space-y-2">
                {missing.map((m, idx) => (
                  <li
                    key={m.field}
                    className="flex items-start justify-between gap-3 bg-white border border-amber-200 rounded-md p-3"
                    data-testid={`phase1-missing-item-${m.field}`}
                  >
                    <div className="text-sm text-gray-800 flex-1">
                      <span className="font-semibold text-amber-800">{idx + 1}.</span>{' '}
                      {m.question}
                    </div>
                    {m.jumpIndex !== null && m.jumpIndex !== undefined ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setVoiceReviewOnlyMissing(true);
                          setVoiceQIdx(m.jumpIndex);
                        }}
                        data-testid={`phase1-missing-jump-${m.field}`}
                      >
                        Answer now →
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-500 italic">Fill in the form below</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setVoiceReviewOnlyMissing(false); setVoiceQIdx(0); }}
                data-testid="phase1-voice-restart"
              >
                ← Restart voice interview
              </Button>
              <Button
                type="button"
                onClick={() => { setVoiceReviewOnlyMissing(false); setVoiceMode(false); setPhase1ReviewMode(true); }}
                data-testid="phase1-voice-review-to-form"
              >
                Continue in text form →
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }
    // Everything answered — drop the client back to the standard review form
    // so they can double-check and hit Submit.
    if (voiceMode && phase1Started && voiceQIdx >= phase1VoiceQuestions.length && missing.length === 0) {
      // Flip out of voice mode + arm the "Review & Submit" banner on the text form.
      // Wrapped in setTimeout(0) so we don't call setState during render.
      setTimeout(() => {
        setVoiceMode(false);
        setPhase1ReviewMode(true);
      }, 0);
    }
  }

  return (
    <Card className="mt-6" data-testid="phase1-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Phase 1 Complete - Client Information</CardTitle>
            <p className="text-sm text-gray-600 mt-2">Please provide complete demographic and background information. All fields are mandatory.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {phase1Started && !voiceMode && (
              <Button
                type="button"
                variant="outline"
                onClick={resumeVoiceInterview}
                data-testid="phase1-form-switch-to-voice-btn"
              >
                <Volume2 className="w-4 h-4 mr-2" />
                Continue Voice Interview
              </Button>
            )}
            <AutoSaveIndicator status={autoSaveStatus} savedAt={autoSavedAt} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {phase1ReviewMode && (
          <div
            className="mb-6 border-2 border-amber-300 bg-amber-50 rounded-lg p-4 flex items-start gap-3"
            data-testid="phase1-review-banner"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-900 mb-1">
                Voice interview complete — please cross-check your answers below
              </h4>
              <p className="text-sm text-amber-800">
                All your voice answers have been filled into the form. Please scroll through
                each field, correct anything that was captured incorrectly, and click
                <span className="font-semibold"> Submit Phase 1 </span>at the bottom when you're ready.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPhase1ReviewMode(false)}
              className="flex-shrink-0 text-amber-700 hover:text-amber-900 text-sm underline"
              data-testid="phase1-review-banner-dismiss"
            >
              Dismiss
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit} onChange={offerPhase1Correction} onBlur={offerPhase1Correction} className="space-y-6">
          {isClientTextCorrectionEnabled(settings) && phase1Correction && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="phase1-correction-suggestion">
              Did you mean “{phase1Correction.corrected}”?
              <Button type="button" size="sm" className="ml-3" data-testid="phase1-use-correction" onClick={() => {
                setFormData((prev) => ({ ...prev, [phase1Correction.field]: phase1Correction.corrected,
                  correction_metadata: { ...(prev.correction_metadata || {}), [phase1Correction.field]: {
                    original_text: phase1Correction.original, final_text: phase1Correction.corrected } } }));
                setPhase1Correction(null);
              }}>Use suggestion</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setPhase1Correction(null)}>Keep original</Button>
            </div>
          )}
          {/* Header Section with Date */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Phase 1 Complete - Client Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Date Of Test (Auto-generated) *</Label>
                <Input 
                  type="date" 
                  value={formData.date_of_test} 
                  readOnly
                  disabled
                  className="bg-gray-100 cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Name (Alphabets and spaces only) *</Label>
              <Input 
                value={formData.name} 
                onChange={handleNameChange} 
                required 
                placeholder="Enter full name"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label>Gender *</Label>
              <select 
                value={formData.gender} 
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })} 
                required
                className={`w-full px-3 py-2 border rounded-md ${errors.gender ? 'border-red-500' : ''}`}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Transgender">Transgender</option>
              </select>
              {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
            </div>
            <div>
              <Label>Date Of Birth (Minimum age: 15 years) *</Label>
              <Input 
                type="date" 
                value={formData.date_of_birth} 
                onChange={handleDOBChange} 
                required 
                max={(() => {
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return yesterday.toISOString().split('T')[0];
                })()}
                className={errors.date_of_birth ? 'border-red-500' : ''}
              />
              {errors.date_of_birth && <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Age (Auto-calculated) *</Label>
              <Input 
                type="number" 
                value={formData.age} 
                readOnly
                disabled
                className="bg-gray-100 cursor-not-allowed"
              />
            </div>
            <div>
              <Label>Nationality *</Label>
              <Input 
                value={formData.nationality} 
                onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} 
                required 
                placeholder="e.g., Indian"
                className={errors.nationality ? 'border-red-500' : ''}
              />
              {errors.nationality && <p className="text-red-500 text-xs mt-1">{errors.nationality}</p>}
            </div>
            <div>
              <Label>State *</Label>
              <Input 
                value={formData.state} 
                onChange={(e) => setFormData({ ...formData, state: e.target.value })} 
                required 
                placeholder="e.g., Andhra Pradesh"
                className={errors.state ? 'border-red-500' : ''}
              />
              {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Religion *</Label>
              <Input 
                value={formData.religion} 
                onChange={(e) => setFormData({ ...formData, religion: e.target.value })} 
                required 
                placeholder="e.g., Hindu, Christian, Muslim"
                className={errors.religion ? 'border-red-500' : ''}
              />
              {errors.religion && <p className="text-red-500 text-xs mt-1">{errors.religion}</p>}
            </div>
          </div>

          {/* Language Proficiency Section */}
          <div className="bg-teal-50 p-6 rounded-lg space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#0D9488' }}>Language Proficiency</h3>
              <p className="text-sm text-gray-600 mb-4">
                Please tell us how many languages you can read, write, and speak. This helps us understand your communication range and comfort level with language.
              </p>
            </div>

            <div>
              <Label>How many languages can you read, write, and speak? *</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Example: Say it in a statement only that i know 2 languages, and the system will pick the number 2.</p>
              <Input 
                type="number"
                value={formData.language_count}
                onChange={(e) => handleLanguageCountChange(e.target.value)}
                required
                min="0"
                max="20"
                placeholder="Enter total number of languages"
                className="max-w-xs"
              />
            </div>

            {/* Dynamic Language Details */}
            {formData.language_count > 0 && (
              <div className="space-y-4 mt-4">
                <h4 className="font-semibold text-teal-900">Language Details</h4>
                {formData.languages.map((lang, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg border-2 border-teal-200 space-y-3">
                    <h5 className="font-medium text-teal-800">Language {index + 1}</h5>
                    
                    <div>
                      <Label>Language Name *</Label>
                      <Input
                        value={lang.name || ''}
                        onChange={(e) => handleLanguageFieldChange(index, 'name', e.target.value)}
                        required
                        placeholder="e.g., English, Hindi, Tamil"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>Can you read this language? *</Label>
                        <select
                          value={lang.can_read || ''}
                          onChange={(e) => handleLanguageFieldChange(index, 'can_read', e.target.value)}
                          required
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>

                      <div>
                        <Label>Can you write this language? *</Label>
                        <select
                          value={lang.can_write || ''}
                          onChange={(e) => handleLanguageFieldChange(index, 'can_write', e.target.value)}
                          required
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>

                      <div>
                        <Label>Can you speak this language? *</Label>
                        <select
                          value={lang.can_speak || ''}
                          onChange={(e) => handleLanguageFieldChange(index, 'can_speak', e.target.value)}
                          required
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Education *</Label>
              <Input 
                value={formData.education} 
                onChange={(e) => setFormData({ ...formData, education: e.target.value })} 
                required 
                placeholder="Highest qualification"
                className={errors.education ? 'border-red-500' : ''}
              />
              {errors.education && <p className="text-red-500 text-xs mt-1">{errors.education}</p>}
            </div>
          </div>

          {/* Disability Section with Toggle */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <div>
              <Label>Have any disability *</Label>
              <select 
                value={formData.disability} 
                onChange={(e) => {
                  setFormData({ ...formData, disability: e.target.value });
                  if (e.target.value === 'No') {
                    setFormData({ ...formData, disability: e.target.value, disability_description: '' });
                  }
                }}
                required
                className={`w-full px-3 py-2 border rounded-md ${errors.disability ? 'border-red-500' : ''}`}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.disability && <p className="text-red-500 text-xs mt-1">{errors.disability}</p>}
            </div>
            {formData.disability === 'Yes' && (
              <div>
                <Label>Please describe the disability *</Label>
                <Textarea 
                  value={formData.disability_description} 
                  onChange={(e) => setFormData({ ...formData, disability_description: e.target.value })} 
                  required={formData.disability === 'Yes'}
                  rows={3}
                  placeholder="Describe the disability"
                  className={errors.disability_description ? 'border-red-500' : ''}
                />
                {errors.disability_description && <p className="text-red-500 text-xs mt-1">{errors.disability_description}</p>}
              </div>
            )}
          </div>

          {/* Marriage & Family */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Marriage *</Label>
              <select 
                value={formData.marital_status} 
                onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })} 
                required
                className={`w-full px-3 py-2 border rounded-md ${errors.marital_status ? 'border-red-500' : ''}`}
              >
                <option value="">Select</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
              {errors.marital_status && <p className="text-red-500 text-xs mt-1">{errors.marital_status}</p>}
            </div>
            <div>
              <Label>No. Of Children (0 allowed) *</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Example: Say it in a statement that "I have two children." The system will pick up the number 2.</p>
              <Input 
                type="number" 
                value={formData.number_of_children} 
                onChange={(e) => setFormData({ ...formData, number_of_children: parseInt(e.target.value) || 0 })} 
                required 
                min="0" 
                max="20"
              />
            </div>
            <div>
              <Label>Occupation *</Label>
              <Input 
                value={formData.occupation} 
                onChange={(e) => setFormData({ ...formData, occupation: e.target.value })} 
                required 
                placeholder="Current occupation"
                className={errors.occupation ? 'border-red-500' : ''}
              />
              {errors.occupation && <p className="text-red-500 text-xs mt-1">{errors.occupation}</p>}
            </div>
            <div>
              <Label>Are you still working? *</Label>
              <select
                value={formData.working_status}
                onChange={(e) => setFormData({ ...formData, working_status: e.target.value })}
                required
                className={`w-full p-2 border rounded-md ${errors.working_status ? 'border-red-500' : ''}`}
              >
                <option value="">Select status</option>
                <option value="working">Working</option>
                <option value="presently not working">Presently not working</option>
                <option value="retired">Retired</option>
              </select>
              {errors.working_status && <p className="text-red-500 text-xs mt-1">{errors.working_status}</p>}
            </div>
          </div>

          {/* Medical Issues Section with Toggle */}
          <div className="bg-red-50 p-4 rounded-lg space-y-3">
            <div>
              <Label>Medical Issues *</Label>
              <select 
                value={formData.medical_issues} 
                onChange={(e) => {
                  setFormData({ ...formData, medical_issues: e.target.value });
                  if (e.target.value === 'No') {
                    setFormData({ ...formData, medical_issues: e.target.value, medical_issues_description: '' });
                  }
                }}
                required
                className={`w-full px-3 py-2 border rounded-md ${errors.medical_issues ? 'border-red-500' : ''}`}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.medical_issues && <p className="text-red-500 text-xs mt-1">{errors.medical_issues}</p>}
            </div>
            {formData.medical_issues === 'Yes' && (
              <div>
                <Label>Please describe the medical issues *</Label>
                <Textarea 
                  value={formData.medical_issues_description} 
                  onChange={(e) => setFormData({ ...formData, medical_issues_description: e.target.value })} 
                  required={formData.medical_issues === 'Yes'}
                  rows={3}
                  placeholder="Describe medical conditions"
                  className={errors.medical_issues_description ? 'border-red-500' : ''}
                />
                {errors.medical_issues_description && <p className="text-red-500 text-xs mt-1">{errors.medical_issues_description}</p>}
              </div>
            )}
          </div>

          {/* Substance Abuse Section with Toggle */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-3">
            <div>
              <Label>History of Substance Abuse *</Label>
              <select 
                value={formData.substance_abuse_history} 
                onChange={(e) => {
                  setFormData({ ...formData, substance_abuse_history: e.target.value });
                  if (e.target.value === 'No') {
                    setFormData({ ...formData, substance_abuse_history: e.target.value, substance_abuse_description: '' });
                  }
                }}
                required
                className={`w-full px-3 py-2 border rounded-md ${errors.substance_abuse_history ? 'border-red-500' : ''}`}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.substance_abuse_history && <p className="text-red-500 text-xs mt-1">{errors.substance_abuse_history}</p>}
            </div>
            {formData.substance_abuse_history === 'Yes' && (
              <div>
                <Label>Please describe the substance abuse history *</Label>
                <Textarea 
                  value={formData.substance_abuse_description} 
                  onChange={(e) => setFormData({ ...formData, substance_abuse_description: e.target.value })} 
                  required={formData.substance_abuse_history === 'Yes'}
                  rows={3}
                  placeholder="Describe substance abuse history"
                  className={errors.substance_abuse_description ? 'border-red-500' : ''}
                />
                {errors.substance_abuse_description && <p className="text-red-500 text-xs mt-1">{errors.substance_abuse_description}</p>}
              </div>
            )}
          </div>

          {/* Financially Dependent Section with Toggle */}
          <div className="bg-yellow-50 p-4 rounded-lg space-y-3">
            <div>
              <Label>Are you financially or emotionally dependent on anyone? Please say yes or no.</Label>
              <select 
                value={formData.dependent} 
                onChange={(e) => {
                  setFormData({ ...formData, dependent: e.target.value });
                  if (e.target.value === 'No') {
                    setFormData({ ...formData, dependent: e.target.value, dependent_explanation: '' });
                  }
                }}
                required
                className={`w-full px-3 py-2 border rounded-md ${errors.dependent ? 'border-red-500' : ''}`}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.dependent && <p className="text-red-500 text-xs mt-1">{errors.dependent}</p>}
            </div>
            {formData.dependent === 'Yes' && (
              <div>
                <Label>Please explain financial dependency *</Label>
                <Textarea 
                  value={formData.dependent_explanation} 
                  onChange={(e) => setFormData({ ...formData, dependent_explanation: e.target.value })} 
                  required={formData.dependent === 'Yes'}
                  rows={3}
                  placeholder="Explain on whom you are financially dependent"
                  className={errors.dependent_explanation ? 'border-red-500' : ''}
                />
                {errors.dependent_explanation && <p className="text-red-500 text-xs mt-1">{errors.dependent_explanation}</p>}
              </div>
            )}
          </div>

          {/* Family Structure */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>No. Of Sisters (Numeric) *</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Example: Say it in a statement only that "I have two sisters." The system will pick up the number 2.</p>
              <Input 
                type="number" 
                value={formData.siblings_sisters} 
                onChange={(e) => setFormData({ ...formData, siblings_sisters: parseInt(e.target.value) || 0 })} 
                required 
                min="0" 
                max="20"
              />
            </div>
            <div>
              <Label>No. Of Brothers (Numeric) *</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Example: Say it in a statement only that "I have two brothers." The system will pick up the number 2.</p>
              <Input 
                type="number" 
                value={formData.siblings_brothers} 
                onChange={(e) => setFormData({ ...formData, siblings_brothers: parseInt(e.target.value) || 0 })} 
                required 
                min="0" 
                max="20"
              />
            </div>
            <div>
              <Label>No. Of Friends In Touch (Numeric) *</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Example: Say it in a statement only that "I have two friends." The system will pick up the number 2.</p>
              <Input 
                type="number" 
                value={formData.friends_in_touch} 
                onChange={(e) => setFormData({ ...formData, friends_in_touch: parseInt(e.target.value) || 0 })} 
                required 
                min="0"
              />
            </div>
          </div>

          {/* Family Members Description */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <Label>Tell me about your family members *</Label>
            <p className="text-sm text-gray-600 mb-2">Please describe your family members (required)</p>
            <textarea
              className="w-full p-3 border rounded-md min-h-[120px] resize-y"
              value={formData.family_members_description}
              onChange={(e) => setFormData({ ...formData, family_members_description: e.target.value })}
              required
              placeholder="Please describe your family members..."
            />
            {errors.family_members_description && (
              <p className="text-red-500 text-sm mt-1">{errors.family_members_description}</p>
            )}
          </div>

          {/* Career Goals Section - 10 Goals */}
          <div className="bg-green-50 p-4 rounded-lg space-y-3">
            <h4 className="font-semibold text-lg mb-3">Goals (10 Goals)</h4>
            <p className="text-sm text-gray-600 mb-2">At least one goal is required. Goals 2-10 are optional.</p>
            <div>
              <Label>Goal 1 *</Label>
              <Input 
                value={formData.life_goal_1} 
                onChange={(e) => setFormData({ ...formData, life_goal_1: e.target.value })} 
                required 
                placeholder="First goal"
                className={errors.life_goal_1 ? 'border-red-500' : ''}
              />
              {errors.life_goal_1 && <p className="text-red-500 text-xs mt-1">{errors.life_goal_1}</p>}
            </div>
            <div>
              <Label>Goal 2</Label>
              <Input 
                value={formData.life_goal_2} 
                onChange={(e) => setFormData({ ...formData, life_goal_2: e.target.value })} 
                placeholder="Second goal (optional)"
              />
            </div>
            <div>
              <Label>Goal 3</Label>
              <Input 
                value={formData.life_goal_3} 
                onChange={(e) => setFormData({ ...formData, life_goal_3: e.target.value })} 
                placeholder="Third goal (optional)"
              />
            </div>
            <div>
              <Label>Goal 4</Label>
              <Input 
                value={formData.life_goal_4} 
                onChange={(e) => setFormData({ ...formData, life_goal_4: e.target.value })} 
                placeholder="Fourth goal (optional)"
              />
            </div>
            <div>
              <Label>Goal 5</Label>
              <Input 
                value={formData.life_goal_5} 
                onChange={(e) => setFormData({ ...formData, life_goal_5: e.target.value })} 
                placeholder="Fifth goal (optional)"
              />
            </div>
            <div>
              <Label>Goal 6</Label>
              <Input 
                value={formData.life_goal_6} 
                onChange={(e) => setFormData({ ...formData, life_goal_6: e.target.value })} 
                placeholder="Sixth goal (optional)"
              />
            </div>
            <div>
              <Label>Goal 7</Label>
              <Input 
                value={formData.life_goal_7} 
                onChange={(e) => setFormData({ ...formData, life_goal_7: e.target.value })} 
                placeholder="Seventh goal (optional)"
              />
            </div>
            <div>
              <Label>Goal 8</Label>
              <Input 
                value={formData.life_goal_8} 
                onChange={(e) => setFormData({ ...formData, life_goal_8: e.target.value })} 
                placeholder="Eighth goal (optional)"
              />
            </div>
            <div>
              <Label>Goal 9</Label>
              <Input 
                value={formData.life_goal_9} 
                onChange={(e) => setFormData({ ...formData, life_goal_9: e.target.value })} 
                placeholder="Ninth goal (optional)"
              />
            </div>
            <div>
              <Label>Goal 10</Label>
              <Input 
                value={formData.life_goal_10} 
                onChange={(e) => setFormData({ ...formData, life_goal_10: e.target.value })} 
                placeholder="Tenth goal (optional)"
              />
            </div>
          </div>

          {/* Obstacles to Goals */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <Label>What do you think is stopping you to achieve your goals? *</Label>
            <p className="text-sm text-gray-600 mb-2">Please describe the obstacles or challenges (required)</p>
            <textarea
              className="w-full p-3 border rounded-md min-h-[120px] resize-y"
              value={formData.obstacles_to_goals}
              onChange={(e) => setFormData({ ...formData, obstacles_to_goals: e.target.value })}
              required
              placeholder="Describe what's stopping you from achieving your goals..."
            />
            {errors.obstacles_to_goals && (
              <p className="text-red-500 text-sm mt-1">{errors.obstacles_to_goals}</p>
            )}
          </div>

          {/* What Irritates You */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-3">
            <div>
              <Label>Do you get irritated by anything? *</Label>
              <div className="flex gap-6 mt-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_irritated"
                    value="Yes"
                    checked={formData.is_irritated === 'Yes'}
                    onChange={() => setFormData({ ...formData, is_irritated: 'Yes' })}
                    data-testid="is-irritated-yes"
                    required
                  />
                  <span>Yes</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_irritated"
                    value="No"
                    checked={formData.is_irritated === 'No'}
                    onChange={() => setFormData({ ...formData, is_irritated: 'No', what_irritates_you: '' })}
                    data-testid="is-irritated-no"
                  />
                  <span>No</span>
                </label>
              </div>
              {errors.is_irritated && (
                <p className="text-red-500 text-sm mt-1">{errors.is_irritated}</p>
              )}
            </div>

            {formData.is_irritated === 'Yes' && (
              <div>
                <Label>What irritates you? *</Label>
                <p className="text-sm text-gray-600 mb-2">Please describe what irritates or frustrates you (required)</p>
                <textarea
                  className={`w-full p-3 border rounded-md min-h-[120px] resize-y ${errors.what_irritates_you ? 'border-red-500' : ''}`}
                  value={formData.what_irritates_you || ''}
                  onChange={(e) => setFormData({ ...formData, what_irritates_you: e.target.value })}
                  placeholder="Describe what irritates you..."
                  data-testid="what-irritates-you-textarea"
                  required
                />
                {errors.what_irritates_you && (
                  <p className="text-red-500 text-sm mt-1">{errors.what_irritates_you}</p>
                )}
              </div>
            )}
          </div>

          {/* Social Norm Section with Toggle */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-3">
            <div>
              <Label>Have you ever disobeyed any social norm? *</Label>
              <select 
                value={formData.disobeyed_social_norm} 
                onChange={(e) => {
                  setFormData({ ...formData, disobeyed_social_norm: e.target.value });
                  if (e.target.value === 'No') {
                    setFormData(prev => ({ ...prev, disobeyed_social_norm_description: '' }));
                  }
                }}
                required
                className={`w-full p-2 border rounded-md ${errors.disobeyed_social_norm ? 'border-red-500' : ''}`}
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.disobeyed_social_norm && (
                <p className="text-red-500 text-xs mt-1">{errors.disobeyed_social_norm}</p>
              )}
            </div>

            {formData.disobeyed_social_norm === 'Yes' && (
              <div>
                <Label>What is it you disobeyed? *</Label>
                <textarea
                  className={`w-full p-3 border rounded-md min-h-[100px] resize-y ${errors.disobeyed_social_norm_description ? 'border-red-500' : ''}`}
                  value={formData.disobeyed_social_norm_description}
                  onChange={(e) => setFormData({ ...formData, disobeyed_social_norm_description: e.target.value })}
                  required
                  placeholder="Please describe what social norm you disobeyed..."
                />
                {errors.disobeyed_social_norm_description && (
                  <p className="text-red-500 text-xs mt-1">{errors.disobeyed_social_norm_description}</p>
                )}
              </div>
            )}
          </div>

          {/* Dream Experiences Section */}
          <div className="bg-indigo-50 p-6 rounded-lg space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#4F46E5' }}>Your Dream Experiences</h3>
              <p className="text-sm text-gray-600 mb-4">
                We'd like to understand what kinds of dreams you typically have. This helps us get to know you better. Please check all the boxes that match your experience.
              </p>
            </div>

            <div>
              <Label>What types of dreams do you usually have? (Select all that apply - you can choose more than one!)</Label>
              <div className="mt-3 space-y-3">
                {[
                  { value: 'recurring', label: 'Recurring dreams', description: 'The same dream or similar dreams that repeat on different nights' },
                  { value: 'continuing', label: 'Continuing dreams', description: 'Dreams that pick up where they left off after you wake up and go back to sleep' },
                  { value: 'nightmares', label: 'Nightmares', description: 'Scary or disturbing dreams that may wake you up' },
                  { value: 'lucid', label: 'Lucid dreams', description: 'Dreams where you know you\'re dreaming while it\'s happening' },
                  { value: 'anxiety', label: 'Anxiety or stress dreams', description: 'Dreams about worries, deadlines, exams, or stressful situations' },
                  { value: 'flying', label: 'Flying dreams', description: 'Dreams where you\'re flying or floating' },
                  { value: 'falling', label: 'Falling dreams', description: 'Dreams where you\'re falling' },
                  { value: 'chased', label: 'Being chased', description: 'Dreams where someone or something is chasing you' },
                  { value: 'people', label: 'Dreams about people I know', description: 'Dreams featuring family, friends, or people from your life' },
                  { value: 'abstract', label: 'Abstract or surreal dreams', description: 'Strange, unusual dreams that don\'t make logical sense' },
                  { value: 'no_recall', label: 'I don\'t remember my dreams', description: 'You rarely or never recall your dreams' },
                  { value: 'other', label: 'Other', description: 'Something different (please describe below)' }
                ].map((dream) => {
                  const isChecked = (formData.dream_types || []).includes(dream.value);
                  return (
                    <div key={dream.value} className="p-3 bg-white rounded border hover:border-indigo-300">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id={`dream-${dream.value}`}
                          checked={isChecked}
                          onChange={() => handleDreamTypeChange(dream.value)}
                          className="mt-1 h-4 w-4 text-indigo-600 rounded"
                          data-testid={`dream-checkbox-${dream.value}`}
                        />
                        <label htmlFor={`dream-${dream.value}`} className="flex-1 cursor-pointer">
                          <span className="font-medium text-gray-900">{dream.label}</span>
                          <span className="text-gray-600"> — {dream.description}</span>
                        </label>
                      </div>
                      {/* Per-dream textarea appears when checked (except 'other', which has a shared textarea below) */}
                      {isChecked && dream.value !== 'other' && (
                        <div className="mt-3 pl-7">
                          <Label className="text-sm text-gray-700">
                            Tell us more about your {dream.label.toLowerCase()} <span className="text-red-500">*</span>
                          </Label>
                          <textarea
                            className={`w-full p-2 border rounded-md min-h-[80px] resize-y mt-1 text-sm ${errors[`dream_desc_${dream.value}`] ? 'border-red-500' : ''}`}
                            value={(formData.dream_descriptions && formData.dream_descriptions[dream.value]) || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormData({
                                ...formData,
                                dream_descriptions: {
                                  ...(formData.dream_descriptions || {}),
                                  [dream.value]: val
                                }
                              });
                              // Clear the error as soon as user types something
                              if (val.trim().length > 0) {
                                setErrors(prev => {
                                  const copy = { ...prev };
                                  delete copy[`dream_desc_${dream.value}`];
                                  return copy;
                                });
                              }
                            }}
                            placeholder={`Describe your ${dream.label.toLowerCase()} — what happens, how they make you feel, how often, etc.`}
                            data-testid={`dream-description-${dream.value}`}
                            required
                          />
                          {errors[`dream_desc_${dream.value}`] && (
                            <p className="text-red-500 text-xs mt-1">{errors[`dream_desc_${dream.value}`]}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Conditional textarea for "Other" */}
            {(formData.dream_types || []).includes('other') && (
              <div>
                <Label>If you selected 'Other' or want to share more details about your dreams (Optional)</Label>
                <textarea
                  className="w-full p-3 border rounded-md min-h-[100px] resize-y mt-2"
                  value={formData.dream_other_description || ''}
                  onChange={(e) => setFormData({ ...formData, dream_other_description: e.target.value })}
                  placeholder="Please describe your dream experiences in more detail..."
                />
              </div>
            )}
          </div>

          {/* Reason For Consultation */}
          <div>
            <Label>Reason for Consultation (Minimum 20 characters) *</Label>
            <Textarea 
              value={formData.reason_for_visiting} 
              onChange={handleReasonChange} 
              required 
              rows={4}
              placeholder="Please describe why you are seeking consultation (at least 20 characters)"
              className={errors.reason_for_visiting ? 'border-red-500' : ''}
            />
            {errors.reason_for_visiting && <p className="text-red-500 text-xs mt-1">{errors.reason_for_visiting}</p>}
            <p className="text-xs text-gray-500 mt-1">Characters: {formData.reason_for_visiting.length}/20 minimum</p>
          </div>

          {/* Validate current inputs on submit; errors from an earlier attempt must not block retry. */}
          <Button type="submit" disabled={loading || isViewOnlyMode} data-testid="phase1-submit-btn" className="w-full" style={{ background: '#2563EB' }}>
            {loading ? 'Submitting...' : 'Submit & Continue to Phase 2'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Phase 2: Moral Reasoning
const Phase2 = ({ sessionId, onComplete, settings, userRole, showCompletionModal, isViewOnlyMode }) => {
  const { notification, showSuccess, showError, closeNotification } = useNotification();
  const [responses, setResponses] = useState([]);
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingData, setExistingData] = useState(null);

  // Phase 2 voice mode state
  const [voiceMode, setVoiceMode] = useState(false);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(
    typeof window !== 'undefined' ? window.localStorage.getItem('phase2_voice_uri') || '' : ''
  );
  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem('phase2_voice_uri', selectedVoiceURI || '');
  }, [selectedVoiceURI]);
  
  const totalQuestions = settings?.phase2_scenarios?.length || 5;

  useEffect(() => {
    fetchExisting();
  }, []);

  const fetchExisting = async () => {
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/phase2`);
      if (res.data.responses) {
        setExistingData(res.data);
        setResponses(res.data.responses.map(r => r.response));
      }
    } catch (error) {}
  };

  // Auto-save integration for Phase 2. Include `voiceMode` so a client who
  // started with Voice Interview stays in STT/TTS mode after switching tabs
  // or refreshing (same UX as Phase 3/4).
  const { status: autoSaveStatus, savedAt: autoSavedAt, loadDraft, clearDraft } = useAutoSave({
    sessionId,
    phaseNumber: 2,
    data: { responses, voiceMode },
    enabled: !existingData && !isViewOnlyMode && started,
    debounceMs: 1200,
  });
  const [draftLoaded, setDraftLoaded] = useState(false);
  useEffect(() => {
    if (draftLoaded || existingData) return;
    (async () => {
      const draft = await loadDraft();
      if (draft && Array.isArray(draft.responses) && draft.responses.length > 0) {
        setResponses(draft.responses);
        setStarted(true);
      }
      // Restore voice-interview mode so refresh keeps the client in STT/TTS.
      if (draft && typeof draft.voiceMode === 'boolean') {
        setVoiceMode(draft.voiceMode);
      }
      setDraftLoaded(true);
    })();
    // eslint-disable-next-line
  }, [sessionId]);

  const startPhase = (intendedVoiceMode = voiceMode) => {
    setStarted(true);
    setVoiceMode(intendedVoiceMode);
    const initialResponses = settings?.phase2_scenarios?.map(() => []) || [];
    setResponses(initialResponses);
    // Fire an immediate direct PUT with the intended voiceMode. React's
    // setState batching means the auto-save effect's `data` closure may not
    // yet reflect voiceMode=true when the first debounce fires, so we bypass
    // the debounce and commit the correct value directly. Prevents the
    // client from being restored to text mode after leaving and returning.
    try {
      axios.put(`${API}/sessions/${sessionId}/drafts/phase2`, {
        responses: initialResponses,
        voiceMode: intendedVoiceMode,
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  };

  const handleOptionToggle = (questionIndex, option) => {
    const newResponses = [...responses];
    const scenario = settings?.phase2_scenarios?.[questionIndex];
    
    if (scenario?.multiple) {
      // Multiple choice - use checkboxes
      if (!Array.isArray(newResponses[questionIndex])) {
        newResponses[questionIndex] = [];
      }
      const currentSelections = newResponses[questionIndex];
      if (currentSelections.includes(option)) {
        newResponses[questionIndex] = currentSelections.filter(o => o !== option);
      } else {
        newResponses[questionIndex] = [...currentSelections, option];
      }
    } else {
      // Single choice - use radio buttons
      newResponses[questionIndex] = [option];
    }
    setResponses(newResponses);
  };

  const isFormValid = () => {
    // Check if all questions have been answered
    if (responses.length === 0) return false;
    const totalQuestions = settings?.phase2_scenarios?.length || 0;
    if (responses.length < totalQuestions) return false;
    
    return responses.every((r, idx) => {
      if (idx >= totalQuestions) return true;
      return r && Array.isArray(r) && r.length > 0;
    });
  };

  const getUnansweredQuestions = () => {
    const totalQuestions = settings?.phase2_scenarios?.length || 0;
    const unanswered = [];
    for (let i = 0; i < totalQuestions; i++) {
      if (!responses[i] || (Array.isArray(responses[i]) && responses[i].length === 0)) {
        unanswered.push(i + 1);
      }
    }
    return unanswered;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all questions are answered
    const unanswered = getUnansweredQuestions();
    if (unanswered.length > 0) {
      showError(`Please answer all questions. Unanswered: Question ${unanswered.join(', ')}`);
      return;
    }
    
    setLoading(true);
    try {
      // Extract values from responses
      const formattedResponses = responses.map((r, i) => {
        const scenario = settings?.phase2_scenarios?.[i];
        let extractedValues = [];
        
        if (scenario?.values && Array.isArray(r)) {
          // For each selected option, get its values
          r.forEach(option => {
            const optionValues = scenario.values[option];
            if (optionValues) {
              extractedValues = [...extractedValues, ...optionValues];
            }
          });
          // Remove duplicates
          extractedValues = [...new Set(extractedValues)];
        }
        
        return { 
          question_id: i, 
          response: Array.isArray(r) ? r.join(', ') : r,
          values: extractedValues
        };
      });
      
      await axios.post(`${API}/sessions/${sessionId}/phase2`, formattedResponses);
      clearDraft();
      showCompletionModal(2);
      onComplete();
    } catch (error) {
      showError('Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mt-6" data-testid="phase2-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Moral Reflection - The Missing Trophy</CardTitle>
          {started && !existingData && <AutoSaveIndicator status={autoSaveStatus} savedAt={autoSavedAt} />}
        </div>
      </CardHeader>
      <CardContent>
        {!started && !existingData ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-blue-600" />
            <h3 className="text-xl font-semibold mb-4">Ready to begin Moral Reflection?</h3>

            {/* Prominent numeric summary */}
            <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="phase2-numeric-summary">
              <div className="p-4 rounded-lg bg-blue-50 border-2 border-blue-200">
                <p className="text-3xl font-bold text-blue-700" data-testid="phase2-total-questions">{totalQuestions}</p>
                <p className="text-xs uppercase tracking-wide text-blue-800 mt-1">Scenarios to Answer</p>
              </div>
              <div className="p-4 rounded-lg bg-indigo-50 border-2 border-indigo-200">
                <p className="text-3xl font-bold text-indigo-700" data-testid="phase2-story-length">1</p>
                <p className="text-xs uppercase tracking-wide text-indigo-800 mt-1">Moral Story to Read</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 border-2 border-amber-200">
                <p className="text-3xl font-bold text-amber-700" data-testid="phase2-timing">Self-Paced</p>
                <p className="text-xs uppercase tracking-wide text-amber-800 mt-1">No Time Limit</p>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left inline-block max-w-2xl">
              <p className="font-semibold text-blue-900 mb-2">📋 Instructions:</p>
              <p className="text-sm text-gray-700 mb-2">Read the moral story carefully, then answer <strong>{totalQuestions}</strong> reflection scenarios.</p>
              <ul className="text-sm text-gray-700 ml-4 mb-2">
                <li>• Select the option that best reflects your view</li>
                <li>• Add a short written explanation for each</li>
                <li>• In voice mode you can tap <strong>Narrate Story</strong> to hear the story read aloud</li>
                <li>• Each scenario question can also be narrated with a single tap</li>
              </ul>
              <p className="text-sm font-semibold text-blue-900">⏱️ There is no timer — take your time.</p>
            </div>

            {/* Narrate story button on the intro screen so the client can preview it */}
            {settings?.moral_story?.content && (
              <div className="mb-4 flex items-center justify-center gap-2 flex-wrap">
                <NarrateButton
                  text={`${settings?.moral_story?.title ? settings.moral_story.title + '. ' : ''}${settings.moral_story.content}`}
                  label="🎧 Narrate story now"
                  selectedVoiceURI={selectedVoiceURI}
                  testId="phase2-intro-narrate-btn"
                />
                <NarrateButton
                  text={`Phase 2: Exploring your moral reasoning. You will read a short moral story, then answer ${totalQuestions} multiple-choice scenarios about it. Pick the option that best matches how you would respond. Choose voice interview to have each question read aloud and answer by speaking, or text form to select answers by tapping. Your responses auto-save as you go so you can return anytime.`}
                  label="🎧 Narrate the rules"
                  selectedVoiceURI={selectedVoiceURI}
                  testId="phase2-intro-narrate-rules-btn"
                />
                <VoicePicker
                  selectedVoiceURI={selectedVoiceURI}
                  onChange={setSelectedVoiceURI}
                />
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                onClick={() => { startPhase(true); }}
                className="px-8 py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
                data-testid="phase2-start-voice-btn"
              >
                <Volume2 className="w-5 h-5 mr-2" />
                Start Voice Interview
              </Button>
              <Button
                onClick={() => { startPhase(false); }}
                variant="outline"
                className="px-8 py-6 text-lg"
                data-testid="phase2-start-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Start Text Form
              </Button>
            </div>
            <VoicePrivacyNotice className="mt-6" testId="phase2-voice-privacy-notice" />
          </div>
        ) : (existingData && existingData.responses && existingData.responses.length >= totalQuestions) ? (
          <div data-testid="phase2-completed-view">
            <p className="text-green-600 font-semibold mb-4">✓ Phase 2 completed - Answered all {totalQuestions} scenarios</p>
            
            <div className="p-6 bg-gray-50 rounded-lg mb-6 max-h-96 overflow-y-auto">
              <h4 className="font-semibold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{settings?.moral_story?.title || 'Moral Story'}</h4>
              <p className="narrative-text text-sm whitespace-pre-line">{settings?.moral_story?.content || ''}</p>
            </div>
            
            <div className="space-y-4">
              {existingData.responses?.map((resp, i) => {
                const scenario = settings?.phase2_scenarios?.[i];
                const scenarioText = scenario?.scenario || '';
                const response = resp.response || 'Undisclosed';
                const values = resp.values || [];
                
                return (
                  <div key={i} className="p-4 bg-white rounded-lg border border-gray-200">
                    <p className="font-semibold mb-2">Scenario {i + 1}: {scenarioText}</p>
                    <p className="text-xs text-gray-600 mb-1">Client Response:</p>
                    <p className={`text-gray-700 mb-2 ${response === 'Undisclosed' ? 'italic text-gray-500' : ''}`} style={{ color: response === 'Undisclosed' ? '#9CA3AF' : '#2563EB' }}>
                      {response}
                    </p>
                    {userRole === 'psychologist' && values.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Values:</p>
                        <div className="flex flex-wrap gap-2">
                          {values.map((val, j) => (
                            <span key={j} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                              {val}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {voiceMode && (
              <div className="flex items-center justify-end mb-2">
                <VoicePicker
                  selectedVoiceURI={selectedVoiceURI}
                  onChange={setSelectedVoiceURI}
                />
              </div>
            )}
            <div className="p-6 bg-gray-50 rounded-lg mb-6 max-h-96 overflow-y-auto">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <h4 className="font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{settings?.moral_story?.title || 'Moral Story'}</h4>
                {settings?.moral_story?.content && (
                  <NarrateButton
                    text={`${settings?.moral_story?.title ? settings.moral_story.title + '. ' : ''}${settings.moral_story.content}`}
                    label="🎧 Narrate story"
                    selectedVoiceURI={selectedVoiceURI}
                    testId="phase2-narrate-story-btn"
                  />
                )}
              </div>
              <p className="narrative-text text-sm whitespace-pre-line">{settings?.moral_story?.content || ''}</p>
            </div>
            
            <div className="space-y-6">
            {settings?.phase2_scenarios?.map((scenario, i) => {
              const hasOptions = scenario.options && scenario.options.length > 0;
              const isAnswered = responses[i] && Array.isArray(responses[i]) && responses[i].length > 0;
              
              return (
                <div key={i} className={`p-6 bg-white rounded-lg border-2 ${!isAnswered ? 'border-red-300 bg-red-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                    <p className="text-base font-semibold flex-1">
                      Scenario {i + 1}: {scenario.scenario}
                    </p>
                    <div className="flex items-center gap-2">
                      {voiceMode && (
                        <NarrateButton
                          text={`Scenario ${i + 1}. ${scenario.scenario}${hasOptions ? '. Options are: ' + scenario.options.join('. ') : ''}`}
                          label="🔊 Read"
                          selectedVoiceURI={selectedVoiceURI}
                          testId={`phase2-narrate-q-${i}`}
                        />
                      )}
                      {!isAnswered && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">Required</span>
                      )}
                    </div>
                  </div>
                  {hasOptions ? (
                    <div className="space-y-3 ml-2">
                      {scenario.options.map((option, optIdx) => (
                        <div key={optIdx} className="flex items-start space-x-3">
                          <input
                            type={scenario.multiple ? "checkbox" : "radio"}
                            id={`q${i}-opt${optIdx}`}
                            name={`question-${i}`}
                            checked={Array.isArray(responses[i]) ? responses[i].includes(option) : responses[i]?.[0] === option}
                            onChange={() => handleOptionToggle(i, option)}
                            disabled={isViewOnlyMode}
                            className="mt-1 disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid={`phase2-q${i}-option${optIdx}`}
                          />
                          <label htmlFor={`q${i}-opt${optIdx}`} className="text-sm flex-1 leading-relaxed cursor-pointer">
                            {option}
                          </label>
                        </div>
                      ))}
                      {scenario.multiple && (
                        <p className="text-sm text-gray-600 italic mt-3">You can select multiple answers</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <Textarea
                        value={responses[i] || ''}
                        onChange={(e) => { const newR = [...responses]; newR[i] = e.target.value; setResponses(newR); }}
                        required
                        data-testid={`phase2-response-${i}`}
                        disabled={isViewOnlyMode}
                        className="flex-1"
                      />
                      {voiceMode && (
                        <MicInput
                          showListeningIndicator
                          onAppend={(chunk) => {
                            const newR = [...responses];
                            const current = (newR[i] && typeof newR[i] === 'string') ? newR[i] : '';
                            newR[i] = (current ? current + ' ' : '') + chunk;
                            setResponses(newR);
                          }}
                          testId={`phase2-mic-${i}`}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
            
            {!isFormValid() && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <div className="text-amber-600">⚠️</div>
                <div>
                  <p className="font-semibold text-amber-800">All questions must be answered</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Please answer questions: {getUnansweredQuestions().join(', ')} before submitting.
                  </p>
                </div>
              </div>
            )}
            
            <Button 
              type="submit" 
              disabled={loading || !isFormValid()} 
              data-testid="phase2-submit-btn"
              className="w-full"
            >
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : 'Complete Phase 2'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

// Phase 3: Exploring Client Views (Response Associations)
const Phase3 = ({ sessionId, settings, onComplete, userRole, showCompletionModal, isViewOnlyMode }) => {
  const { notification, showSuccess, showError, closeNotification } = useNotification();
  const [session, setSession] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [associations, setAssociations] = useState([]);
  const [currentAssociation, setCurrentAssociation] = useState('');
  const [phase3GibberishError, setPhase3GibberishError] = useState('');
  const [phase3Correction, setPhase3Correction] = useState(null);
  const [phase3CorrectionDismissedFor, setPhase3CorrectionDismissedFor] = useState('');
  useEffect(() => {
    if (!isClientTextCorrectionEnabled(settings)) {
      setPhase3Correction(null);
      setPhase3CorrectionDismissedFor('');
    }
  }, [settings?.client_text_correction_enabled]);
  const [currentCorrectionMetadata, setCurrentCorrectionMetadata] = useState(null);
  const [associationCorrectionMetadata, setAssociationCorrectionMetadata] = useState([]);
  const [wordAssociations, setWordAssociations] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);
  // Voice-mode: when timer reaches 0, freeze all inputs and show a banner
  // instructing the client to click "Next word" to continue.
  const [timeExpired, setTimeExpired] = useState(false);
  const [awaitingTimeoutNext, setAwaitingTimeoutNext] = useState(false);
  // Voice-mode: mic waits until the stimulus word finishes narrating.
  const [narrateDone, setNarrateDone] = useState(false);
  const [wordStartTime, setWordStartTime] = useState(null);
  const [lastResponseTime, setLastResponseTime] = useState(null);
  const [responseTimes, setResponseTimes] = useState([]);
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const phase3SessionRef = useRef(sessionId);
  phase3SessionRef.current = sessionId;
  const phase3FetchRef = useRef(0);
  const phase3RevisionRef = useRef(0);
  const classificationEditable = Boolean(userRole === 'psychologist' && !isViewOnlyMode &&
    existingData?.session_id === sessionId && existingData?.classification_editable);
  const valence = usePhase3Valence({ sessionId, API, data: existingData,
    editable: classificationEditable, refresh: () => fetchExisting(), revision: phase3RevisionRef });
  const phase3GenerationRef = useRef(0);
  const phase3FinalizingRef = useRef(false);
  const [phase3SubmissionError, setPhase3SubmissionError] = useState('');
  const [phase3WordSaveError, setPhase3WordSaveError] = useState(false);
  const [phase3SavingWord, setPhase3SavingWord] = useState(false);

  // Get the actual words to show based on settings
  const [wordsToShow, setWordsToShow] = useState([]);
  const [phase3WordsLoaded, setPhase3WordsLoaded] = useState(false);
  const totalWords = wordsToShow.length;
  const allRequiredResponsesCompleted = phase3WordsLoaded && totalWords > 0 && wordAssociations.length >= totalWords;

  // Phase 3 voice mode state — does NOT touch timer/scoring logic
  const [voiceMode, setVoiceMode] = useState(false);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(
    typeof window !== 'undefined' ? window.localStorage.getItem('phase3_voice_uri') || '' : ''
  );
  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem('phase3_voice_uri', selectedVoiceURI || '');
  }, [selectedVoiceURI]);

  useEffect(() => {
    let active = true;
    setExistingData(null);
    setWordsToShow([]);
    setPhase3WordsLoaded(false);
    fetchSession();
    fetchExisting();
    // The server selects the configured predefined words and appends assigned extras.
    (async () => {
      try {
        const res = await axios.get(`${API}/sessions/${sessionId}/phase3-words`);
        if (active) {
          setWordsToShow([...res.data.predefined_words, ...res.data.words]);
          setPhase3WordsLoaded(true);
        }
      } catch (e) {
        if (active) showError('Unable to load Phase 3 words. Please refresh to try again.');
      }
    })();
    return () => { active = false; };
    // This component normally remounts between analyses, but retaining the
    // dependency prevents old stimuli if routing ever reuses the instance.
  }, [sessionId]);

  // True only after the server accepts the complete administration.
  // Intermediate word saves do not complete the phase.
  const phase3IsComplete = Boolean(existingData && existingData.submitted === true);

  // Auto-save integration for Phase 3.
  // We deliberately DISABLE the debounced auto-save inside useAutoSave
  // (enabled: false) and drive ALL persistence through our own snapshot save
  // below. Reason: the useAutoSave hook uses its `data` argument as BOTH the
  // change trigger and the payload, but we need those to differ — the trigger
  // must exclude fast-ticking fields like `timeLeft` (to avoid a debounce
  // death spiral), while the payload MUST include `timeLeft` (so refreshes
  // resume the timer at the right second). Keeping only `loadDraft`/
  // `clearDraft` from the hook lets us reuse the shared GET/DELETE helpers.
  const [phase3AutoSaveStatus, setPhase3AutoSaveStatus] = useState('idle');
  const [phase3AutoSavedAt, setPhase3AutoSavedAt] = useState(null);
  const { loadDraft: loadPhase3Draft, clearDraft: clearPhase3Draft } = useAutoSave({
    sessionId,
    phaseNumber: 3,
    data: {},
    enabled: false,
  });
  const autoSaveStatus = phase3AutoSaveStatus;
  const autoSavedAt = phase3AutoSavedAt;

  // Fresh-values ref for async callbacks (setTimeout / visibilitychange /
  // beforeunload / interval) so every save reads the LATEST state — no
  // stale-closure bugs. Updated on every render.
  const phase3StateRef = useRef({});
  phase3StateRef.current = {
    wordAssociations, currentWordIndex, associations, currentAssociation,
    responseTimes, timeLeft, lastResponseTime, voiceMode, timeExpired, awaitingTimeoutNext, associationCorrectionMetadata,
  };

  const phase3ActiveRef = useRef(false);
  phase3ActiveRef.current = Boolean(
    sessionId && !phase3IsComplete && !loading && !isViewOnlyMode && started && userRole !== 'psychologist'
  );

  // Snapshot save — always PUTs the FULL current state (via ref). Used by:
  // (a) 700ms debounce on content changes, (b) 1s periodic timer save,
  // (c) visibilitychange (tab hidden), (d) beforeunload, (e) Start button.
  const savePhase3Snapshot = useCallback(async () => {
    if (!phase3ActiveRef.current) return;
    setPhase3AutoSaveStatus('saving');
    try {
      await axios.put(`${API}/sessions/${sessionId}/drafts/phase3`, phase3StateRef.current);
      setPhase3AutoSavedAt(new Date().toISOString());
      setPhase3AutoSaveStatus('saved');
    } catch (_) {
      setPhase3AutoSaveStatus('error');
    }
  }, [sessionId]);
  const [phase3DraftLoaded, setPhase3DraftLoaded] = useState(false);
  useEffect(() => {
    if (phase3DraftLoaded || phase3IsComplete) return;
    (async () => {
      const draft = await loadPhase3Draft();
      // Only restore *in-progress* state from the draft (associations for the
      // current word, what's typed but not yet added, and per-response times).
      // `wordAssociations` and `currentWordIndex` are authoritative from the
      // server (`fetchExisting`) — never overwrite them from the draft, or a
      // stale draft could rewind the client's completed-word count.
      const hasDraftData = draft && (
        (Array.isArray(draft.associations) && draft.associations.length > 0) ||
        (typeof draft.currentAssociation === 'string' && draft.currentAssociation.length > 0) ||
        (typeof draft.timeLeft === 'number' && draft.timeLeft >= 0) ||
        (Array.isArray(draft.wordAssociations) && draft.wordAssociations.length > 0) ||
        (typeof draft.voiceMode === 'boolean')
      );
      if (draft && Array.isArray(draft.associations) && draft.associations.length > 0) {
        setAssociations(draft.associations);
      }
      if (draft && typeof draft.currentAssociation === 'string' && draft.currentAssociation.length > 0) {
        setCurrentAssociation(draft.currentAssociation);
      }
      if (draft && Array.isArray(draft.responseTimes) && draft.responseTimes.length > 0) {
        setResponseTimes(draft.responseTimes);
      }
      if (draft && Array.isArray(draft.associationCorrectionMetadata)) setAssociationCorrectionMetadata(draft.associationCorrectionMetadata);
      if (draft && draft.awaitingTimeoutNext === true && draft.voiceMode !== true) {
        setAwaitingTimeoutNext(true);
        setTimeExpired(true);
      }
      // Timer resume — restore the exact `timeLeft` the client last saved.
      // The tick effect only runs when the tab is active, so saving the
      // remaining seconds means the timer effectively pauses when the client
      // leaves and resumes on return (matches Phase 2 UX).
      const totalPerWord = settings?.phase3_timer_per_word || 45;
      if (draft && typeof draft.timeLeft === 'number' && draft.timeLeft >= 0 && draft.timeLeft <= totalPerWord) {
        setTimeLeft(draft.timeLeft);
        if (draft.timeLeft === 0 && !draft.timeExpired) {
          setTimeExpired(true);
        } else if (typeof draft.timeExpired === 'boolean') {
          setTimeExpired(draft.timeExpired);
        }
      }
      // Restore Voice-mode toggle + the timestamps we still use for
      // per-response timing calculations.
      if (draft && typeof draft.voiceMode === 'boolean') {
        setVoiceMode(draft.voiceMode);
      }
      if (draft && typeof draft.lastResponseTime === 'number' && draft.lastResponseTime > 0) {
        setLastResponseTime(draft.lastResponseTime);
      }
      // If we have ANY meaningful draft state, skip the intro screen and take
      // the client straight into the active word view (they've already
      // clicked Start once — no need to make them click it again).
      if (hasDraftData && userRole !== 'psychologist') {
        setStarted(true);
      }
      setPhase3DraftLoaded(true);
    })();
    // eslint-disable-next-line
  }, [sessionId]);

  const [pageVisible, setPageVisible] = useState(typeof document === 'undefined' ? true : !(document.hidden || document.visibilityState === 'hidden'));
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVis = () => setPageVisible(!(document.hidden || document.visibilityState === 'hidden'));
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (started && timeLeft > 0 && currentWordIndex < totalWords && pageVisible) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && associations.length < 3 && !timeExpired && pageVisible) {
      // Freeze input and wait for an explicit client action. Voice keeps its
      // existing manual pacing; text form additionally persists an explicit
      // awaiting-timeout-next draft state.
      // The `!timeExpired` guard also prevents cascading timeouts on resume —
      // if the client came back after the timer had already run out on a word,
      // we mark it expired and require them to click Next word manually,
      // instead of auto-filling every remaining word with "Timeout".
      if (voiceMode) {
        setTimeExpired(true);
      } else {
        handleTimeout();
      }
    }
  }, [started, timeLeft, currentWordIndex, totalWords, associations, timeExpired, pageVisible]);

  const fetchSession = async () => {
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}`);
      if (phase3SessionRef.current === sessionId) setSession(res.data);
    } catch (error) {
      console.error('Failed to fetch session:', error);
    }
  };

  const fetchExisting = async () => {
    const request = ++phase3FetchRef.current;
    const revision = phase3RevisionRef.current;
    try {
      console.log('[Phase3] fetchExisting: sessionId=', sessionId, 'role=', userRole);
      const res = await axios.get(`${API}/sessions/${sessionId}/phase3`, { timeout: 30000, skipGlobalLoading: userRole === 'psychologist' });
      if (phase3SessionRef.current !== sessionId || request !== phase3FetchRef.current || revision !== phase3RevisionRef.current) return;
      const savedAssociations = res.data?.word_associations || [];
      console.log('[Phase3] fetched:', savedAssociations.length, 'word_associations');
      if (savedAssociations.length === 0) return; // nothing on server yet
      // Keep the raw server data — the psychologist view + the phase3IsComplete
      // gate both rely on this. We deliberately do NOT compare against
      // `totalWords` here because it may still be 0 while settings/extras
      // are loading; `phase3IsComplete` re-evaluates later at render time.
      setExistingData(res.data);
      setWordAssociations(savedAssociations);
      setCurrentWordIndex(savedAssociations.length);
      if (userRole !== 'psychologist') {
        setStarted(true);
      }
      console.log(`✅ Phase 3 state restored — position ${savedAssociations.length}`);
      if (userRole !== 'psychologist') showSuccess(`Resuming from response ${savedAssociations.length + 1}`);
      return true;
    } catch (error) {
      console.error('[Phase3] Failed to fetch Phase 3 data:', error?.response?.status, error?.message);
      return false;
    }
  };

  const startPhase = () => {
    setStarted(true);
    setTimeLeft(settings?.phase3_timer_per_word || 45);
    const now = Date.now();
    setWordStartTime(now);
    setLastResponseTime(now);
    // Fire an immediate snapshot save so a refresh RIGHT AFTER clicking Start
    // captures voiceMode + starting timeLeft. Uses the fresh-values ref so it
    // is NOT affected by React's setState batching / stale closures.
    setTimeout(() => { savePhase3Snapshot(); }, 60);
  };

  // Periodic snapshot save (backup) — ensures timer + voiceMode are persisted
  // every 1s while the client is mid-flow, so a hard refresh loses at most
  // ~1s of timer.
  useEffect(() => {
    if (!started || phase3IsComplete || isViewOnlyMode || userRole === 'psychologist') return;
    const id = setInterval(() => { savePhase3Snapshot(); }, 1000);
    return () => clearInterval(id);
  }, [started, phase3IsComplete, isViewOnlyMode, userRole, savePhase3Snapshot]);

  // Fast content-change debounced save (700ms) — captures the moment the
  // client stops typing an association so their input survives a refresh
  // BEFORE the next 1s periodic tick.
  const phase3ContentDebounceRef = useRef(null);
  useEffect(() => {
    if (!started || phase3IsComplete || isViewOnlyMode || userRole === 'psychologist') return;
    setPhase3AutoSaveStatus('pending');
    if (phase3ContentDebounceRef.current) clearTimeout(phase3ContentDebounceRef.current);
    phase3ContentDebounceRef.current = setTimeout(() => { savePhase3Snapshot(); }, 700);
    return () => { if (phase3ContentDebounceRef.current) clearTimeout(phase3ContentDebounceRef.current); };
    // Intentionally excludes timeLeft/timeExpired to avoid debounce death
    // spiral. Timer state is captured by the 1s periodic save above.
  }, [wordAssociations, currentWordIndex, associations, currentAssociation, responseTimes, voiceMode, started, phase3IsComplete, isViewOnlyMode, userRole]);

  // Save snapshot immediately when the tab is hidden or on beforeunload so
  // the timer freezes at the moment the client leaves and resumes to the
  // same second when they return.
  useEffect(() => {
    if (!started || phase3IsComplete || isViewOnlyMode || userRole === 'psychologist') return;
    const onVisChange = () => { if (document.hidden || document.visibilityState === 'hidden') savePhase3Snapshot(); };
    const onUnload = () => { savePhase3Snapshot(); };
    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [started, phase3IsComplete, isViewOnlyMode, userRole, savePhase3Snapshot]);

  const handleTimeout = () => {
    if (phase3FinalizingRef.current) return;
    // In voice mode we don't auto-advance on timeout — the client controls
    // pacing via the "Next word" button. We simply stop the visible timer.
    if (voiceMode) return;
    const expiredAssociations = [...associations];
    const expiredResponseTimes = [...responseTimes];
    while (expiredAssociations.length < 3) {
      const currentTime = Date.now();
      const timeSinceLastResponse = (currentTime - lastResponseTime) / 1000;
      // Timer ran out → mark as "Timeout" (not "Undisclosed"). Undisclosed
      // is reserved for cases where the client explicitly skipped without the
      // timer expiring; Timeout distinguishes "the 45-second window closed
      // before an answer was given".
      expiredAssociations.push('');
      expiredResponseTimes.push(timeSinceLastResponse);
      setLastResponseTime(currentTime);
    }
    setAssociations(expiredAssociations);
    setResponseTimes(expiredResponseTimes);
    setTimeExpired(true);
    setAwaitingTimeoutNext(true);
  };

  const addAssociation = () => {
    if (phase3FinalizingRef.current || phase3WordSaveError) return;
    if (currentAssociation.trim() && associations.length < 3) {
      if (isReservedClientTimeoutText(currentAssociation)) {
        showError(RESERVED_TIMEOUT_MESSAGE);
        return;
      }
      if (!validateClientTextInput(currentAssociation).valid) {
        setPhase3GibberishError(CLIENT_TEXT_GIBBERISH_MESSAGE);
        return;
      }
      const currentTime = Date.now();
      const timeSinceLastResponse = (currentTime - lastResponseTime) / 1000;
      
      const newAssociations = [...associations, currentAssociation.trim()];
      const newResponseTimes = [...responseTimes, timeSinceLastResponse];
      const newCorrectionMetadata = [...associationCorrectionMetadata, currentCorrectionMetadata];
      
      setAssociations(newAssociations);
      setResponseTimes(newResponseTimes);
      setAssociationCorrectionMetadata(newCorrectionMetadata);
      setLastResponseTime(currentTime);
      setCurrentAssociation('');
      setPhase3GibberishError('');
      setCurrentCorrectionMetadata(null);
      setPhase3Correction(null);
      
      // Auto-advance to the next stimulus word once the 3rd association lands.
      // Fires in both voice and text mode — the previous voice-mode manual
      // "Next word →" step is no longer required after the 3rd Enter/click.
      if (newAssociations.length === 3) {
        const generation = phase3GenerationRef.current;
        setTimeout(() => moveToNextWord(newAssociations, newResponseTimes, false, newCorrectionMetadata, generation), 100);
      }
    }
  };

  // Voice-mode explicit advance: client clicks "Next word →" once they've
  // confirmed all 3 associations. Any missing slots are filled with either
  // "Timeout" (when the 45-second window expired) or "Undisclosed" (when the
  // client explicitly skipped early) so the psychologist can distinguish.
  const advanceToNextWordManually = () => {
    if (phase3FinalizingRef.current) return;
    let assocs = [...associations];
    let respTimes = [...responseTimes];
    const filler = timeExpired ? '' : 'Undisclosed';
    while (assocs.length < 3) {
      const currentTime = Date.now();
      const timeSinceLastResponse = (currentTime - lastResponseTime) / 1000;
      assocs.push(filler);
      respTimes.push(timeSinceLastResponse);
    }
    moveToNextWord(
      assocs,
      respTimes,
      timeExpired,
      associationCorrectionMetadata,
      phase3GenerationRef.current
    );
  };

  const moveToNextWord = async (assocs = associations, respTimes = responseTimes, systemExpired = false, correctionMeta = associationCorrectionMetadata, generation = phase3GenerationRef.current) => {
    if (generation !== phase3GenerationRef.current || phase3FinalizingRef.current) return;
    phase3FinalizingRef.current = true;
    setPhase3SavingWord(true);
    setPhase3WordSaveError(false);
    // Auto-classify filler entries: "Timeout" text (voice-mode expiry) OR
    // legacy "Undisclosed" filler (text-mode timeout) → "Timeout" classification.
    // Real responses start empty so AI can fill them without replacing existing choices.
    const timedOutSlots = getPhase3TimedOutSlots(assocs, systemExpired);
    const initialClassifications = assocs.map((_, index) => timedOutSlots[index] ? 'Time Out' : '');
    
    const newWordAssociation = {
      word: wordsToShow[currentWordIndex],
      associations: assocs,
      classifications: initialClassifications,
      response_times: respTimes,
      system_states: timedOutSlots.map((timedOut) => timedOut ? SYSTEM_TIME_EXPIRED : null),
      timed_out: timedOutSlots,
      correction_metadata: assocs.map((_, index) => correctionMeta[index] || null)
    };
    
    const updatedWordAssociations = [...wordAssociations, newWordAssociation];
    // Persist every word before the automatic final submission runs.

    // Auto-save progress after each response
    try {
      await axios.post(`${API}/sessions/${sessionId}/phase3`, updatedWordAssociations);
      console.log('✅ Phase 3 progress auto-saved:', wordsToShow[currentWordIndex]);
    } catch (error) {
      console.error('❌ Failed to auto-save Phase 3 progress:', error);
      phase3FinalizingRef.current = false;
      setPhase3SavingWord(false);
      setPhase3WordSaveError(true);
      return;
    }

    setWordAssociations(updatedWordAssociations);
    phase3GenerationRef.current += 1;
    setAssociations([]);
    setResponseTimes([]);
    setAssociationCorrectionMetadata([]);
    setCurrentAssociation('');
    setCurrentWordIndex(currentWordIndex + 1);
    setTimeLeft(settings?.phase3_timer_per_word || 45);
    setTimeExpired(false);
    setAwaitingTimeoutNext(false);
    setNarrateDone(false);
    const now = Date.now();
    setWordStartTime(now);
    setLastResponseTime(now);
    phase3FinalizingRef.current = false;
    setPhase3SavingWord(false);
  };

  const handleSubmit = async () => {
    if (!allRequiredResponsesCompleted || phase3IsComplete || loading || phase3FinalizingRef.current || isViewOnlyMode || userRole !== 'client') return;
    phase3GenerationRef.current += 1;
    phase3FinalizingRef.current = true;
    phase3ActiveRef.current = false;
    if (phase3ContentDebounceRef.current) clearTimeout(phase3ContentDebounceRef.current);
    setLoading(true);
    setPhase3SubmissionError('');
    try {
      // Reconcile first: a previous request may have succeeded even if its
      // response was lost. Never repost an already submitted administration.
      const saved = await axios.get(`${API}/sessions/${sessionId}/phase3`, { timeout: 30000, skipGlobalLoading: userRole === 'psychologist' });
      if (saved.data?.submitted !== true) {
        const result = await axios.post(`${API}/sessions/${sessionId}/phase3?final=true`, wordAssociations);
        if (result.data?.submitted !== true) throw new Error('Completion was not confirmed');
      }
      phase3FetchRef.current += 1;
      setExistingData({ ...saved.data, session_id: sessionId,
        word_associations: saved.data?.submitted === true ? saved.data.word_associations : wordAssociations,
        submitted: true });
      // Draft cleanup is best effort; a cleanup failure must not resubmit.
      try { await clearPhase3Draft(); } catch (_) {}
      await onComplete();
      showCompletionModal(3);
    } catch (error) {
      setPhase3SubmissionError('Phase 3 could not be completed. Your saved responses are safe. Please retry.');
      phase3FinalizingRef.current = false;
      phase3ActiveRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  // The same finalization path handles fresh completion and recovery after
  // reopening a fully saved, but not yet submitted, administration.
  useEffect(() => {
    if (allRequiredResponsesCompleted && !phase3SubmissionError) handleSubmit();
  }, [allRequiredResponsesCompleted, phase3IsComplete, phase3SubmissionError, userRole, isViewOnlyMode]); // eslint-disable-line

  // Intermediate timeout pacing stays unchanged. Only the final required
  // word advances automatically, using the existing timeout filler rules.
  useEffect(() => {
    if (started && timeExpired && currentWordIndex === totalWords - 1 &&
        !phase3IsComplete && !phase3WordSaveError && !isViewOnlyMode && userRole === 'client') {
      advanceToNextWordManually();
    }
  }, [started, timeExpired, currentWordIndex, totalWords, phase3IsComplete, phase3WordSaveError, isViewOnlyMode, userRole]); // eslint-disable-line

  const handleClassification = (wordIdx, assocIdx, classification) =>
    valence.save(wordIdx, assocIdx, classification);

  // Psychologist view with classification — fires whenever the client has
  // submitted at least one word (partial or complete).
  if (existingData && userRole === 'psychologist') {
    const classificationCounts = getPhase3ClassificationCounts(existingData.word_associations);

    return (
      <Card className="mt-6" data-testid="phase3-psychologist-view">
        <CategorizationProgress phase={3} progress={valence.progress} />
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Phase 3 - Exploring Client Views</CardTitle>
          
          {/* Status Info */}
          <div className="mt-3 p-3 bg-gray-100 rounded-lg text-sm">
            <p className="font-semibold text-gray-700">
              Showing {wordsToShow.length} predefined words | Client completed {existingData.word_associations?.length || 0} words
            </p>
          </div>

          {/* Cultural Context Chip — the exact religion / nationality / state (from Phase 1) the AI
              used when it drafted valence suggestions. Helps the psychologist know at a glance what
              cultural lens the AI is reasoning through and challenge suggestions that ignore it. */}
          {(() => {
            const ctx = existingData.ai_suggestions?.find((s) => s.cultural_context)?.cultural_context;
            if (!ctx) return null;
            const parts = [
              ctx.religion && ctx.religion !== 'Not disclosed' ? `Religion: ${ctx.religion}` : null,
              ctx.nationality && ctx.nationality !== 'Not disclosed' ? `Nationality: ${ctx.nationality}` : null,
              ctx.state && ctx.state !== 'Not disclosed' ? `State: ${ctx.state}` : null,
            ].filter(Boolean);
            if (parts.length === 0) return null;
            return (
              <div
                className="mt-3 flex items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-900"
                data-testid="phase3-cultural-context-chip"
              >
                <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-indigo-600" />
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">Cultural context used for AI valence</div>
                  <div className="text-gray-800">{parts.join(' · ')}</div>
                </div>
              </div>
            );
          })()}

          {/* Missing-Suggestion Banner + Regenerate — surfaces when phase3_data.ai_suggestions is
              empty. Explains to the psychologist that they can click Regenerate to force a pass. */}
          {userRole === 'psychologist' && (!existingData.ai_suggestions || existingData.ai_suggestions.length === 0) && (
            <div
              className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs"
              data-testid="phase3-ai-missing-banner"
            >
              <span className="inline-flex items-center gap-1 font-semibold text-amber-800">
                <AlertCircle className="w-3.5 h-3.5" /> AI suggestions not generated yet
              </span>
              <span className="text-amber-900/80 flex-1 min-w-0">
                They generate automatically the moment the client submits Phase 3. If you just want to
                force a fresh pass, click Regenerate.
              </span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const token = sessionStorage.getItem('token');
                    await axios.post(
                      `${API}/sessions/${sessionId}/ai-suggestions/regenerate?phase=3`,
                      null,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (phase3SessionRef.current === sessionId) await fetchExisting();
                  } catch (err) {
                    showError(err.response?.data?.detail || 'Regenerate failed');
                  }
                }}
                className="inline-flex items-center gap-1 rounded border border-amber-400 bg-white text-amber-800 hover:bg-amber-100 px-2 py-1"
                disabled={!classificationEditable}
                data-testid="btn-regen-phase3-ai"
              >
                <RefreshCw className="w-3 h-3" /> Regenerate
              </button>
            </div>
          )}
          
          {/* Instructions for Psychologists Only */}
          <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
            <h4 className="font-semibold text-blue-900 mb-2">📋 Instructions for categorizing client responses:</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p>• <strong>Assess the Connotation:</strong> Ask yourself: What is the primary emotional or cultural feeling associated with this client response?</p>
              <p>• <strong>Match to a Category:</strong> Based on the connotation, assign the client response to Positive or Negative or Neutral.</p>
              <p>• <strong>Handle Non-Responses:</strong> If no client response is given, categorize it as Undisclosed, or System Time Expired when the system timer expires.</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {valence.pending > 0 && <p role="status" className="mb-3 text-sm text-blue-700">Saving valence selections…</p>}
          {valence.error && <div role="alert" className="mb-3 text-sm text-red-700">
            {valence.error} <button type="button" disabled={!classificationEditable || valence.pending > 0} onClick={valence.progress.retry} className="underline">Retry AI selection</button>
          </div>}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold mb-3">Classification Summary</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {Object.entries(classificationCounts).map(([type, count]) => (
                <div key={type} className="text-center p-2 bg-white rounded">
                  <p className="text-2xl font-bold text-gray-700">{count}</p>
                  <p className="text-xs text-gray-600">{displayResponseStatus(type)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {/* Show ALL predefined words (up to phase3_num_words), not just completed ones */}
            {wordsToShow.map((predefinedWord, wIdx) => {
              // Find matching response association from client data (DB field is `word`)
              const storedWordIndex = existingData.word_associations?.findIndex(w => w && w.word === predefinedWord);
              const wa = existingData.word_associations?.[storedWordIndex] || {
                word: predefinedWord,
                associations: ['Undisclosed', 'Undisclosed', 'Undisclosed'],
                response_times: [0, 0, 0],
                classifications: ['', '', '']
              };
              
              const isCompleted = existingData.word_associations?.some(w => w && w.word === predefinedWord);
              
              return (
                <div key={wIdx} className={`p-4 rounded-lg ${isCompleted ? 'bg-gray-50' : 'bg-yellow-50 border-2 border-yellow-300'}`}>
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">{wIdx + 1}. Predefined Word</p>
                      <p className="font-semibold text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{wa.word}</p>
                    </div>
                    {!isCompleted && (
                      <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded font-semibold">
                        ⏳ Not Completed by Client
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {(Array.isArray(wa.associations) ? wa.associations : []).map((assoc, aIdx) => {
                      // Look up the AI's draft valence + reasoning for this specific
                      // (predefined word, response) pair. Undefined if suggestions haven't
                      // been generated yet (client hasn't submitted, or backend is still
                      // running the background pass).
                      const aiSug = phase3SuggestionFor(existingData, storedWordIndex, aIdx);
                      const sugColor = aiSug?.ai_suggestion === 'Positive' ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                        : aiSug?.ai_suggestion === 'Negative' ? 'bg-rose-50 border-rose-300 text-rose-800'
                        : aiSug?.ai_suggestion === 'Neutral' ? 'bg-slate-50 border-slate-300 text-slate-800'
                        : 'bg-amber-50 border-amber-300 text-amber-800';
                      return (
                      <div key={aIdx} className="p-2 bg-white rounded space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <span className="text-xs text-gray-600 mr-2">
                              {aIdx === 0 ? 'First Thought' : aIdx === 1 ? 'Second Thought' : 'Third Thought'}:
                            </span>
                            <span className={`font-medium ${assoc === 'Undisclosed' ? 'text-gray-400 italic' : ''}`}>
                              {isPhase3SlotTimedOut(wa, aIdx) ? SYSTEM_TIME_EXPIRED_DISPLAY : assoc}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">Time Taken: {wa.response_times?.[aIdx]?.toFixed(1)}s</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Assign Valence :</label>
                            <Select
                              value={canonicalTimeoutCategory(wa.classifications?.[aIdx], 'Time Out') || ''}
                              disabled={!classificationEditable || !isCompleted}
                              onValueChange={(val) => handleClassification(storedWordIndex, aIdx, val)}
                            >
                              <SelectTrigger className="w-48" data-testid={`classify-${wIdx}-${aIdx}`}>
                                <SelectValue placeholder="Classify..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Positive">Positive</SelectItem>
                                <SelectItem value="Negative">Negative</SelectItem>
                                <SelectItem value="Neutral">Neutral</SelectItem>
                                <SelectItem value="Undisclosed">Undisclosed</SelectItem>
                                <SelectItem value="Time Out">System Time Expired</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {aiSug ? (
                          <div
                            className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-indigo-100 bg-indigo-50/40 px-2.5 py-1.5 text-xs"
                            data-testid={`ai-inline-${wIdx}-${aIdx}`}
                          >
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
                              <Sparkles className="w-3 h-3" /> AI Suggestion
                            </span>
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-semibold ${sugColor}`}>
                              {aiSug.ai_error ? 'AI suggestion unavailable — categorize manually.' : displayResponseStatus(aiSug.ai_suggestion)}
                            </span>
                            {aiSug.reasoning && (
                              <>
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600 ml-1">Reasoning</span>
                                <span className="text-green-700 italic flex-1 min-w-0" title={aiSug.reasoning}>
                                  {aiSug.reasoning}
                                </span>
                              </>
                            )}
                          </div>
                        ) : (existingData.ai_suggestions && existingData.ai_suggestions.length > 0 && (
                          <div
                            className="mt-1 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 bg-gray-50 px-2 py-1 text-[11px] text-gray-500 italic"
                            data-testid={`ai-inline-missing-${wIdx}-${aIdx}`}
                          >
                            <Sparkles className="w-3 h-3" /> AI suggestion pending
                          </div>
                        ))}
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Client completed view - only show if the client explicitly hit Submit.
  // (Intermediate auto-saves fill word_associations too, so we must NOT gate
  // on length alone — that would mark the phase complete before the client
  // confirms.)
  if (phase3IsComplete) {
    return (
      <Card className="mt-6" data-testid="phase3-completed-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Phase 3 - Exploring Client Views Complete</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-green-600 font-semibold mb-4">✓ Phase 3 completed - All {totalWords} words processed</p>
          <div className="grid md:grid-cols-2 gap-4">
            {(existingData.word_associations || []).filter(Boolean).map((wa, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-lg">
                <div className="mb-2">
                  <p className="text-xs text-gray-600 mb-1">{i + 1}. Predefined Word</p>
                  <p className="font-semibold text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{wa.word}</p>
                </div>
                <div className="space-y-1">
                  {(Array.isArray(wa.associations) ? wa.associations : []).map((a, j) => (
                    <div key={j} className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">
                          {j === 0 ? 'First Thought' : j === 1 ? 'Second Thought' : 'Third Thought'}:
                        </span>
                        <span className={`association-chip ${a === 'Undisclosed' ? 'neutral' : (wa.classifications?.[j] || '').toLowerCase()}`}>
                          {isPhase3SlotTimedOut(wa, j) ? SYSTEM_TIME_EXPIRED_DISPLAY : a}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">Time Taken: {wa.response_times?.[j]?.toFixed(1)}s</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!phase3WordsLoaded) {
    return <p className="mt-6">Loading Phase 3 words…</p>;
  }

  if (!started) {
    const timePerWord = settings?.phase3_timer_per_word || 45;
    const associationsPerWord = 3;
    const totalTimeSec = totalWords * timePerWord;
    return (
      <Card className="mt-6" data-testid="phase3-start-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Psychosocial Stressors - Response Associations</CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            You will see <strong>{totalWords}</strong> words. For each word, provide <strong>{associationsPerWord}</strong> associations within <strong>{timePerWord}</strong> seconds.
          </p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Brain className="w-16 h-16 mx-auto mb-4 text-blue-600" />
            <h3 className="text-xl font-semibold mb-4">Ready to start Phase 3?</h3>

            {/* Prominent numeric summary */}
            <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="phase3-numeric-summary">
              <div className="p-4 rounded-lg bg-blue-50 border-2 border-blue-200">
                <p className="text-3xl font-bold text-blue-700" data-testid="phase3-total-words">{totalWords}</p>
                <p className="text-xs uppercase tracking-wide text-blue-800 mt-1">Words to Respond To</p>
              </div>
              <div className="p-4 rounded-lg bg-indigo-50 border-2 border-indigo-200">
                <p className="text-3xl font-bold text-indigo-700" data-testid="phase3-associations-per-word">{associationsPerWord}</p>
                <p className="text-xs uppercase tracking-wide text-indigo-800 mt-1">Associations per Word</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 border-2 border-amber-200">
                <p className="text-3xl font-bold text-amber-700" data-testid="phase3-time-per-word">
                  {timePerWord >= 60 ? `${Math.floor(timePerWord / 60)}:${(timePerWord % 60).toString().padStart(2, '0')}` : `${timePerWord}s`}
                </p>
                <p className="text-xs uppercase tracking-wide text-amber-800 mt-1">Time per Word {timePerWord >= 60 ? '(mm:ss)' : '(seconds)'}</p>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left inline-block max-w-2xl">
              <p className="font-semibold text-blue-900 mb-2">📋 Instructions:</p>
              <p className="text-sm text-gray-700 mb-2">For each of the <strong>{totalWords}</strong> words shown:</p>
              <ul className="text-sm text-gray-700 ml-4 mb-3">
                <li>• Type <strong>{associationsPerWord}</strong> words that come to mind</li>
                <li>• In Text Form, click <strong>Next</strong> after the timer expires</li>
                <li>• In voice mode, each stimulus word is <strong>read aloud</strong> when it appears and you can speak your associations</li>
              </ul>
              <p className="text-sm font-semibold text-blue-900">⏱️ You will have <strong>{timePerWord}</strong> seconds per word.</p>
              <p className="text-xs text-blue-700 mt-3 pt-3 border-t border-blue-200">
                Total words: <strong>{totalWords}</strong> · Total associations to enter: <strong>{totalWords * associationsPerWord}</strong> · Max total time: <strong>{Math.floor(totalTimeSec / 60)} min {totalTimeSec % 60} sec</strong>
              </p>
            </div>

            {/* Voice picker preview on intro */}
            <div className="mb-4 flex items-center justify-center gap-2 flex-wrap">
              <NarrateButton
                text={`Phase 3: Response associations. You will be shown ${totalWords} stimulus words, one at a time. For each word, enter ${associationsPerWord} associations that come to mind. You have ${timePerWord} seconds per word. In the text form, click Next if time expires. Choose voice interview to hear each word read aloud and speak your associations, or text form to type them.`}
                label="🎧 Narrate the rules"
                selectedVoiceURI={selectedVoiceURI}
                testId="phase3-intro-narrate-rules-btn"
              />
              <VoicePicker selectedVoiceURI={selectedVoiceURI} onChange={setSelectedVoiceURI} />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                onClick={() => { setVoiceMode(true); startPhase(); }}
                className="px-8 py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
                data-testid="phase3-start-voice-btn"
              >
                <Volume2 className="w-5 h-5 mr-2" />
                Start Voice Interview
              </Button>
              <Button
                onClick={() => { setVoiceMode(false); startPhase(); }}
                variant="outline"
                className="px-8 py-6 text-lg"
                data-testid="phase3-start-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Start Text Form
              </Button>
            </div>
            <VoicePrivacyNotice className="mt-6" testId="phase3-voice-privacy-notice" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (allRequiredResponsesCompleted) {
    return (
      <Card className="mt-6" data-testid="phase3-submit-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Phase 3 - Exploring Client Views Complete</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-6" role="status">{phase3SubmissionError || 'All words completed. Saving Phase 3…'}</p>
          {phase3SubmissionError && (
            <Button onClick={handleSubmit} disabled={loading || isViewOnlyMode || userRole !== 'client'} data-testid="phase3-submit-retry">
              Retry completion
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (currentWordIndex >= totalWords) {
    return <Card className="mt-6"><CardContent className="p-6">Loading required words. If this persists, refresh to retry.</CardContent></Card>;
  }

  return (
    <Card className="mt-6" data-testid="phase3-active-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Response {currentWordIndex + 1} of {totalWords}</CardTitle>
          <div className="flex items-center gap-3">
            <AutoSaveIndicator status={autoSaveStatus} savedAt={autoSavedAt} />
            {voiceMode && (
              <VoicePicker selectedVoiceURI={selectedVoiceURI} onChange={setSelectedVoiceURI} />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center bg-blue-50 p-4 rounded-lg mb-6">
          <Clock className="w-6 h-6" style={{ color: '#2563EB' }} />
          <span className="timer-display" data-testid="phase3-timer">{timeLeft}s</span>
        </div>
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 flex-wrap mb-4">
            <h3 className="text-5xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2563EB' }} data-testid="phase3-current-response">
              {wordsToShow[currentWordIndex]}
            </h3>
            {voiceMode && wordsToShow[currentWordIndex] && (
              <Phase3AutoNarrate
                word={wordsToShow[currentWordIndex]}
                selectedVoiceURI={selectedVoiceURI}
                onEnd={() => setNarrateDone(true)}
              />
            )}
          </div>
          <p className="text-gray-600">Provide {3 - associations.length} association(s) in a statement or a single unique response</p>
        </div>
        {timeExpired && (
          <div className="mb-4 p-4 rounded-lg border-2 border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="phase3-time-expired-banner">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              {voiceMode ? <>
                <p className="font-semibold">Time is up for this word.</p>
                <p className="mt-1">You can no longer add or edit associations. Please click <strong>Next word</strong> below to continue. Any unfilled slots will be recorded as <em>{SYSTEM_TIME_EXPIRED_DISPLAY}</em>.</p>
              </> : <p className="font-semibold">
                Time has expired for this word. Your completed responses have been saved. Click Next to {currentWordIndex + 1 >= totalWords ? 'complete Phase 3' : 'continue'}.
              </p>}
            </div>
          </div>
        )}
        <div className="flex gap-2 mb-4">
          <Input
            value={currentAssociation}
            onChange={(e) => {
              if (timeExpired) return;
              const value = e.target.value;
              if (validateLettersOnly(value) || value === '') {
                setCurrentAssociation(value);
                setPhase3GibberishError('');
                const suggestion = isClientTextCorrectionEnabled(settings) ? getTextCorrectionSuggestion(value) : null;
                setPhase3Correction(suggestion && phase3CorrectionDismissedFor !== value ? suggestion : null);
              } else {
                showError('⚠️ Only letters and spaces are allowed. Numbers and special characters are not permitted.');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !timeExpired) {
                e.preventDefault();
                addAssociation();
              }
            }}
            placeholder={timeExpired ? 'Time is up — click Next word to continue' : 'Type your association (letters only)'}
            data-testid="phase3-association-input"
            disabled={timeExpired || phase3SavingWord || phase3WordSaveError}
            className={timeExpired ? 'bg-gray-100 cursor-not-allowed' : ''}
          />
          {voiceMode && !timeExpired && narrateDone && (
            <MicInput
              key={`p3-mic-${currentWordIndex}-${associations.length}`}
              showListeningIndicator
              onAppend={(chunk) => {
                // Accept the full transcript (letters/spaces only), matching phase3 input rules.
                const cleaned = String(chunk).replace(/[^A-Za-z\s]/g, '').replace(/\s+/g, ' ').trim();
                if (cleaned) setCurrentAssociation((prev) => (prev ? `${prev} ${cleaned}` : cleaned));
              }}
              testId="phase3-mic-input"
            />
          )}
          <Button
            onClick={addAssociation}
            disabled={timeExpired || phase3SavingWord || phase3WordSaveError || associations.length >= 3 || !currentAssociation.trim()}
            data-testid="phase3-add-association-btn"
            className={voiceMode ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            {voiceMode ? (
              <><CheckCircle className="w-4 h-4 mr-2" /> Confirm</>
            ) : 'Add'}
          </Button>
        </div>
        {phase3GibberishError && !timeExpired && (
          <p className="text-red-600 text-sm mb-4" role="alert" data-testid="phase3-gibberish-error">{phase3GibberishError}</p>
        )}
        {isClientTextCorrectionEnabled(settings) && phase3Correction && !timeExpired && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm" data-testid="phase3-correction-suggestion">
            Did you mean “{phase3Correction.corrected}”?
            <Button type="button" size="sm" className="ml-3" data-testid="phase3-use-correction" onClick={() => {
              setCurrentAssociation(phase3Correction.corrected);
              setCurrentCorrectionMetadata({ original_response: phase3Correction.original, corrected_response: phase3Correction.corrected });
              setPhase3Correction(null);
            }}>Use suggestion</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => {
              setPhase3CorrectionDismissedFor(currentAssociation); setPhase3Correction(null); setCurrentCorrectionMetadata(null);
            }}>Keep original</Button>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mb-4">
          {associations.map((a, i) => (
            <span key={i} className="association-chip" data-testid={`phase3-association-${i}`}>
              {a} <span className="text-xs">({responseTimes[i]?.toFixed(1)}s)</span>
            </span>
          ))}
        </div>
        {phase3WordSaveError && (
          <div role="alert" className="mb-4">
            <p>This word could not be saved. Your responses are still here.</p>
            <Button onClick={() => moveToNextWord(associations, responseTimes, timeExpired)} data-testid="phase3-word-save-retry">Retry saving word</Button>
          </div>
        )}
        {(voiceMode || timeExpired) && (
          <div className="flex items-center justify-between border-t pt-4 mt-2">
            <p className="text-xs text-gray-600">
              {timeExpired
                ? `Time expired. Click "${voiceMode ? 'Next word' : 'Next'}" to ${currentWordIndex + 1 >= totalWords ? 'complete Phase 3' : 'move on'}.`
                : associations.length < 3
                  ? `Confirm ${3 - associations.length} more association${3 - associations.length === 1 ? '' : 's'} to unlock the next word.`
                  : 'All associations confirmed. Click "Next word" to continue.'}
            </p>
            <Button
              type="button"
              onClick={advanceToNextWordManually}
              disabled={phase3SavingWord || phase3WordSaveError || (!timeExpired && associations.length < 3)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="phase3-next-word-btn"
            >
              {voiceMode ? 'Next word' : 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Phase 4: Exploring Client Aspirations (Structured Favorite Things; Learning and Coping Mechanisms)
// Complete rewrite to match exact specification

// Small helper: auto-plays the current stimulus word once when it changes,
// invokes onEnd once TTS is done, then leaves a Play/Stop button in place.
const Phase3AutoNarrate = ({ word, selectedVoiceURI, onEnd }) => {
  const { isPlaying, play, stop, supported } = useSpeak(selectedVoiceURI);
  useEffect(() => {
    if (!supported || !word) { onEnd?.(); return; }
    const t = setTimeout(() => play(word, { onEnd }), 200);
    return () => clearTimeout(t);
  }, [word]); // eslint-disable-line
  return (
    <button
      type="button"
      onClick={() => (isPlaying ? stop() : play(word, { onEnd }))}
      disabled={!supported}
      className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${
        isPlaying ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-700'
      } hover:opacity-90 disabled:opacity-40`}
      title={isPlaying ? 'Stop' : 'Replay word'}
      data-testid="phase3-narrate-word-btn"
    >
      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
    </button>
  );
};

// Phase 4 auto-narrator: reads the item prompts aloud once per item change.
const Phase4AutoNarrate = ({ itemIndex, totalItems, selectedVoiceURI }) => {
  const { isPlaying, play, stop, supported } = useSpeak(selectedVoiceURI);
  const script = `Favorite thing ${itemIndex + 1} of ${totalItems}. Please tell me the name of a favourite thing, describe what it is, and list five unique words that describe why you like it.`;
  useEffect(() => {
    if (!supported) return;
    const t = setTimeout(() => play(script), 250);
    return () => clearTimeout(t);
  }, [itemIndex]); // eslint-disable-line
  return (
    <div className="mb-4 flex items-center justify-end">
      <button
        type="button"
        onClick={() => (isPlaying ? stop() : play(script))}
        disabled={!supported}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border ${
          isPlaying ? 'bg-red-50 border-red-300 text-red-700' : 'bg-indigo-50 border-indigo-300 text-indigo-700'
        } hover:opacity-90 disabled:opacity-40`}
        data-testid="phase4-narrate-item-btn"
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        {isPlaying ? 'Stop' : 'Replay prompt'}
      </button>
    </div>
  );
};

const Phase4 = ({ sessionId, settings, onComplete, userRole, showCompletionModal, isViewOnlyMode }) => {
  const { notification, showSuccess, showError, showInfo, closeNotification } = useNotification();
  
  // State management
  const [existingData, setExistingData] = useState(null);
  const categoryRevision = useRef(0);
  const phase4Active = useRef(true);
  useEffect(() => { phase4Active.current = true; return () => { phase4Active.current = false; }; }, []);
  const categoryEditable = Boolean(userRole === 'psychologist' && !isViewOnlyMode && existingData?.categorization_editable);
  const [phase4Hydrated, setPhase4Hydrated] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [phase4AwaitingTimeoutNext, setPhase4AwaitingTimeoutNext] = useState(false);

  // Durable server data and the current configured item count are the sole
  // authority for completion. Local start/index/draft state must never reopen
  // a Phase 4 whose required items have already been persisted.
  const phase4SettingsReady = Number.isFinite(Number(settings?.phase4_num_items)) && Number(settings.phase4_num_items) > 0;
  const totalItems = phase4SettingsReady ? Number(settings.phase4_num_items) : 10;
  const wordsPerItem = settings?.phase4_words_per_item || 5;
  const timePerItem = settings?.phase4_timer_total || 720;
  const isPhase4Reviewer = userRole === 'psychologist' || userRole === 'admin';
  
  // Client input states
  const [started, setStarted] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [currentItem, setCurrentItem] = useState({
    name: '',
    what_is_it: '',
    words: [],
    currentWord: ''
  });
  const phase4WhatIsItRef = useRef(null);
  const phase4DescriptorRef = useRef(null);
  const [completedItems, setCompletedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemStartTime, setItemStartTime] = useState(null);
  const [phase4Correction, setPhase4Correction] = useState(null);
  const [phase4GibberishError, setPhase4GibberishError] = useState('');
  const [phase4CorrectionMetadata, setPhase4CorrectionMetadata] = useState({});
  useEffect(() => {
    if (!isClientTextCorrectionEnabled(settings)) setPhase4Correction(null);
  }, [settings?.client_text_correction_enabled]);
  const correctPhase4Field = (field) => {
    if (phase4AwaitingTimeoutNext || !isClientTextCorrectionEnabled(settings)) return;
    const suggestion = getTextCorrectionSuggestion(currentItem[field]);
    if (!suggestion) return;
    setCurrentItem((prev) => ({ ...prev, [field]: suggestion.corrected }));
    setPhase4Correction({ field, ...suggestion });
    setPhase4CorrectionMetadata((prev) => ({ ...prev, [field]: { original_text: suggestion.original, final_text: suggestion.corrected } }));
  };
  const undoPhase4Correction = () => {
    if (!phase4Correction) return;
    if (phase4Correction.field === 'words') {
      setCurrentItem((prev) => ({ ...prev, words: prev.words.map((word, index) => index === phase4Correction.index ? phase4Correction.original : word) }));
    } else {
      setCurrentItem((prev) => ({ ...prev, [phase4Correction.field]: phase4Correction.original }));
    }
    setPhase4CorrectionMetadata((prev) => { const next = { ...prev }; delete next[phase4Correction.metadataKey || phase4Correction.field]; return next; });
    setPhase4Correction(null);
  };
  
  // Phase 4 voice mode state — does NOT touch timer or scoring logic
  const [voiceMode, setVoiceMode] = useState(false);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(
    typeof window !== 'undefined' ? window.localStorage.getItem('phase4_voice_uri') || '' : ''
  );
  useEffect(() => {
    if (typeof window !== 'undefined') window.localStorage.setItem('phase4_voice_uri', selectedVoiceURI || '');
  }, [selectedVoiceURI]);
  const phase4IsComplete = isPhase4DurablyComplete(existingData, settings);
  const phase4Ready = phase4Hydrated && phase4SettingsReady;
  const phase4ReviewerRenderState = getPhase4ReviewerRenderState({
    phase4Data: existingData,
    settings,
    dataReady: phase4Hydrated,
    settingsReady: phase4SettingsReady,
  });

  // Auto-save integration for Phase 4.
  // Same pattern as Phase 3: disable useAutoSave's debounced save and drive
  // all persistence via our own snapshot save that reads a fresh-values ref.
  // This guarantees `voiceMode` (which Phase 4 previously did NOT save) is
  // captured on every write, so a refresh restores the client back into
  // voice-interview mode rather than falling back to the text form.
  const [phase4AutoSaveStatus, setPhase4AutoSaveStatus] = useState('idle');
  const [phase4AutoSavedAt, setPhase4AutoSavedAt] = useState(null);
  const { loadDraft: loadPhase4Draft, clearDraft: clearPhase4Draft } = useAutoSave({
    sessionId,
    phaseNumber: 4,
    data: {},
    enabled: false,
  });

  // Fresh-values ref so every snapshot save reads the LATEST state.
  const phase4StateRef = useRef({});
  const [phase4DraftLoaded, setPhase4DraftLoaded] = useState(false);
  const phase4GenerationRef = useRef(0);
  const phase4SubmittingRef = useRef(false);
  const phase4FetchSequenceRef = useRef(0);
  useEffect(() => {
    // A saved full response set is ready for final submission, even with a stale item draft.
    if (phase4DraftLoaded || phase4IsComplete || !phase4Ready || getDurablePhase4Items(existingData).length >= totalItems) return;
    (async () => {
      const draft = await loadPhase4Draft();
      const hasDraftData = draft && (
        (Array.isArray(draft.completedItems) && draft.completedItems.length > 0) ||
        (draft.currentItem && (draft.currentItem.name || draft.currentItem.what_is_it || (draft.currentItem.words || []).length > 0)) ||
        (typeof draft.itemStartTime === 'number' && draft.itemStartTime > 0) ||
        draft.awaiting_timeout_next === true ||
        (typeof draft.voiceMode === 'boolean')
      );
      // Durable completed items are restored by fetchExisting and always win
      // over a possibly older draft. Only use draft completions when the
      // server has no durable progress yet.
      if (draft && Array.isArray(draft.completedItems) && draft.completedItems.length > 0) {
        try {
          const durable = await axios.get(`${API}/sessions/${sessionId}/phase4`);
          const durableItems = durable.data?.favorite_things || [];
          setCompletedItems(durableItems.length >= draft.completedItems.length ? durableItems : draft.completedItems);
          setCurrentItemIndex(Math.max(durableItems.length, draft.currentItemIndex || 0));
          if (durableItems.length >= totalItems) {
            setExistingData(durable.data);
            setStarted(false);
            setCurrentItem({ name: '', what_is_it: '', words: [], currentWord: '' });
            setItemStartTime(null);
            setPhase4DraftLoaded(true);
            return;
          }
        } catch (_) {
          setCompletedItems(draft.completedItems);
        }
      }
      if (draft && draft.currentItem) setCurrentItem(draft.currentItem);
      if (draft && draft.phase4CorrectionMetadata) setPhase4CorrectionMetadata(draft.phase4CorrectionMetadata);
      if (draft && draft.awaiting_timeout_next === true && draft.voiceMode !== true) {
        setPhase4AwaitingTimeoutNext(true);
      }
      if (draft && typeof draft.currentItemIndex === 'number') {
        setCurrentItemIndex((index) => Math.max(index, draft.currentItemIndex));
      }
      // Restore voice-interview mode so a refresh keeps the client in STT/TTS.
      if (draft && typeof draft.voiceMode === 'boolean') {
        setVoiceMode(draft.voiceMode);
      }
      // Timer resume — prefer the EXACT `itemTimeLeft` we last persisted so
      // the timer PAUSES while the tab is hidden and resumes at the same
      // second on return (same UX as Phase 3). Fall back to wall-clock
      // computation from `itemStartTime` only for drafts written before we
      // started saving `itemTimeLeft`.
      if (draft && typeof draft.itemStartTime === 'number' && draft.itemStartTime > 0) {
        setItemStartTime(draft.itemStartTime);
      }
      if (draft && typeof draft.itemTimeLeft === 'number' && draft.itemTimeLeft >= 0 && draft.itemTimeLeft <= timePerItem) {
        setItemTimeLeft(draft.itemTimeLeft);
      } else if (draft && typeof draft.itemStartTime === 'number' && draft.itemStartTime > 0) {
        const elapsed = Math.floor((Date.now() - draft.itemStartTime) / 1000);
        const remaining = Math.max(0, timePerItem - elapsed);
        setItemTimeLeft(remaining);
      }
      if (hasDraftData && !isPhase4Reviewer) {
        setStarted(true);
      }
      setPhase4DraftLoaded(true);
    })();
    // eslint-disable-next-line
  }, [sessionId, phase4Ready]);
  const [itemTimeLeft, setItemTimeLeft] = useState(timePerItem);

  // Update the fresh-values ref on every render so all snapshot saves see
  // the LATEST state — no stale closures. Includes voiceMode + timer state.
  phase4StateRef.current = {
    completedItems, currentItem, currentItemIndex, itemStartTime,
    itemTimeLeft, voiceMode, phase4CorrectionMetadata,
    awaiting_timeout_next: phase4AwaitingTimeoutNext,
  };

  const phase4ActiveRef = useRef(false);
  phase4ActiveRef.current = Boolean(
    sessionId && phase4Ready && !phase4IsComplete && !isViewOnlyMode && started && !isPhase4Reviewer
  );

  // Snapshot save — always PUTs the FULL current state (via ref).
  const savePhase4Snapshot = useCallback(async () => {
    if (!phase4ActiveRef.current || phase4SubmittingRef.current) return;
    setPhase4AutoSaveStatus('saving');
    try {
      await axios.put(`${API}/sessions/${sessionId}/drafts/phase4`, phase4StateRef.current);
      setPhase4AutoSavedAt(new Date().toISOString());
      setPhase4AutoSaveStatus('saved');
    } catch (_) {
      setPhase4AutoSaveStatus('error');
    }
  }, [sessionId]);

  // Debounced content-change save (700ms) — captures user typing quickly.
  const phase4ContentDebounceRef = useRef(null);
  useEffect(() => {
    if (!phase4Ready || !started || phase4IsComplete || isViewOnlyMode || isPhase4Reviewer) return;
    setPhase4AutoSaveStatus('pending');
    if (phase4ContentDebounceRef.current) clearTimeout(phase4ContentDebounceRef.current);
    phase4ContentDebounceRef.current = setTimeout(() => { savePhase4Snapshot(); }, 700);
    return () => { if (phase4ContentDebounceRef.current) clearTimeout(phase4ContentDebounceRef.current); };
  }, [completedItems, currentItem, currentItemIndex, itemStartTime, voiceMode, started, phase4IsComplete, isViewOnlyMode, userRole]);

  // Periodic 1s snapshot — keeps the timer + voiceMode fresh so a refresh
  // loses at most ~1s of progress.
  useEffect(() => {
    if (!phase4Ready || !started || phase4IsComplete || isViewOnlyMode || isPhase4Reviewer) return;
    const id = setInterval(() => { savePhase4Snapshot(); }, 1000);
    return () => clearInterval(id);
  }, [started, phase4IsComplete, isViewOnlyMode, userRole, savePhase4Snapshot]);

  // Tab-hidden / unload triggers so the very latest state is persisted the
  // moment the client leaves the tab or refreshes.
  useEffect(() => {
    if (!phase4Ready || !started || phase4IsComplete || isViewOnlyMode || isPhase4Reviewer) return;
    const onVisChange = () => { if (document.hidden || document.visibilityState === 'hidden') savePhase4Snapshot(); };
    const onUnload = () => { savePhase4Snapshot(); };
    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [started, phase4IsComplete, isViewOnlyMode, userRole, savePhase4Snapshot]);
  
  // Coping/Learning Mechanisms - from admin settings or default
  // Normalize whitespace: replace line breaks and multiple spaces with single space
  // Keep full text including descriptive parentheses
  const copingMechanisms = getPhase4CategoryOptions(settings?.phase4_coping_mechanisms, [
      "Watching", "Listening", "Playing", "Reading", "Socializing",
      "Through Success Stories", "Family Members", "Petting", "Praying",
      "Visiting Native Place", "Travelling", "Singing", "Eating",
      "Writing", "Drawing", "Exercising", "Drugs", "Romancing",
      "Sleeping", "Love", "Undisclosed"
  ]);
  
  // Thematic Domains - from admin settings or default
  // Normalize whitespace: replace line breaks and multiple spaces with single space
  // Keep full text including descriptive parentheses
  const thematicDomains = getPhase4CategoryOptions(settings?.phase4_thematic_domains, [
      "Family", "Friends", "Love", "Patriotism", "Personality Development",
      "Horror", "Suspense", "Planning", "Fiction", "Fantasy", "Nature",
      "Memories", "Hobbies", "Philosophy", "Spirituality", "Romance",
      "Undisclosed"
  ]);
  
  const categories = usePhase4Categories({ sessionId, API, data: existingData,
    editable: categoryEditable, refresh: () => fetchExisting(true), revision: categoryRevision,
    copingOptions: copingMechanisms, domainOptions: thematicDomains });

  useEffect(() => {
    fetchExisting();
  }, [sessionId]);

  // Settings and durable data hydrate independently. Once both are present,
  // resume a genuinely partial client session at the first remaining item.
  useEffect(() => {
    if (!phase4Ready || phase4IsComplete || isPhase4Reviewer) return;
    const durableItems = existingData?.favorite_things;
    if (!Array.isArray(durableItems) || durableItems.length === 0) return;
    setCompletedItems(durableItems);
    setCurrentItemIndex(durableItems.length);
    setStarted(durableItems.length < totalItems);
  }, [phase4Ready, phase4IsComplete, isPhase4Reviewer, existingData]);
  
  // Update timer when settings change
  useEffect(() => {
    if (!started) {
      // Only update if timer hasn't started yet
      setItemTimeLeft(timePerItem);
    }
  }, [timePerItem, started]);
  
  // Track tab visibility so the countdown effectively pauses while the tab
  // is hidden (matches Phase 3 UX). We save `itemTimeLeft` directly on
  // hide, so return-to-tab resumes at the exact same second.
  const [phase4PageVisible, setPhase4PageVisible] = useState(typeof document === 'undefined' ? true : !(document.hidden || document.visibilityState === 'hidden'));
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVis = () => setPhase4PageVisible(!(document.hidden || document.visibilityState === 'hidden'));
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Timer effect - PER ITEM (calculated from total time)
  useEffect(() => {
    if (phase4Ready && !phase4IsComplete && !isPhase4Reviewer && started && !phase4AwaitingTimeoutNext && itemTimeLeft > 0 && phase4PageVisible) {
      const generation = phase4GenerationRef.current;
      const timer = setTimeout(() => {
        if (generation !== phase4GenerationRef.current || phase4SubmittingRef.current) return;
        setItemTimeLeft(value => Math.max(0, value - 1));
      }, 1000);
      return () => clearTimeout(timer);
    } else if (phase4Ready && !phase4IsComplete && !isPhase4Reviewer && started && !phase4AwaitingTimeoutNext && itemTimeLeft === 0 && phase4PageVisible) {
      handleItemTimeExpired(phase4GenerationRef.current);
    }
  }, [phase4Ready, phase4IsComplete, isPhase4Reviewer, started, phase4AwaitingTimeoutNext, itemTimeLeft, phase4PageVisible]);
  
  const fetchExisting = async (background = false) => {
    const requestSequence = ++phase4FetchSequenceRef.current;
    const revision = categoryRevision.current;
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/phase4`, { skipGlobalLoading: background, timeout: 30000 });
      if (!phase4Active.current || revision !== categoryRevision.current || requestSequence !== phase4FetchSequenceRef.current || phase4SubmittingRef.current) return;
      if (res.data && res.data.favorite_things && res.data.favorite_things.length > 0) {
        setExistingData((previous) => (
          isPhase4DurablyComplete(previous, settings) && !isPhase4DurablyComplete(res.data, settings)
            ? previous
            : res.data
        ));
        const durablyComplete = isPhase4DurablyComplete(res.data, settings);
        if (durablyComplete) {
          setCompletedItems(res.data.favorite_things);
          setStarted(false);
          setCurrentItem({ name: '', what_is_it: '', words: [], currentWord: '' });
          setItemStartTime(null);
          setItemTimeLeft(0);
        } else if (settings && !isPhase4Reviewer) {
          setCompletedItems(res.data.favorite_things);
          setCurrentItemIndex(res.data.favorite_things.length);
          setStarted(res.data.favorite_things.length < totalItems);
        }
      }
      return true;
    } catch (error) {
      console.error('Failed to fetch Phase 4 data:', error);
      return false;
    } finally {
      if (phase4Active.current) setPhase4Hydrated(true);
    }
  };
  
  const startPhase = () => {
    setStarted(true);
    setItemStartTime(Date.now());
    setItemTimeLeft(timePerItem); // Reset to admin-configured time for first item
    // Fire an immediate snapshot save so a refresh RIGHT AFTER clicking Start
    // captures voiceMode + itemStartTime. Uses the fresh-values ref so it is
    // NOT affected by React's setState batching / stale closures.
    setTimeout(() => { savePhase4Snapshot(); }, 60);
  };

  const withoutInvalidPendingClientText = (item) => ({
    ...item,
    name: validateClientTextInput(item?.name).valid ? item?.name : '',
    what_is_it: validateClientTextInput(item?.what_is_it).valid ? item?.what_is_it : '',
    currentWord: validateClientTextInput(item?.currentWord).valid ? item?.currentWord : '',
    words: (item?.words || []).map((word) => validateClientTextInput(word).valid ? word : ''),
  });
  
  const handleItemTimeExpired = async (generation = phase4GenerationRef.current, manualNext = false) => {
    if (generation !== phase4GenerationRef.current || phase4SubmittingRef.current) return;
    if (!voiceMode && !manualNext) {
      const timeoutSlots = createPhase4TimeoutSlots(withoutInvalidPendingClientText(currentItem), wordsPerItem);
      const finalizedCurrentItem = {
        ...currentItem,
        name: timeoutSlots.item,
        what_is_it: timeoutSlots.what_is_it,
        words: timeoutSlots.words,
        currentWord: '',
        field_system_states: timeoutSlots.field_system_states,
        field_timed_out: timeoutSlots.field_timed_out,
        descriptor_system_states: timeoutSlots.descriptor_system_states,
        descriptor_timed_out: timeoutSlots.descriptor_timed_out,
        required_descriptor_count: timeoutSlots.required_descriptor_count,
      };
      setCurrentItem(finalizedCurrentItem);
      setPhase4AwaitingTimeoutNext(true);
      setItemTimeLeft(0);
      phase4StateRef.current = {
        ...phase4StateRef.current,
        currentItem: finalizedCurrentItem,
        itemTimeLeft: 0,
        awaiting_timeout_next: true,
      };
      setTimeout(() => { savePhase4Snapshot(); }, 0);
      return;
    }
    if (!voiceMode && manualNext && !phase4AwaitingTimeoutNext) return;
    phase4SubmittingRef.current = true;
    phase4ActiveRef.current = false;
    if (phase4ContentDebounceRef.current) clearTimeout(phase4ContentDebounceRef.current);
    if (voiceMode) showInfo(`⏱️ Time's up for item ${currentItemIndex + 1}! Auto-saving and moving to next item...`);
    
    // Save current item with whatever data exists (partial response allowed)
    const responseTime = (Date.now() - itemStartTime) / 1000;
    
    const timeoutSlots = createPhase4TimeoutSlots(withoutInvalidPendingClientText(currentItem), wordsPerItem);
    const newItem = {
      ...timeoutSlots,
      response_time: responseTime,
      timed_out: true,
      ...phase4ExpiredCategories(timeoutSlots),
      categorization_explanations: Array(timeoutSlots.words.length).fill(''),
      correction_metadata: phase4CorrectionMetadata
    };
    
    const updatedItems = [...completedItems, newItem];
    setCompletedItems(updatedItems);
    
    // Determine if this was the FINAL item BEFORE saving so we pass
    // ?final=true to the backend and the phase gets marked submitted.
    const isFinalItem = updatedItems.length >= totalItems;

    // Auto-save
    try {
      await saveData(updatedItems, isFinalItem);
      phase4GenerationRef.current += 1;
      phase4SubmittingRef.current = false;
      setPhase4AwaitingTimeoutNext(false);
      
      if (!isFinalItem) {
        // Move to next item
        setCurrentItemIndex(currentItemIndex + 1);
        setCurrentItem({ name: '', what_is_it: '', words: [], currentWord: '' });
        setPhase4Correction(null);
        setPhase4CorrectionMetadata({});
        setItemStartTime(Date.now());
        setItemTimeLeft(timePerItem); // Reset timer to admin-configured time
        setStarted(true); // Continue timer for next item
        showInfo(`Moving to item ${currentItemIndex + 2}...`);
      } else {
        setStarted(false);
        // Invalidate every older GET/draft callback before installing the
        // durable final response locally. Completion wins synchronously.
        phase4FetchSequenceRef.current += 1;
        setCurrentItem({ name: '', what_is_it: '', words: [], currentWord: '' });
        setItemStartTime(null);
        setItemTimeLeft(0);
        setExistingData((previous) => ({ ...previous, favorite_things: updatedItems, submitted: true }));
        try { await clearPhase4Draft(); } catch (_) {}
        showCompletionModal(4);
        onComplete();
      }
    } catch (error) {
      showError('❌ Failed to save. Please try again.');
      console.error('Save error:', error);
      setStarted(false); // Stop timer on error
      phase4SubmittingRef.current = false;
    }
  };
  
  const addWord = () => {
    if (phase4AwaitingTimeoutNext) return;
    if (currentItem.currentWord.trim() && currentItem.words.length < wordsPerItem) {
      if (isReservedClientTimeoutText(currentItem.currentWord)) {
        showError(RESERVED_TIMEOUT_MESSAGE);
        return;
      }
      if (!validateClientTextInput(currentItem.currentWord).valid) {
        setPhase4GibberishError(CLIENT_TEXT_GIBBERISH_MESSAGE);
        return;
      }
      const suggestion = isClientTextCorrectionEnabled(settings) ? getTextCorrectionSuggestion(currentItem.currentWord.trim()) : null;
      const finalWord = suggestion?.corrected || currentItem.currentWord.trim();
      const wordIndex = currentItem.words.length;
      setCurrentItem({
        ...currentItem,
        words: [...currentItem.words, finalWord],
        currentWord: ''
      });
      setPhase4GibberishError('');
      if (suggestion) {
        const metadataKey = `word_${wordIndex}`;
        setPhase4Correction({ field: 'words', index: wordIndex, metadataKey, ...suggestion });
        setPhase4CorrectionMetadata((prev) => ({ ...prev, [metadataKey]: { original_text: suggestion.original, final_text: suggestion.corrected } }));
      }
      if (currentItem.words.length + 1 < wordsPerItem) {
        window.requestAnimationFrame(() => phase4DescriptorRef.current?.focus());
      }
    }
  };
  
  const removeWord = (index) => {
    if (phase4AwaitingTimeoutNext) return;
    setCurrentItem({
      ...currentItem,
      words: currentItem.words.filter((_, i) => i !== index)
    });
  };
  
  const handleSubmitClick = () => {
    if ([currentItem.name, currentItem.what_is_it, ...currentItem.words].some(isReservedClientTimeoutText)) {
      showError(RESERVED_TIMEOUT_MESSAGE);
      return;
    }
    if ([currentItem.name, currentItem.what_is_it, ...currentItem.words].some((value) => !validateClientTextInput(value).valid)) {
      setPhase4GibberishError(CLIENT_TEXT_GIBBERISH_MESSAGE);
      return;
    }
    // Validation with clear feedback
    if (!currentItem.name.trim()) {
      showError('❌ Please enter the name of your favorite thing');
      return;
    }
    if (!currentItem.what_is_it.trim()) {
      showError('❌ Please describe what it is');
      return;
    }
    if (currentItem.words.length === 0) {
      showError('❌ Please add at least 1 response');
      return;
    }
    
    // Show confirmation dialog - accepts any number of words (1-5)
    setShowSubmitConfirm(true);
  };
  
  const completeCurrentItem = async (allowPartial = false) => {
    if (phase4SubmittingRef.current) return;
    if ([currentItem.name, currentItem.what_is_it, ...currentItem.words].some(isReservedClientTimeoutText)) {
      showError(RESERVED_TIMEOUT_MESSAGE);
      return;
    }
    if ([currentItem.name, currentItem.what_is_it, ...currentItem.words].some((value) => !validateClientTextInput(value).valid)) {
      setPhase4GibberishError(CLIENT_TEXT_GIBBERISH_MESSAGE);
      return;
    }
    // Close confirmation dialog if open
    setShowSubmitConfirm(false);
    
    // Invalidate the timer synchronously, before React can commit started=false.
    phase4SubmittingRef.current = true;
    phase4GenerationRef.current += 1;
    phase4ActiveRef.current = false;
    if (phase4ContentDebounceRef.current) clearTimeout(phase4ContentDebounceRef.current);
    // STOP THE TIMER when item is submitted
    setStarted(false);
    
    // Show saving message
    showInfo('💾 Saving...');
    
    const responseTime = (Date.now() - itemStartTime) / 1000;
    
    const newItem = {
      item: currentItem.name.trim(),
      what_is_it: currentItem.what_is_it.trim(),
      words: currentItem.words,
      response_time: responseTime,
      timed_out: false, // User submitted manually, not timed out
      coping_mechanism: null,
      thematic_domains: Array(currentItem.words.length).fill(null),
      categorization_explanations: Array(currentItem.words.length).fill(''),
      correction_metadata: phase4CorrectionMetadata
    };
    
    const updatedItems = [...completedItems, newItem];
    setCompletedItems(updatedItems);
    
    // Auto-save after each item
    try {
      const isFinalItem = currentItemIndex + 1 >= totalItems;
      await saveData(updatedItems, isFinalItem);
      phase4SubmittingRef.current = false;
      
      if (currentItemIndex + 1 < totalItems) {
        // Move to next item
        setCurrentItemIndex(currentItemIndex + 1);
        setCurrentItem({ name: '', what_is_it: '', words: [], currentWord: '' });
        setPhase4Correction(null);
        setPhase4CorrectionMetadata({});
        setItemStartTime(Date.now());
        setItemTimeLeft(timePerItem); // Reset timer to admin-configured time for next item
        // RESTART TIMER for next item
        setStarted(true);
        showSuccess(`✅ Item ${currentItemIndex + 1} saved! Moving to item ${currentItemIndex + 2}`);
      } else {
        phase4FetchSequenceRef.current += 1;
        setCurrentItem({ name: '', what_is_it: '', words: [], currentWord: '' });
        setItemStartTime(null);
        setItemTimeLeft(0);
        setExistingData((previous) => ({ ...previous, favorite_things: updatedItems, submitted: true }));
        try { await clearPhase4Draft(); } catch (_) {}
        showCompletionModal(4);
        onComplete();
      }
    } catch (error) {
      showError('❌ Failed to save. Please try again.');
      console.error('Save error:', error);
      // RESTART TIMER if save failed
      phase4SubmittingRef.current = false;
      phase4ActiveRef.current = true;
      setStarted(true);
    }
  };
  
  const submitSavedPhase4 = async () => {
    if (phase4SubmittingRef.current || phase4IsComplete || completedItems.length < totalItems || isViewOnlyMode || isPhase4Reviewer) return;
    phase4SubmittingRef.current = true;
    setLoading(true);
    try {
      await saveData(completedItems, true);
      setExistingData(previous => ({ ...previous, favorite_things: completedItems, submitted: true }));
      try { await clearPhase4Draft(); } catch (_) {}
      showCompletionModal(4);
      onComplete();
    } catch (_) {
      showError('Failed to submit. Please try again.');
    } finally {
      phase4SubmittingRef.current = false;
      setLoading(false);
    }
  };

  const saveData = async (data, final = false) => {
    const suffix = final ? '?final=true' : '';
    const response = await axios.post(`${API}/sessions/${sessionId}/phase4${suffix}`, data);
    console.log('Phase 4 data saved:', data.length, 'items', final ? '(FINAL)' : '(draft)');
    return response.data;
  };
  
  const handleCategorize = (itemIndex, mechanism) => categories.save(itemIndex, 'coping_mechanism', null, mechanism);
  const handleCategorizeWord = (itemIndex, wordIndex, domain) => categories.save(itemIndex, 'thematic_domain', wordIndex, domain);

  const handleAddExplanation = async (itemIndex, wordIndex, explanation) => {
    try {
      // Update local state immediately for better UX
      setExistingData(prev => {
        const updated = { ...prev };
        if (updated.favorite_things && updated.favorite_things[itemIndex]) {
          const item = { ...updated.favorite_things[itemIndex] };
          if (!Array.isArray(item.categorization_explanations)) {
            item.categorization_explanations = Array(item.words?.length || 5).fill('');
          }
          item.categorization_explanations[wordIndex] = explanation;
          updated.favorite_things[itemIndex] = item;
        }
        return updated;
      });
      
      // Debounce backend save
      if (window.explanationTimeout) {
        clearTimeout(window.explanationTimeout);
      }
      
      window.explanationTimeout = setTimeout(async () => {
        await axios.patch(`${API}/sessions/${sessionId}/phase4/add-explanation?item_index=${itemIndex}&word_index=${wordIndex}&explanation=${encodeURIComponent(explanation)}`);
        console.log('Explanation saved to backend');
      }, 1000);
      
    } catch (error) {
      console.error('Failed to save explanation:', error);
    }
  };
  
  // ============================================
  // PSYCHOLOGIST VIEW
  // ============================================
  let mainContent = null;
  
  if (!phase4Ready) {
    mainContent = (
      <Card className="mt-6" data-testid="phase4-hydrating">
        <CardContent className="flex items-center justify-center gap-2 py-12 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading Phase 4 responses...
        </CardContent>
      </Card>
    );
  }
  else if (isPhase4Reviewer && phase4ReviewerRenderState === 'review') {
    // Calculate counts
    const { mechanismCounts, themeCounts } = getPhase4SummaryCounts(existingData.favorite_things);
    
    const isNonData = (k) => k === 'Undisclosed' || k === 'Timeout' || k === 'Undisclosed/Timeout';
    const sortedMechanisms = Object.entries(mechanismCounts)
      .filter(([k]) => !isNonData(k))
      .sort(([, a], [, b]) => b - a);
    const sortedThemes = Object.entries(themeCounts)
      .filter(([k]) => !isNonData(k))
      .sort(([, a], [, b]) => b - a);
    const mechUndisclosed = (mechanismCounts['Undisclosed'] || 0) + (mechanismCounts['Undisclosed/Timeout'] || 0);
    const mechTimeout = mechanismCounts['Timeout'] || 0;
    const themeUndisclosed = (themeCounts['Undisclosed'] || 0) + (themeCounts['Undisclosed/Timeout'] || 0);
    const themeTimeout = themeCounts['Timeout'] || 0;
    
    mainContent = (
      <Card className="mt-6" data-testid="phase4-psychologist-view">
        <CategorizationProgress phase={4} progress={categories.progress} />
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Phase 4 - Exploring Client Aspirations
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Categorize the client's favorite things into Coping/Learning Mechanisms and Thematic Domains
          </p>
          
          {/* Instructions for Psychologists Only */}
          <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
            <h4 className="font-semibold text-blue-900 mb-2">📋 Instructions for Assigning Client Responses to Domains</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p>• <strong>There are no written rules</strong> for assigning client responses to a domain.</p>
              <p>• <strong>The only thing is that client response should naturally belong</strong> or fall to a domain.</p>
              <p>• <strong>If a client response falls under more than one domain,</strong> then see the context of the client response and assign it to that domain.</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-3 text-blue-900">Dominant Coping/Learning Mechanisms</h4>
              <div className="space-y-2">
                {sortedMechanisms.length > 0 ? (
                  sortedMechanisms.slice(0, 5).map(([mech, count]) => (
                    <div key={mech} className="flex justify-between items-center">
                      <span className="text-sm">{mech}</span>
                      <span className="font-bold text-lg text-blue-600">{count}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No categorizations yet</p>
                )}
                <div className="mt-3 pt-3 border-t border-blue-200 space-y-2">
                  <div className="flex justify-between items-center" data-testid="mech-undisclosed-row">
                    <span className="text-sm text-gray-600">Undisclosed</span>
                    <span className="font-bold text-lg text-gray-500">{mechUndisclosed}</span>
                  </div>
                  <div className="flex justify-between items-center" data-testid="mech-timeout-row">
                    <span className="text-sm text-gray-600">{SYSTEM_TIME_EXPIRED_DISPLAY}</span>
                    <span className="font-bold text-lg text-gray-500">{mechTimeout}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-3 text-green-900">Dominant Thematic Domains</h4>
              <div className="space-y-2">
                {sortedThemes.length > 0 ? (
                  sortedThemes.slice(0, 5).map(([theme, count]) => (
                    <div key={theme} className="flex justify-between items-center">
                      <span className="text-sm">{theme}</span>
                      <span className="font-bold text-lg text-blue-600">{count}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No categorizations yet</p>
                )}
                <div className="mt-3 pt-3 border-t border-green-200 space-y-2">
                  <div className="flex justify-between items-center" data-testid="theme-undisclosed-row">
                    <span className="text-sm text-gray-600">Undisclosed</span>
                    <span className="font-bold text-lg text-gray-500">{themeUndisclosed}</span>
                  </div>
                  <div className="flex justify-between items-center" data-testid="theme-timeout-row">
                    <span className="text-sm text-gray-600">{SYSTEM_TIME_EXPIRED_DISPLAY}</span>
                    <span className="font-bold text-lg text-gray-500">{themeTimeout}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {categories.pending > 0 && <p role="status" className="mb-3 text-sm text-blue-700">Saving category selections…</p>}
          {categories.error && <div role="alert" className="mb-3 text-sm text-red-700">
            {categories.error} <button type="button" disabled={!categoryEditable || categories.pending > 0} onClick={categories.progress.retry} className="underline">Retry AI selection</button>
          </div>}
          {/* Client Responses */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Client's Favorite Things</h3>
            {existingData.favorite_things.map((ft, i) => {
              // Look up AI draft categorizations for this item. `coping_mechanism` suggestion is
              // one row per item; `thematic_domain` suggestions are one row per (item, word).
              const target = existingData.selection_targets?.[i];
              const copingSug = phase4SuggestionFor(existingData, target, 'coping_mechanism');
              const findDomainSug = (wIdx) => phase4SuggestionFor(existingData, target, 'thematic_domain', target?.word_indices?.[wIdx]);
              return (
              <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Client Favourite Thing Name</p>
                    <h4 className="text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {i + 1}. {isPhase4FieldTimedOut(ft, 'item') ? SYSTEM_TIME_EXPIRED_DISPLAY : ft.item}
                    </h4>
                    <div className="mt-2">
                      <span className="text-xs text-gray-600">What Is This Favourite Thing: </span>
                      <span className="text-sm text-gray-700 italic">{isPhase4FieldTimedOut(ft, 'what_is_it') ? SYSTEM_TIME_EXPIRED_DISPLAY : ft.what_is_it}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                    {ft.response_time?.toFixed(1)}s
                  </span>
                </div>
                
                {/* Categorize "What is it" into Coping/Learning Mechanism */}
                <div className="mb-4 p-3 bg-blue-50 rounded">
                  <Label className="text-sm font-semibold text-blue-900">
                    Categorize into Coping/Learning Mechanism (based on "what is it"):
                  </Label>
                  <Select 
                    value={resolvePhase4Category(ft.coping_mechanism, copingMechanisms) || ''}
                    onValueChange={(val) => handleCategorize(i, val)}
                    disabled={!categoryEditable}
                  >
                    <SelectTrigger className="mt-2 bg-white" data-testid={`categorize-coping-${i}`}>
                      <SelectValue placeholder={isViewOnlyMode ? "Not categorized" : "Select mechanism..."} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {copingMechanisms.map((m, idx) => (
                        <SelectItem 
                          key={idx} 
                          value={m}
                        >
                          {displayResponseStatus(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {copingSug && (
                    <div
                      className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-blue-200 bg-white px-2.5 py-1.5 text-xs shadow-sm"
                      data-testid={`ai-inline-coping-${i}`}
                    >
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-blue-700">
                        <Sparkles className="w-3 h-3" /> AI Suggestion
                      </span>
                      <span className="inline-flex items-center rounded-full border px-2 py-0.5 font-semibold bg-blue-50 border-blue-300 text-blue-800">
                        {copingSug.ai_error ? 'AI suggestion unavailable — categorize manually.' : displayResponseStatus(copingSug.ai_suggestion)}
                      </span>
                      {copingSug.reasoning && (
                        <>
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600 ml-1">Reasoning</span>
                          <span className="text-green-700 italic flex-1 min-w-0" title={copingSug.reasoning}>
                            {copingSug.reasoning}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Categorize each of the 5 words into Thematic Domains */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-green-900">
                    What I Like Most In This Favourite Thing :
                  </Label>
                  {ft.words?.map((response, wIdx) => {
                    const dSug = findDomainSug(wIdx);
                    return (
                    <div key={wIdx} className="p-3 bg-white rounded border border-gray-200">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <span className="text-sm text-gray-600 min-w-[120px]">
                          Client Response:
                        </span>
                        <span className="font-medium flex-1">{isPhase4DescriptorTimedOut(ft, wIdx) ? SYSTEM_TIME_EXPIRED_DISPLAY : response}</span>
                        <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Categorize Each Response Into Any Of These Thematic Domain :</label>
                        <Select 
                          value={resolvePhase4Category(ft.thematic_domains?.[wIdx], thematicDomains) || ''}
                          onValueChange={(val) => handleCategorizeWord(i, wIdx, val)}
                          disabled={!categoryEditable}
                        >
                          <SelectTrigger className="w-48" data-testid={`categorize-domain-${i}-${wIdx}`}>
                            <SelectValue placeholder={isViewOnlyMode ? "Not categorized" : "Select domain..."} />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {thematicDomains.map((d, idx) => (
                              <SelectItem 
                                key={idx} 
                                value={d}
                              >
                                {displayResponseStatus(d)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {dSug && (
                        <div
                          className="mt-1 mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-emerald-200 bg-emerald-50/40 px-2.5 py-1.5 text-xs"
                          data-testid={`ai-inline-domain-${i}-${wIdx}`}
                        >
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                            <Sparkles className="w-3 h-3" /> AI Suggestion
                          </span>
                          <span className="inline-flex items-center rounded-full border px-2 py-0.5 font-semibold bg-emerald-100 border-emerald-300 text-emerald-800">
                            {dSug.ai_error ? 'AI suggestion unavailable — categorize manually.' : displayResponseStatus(dSug.ai_suggestion)}
                          </span>
                          {dSug.reasoning && (
                            <>
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600 ml-1">Reasoning</span>
                              <span className="text-green-700 italic flex-1 min-w-0" title={dSug.reasoning}>
                                {dSug.reasoning}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Optional explanation */}
                      {(Array.isArray(ft.thematic_domains) && ft.thematic_domains[wIdx]) && (
                        <ExplanationVoiceInput
                          i={i}
                          wIdx={wIdx}
                          value={(Array.isArray(ft.categorization_explanations) ? ft.categorization_explanations[wIdx] : null) || ''}
                          onSave={(text) => handleAddExplanation(i, wIdx, text)}
                          disabled={isViewOnlyMode}
                        />
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
          
        </CardContent>
      </Card>
    );
  }
  
  // ============================================
  // CLIENT COMPLETED VIEW
  // ============================================
  else if (phase4IsComplete && userRole === 'client') {
    mainContent = (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Phase 4 - Exploring Client Aspirations Complete
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-green-600 font-semibold mb-4">
            ✓ You completed {existingData.favorite_things.length} out of {totalItems} favorite things
          </p>
          
          <div className="space-y-4">
            {existingData.favorite_things.map((ft, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Client Favourite Thing Name</p>
                    <h4 className="font-semibold text-lg">{i + 1}. {isPhase4FieldTimedOut(ft, 'item') ? SYSTEM_TIME_EXPIRED_DISPLAY : ft.item}</h4>
                  </div>
                  <span className="text-xs text-gray-500">Response time: {ft.response_time?.toFixed(1)}s</span>
                </div>
                
                <div className="mb-3">
                  <span className="text-xs text-gray-600">What Is This Favourite Thing: </span>
                  <span className="text-sm text-gray-700 italic">{isPhase4FieldTimedOut(ft, 'what_is_it') ? SYSTEM_TIME_EXPIRED_DISPLAY : ft.what_is_it}</span>
                </div>
                
                <div>
                  <p className="text-xs text-gray-600 mb-2">What I Like Most In This Favourite Thing</p>
                  <div className="flex flex-wrap gap-2">
                    {ft.words?.map((w, j) => (
                      <span key={j} className="association-chip">
                        {isPhase4DescriptorTimedOut(ft, j) ? SYSTEM_TIME_EXPIRED_DISPLAY : w}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // ============================================
  // CLIENT INPUT VIEW
  // ============================================
  else if (completedItems.length >= totalItems && userRole === 'client') {
    mainContent = (
      <Card className="mt-6">
        <CardHeader><CardTitle>Phase 4 - Ready to Submit</CardTitle></CardHeader>
        <CardContent>
          <p className="mb-6">All required favorite things have been saved.</p>
          <Button onClick={submitSavedPhase4} disabled={loading || isViewOnlyMode} data-testid="phase4-final-submit-btn">
            {loading ? 'Submitting...' : 'Complete Phase 4'}
          </Button>
        </CardContent>
      </Card>
    );
  }
  else if (!started) {
    mainContent = (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Phase 4 - Exploring Client Aspirations
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            List your <strong>{totalItems}</strong> favorite things, explaining what each is and why you like it using exactly <strong>{wordsPerItem}</strong> unique descriptive words. You will have <strong>{timePerItem}</strong> seconds per item.
          </p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Brain className="w-16 h-16 mx-auto mb-4 text-blue-600" />
            <h3 className="text-xl font-semibold mb-4">Ready to explore your favorite things?</h3>

            {/* Prominent numeric summary */}
            <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="phase4-numeric-summary">
              <div className="p-4 rounded-lg bg-blue-50 border-2 border-blue-200">
                <p className="text-3xl font-bold text-blue-700" data-testid="phase4-total-items">{totalItems}</p>
                <p className="text-xs uppercase tracking-wide text-blue-800 mt-1">Favorite Things</p>
              </div>
              <div className="p-4 rounded-lg bg-indigo-50 border-2 border-indigo-200">
                <p className="text-3xl font-bold text-indigo-700" data-testid="phase4-words-per-item">{wordsPerItem}</p>
                <p className="text-xs uppercase tracking-wide text-indigo-800 mt-1">Unique Responses per Favorite Things</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 border-2 border-amber-200">
                <p className="text-3xl font-bold text-amber-700" data-testid="phase4-time-per-item">
                  {timePerItem >= 60 ? `${Math.floor(timePerItem / 60)}:${(timePerItem % 60).toString().padStart(2, '0')}` : `${timePerItem}s`}
                </p>
                <p className="text-xs uppercase tracking-wide text-amber-800 mt-1">Time per Item {timePerItem >= 60 ? '(mm:ss)' : '(seconds)'}</p>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left inline-block">
              <p className="font-semibold text-blue-900 mb-2">📋 Instructions:</p>
              <p className="text-sm text-gray-700 mb-3">List your <strong>{totalItems}</strong> favorite things one by one.</p>
              <p className="text-sm text-gray-700 mb-2">For each favorite thing:</p>
              <ul className="text-sm text-gray-700 ml-4 mb-3">
                <li>• Enter its name</li>
                <li>• Describe what it is (e.g., activity, person, place, object, etc.)</li>
                <li>• Explain why you like it using exactly <strong>{wordsPerItem}</strong> unique descriptive words</li>
                <li>• In voice mode, each prompt is narrated when the item appears and you can dictate the name and description</li>
              </ul>
              <p className="text-sm font-semibold text-blue-900">⏱️ You will have <strong>{timePerItem}</strong> seconds for each item.</p>
              <p className="text-sm text-gray-700">Once time expires, the system will move to the next item automatically.</p>
              <p className="text-xs text-blue-700 mt-3 pt-3 border-t border-blue-200">
                Total items: <strong>{totalItems}</strong> · Total unique words to enter overall: <strong>{totalItems * wordsPerItem}</strong> · Max total time: <strong>{Math.floor((totalItems * timePerItem) / 60)} min {(totalItems * timePerItem) % 60} sec</strong>
              </p>
            </div>
            <div className="mb-4 flex items-center justify-center gap-2 flex-wrap">
              <NarrateButton
                text={`Phase 4: Client aspirations. You will describe ${totalItems} of your favorite things. For each one, enter its name, describe what it is, and explain why you like it using exactly ${wordsPerItem} unique descriptive words. You have ${timePerItem} seconds per item. In the text form, click Next if time expires. Choose voice interview to hear each prompt read aloud and dictate your answers, or text form to type them.`}
                label="🎧 Narrate the rules"
                selectedVoiceURI={selectedVoiceURI}
                testId="phase4-intro-narrate-rules-btn"
              />
              <VoicePicker selectedVoiceURI={selectedVoiceURI} onChange={setSelectedVoiceURI} />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                onClick={() => { setVoiceMode(true); startPhase(); }}
                className="px-8 py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
                data-testid="phase4-start-voice-btn"
              >
                <Volume2 className="w-5 h-5 mr-2" />
                Start Voice Interview
              </Button>
              <Button
                onClick={() => { setVoiceMode(false); startPhase(); }}
                variant="outline"
                className="px-8 py-6 text-lg"
                data-testid="phase4-start-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Start Text Form
              </Button>
            </div>
            <VoicePrivacyNotice className="mt-6" testId="phase4-voice-privacy-notice" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Active input form
  else {
    const secondsLeft = itemTimeLeft;
    
    mainContent = (
    <Card className="mt-6" data-testid="phase4-active-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Favorite Thing {currentItemIndex + 1} of {totalItems}
          </CardTitle>
          <div className="flex items-center gap-3">
            <AutoSaveIndicator status={phase4AutoSaveStatus} savedAt={phase4AutoSavedAt} />
            {voiceMode && (
              <VoicePicker selectedVoiceURI={selectedVoiceURI} onChange={setSelectedVoiceURI} />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {voiceMode && (
          <Phase4AutoNarrate itemIndex={currentItemIndex} totalItems={totalItems} selectedVoiceURI={selectedVoiceURI} />
        )}
        {/* Per-Item Timer */}
        <div className="flex justify-between items-center bg-blue-50 p-4 rounded-lg mb-6">
          <Clock className="w-6 h-6 text-blue-600" />
          <div className="text-center flex-1">
            <span className={`text-4xl font-bold ${secondsLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-blue-600'}`}>
              {secondsLeft}s
            </span>
            <p className="text-xs text-gray-600 mt-1">Time left for this item ({timePerItem} seconds per item)</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-gray-700">Item {currentItemIndex + 1}/{totalItems}</p>
            <p className="text-xs text-gray-500">{voiceMode ? 'Auto-advances when time expires' : 'Click Next after time expires'}</p>
          </div>
        </div>
        {phase4AwaitingTimeoutNext && !voiceMode && (
          <div className="mb-6 p-4 rounded-lg border-2 border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="phase4-time-expired-banner">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="font-semibold text-amber-900">
              Time has expired for this item. Your completed responses have been saved. Click Next to {currentItemIndex + 1 >= totalItems ? 'complete Phase 4' : 'continue'}.
            </p>
          </div>
        )}
        {phase4GibberishError && !phase4AwaitingTimeoutNext && (
          <p className="text-red-600 text-sm mb-4" role="alert" data-testid="phase4-gibberish-error">{phase4GibberishError}</p>
        )}
        
        {/* Input Form */}
        <div className="space-y-6">
          {/* 1. Name of favorite thing */}
          <div>
            <Label className="text-base font-semibold">1. Favorite thing name</Label>
            <div className="flex items-start gap-2 mt-2">
              <Input
                value={isPhase4FieldTimedOut({ ...currentItem, item: currentItem.name }, 'item') ? SYSTEM_TIME_EXPIRED_DISPLAY : currentItem.name}
                disabled={phase4AwaitingTimeoutNext}
                onChange={(e) => {
                  const value = e.target.value;
                  if (validateLettersOnly(value) || value === '') {
                    setCurrentItem({...currentItem, name: value});
                    setPhase4GibberishError('');
                  } else {
                    showError('⚠️ Only letters and spaces are allowed. Numbers and special characters are not permitted.');
                  }
                }}
                onBlur={() => correctPhase4Field('name')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') correctPhase4Field('name');
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    phase4WhatIsItRef.current?.focus();
                  }
                }}
                placeholder="e.g., Reading, Dancing, Gardening... (letters only)"
                className="flex-1"
                data-testid="phase4-favorite-name-input"
              />
              {voiceMode && (
                <MicInput
                  showListeningIndicator
                  onAppend={(chunk) => {
                    const cleaned = String(chunk).replace(/[^A-Za-z\s]/g, '').replace(/\s+/g, ' ').trim();
                    if (cleaned) {
                      setCurrentItem((prev) => ({ ...prev, name: (prev.name ? prev.name + ' ' : '') + cleaned }));
                    }
                  }}
                  testId="phase4-mic-name"
                />
              )}
            </div>
          </div>
          
          {/* 2. What is it */}
          <div>
            <Label className="text-base font-semibold">2. What is this</Label>
            <div className="flex items-start gap-2 mt-2">
              <Textarea
                ref={phase4WhatIsItRef}
                value={isPhase4FieldTimedOut(currentItem, 'what_is_it') ? SYSTEM_TIME_EXPIRED_DISPLAY : currentItem.what_is_it}
                disabled={phase4AwaitingTimeoutNext}
                onChange={(e) => {
                  const value = e.target.value;
                  if (validateLettersOnly(value) || value === '') {
                    setCurrentItem({...currentItem, what_is_it: value});
                    setPhase4GibberishError('');
                  } else {
                    showError('⚠️ Only letters and spaces are allowed. Numbers and special characters are not permitted.');
                  }
                }}
                onBlur={() => correctPhase4Field('what_is_it')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) correctPhase4Field('what_is_it');
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    phase4DescriptorRef.current?.focus();
                  }
                }}
                placeholder="Describe your favorite thing briefly... (letters only)"
                rows={2}
                className="flex-1"
                data-testid="phase4-what-is-it-input"
              />
              {voiceMode && (
                <MicInput
                  showListeningIndicator
                  onAppend={(chunk) => {
                    const cleaned = String(chunk).replace(/[^A-Za-z\s]/g, '').replace(/\s+/g, ' ').trim();
                    if (cleaned) {
                      setCurrentItem((prev) => ({ ...prev, what_is_it: (prev.what_is_it ? prev.what_is_it + ' ' : '') + cleaned }));
                    }
                  }}
                  testId="phase4-mic-what-is-it"
                />
              )}
            </div>
          </div>
          
          {/* 3. 5 unique words */}
          {isClientTextCorrectionEnabled(settings) && phase4Correction && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm" data-testid="phase4-correction-notice">
              Corrected from “{phase4Correction.original}” · <button type="button" className="underline font-medium" data-testid="phase4-correction-undo" onClick={undoPhase4Correction}>Undo</button>
            </div>
          )}
          <div>
            <Label className="text-base font-semibold">
              3. What you like in this Favorite thing
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                ref={phase4DescriptorRef}
                value={currentItem.currentWord}
                onChange={(e) => {
                  const value = e.target.value;
                  if (validateLettersOnly(value) || value === '') {
                    setCurrentItem({...currentItem, currentWord: value});
                    setPhase4GibberishError('');
                  } else {
                    showError('⚠️ Only letters and spaces are allowed. Numbers and special characters are not permitted.');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && currentItem.words.length < wordsPerItem) {
                    e.preventDefault();
                    addWord();
                  }
                }}
                placeholder="Enter a descriptive response (letters only)"
                disabled={phase4AwaitingTimeoutNext || currentItem.words.length >= wordsPerItem}
                data-testid="phase4-descriptor-input"
              />
              {voiceMode && currentItem.words.length < wordsPerItem && (
                <MicInput
                  key={`p4-mic-word-${currentItemIndex}-${currentItem.words.length}`}
                  showListeningIndicator
                  onAppend={(chunk) => {
                    const cleaned = String(chunk).replace(/[^A-Za-z\s]/g, '').replace(/\s+/g, ' ').trim();
                    if (cleaned) {
                      setCurrentItem((prev) => ({
                        ...prev,
                        currentWord: prev.currentWord ? `${prev.currentWord} ${cleaned}` : cleaned,
                      }));
                    }
                  }}
                  testId="phase4-mic-word"
                />
              )}
              <Button 
                onClick={addWord} 
                disabled={phase4AwaitingTimeoutNext || currentItem.words.length >= wordsPerItem || !currentItem.currentWord.trim()}
                className={voiceMode ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                data-testid="phase4-add-descriptor-btn"
              >
                {voiceMode ? (
                  <><CheckCircle className="w-4 h-4 mr-2" /> Confirm</>
                ) : 'Add'}
              </Button>
            </div>
            
            {/* Display added words */}
            <div className="flex flex-wrap gap-2 mt-3">
              {currentItem.words.map((response, i) => (
                <span key={i} className="association-chip bg-blue-100 text-blue-800">
                  {isPhase4DescriptorTimedOut(currentItem, i) ? SYSTEM_TIME_EXPIRED_DISPLAY : response}
                  <button 
                    onClick={() => removeWord(i)}
                    disabled={phase4AwaitingTimeoutNext}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            
            <p className="text-sm text-gray-600 mt-2">
              Add {wordsPerItem} unique words total. You've added {currentItem.words.length} so far.
            </p>
          </div>
          
          {/* Skip button for incomplete entries - optional alternative to main button */}
          {!phase4AwaitingTimeoutNext && currentItem.words.length > 0 && currentItem.words.length < wordsPerItem && currentItemIndex + 1 < totalItems && (
            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <p className="text-sm text-gray-700 mb-2">Ready to move to the next item?</p>
              <Button 
                variant="outline" 
                onClick={() => completeCurrentItem(true)}
                className="w-full"
              >
                Save with {currentItem.words.length} response{currentItem.words.length > 1 ? 's' : ''} and continue to next
              </Button>
            </div>
          )}
          
          {/* Complete button */}
          <Button 
            onClick={phase4AwaitingTimeoutNext ? () => handleItemTimeExpired(phase4GenerationRef.current, true) : handleSubmitClick}
            className="w-full py-6 text-lg"
            disabled={!phase4AwaitingTimeoutNext && (
              !currentItem.name.trim() || 
              !currentItem.what_is_it.trim() || 
              currentItem.words.length === 0
            )}
            data-testid={phase4AwaitingTimeoutNext ? 'phase4-timeout-next-btn' : 'phase4-submit-item-btn'}
          >
            {phase4AwaitingTimeoutNext ? 'Next' : (currentItemIndex + 1 < totalItems ? 'Save and Continue to Next' : 'Complete Phase 4')}
          </Button>
          
          {/* Helper text */}
          {currentItem.words.length > 0 && currentItem.words.length < wordsPerItem && (
            <p className="text-sm text-center text-gray-600">
              ✓ You can submit with {currentItem.words.length} response{currentItem.words.length > 1 ? 's' : ''} or continue adding up to {wordsPerItem} words
            </p>
          )}
          
          {/* Progress indicator */}
          <div className="text-center text-sm text-gray-600">
            Completed: {completedItems.length} / {totalItems} items
          </div>
        </div>
      </CardContent>
      
      {/* Confirmation Dialog */}
      <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Submission</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700 mb-4">
              {currentItemIndex + 1 < totalItems 
                ? `You are about to save favorite thing ${currentItemIndex + 1} of ${totalItems}.`
                : `You are about to complete Phase 4 with ${completedItems.length + 1} favorite thing(s).`
              }
            </p>
            <p className="text-sm text-gray-600">
              Are you sure you want to submit?
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowSubmitConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={() => completeCurrentItem(false)} className="bg-green-600 hover:bg-green-700">
              Confirm & Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
    );
  }
  
  return mainContent;
};
const Phase5 = ({ sessionId, onComplete, refreshSession, userRole, currentUser, showCompletionModal, isViewOnlyMode, canManageHelpfulResources = false, session, setIsAnyReportGenerating, registerClientSummaryNavigationGuard }) => {
  const { notification, showSuccess, showError, showInfo, showWarning, closeNotification } = useNotification();
  const clientDisplayName = String(session?.client_name || 'Client').trim() || 'Client';
  const clientSynthesisName = CLIENT_SYNTHESIS_DISPLAY_NAME;
  const clientSynthesisFileName = getClientSynthesisPdfFilename(clientDisplayName);

  // Formats the `generated_at` ISO string on a retry-badge tooltip
  // into a friendly local timestamp like "Feb 13, 3:14 PM".
  const formatRetryTime = (iso) => {
    if (!iso) return 'unknown time';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return 'unknown time';
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return 'unknown time';
    }
  };
  const [synthesisPoints, setSynthesisPoints] = useState([]);
  // Admin-controlled report visibility. Missing key = enabled (default open).
  const [reportVisibility, setReportVisibility] = useState({});
  const fetchReportVisibility = useCallback(async () => {
      try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`${API}/report-visibility`, {
          headers: { Authorization: `Bearer ${token}` }, skipGlobalLoading: true, timeout: 30000
        });
        setReportVisibility(res.data?.report_visibility || {});
      } catch (e) {
        console.warn('Failed to load report visibility, defaulting to all-enabled', e);
        setReportVisibility({});
      }
  }, []);
  useEffect(() => { fetchReportVisibility(); }, [fetchReportVisibility]);
  const isReportEnabled = (key) => reportVisibility[key] !== false;
  const hasEnabledResearchResource = ['apa_case_parallels', 'research_framework_synthesis', 'developmental_ecological_cultural']
    .some((key) => isReportEnabled(key));
  const hasAnyReportEnabled = (() => {
    const keys = Object.keys(reportVisibility || {});
    if (keys.length === 0) return true; // no admin overrides -> default enabled
    return keys.some(k => reportVisibility[k] !== false);
  })();

  const [loading, setLoading] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [reportLoadState, setReportLoadState] = useState('loading');
  const [reportLoadError, setReportLoadError] = useState('');
  const reportReadRef = useRef(null);
  const reportMountedRef = useRef(true);
  useEffect(() => {
    reportMountedRef.current = true;
    return () => {
      reportMountedRef.current = false;
      reportReadRef.current?.controller.abort();
      reportReadRef.current = null;
    };
  }, []);

  const [generatingReports, setGeneratingReports] = useState(false);
  const [generatingIndividualReport, setGeneratingIndividualReport] = useState(null); // Tracks which individual report is being generated
  const [showIndividualTimer, setShowIndividualTimer] = useState(false); // Controls timer visibility
  const [reportProgressMessage, setReportProgressMessage] = useState(''); // Stores AI generation progress messages
  const [generatingPoints, setGeneratingPoints] = useState(false);
  const generationPointsRef = useRef(false);
  const reportAbortControllerRef = useRef(null); // Store abort controller for cleanup
  const [aiQuery, setAiQuery] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [currentQueryPage, setCurrentQueryPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [editingClientSummary, setEditingClientSummary] = useState(false);
  const [clientSummaryDraft, setClientSummaryDraft] = useState('');
  const [clientSummaryOriginal, setClientSummaryOriginal] = useState('');
  const [savingClientSummary, setSavingClientSummary] = useState(false);
  const [clientSummaryDiscardAction, setClientSummaryDiscardAction] = useState(null);
  const [queryFile, setQueryFile] = useState(null);
  const [includeFileInPrompt, setIncludeFileInPrompt] = useState(false);

  const selectQueryFile = (file) => {
    setQueryFile(file);
    setIncludeFileInPrompt(Boolean(file));
  };

  const removeQueryFile = () => {
    setQueryFile(null);
    setIncludeFileInPrompt(false);
  };
  
  // 14 Points editing states
  const [editablePoints, setEditablePoints] = useState([]);
  const [isEditing16Points, setIsEditing16Points] = useState(false);
  const editingPointsRef = useRef(false);
  editingPointsRef.current = isEditing16Points;
  // Per-point collapse state (set of collapsed point_numbers). Empty = all expanded.
  const [collapsedPoints, setCollapsedPoints] = useState(() => new Set());
  const toggleCollapsedPoint = (pn) => setCollapsedPoints((prev) => {
    const next = new Set(prev);
    if (next.has(pn)) next.delete(pn); else next.add(pn);
    return next;
  });
  const [updating16Points, setUpdating16Points] = useState(false);
  const [show16PointsUpdateSuccessModal, setShow16PointsUpdateSuccessModal] = useState(false);
  const [show16PointsReviewSuccessModal, setShow16PointsReviewSuccessModal] = useState(false);
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [showGeneratedModal, setShowGeneratedModal] = useState(false);

  // Delete confirmation modal state (replaces window.confirm popup)
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    kind: null,      // 'ai_report' | 'custom_report' | 'ask_buddy'
    index: null,
    reportName: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const closeDeleteConfirm = () => setDeleteConfirm({ open: false, kind: null, index: null, reportName: '' });
  
  // "Why" (Patterns & Contributing Themes) + "Next Steps" tabs
  const [sqisTab, setSqisTab] = useState(() => normalizePhase5ReportTab(readPhase5ReportUi(sessionId).activeReportTab || 'sqis'));
  const [selectedResearchReport, setSelectedResearchReport] = useState(null);
  useEffect(() => {
    if (existingData && !existingData.synthesis_reviewed && !['data-quality', 'sqis'].includes(sqisTab)) {
      setSqisTab('sqis');
    }
  }, [existingData, sqisTab]);
  const selectSqisTab = (tab) => {
    requestClientSummaryAction(() => {
      setSqisTab(tab);
      updatePhase5ReportUi(sessionId, (current) => ({ ...current, activeReportTab: tab }));
    });
  };
  useEffect(() => {
    const stored = normalizePhase5ReportTab(readPhase5ReportUi(sessionId).activeReportTab || 'sqis');
    setSqisTab(stored);
    setSelectedResearchReport(null);
    setCustomReportName('');
    setCustomReportFocus('');
    if (stored === 'nexus' || stored === 'custom_reports') updatePhase5ReportUi(sessionId, (current) => ({ ...current, activeReportTab: stored }));
  }, [sessionId]);
  const [helpfulResourcesOpen, setHelpfulResourcesOpen] = useState(false);
  const [researchResourcesOpen, setResearchResourcesOpen] = useState(false);
  const helpfulResourcesRef = useRef(null);
  const researchResourcesRef = useRef(null);
  useEffect(() => {
    // A persisted landing-page selection must not leave a non-admin user on an
    // empty Research Resources panel after Admin disables the final resource.
    // Keep a selected disabled report in place so its explicit safe-state guard
    // can explain why direct/stale access is unavailable.
    if (sqisTab === 'research' && !selectedResearchReport && !hasEnabledResearchResource) {
      setSqisTab('sqis');
      updatePhase5ReportUi(sessionId, (current) => ({ ...current, activeReportTab: 'sqis' }));
      setResearchResourcesOpen(false);
    }
  }, [sqisTab, selectedResearchReport, hasEnabledResearchResource, sessionId]);
  useEffect(() => {
    const closeHelpfulResources = (event) => {
      if (helpfulResourcesRef.current && !helpfulResourcesRef.current.contains(event.target)) setHelpfulResourcesOpen(false);
      if (researchResourcesRef.current && !researchResourcesRef.current.contains(event.target)) setResearchResourcesOpen(false);
    };
    document.addEventListener('mousedown', closeHelpfulResources);
    return () => document.removeEventListener('mousedown', closeHelpfulResources);
  }, []);
  const [generatingWhy, setGeneratingWhy] = useState(false);
  const [generatingNext, setGeneratingNext] = useState(false);
  const [generatingNexus, setGeneratingNexus] = useState(false);
  const [phase5RegenerationKind, setPhase5RegenerationKind] = useState(null);
  const phase5SubmissionGuardsRef = useRef({ why: false, next: false, nexus: false });
  const nextStepsState = useMemo(() => createNextStepsState(), [sessionId]);
  const currentNextStepsState = useRef(nextStepsState);
  currentNextStepsState.current = nextStepsState;
  useEffect(() => () => nextStepsState.cancel(), [nextStepsState]);
  const [whyNextModal, setWhyNextModal] = useState({
    open: false,
    kind: 'why',            // 'why' | 'next'
    phase: 'generating',    // 'generating' | 'done' | 'error'
    isRegenerate: false,
    errorMessage: '',
    attempts: 0,            // job attempts (from /jobs/{id} poll)
    maxAttempts: 0,         // admin-configured retry cap
  });
  const closeWhyNextModal = () =>
    setWhyNextModal((m) => ({ ...m, open: false }));

  // Unified progress modal for the 14-Point SQIS and individual reports
  const [progressModal, setProgressModal] = useState({
    open: false,
    kind: 'sqis',           // 'sqis' | 'report' | 'books' | 'movies' | 'custom'
    label: '',              // human-readable label (e.g. "Aptitude Report")
    phase: 'generating',    // 'generating' | 'done' | 'error'
    isRegenerate: false,
    errorMessage: '',
  });
  const closeProgressModal = () =>
    setProgressModal((m) => ({ ...m, open: false }));
  // Shared abort controller for all cancellable generations (SQIS, reports batch,
  // custom report, books, movies). Individual reports use their own dedicated
  // `reportAbortControllerRef` because they stream via native fetch.
  const genAbortControllerRef = useRef(null);
  const cancelProgressGeneration = () => {
    try {
      // Cancel any in-flight axios/fetch requests
      if (genAbortControllerRef.current) {
        genAbortControllerRef.current.abort();
      }
      if (reportAbortControllerRef.current) {
        reportAbortControllerRef.current.abort();
      }
    } catch (e) {
      console.warn('Cancel abort failed', e);
    }
    // Clear all generating flags so the modal + overlay close cleanly
    setGeneratingPoints(false);
    setGeneratingReports(false);
    setGeneratingCustomReport(false);
    setGeneratingBooks(false);
    setGeneratingMovies(false);
    setGeneratingWhy(false);
    setGeneratingNext(false);
    setGeneratingNexus(false);
    setGeneratingIndividualReport(null);
    setShowIndividualTimer(false);
    setReportProgressMessage('');
    setProgressModal((m) => ({ ...m, open: false }));
    setWhyNextModal((m) => ({ ...m, open: false }));
  };
  
  // Poll a Phase 5 background job until it completes or fails.
  // Backend returns 202 { job_id } from generate-why-patterns / generate-next-steps /
  // generate-nexus; we poll GET /jobs/{job_id} every ~3s so Cloudflare's 100s edge
  // timeout can never drop the LLM call anymore. `onProgress({attempts,max_attempts})`
  // is invoked on every non-terminal poll. Restart-interrupted jobs are terminal:
  // the user must explicitly choose Generate/Regenerate to submit a new request.
  const pollPhase5Job = async (jobId, { signal, onProgress } = {}) => {
    const token = sessionStorage.getItem('token');
    const POLL_MS = 3000;
    // Only authoritative terminal job status ends generation; elapsed browser
    // polling time does not mean a durable backend job failed.
    while (!signal?.aborted) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      try {
        const { data } = await axios.get(`${API}/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal, timeout: 30000, skipGlobalLoading: true,
        });
        if (data && onProgress) {
          onProgress({
            attempts: data.attempts,
            maxAttempts: data.max_attempts,
            status: data.status,
          });
        }
        // Partial jobs contain a usable, persisted report plus explicit missing-section
        // metadata. Let the report view present that result instead of replacing it
        // with the generic generation-failure flow.
        if (data?.status === 'complete' || data?.status === 'partial') return data;
        if (data?.status === 'failed') {
          throw phase5JobFailure(data);
        }
        if (data?.status === 'interrupted') {
          const err = new Error(data?.error || 'Previous generation was interrupted. You may regenerate when ready.');
          err.jobFailed = true;
          throw err;
        }
      } catch (err) {
        if (err?.jobFailed) throw err;
        if (axios.isCancel?.(err) || err.name === 'CanceledError' || err.name === 'AbortError') throw err;
        // Terminal HTTP errors (auth/not-found) should surface immediately — the job is not recoverable.
        const status = err?.response?.status;
        if (status === 401 || status === 403 || status === 404) throw err;
        // Otherwise (network blip, transient 5xx) — swallow and retry.
      }
      await new Promise((r) => setTimeout(r, POLL_MS));
    }
    throw new DOMException('Aborted', 'AbortError');
  };

  // Viewing/remounting never starts generation. It only discovers and follows an
  // already-leased backend job so navigation cannot invite a duplicate POST.
  useEffect(() => {
    if (userRole !== 'psychologist' || !sessionId) return undefined;
    const controller = new AbortController();
    genAbortControllerRef.current = controller;
    const definitions = [
      { kind: 'why', reportType: 'why_patterns', setGenerating: setGeneratingWhy },
      { kind: 'nexus', reportType: 'nexus', setGenerating: setGeneratingNexus },
    ];
    const resume = async ({ kind, reportType, setGenerating }) => {
      if (phase5SubmissionGuardsRef.current[kind]) return;
      try {
        const token = sessionStorage.getItem('token');
        const { data } = await axios.get(
          `${API}/sessions/${sessionId}/phase5/jobs/active?report_type=${reportType}`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal, skipGlobalLoading: true, timeout: 30000 }
        );
        if (!data?.active || !data?.job_id) return;
        phase5SubmissionGuardsRef.current[kind] = true;
        setGenerating(true);
        setWhyNextModal({ open: true, kind, phase: 'generating', isRegenerate: false, errorMessage: 'Generation already in progress. Reconnected to the existing job.', attempts: 0, maxAttempts: 0 });
        await pollPhase5Job(data.job_id, { signal: controller.signal });
        await fetchExisting();
        setWhyNextModal((m) => ({ ...m, phase: 'done' }));
      } catch (err) {
        if (!(axios.isCancel?.(err) || err.name === 'CanceledError' || err.name === 'AbortError')) {
          const detail = err.response?.data?.detail;
          const message = typeof detail === 'string' ? detail : detail?.message;
          setWhyNextModal((m) => ({ ...m, open: true, kind, phase: 'error', errorMessage: message || err.message || 'Failed to resume generation.' }));
        }
      } finally {
        phase5SubmissionGuardsRef.current[kind] = false;
        setGenerating(false);
      }
    };
    const resumeNext = async () => {
      let attempt;
      try {
        const { data } = await axios.get(`${API}/sessions/${sessionId}/phase5/jobs/active?report_type=next_steps`, { signal: controller.signal, skipGlobalLoading: true, timeout: 30000 });
        if (controller.signal.aborted || !data?.active || !data?.job_id) return;
        attempt = nextStepsState.claim();
        if (!attempt) return; // A click or another resume already owns this report.
        attempt.jobId = data.job_id;
        setGeneratingNext(true);
        setWhyNextModal({ open: true, kind: 'next', phase: 'generating', isRegenerate: false, errorMessage: 'Generation already in progress. Reconnected to the existing job.', attempts: 0, maxAttempts: 0 });
        await pollPhase5Job(data.job_id, { signal: attempt.controller.signal });
        if (!nextStepsState.owns(attempt)) return;
        await fetchExisting();
        if (nextStepsState.owns(attempt)) setWhyNextModal(m => ({ ...m, phase: 'done' }));
      } catch (err) {
        if (attempt && nextStepsState.owns(attempt) && !controller.signal.aborted) {
          setWhyNextModal(m => ({ ...m, phase: 'error', errorMessage: err.message || 'Failed to resume generation.' }));
        }
      } finally {
        if (attempt && nextStepsState.release(attempt)) {
          setGeneratingNext(false);
        }
      }
    };
    resumeNext();
    definitions.forEach((definition) => { resume(definition); });
    return () => {
      controller.abort();
      if (genAbortControllerRef.current === controller) genAbortControllerRef.current = null;
    };
  }, [sessionId, userRole]);

  const handleGenerateWhyPatterns = async (regenerationConfirmed = false) => {
    const isRegen = Boolean(existingData?.why_patterns?.content?.trim());
    if (isRegen && !regenerationConfirmed) {
      setPhase5RegenerationKind('why');
      return;
    }
    if (phase5SubmissionGuardsRef.current.why) return;
    phase5SubmissionGuardsRef.current.why = true;
    setWhyNextModal({ open: true, kind: 'why', phase: 'generating', isRegenerate: isRegen, errorMessage: '', attempts: 0, maxAttempts: 0 });
    setGeneratingWhy(true);
    genAbortControllerRef.current = new AbortController();
    try {
      const token = sessionStorage.getItem('token');
      const { data: queued } = await axios.post(
        `${API}/sessions/${sessionId}/phase5/generate-why-patterns?regenerate=${isRegen}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, signal: genAbortControllerRef.current.signal }
      );
      if (queued?.job_id) {
        await pollPhase5Job(queued.job_id, {
          signal: genAbortControllerRef.current.signal,
          onProgress: ({ attempts, maxAttempts }) =>
            setWhyNextModal((m) => ({ ...m, attempts: attempts ?? m.attempts, maxAttempts: maxAttempts ?? m.maxAttempts })),
        });
      }
      const refreshedData = await fetchExisting();
      if (typeof refreshedData?.why_patterns?.content !== 'string' || !refreshedData.why_patterns.content.trim()) {
        throw new Error('Generation completed without a usable Patterns & Contributing Themes Analysis.');
      }
      setWhyNextModal((m) => ({ ...m, phase: 'done' }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'generation_in_progress' && detail?.job_id) {
        setWhyNextModal((m) => ({ ...m, phase: 'generating', errorMessage: 'Generation already in progress. Reconnected to the existing job.' }));
        await pollPhase5Job(detail.job_id, { signal: genAbortControllerRef.current?.signal });
        await fetchExisting();
        setWhyNextModal((m) => ({ ...m, phase: 'done' }));
      } else if (axios.isCancel?.(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
        setWhyNextModal((m) => ({ ...m, open: false }));
      } else {
        const message = typeof detail === 'string' ? detail : detail?.message;
        setWhyNextModal((m) => ({ ...m, phase: 'error', errorMessage: message || err.message || 'Failed to generate' }));
      }
    } finally {
      phase5SubmissionGuardsRef.current.why = false;
      setGeneratingWhy(false);
      genAbortControllerRef.current = null;
    }
  };
  
  const handleGenerateNextSteps = async (regenerationConfirmed = false) => {
    const isRegen = Boolean(existingData?.next_steps?.content?.trim());
    if (isRegen && regenerationConfirmed !== true) {
      setPhase5RegenerationKind('next');
      return;
    }
    const attempt = nextStepsState.claim();
    if (!attempt) return;
    const signal = attempt.controller.signal;
    genAbortControllerRef.current = attempt.controller;
    setWhyNextModal({ open: true, kind: 'next', phase: 'generating', isRegenerate: isRegen, errorMessage: '', attempts: 0, maxAttempts: 0 });
    setGeneratingNext(true);
    try {
      const token = sessionStorage.getItem('token');
      let jobId;
      try {
        const { data } = await axios.post(
          `${API}/sessions/${sessionId}/phase5/generate-next-steps?regenerate=${isRegen}`, {},
          { headers: { Authorization: `Bearer ${token}` }, signal }
        );
        jobId = data?.job_id;
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (err.response?.status !== 409 || detail?.code !== 'generation_in_progress' || !detail?.job_id) throw err;
        jobId = detail.job_id;
      }
      if (!nextStepsState.owns(attempt)) return;
      attempt.jobId = jobId;
      if (jobId) await pollPhase5Job(jobId, {
        signal,
        onProgress: ({ attempts, maxAttempts }) => {
          if (nextStepsState.owns(attempt)) setWhyNextModal(m => ({ ...m, attempts: attempts ?? m.attempts, maxAttempts: maxAttempts ?? m.maxAttempts }));
        },
      });
      if (!nextStepsState.owns(attempt)) return;
      const refreshedData = await fetchExisting();
      if (!nextStepsState.owns(attempt)) return;
      if (typeof refreshedData?.next_steps?.content !== 'string' || !refreshedData.next_steps.content.trim()) {
        throw new Error('Generation completed without a usable Next Steps Analysis.');
      }
      setWhyNextModal(m => ({ ...m, phase: 'done' }));
    } catch (err) {
      if (nextStepsState.owns(attempt)) {
        const detail = err.response?.data?.detail;
        setWhyNextModal(m => ({ ...m, phase: 'error', errorMessage: (typeof detail === 'string' ? detail : detail?.message) || err.message || 'Failed to generate' }));
      }
    } finally {
      // An old completion must never release a newer request's guard.
      if (nextStepsState.release(attempt)) setGeneratingNext(false);
      if (genAbortControllerRef.current === attempt.controller) genAbortControllerRef.current = null;
    }
  };

  const handleGenerateNexus = async (regenerationConfirmed = false) => {
    const isRegen = Boolean(existingData?.nexus?.content?.trim());
    if (isRegen && !regenerationConfirmed) {
      setPhase5RegenerationKind('nexus');
      return;
    }
    if (phase5SubmissionGuardsRef.current.nexus) return;
    phase5SubmissionGuardsRef.current.nexus = true;
    setWhyNextModal({ open: true, kind: 'nexus', phase: 'generating', isRegenerate: isRegen, errorMessage: '', attempts: 0, maxAttempts: 0 });
    setGeneratingNexus(true);
    genAbortControllerRef.current = new AbortController();
    try {
      const token = sessionStorage.getItem('token');
      const { data: queued } = await axios.post(
        `${API}/sessions/${sessionId}/phase5/generate-nexus?regenerate=${isRegen}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, signal: genAbortControllerRef.current.signal }
      );
      if (queued?.job_id) {
        await pollPhase5Job(queued.job_id, {
          signal: genAbortControllerRef.current.signal,
          onProgress: ({ attempts, maxAttempts }) =>
            setWhyNextModal((m) => ({ ...m, attempts: attempts ?? m.attempts, maxAttempts: maxAttempts ?? m.maxAttempts })),
        });
      }
      const refreshedData = await fetchExisting();
      if (typeof refreshedData?.nexus?.content !== 'string' || !refreshedData.nexus.content.trim()) {
        throw new Error('Generation completed without a usable Nexus Analysis.');
      }
      setWhyNextModal((m) => ({ ...m, phase: 'done' }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'generation_in_progress' && detail?.job_id) {
        setWhyNextModal((m) => ({ ...m, phase: 'generating', errorMessage: 'Generation already in progress. Reconnected to the existing job.' }));
        await pollPhase5Job(detail.job_id, { signal: genAbortControllerRef.current?.signal });
        await fetchExisting();
        setWhyNextModal((m) => ({ ...m, phase: 'done' }));
      } else if (axios.isCancel?.(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
        setWhyNextModal((m) => ({ ...m, open: false }));
      } else {
        const message = typeof detail === 'string' ? detail : detail?.message;
        setWhyNextModal((m) => ({ ...m, phase: 'error', errorMessage: message || err.message || 'Failed to generate' }));
      }
    } finally {
      phase5SubmissionGuardsRef.current.nexus = false;
      setGeneratingNexus(false);
      genAbortControllerRef.current = null;
    }
  };

  // ===== Nexus Roleplay Simulator =====
  // Modal is mounted globally in App.js (NexusRoleplaySimulator). We open it
  // via a window event so per-tab remounts / StrictMode double-invokes can't
  // discard the state (which was blocking the modal from opening reliably).
  const openRoleplay = () => {
    console.log('[NexusRoleplay] dispatching nexus-roleplay-open for session', sessionId);
    window.dispatchEvent(new CustomEvent('nexus-roleplay-open', { detail: { sessionId } }));
  };
  // Simple PDF download for the "Why" / "Next Steps" / "Nexus" tabs
  const downloadSqisExtensionPDF = async (kind) => {
    const doc =
      kind === 'why'
        ? existingData?.why_patterns
        : kind === 'nexus'
        ? existingData?.nexus
        : existingData?.next_steps;
    if (!doc?.content) {
      showError('Nothing to download yet — generate the section first.');
      return;
    }
    try {
      const pdfContent = kind === 'next' ? nextStepsDisplayHtml(doc.content) : doc.content;
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const { registerReportPdfFonts } = await import('../utils/reportPdfFonts');
      registerReportPdfFonts(pdf);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const maxWidth = pageWidth - margin * 2;
      let y = margin;

      const title = kind === 'why'
        ? '"Why" (Patterns & Contributing Themes)'
        : kind === 'nexus'
        ? 'Nexus — Conversational Playbook & Resistance Simulator'
        : 'Next Steps';

      pdf.setFont('times', 'bold');
      pdf.setFontSize(18);
      pdf.text(title, margin, y);
      y += 8;

      pdf.setFont('times', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(90, 90, 90);
      if (existingData?.psychologist_name) {
        pdf.text(`By: ${withDoctorTitle(existingData.psychologist_name)}`, margin, y);
        y += 5;
      }
      if (doc.generated_at) {
        pdf.text(`Generated: ${new Date(doc.generated_at).toLocaleString()}`, margin, y);
        y += 5;
      }
      if (kind === 'why' && doc.completion_status === 'partial') {
        pdf.setFont('times', 'bold');
        pdf.setTextColor(180, 83, 9);
        pdf.text(`Partial report — missing sections: ${(doc.missing_sections || []).join(', ')}`, margin, y, { maxWidth });
        y += 8;
        pdf.setFont('times', 'normal');
        pdf.setTextColor(90, 90, 90);
      }
      pdf.setDrawColor(180, 180, 180);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 6;

      pdf.setTextColor(20, 20, 20);
      pdf.setFontSize(11);

      // Strip a Markdown code fence wrapper if the AI wrapped the entire body in ```html ... ```
      const stripFence = (raw) => {
        if (!raw || typeof raw !== 'string') return raw;
        let t = raw.trim();
        // Fully-fenced (open + close)
        const full = t.match(/^```(?:html|markdown|md)?\s*\n([\s\S]*?)\n```\s*$/i);
        if (full) return full[1];
        // Only leading fence — no closing fence (common LLM issue)
        t = t.replace(/^```(?:html|markdown|md)?\s*\n/i, '');
        // Only trailing fence
        t = t.replace(/\n?```\s*$/i, '');
        return t;
      };

      // Convert HTML+Markdown content into structured PDF-friendly blocks.
      // Handles <details>/<summary> from the admin HTML Collapsible Formatter,
      // strips remaining HTML, and preserves bold/italic markdown emphasis
      // by rendering it in bold via jsPDF font switching.
      const renderPdfBlocks = (raw) => {
        const emit = (text, opts = {}) => {
          const { size = 11, bold = false, italic = false, indent = 0, spacingAfter = 5 } = opts;
          if (!text || !text.trim()) return;
          const fontStyle = bold && italic ? 'bolditalic' : bold ? 'bold' : italic ? 'italic' : 'normal';
          // Core PDF fonts cannot encode arrows and many other report glyphs.
          // Keep existing headings and font sizes; use an embedded serif only
          // when the text requires characters outside the core font encoding.
          const needsUnicode = /[^\u0000-\u00ff\u2010-\u2027\u2030-\u203a\u20ac\u2122]/u.test(text);
          pdf.setFont(needsUnicode ? 'report-serif' : 'times', needsUnicode ? (bold ? 'bold' : 'normal') : fontStyle);
          pdf.setFontSize(size);
          if (opts.emphasis && !bold) {
            y = drawReportEmphasis(pdf, text, { responses: opts.suggested ? [] : reportSourceResponses(existingData.synthesis_points), font: needsUnicode ? 'report-serif' : 'times', size, x: margin + indent, y, width: maxWidth - indent, pageHeight, margin, lineHeight: spacingAfter });
            return;
          }
          const lines = pdf.splitTextToSize(text, maxWidth - indent);
          for (const line of lines) {
            if (y > pageHeight - margin) {
              pdf.addPage();
              y = margin;
            }
            pdf.text(line, margin + indent, y);
            y += spacingAfter;
          }
        };

        // Replace <br> and <br/> with newlines, then normalize
        let src = stripFence(String(raw))
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/\r\n?/g, '\n');

        // Nexus persists server-owned paragraph wrappers around model plain text.
        // Preserve those paragraph boundaries for PDF layout before tags are removed.
        if (kind === 'nexus') {
          src = normalizeNexusMarkupForPdf(src);
        }

        src = preserveReportListMarkersHtml(src);

        // Walk the string, splitting on <details>...</details> blocks so we can
        // treat each block's <summary> as a heading and the body as regular text.
        const blockPattern = /<details[^>]*>([\s\S]*?)<\/details>/gi;
        const chunks = [];
        let lastIndex = 0;
        let m;
        while ((m = blockPattern.exec(src)) !== null) {
          if (m.index > lastIndex) {
            chunks.push({ type: 'body', text: src.slice(lastIndex, m.index) });
          }
          chunks.push({ type: 'details', text: m[1] });
          lastIndex = m.index + m[0].length;
        }
        if (lastIndex < src.length) {
          chunks.push({ type: 'body', text: src.slice(lastIndex) });
        }
        if (chunks.length === 0) {
          chunks.push({ type: 'body', text: src });
        }

        const renderInlineMarkdown = (text, opts = {}) => {
          let suggested = false;
          reportFieldPdfRuns(text).forEach((run) => {
            if (run.label) {
              suggested = /(?:possible client wording|suggested|hypothetical|response script|role.?play|paraphrase)/i.test(run.text);
              y += 3;
              pdf.setTextColor(54, 87, 127);
              emit(run.text.toUpperCase(), { ...opts, bold: true, spacingAfter: 5.5 });
              pdf.setTextColor(20, 20, 20);
              y += 1;
            } else emit(run.text, { ...opts, emphasis: true, suggested });
          });
        };

        const comparableNexusHeading = (value) => String(value || '')
          .replace(/^\s*\d+(?:\.\d+)*[.)]?\s*/, '')
          .replace(/[“”"'`]/g, '')
          .replace(/[^A-Za-z0-9]+/g, ' ')
          .trim()
          .toLowerCase();

        const renderBodyText = (text, headingLevel = 0, sectionHeading = '') => {
          const lines = text.split('\n');
          for (const rawLine of lines) {
            const line = rawLine.trimEnd();
            if (!line.trim()) { y += 2; continue; }

            if (reportFieldPdfRuns(line).some((run) => run.label)) {
              renderInlineMarkdown(line, { spacingAfter: 5 });
              continue;
            }

            // Markdown headings inside body
            const hMatch = line.match(/^(#{1,6})\s+(.+)$/);
            if (hMatch) {
              const bodyHeading = hMatch[2].replace(/[*_`]/g, '');
              if (kind !== 'nexus' || comparableNexusHeading(bodyHeading) !== comparableNexusHeading(sectionHeading)) {
                emit(bodyHeading, { size: 13, bold: true, spacingAfter: 6 });
              }
              continue;
            }
            // ALL-CAPS section heading (>= 3 letters, <= 90 chars, multi-word or ending with ':')
            const capsCandidate = line.trim().replace(/:$/, '');
            const letters = capsCandidate.replace(/[^A-Za-z]/g, '');
            if (
              letters.length >= 3 &&
              capsCandidate.length <= 90 &&
              letters === letters.toUpperCase() &&
              (capsCandidate.split(/\s+/).length >= 2 || line.trim().endsWith(':'))
            ) {
              y += 2;
              renderInlineMarkdown(line.trim(), { size: 13, bold: true, spacingAfter: 6 });
              continue;
            }
            // Bullet list
            const bMatch = line.match(/^\s*([-•])\s+(.+)$/);
            if (bMatch) {
              renderInlineMarkdown(bMatch[1] + ' ' + bMatch[2], { indent: 4, spacingAfter: 5 });
              continue;
            }
            // Numbered list
            const nMatch = line.match(/^\s*(\d{1,2}[.)])\s+(.+)$/);
            if (nMatch) {
              renderInlineMarkdown(`${nMatch[1]} ${nMatch[2]}`, { indent: 4, spacingAfter: 5 });
              continue;
            }
            renderInlineMarkdown(line, { spacingAfter: 5 });
          }
        };

        let exportedSectionNumber = 0;
        for (const chunk of chunks) {
          if (chunk.type === 'details') {
            const summaryMatch = chunk.text.match(/<summary[^>]*>([\s\S]*?)<\/summary>/i);
            const heading = summaryMatch ? summaryMatch[1].replace(/<\/?[^>]+>/g, '').trim() : '';
            const body = summaryMatch
              ? chunk.text.replace(summaryMatch[0], '').trim()
              : chunk.text.trim();
            if (heading) {
              y += 3;
              emit(kind === 'next' && !/^\d+[.)]/.test(heading) ? `${++exportedSectionNumber}. ${heading}` : heading, { size: 13, bold: true, spacingAfter: 7 });
            }
            renderBodyText(body, 0, heading);
            y += 3;
          } else {
            renderBodyText(chunk.text);
          }
        }
      };

      renderPdfBlocks(pdfContent);

      const filename =
        kind === 'why'
          ? 'Why_Patterns_and_Contributing_Themes.pdf'
          : kind === 'nexus'
          ? 'Nexus_Conversational_Playbook.pdf'
          : 'Next_Steps.pdf';
      pdf.save(filename);
      showSuccess('PDF downloaded');
    } catch (err) {
      console.error('[PDF] Download failed:', err);
      showError('Failed to generate PDF');
    }
  };
  const runDeleteConfirm = async () => {
    if (deleteConfirm.index === null || !deleteConfirm.kind) return;
    setIsDeleting(true);
    try {
      if (deleteConfirm.kind === 'ai_report') {
        await confirmDeleteAiReport(deleteConfirm.index);
      } else if (deleteConfirm.kind === 'custom_report') {
        await confirmDeleteCustomReport(deleteConfirm.index);
      } else if (deleteConfirm.kind === 'ask_buddy') {
        await confirmDeleteQuery(deleteConfirm.index);
      }
    } finally {
      setIsDeleting(false);
      closeDeleteConfirm();
    }
  };
  
  // Custom Report Generation states
  const [showCustomReportModal, setShowCustomReportModal] = useState(false);
  const [customReportName, setCustomReportName] = useState('');
  const [customReportFocus, setCustomReportFocus] = useState('');
  const [generatingCustomReport, setGeneratingCustomReport] = useState(false);
  
  // Pagination for combined AI + Custom reports table
  const [allReportsPage, setAllReportsPage] = useState(1);
  const allReportsPerPage = 5; // Show 5 reports per page
  
  // Filter custom reports based on user role and visibility
  const filteredCustomReports = React.useMemo(() => {
    if (!existingData?.custom_reports) return [];
    return actualCustomReports(existingData.custom_reports).filter(report => {
      // Psychologists see all reports
      if (userRole === 'psychologist') return true;
      // Clients only see reports marked as visible
      return report.visible_to_client === true;
    });
  }, [existingData?.custom_reports, userRole])
  
  // Book recommendations states
  const [generatingBooks, setGeneratingBooks] = useState(false);
  const [showBooksTimer, setShowBooksTimer] = useState(false);
  const [bookRecommendations, setBookRecommendations] = useState(null);
  const [bookRecsShared, setBookRecsShared] = useState(false);
  const bookGenerationInFlightRef = useRef(false);
  
  // Movie recommendations states
  const [generatingMovies, setGeneratingMovies] = useState(false);
  const [showMoviesTimer, setShowMoviesTimer] = useState(false);
  const [movieRecommendations, setMovieRecommendations] = useState(null);
  const [movieRecsShared, setMovieRecsShared] = useState(false);
  const movieGenerationInFlightRef = useRef(false);

  const [generatingMusic, setGeneratingMusic] = useState(false);
  const [musicRecsShared, setMusicRecsShared] = useState(false);
  const musicGenerationInFlightRef = useRef(false);
  const [shareConfirmation, setShareConfirmation] = useState({ open: false, resource: null, share: true });
  const [shareMutationPending, setShareMutationPending] = useState(false);
  const [clientResourceView, setClientResourceView] = useState(null);
  
  // Update parent component when ANY report generation state changes
  useEffect(() => {
    const isGenerating = !!(
      generatingIndividualReport || 
      generatingCustomReport || 
      queryLoading || 
      generatingBooks || 
      generatingMovies ||
      generatingMusic ||
      generatingPoints ||
      generatingReports
    );
    setIsAnyReportGenerating(isGenerating);
  }, [generatingIndividualReport, generatingCustomReport, queryLoading, generatingBooks, generatingMovies, generatingMusic, generatingPoints, generatingReports, setIsAnyReportGenerating]);
  
  // Transcription functionality removed
  
  // Text-to-Speech state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsExpanded, setTtsExpanded] = useState(() => typeof window === 'undefined' || window.innerWidth > 640);
  const [currentSpeakingReport, setCurrentSpeakingReport] = useState(null);
  const [currentSpeakingText, setCurrentSpeakingText] = useState(''); // Store current text

  // Modal notification system (replaces toast messages)
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    type: 'info', // 'success', 'error', 'info'
    message: ''
  });

  const [speechRate, setSpeechRate] = useState(1.0);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  
  // Modal states for Phase 5 sections
  const [showSynthesisSuccessModal, setShowSynthesisSuccessModal] = useState(false);
  const [showSynthesisInfoModal, setShowSynthesisInfoModal] = useState(false);
  const [showAIReportsConfirmModal, setShowAIReportsConfirmModal] = useState(false);

  // Cleanup: Abort ongoing report generation when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      if (reportAbortControllerRef.current) {
        console.log('🛑 Aborting ongoing report generation due to navigation');
        reportAbortControllerRef.current.abort();
        reportAbortControllerRef.current = null;
      }
    };
  }, []);
  const [showAIReportsSuccessModal, setShowAIReportsSuccessModal] = useState(false);
  const [showAIReportsInfoModal, setShowAIReportsInfoModal] = useState(false);
  const [showCustomReportSuccessModal, setShowCustomReportSuccessModal] = useState(false);
  const [showCustomReportInfoModal, setShowCustomReportInfoModal] = useState(false);
  const [showAskBuddyInfoModal, setShowAskBuddyInfoModal] = useState(false);
  const [showAskBuddySuccessModal, setShowAskBuddySuccessModal] = useState(false);
  const [showBooksConfirmModal, setShowBooksConfirmModal] = useState(false);
  const [showBooksSuccessModal, setShowBooksSuccessModal] = useState(false);
  const [showBooksInfoModal, setShowBooksInfoModal] = useState(false);
  const [showMoviesConfirmModal, setShowMoviesConfirmModal] = useState(false);
  const [showMoviesSuccessModal, setShowMoviesSuccessModal] = useState(false);
  const [showMoviesInfoModal, setShowMoviesInfoModal] = useState(false);
  const [showReviewWarningModal, setShowReviewWarningModal] = useState(false);
  const [showFinalizeValidationModal, setShowFinalizeValidationModal] = useState(false);
  
  // Saved SQIS opens immediately; the existing Hide/Show control remains available.
  const [isSynthesisVisible, setIsSynthesisVisible] = useState(true);

  // Client validation state for 14 Points (LEGACY — kept only to avoid breaking references; new flow uses feedbackDraft below)
  const [clientValidationDrafts, setClientValidationDrafts] = useState({});
  const [flaggingPointNumber, setFlaggingPointNumber] = useState(null);
  const [validationSubmitting, setValidationSubmitting] = useState(false);
  const [finalizeInlineMessage, setFinalizeInlineMessage] = useState('');
  const [finalizeInlineMessageType, setFinalizeInlineMessageType] = useState('success');

  // NEW: SQIS single feedback flow
  const [feedbackDraft, setFeedbackDraft] = useState({ overall_resonance: '', context_clarification: '' });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackInlineMessage, setFeedbackInlineMessage] = useState('');
  const [feedbackInlineMessageType, setFeedbackInlineMessageType] = useState('success');
  
  const queriesPerPage = 5;

  useEffect(() => {
    // React Router can reuse this component when only :sessionId changes.
    // Clear the prior report before loading the SQIS for the new route target.
    setExistingData(null);
    setSynthesisPoints([]);
    fetchExisting(true);
  }, [sessionId]);

  useEffect(() => {
    loadVoices();
  }, []);

  // Track movie recommendations state changes
  useEffect(() => {
    // Movie recommendations state is tracked for rendering logic
  }, [userRole, movieRecsShared, movieRecommendations, existingData]);

  // Sync client validation drafts from backend
  useEffect(() => {
    if (existingData?.client_validations && typeof existingData.client_validations === 'object') {
      setClientValidationDrafts(existingData.client_validations);
    } else {
      setClientValidationDrafts({});
    }
  }, [existingData?.client_validations, existingData?.client_validation_complete]);

  const handleClientValidatePoint = async (pointNumber, status, feedback = '', suggestedContent = '') => {
    try {
      await axios.patch(`${API}/sessions/${sessionId}/phase5/client-validate-point`, {
        point_number: pointNumber,
        status,
        feedback: feedback || null,
        suggested_content: suggestedContent || null,
      });
      setClientValidationDrafts(prev => ({
        ...prev,
        [pointNumber]: {
          status,
          feedback: feedback || null,
          suggested_content: suggestedContent || null,
          validated_at: new Date().toISOString(),
        }
      }));
      setFlaggingPointNumber(null);
      await fetchExisting();
      showSuccess(status === 'validated' ? `Point ${pointNumber} confirmed` : `Point ${pointNumber} flagged with your suggested change`);
    } catch (e) {
      showError(e?.response?.data?.detail || 'Failed to save validation');
    }
  };

  const handleSubmitClientValidation = async () => {
    try {
      setValidationSubmitting(true);
      console.log('[SubmitValidation] Submitting to backend...');
      const res = await axios.post(`${API}/sessions/${sessionId}/phase5/submit-client-validation`, {});
      console.log('[SubmitValidation] Success:', res.data);
      await fetchExisting();
      const { flagged_count, validated_count } = res.data || {};
      showSuccess(`Validation submitted to your psychologist (${validated_count} confirmed, ${flagged_count} flagged)`);
    } catch (e) {
      console.error('[SubmitValidation] Error:', e?.response?.data || e?.message || e);
      showError(e?.response?.data?.detail || e?.message || 'Failed to submit validation');
    } finally {
      setValidationSubmitting(false);
    }
  };

  const handleSubmitClientSQISFeedback = async () => {
    if (!feedbackDraft.overall_resonance) {
      setFeedbackInlineMessageType('error');
      setFeedbackInlineMessage('Please select an Overall Resonance option.');
      return;
    }
    if (!feedbackDraft.context_clarification || !feedbackDraft.context_clarification.trim()) {
      setFeedbackInlineMessageType('error');
      setFeedbackInlineMessage('Please provide your Context & Clarification.');
      return;
    }
    try {
      setSubmittingFeedback(true);
      setFeedbackInlineMessage('');
      const res = await axios.post(`${API}/sessions/${sessionId}/phase5/submit-client-feedback`, {
        overall_resonance: feedbackDraft.overall_resonance,
        context_clarification: feedbackDraft.context_clarification.trim(),
      });
      await fetchExisting();
      setFeedbackInlineMessageType('success');
      setFeedbackInlineMessage('✓ Feedback submitted. Your psychologist can now generate AI Analysis.');
      toast.success('Feedback submitted successfully.');
    } catch (e) {
      console.error('[SubmitFeedback] Error:', e?.response?.data || e?.message || e);
      setFeedbackInlineMessageType('error');
      setFeedbackInlineMessage('✗ ' + (e?.response?.data?.detail || e?.message || 'Failed to submit feedback'));
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleFinalizeClientValidation = async () => {
    if (!window.confirm('Are you sure you want to mark your validation as DONE? Once finalized, no further changes can be made by you or your psychologist, and AI Analysis will be enabled immediately.')) {
      return;
    }
    try {
      setValidationSubmitting(true);
      console.log('[FinalizeValidation] Finalizing...');
      const res = await axios.post(`${API}/sessions/${sessionId}/phase5/client-finalize-validation`, {});
      console.log('[FinalizeValidation] Success:', res.data);
      await fetchExisting();
      setFinalizeInlineMessageType('success');
      setFinalizeInlineMessage('✓ Validation finalized. AI Analysis is now unlocked for your psychologist.');
      toast.success('Validation finalized. AI Analysis is now unlocked for your psychologist.');
    } catch (e) {
      console.error('[FinalizeValidation] Error:', e?.response?.data || e?.message || e);
      setFinalizeInlineMessageType('error');
      setFinalizeInlineMessage('✗ ' + (e?.response?.data?.detail || e?.message || 'Failed to finalize validation'));
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to finalize validation');
    } finally {
      setValidationSubmitting(false);
    }
  };



  // Load available voices
  const loadVoices = () => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      // Filter to working voices (Google and Microsoft voices tend to work better)
      const workingVoices = voices.filter(v => {
        // Prioritize Google and Microsoft voices, but include all
        return true; // Keep all for now, user can test
      });
      setAvailableVoices(workingVoices);
      // Set default to first English voice or first available
      const defaultVoice = workingVoices.find(v => v.lang.startsWith('en')) || workingVoices[0];
      setSelectedVoice(defaultVoice);
    }
  };

  // Voices might load asynchronously
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  // Text-to-Speech functions
  const speakReport = (reportContent, reportId) => {
    // Stop any ongoing speech
    window.speechSynthesis.cancel();
    
    if (currentSpeakingReport === reportId && isSpeaking) {
      // If clicking the same report, stop it
      setIsSpeaking(false);
      setCurrentSpeakingReport(null);
      setCurrentSpeakingText('');
      return;
    }
    
    // Clean the content (remove excessive formatting and improve speech readability)
    if (reportId === 'next_steps' || reportId === 'next') reportContent = nextStepsText(reportContent);
    const cleanContent = reportContent
      .replace(/--+/g, ', ')        // Replace dashes with comma pause (avoid "dash dash")
      .replace(/[-—–]/g, ', ')      // Replace single dashes with comma pause
      .replace(/[#*_`]/g, '')       // Remove markdown
      .replace(/\n\n+/g, '. ')      // Replace double newlines with period
      .replace(/\n/g, ' ')          // Replace single newlines with space
      .replace(/\s+/g, ' ')         // Normalize multiple spaces
      .replace(/,\s*,/g, ',')       // Remove duplicate commas
      .trim();
    
    // Store the text for replay when settings change
    setCurrentSpeakingText(cleanContent);
    setCurrentSpeakingReport(reportId);
    
    const utterance = new SpeechSynthesisUtterance(cleanContent);
    utterance.rate = speechRate;
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    utterance.onstart = () => {
      setIsSpeaking(true);
    };
    
    utterance.onend = () => {
      setIsSpeaking(false);
      setCurrentSpeakingReport(null);
      setCurrentSpeakingText('');
    };
    
    utterance.onerror = (event) => {
      console.error('Speech error:', event);
      if (event.error === 'not-allowed' || event.error === 'audio-busy') {
        showError('Speech not allowed. Please check browser permissions.');
      } else if (event.error === 'voice-unavailable') {
        showError('This voice is not available. Please try another voice.');
      } else {
        showError('Failed to read Analysis. Please try again or select a different voice.');
      }
      setIsSpeaking(false);
      setCurrentSpeakingReport(null);
      setCurrentSpeakingText('');
    };
    
    try {
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Failed to start speech:', error);
      showError('Failed to start text-to-speech. Please try again.');
      setIsSpeaking(false);
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setCurrentSpeakingReport(null);
    setCurrentSpeakingText('');
  };

  const resumeSpeaking = () => {
    window.speechSynthesis.resume();
    setIsSpeaking(true);
  };
  
  // Restart speech with new settings when rate or voice changes
  const restartSpeechWithNewSettings = () => {
    if (isSpeaking && currentSpeakingText && currentSpeakingReport) {
      // Stop current speech
      window.speechSynthesis.cancel();
      
      // Restart with new settings
      setTimeout(() => {
        speakReport(currentSpeakingText, currentSpeakingReport);
      }, 100);
    }
  };

  // Custom Report Generation Function
  const handleGenerateCustomReport = async (sourceReport = null) => {
    const requestedName = sourceReport?.report_name || customReportName.trim();
    const requestedFocus = sourceReport?.custom_focus || customReportFocus.trim();
    if (!requestedName) {
      showModalNotification('error', 'Please enter an Analysis name');
      return;
    }
    if (!requestedFocus) {
      showModalNotification('error', 'Please describe what you want in the Analysis');
      return;
    }

    setProgressModal({ open: true, kind: 'custom', label: requestedName, phase: 'generating', isRegenerate: Boolean(sourceReport), errorMessage: '' });
    setGeneratingCustomReport(true);
    
    try {
      const response = await axios.post(
        `${API}/sessions/${sessionId}/phase5/generate-custom-report`,
        {
          report_name: requestedName,
          custom_focus: requestedFocus
        }
      );
      
      console.log('[CUSTOM REPORT] Generation response:', response.data);
      setCustomReportName('');
      setCustomReportFocus('');
      console.log('[CUSTOM REPORT] Fetching updated data...');
      await fetchExisting(); // Refresh to show new report
      console.log('[CUSTOM REPORT] Data refreshed, existingData updated');
      setProgressModal((m) => ({ ...m, phase: 'done' }));
    } catch (error) {
      console.error('Custom report error:', error);
      let errorDetail = 'Failed to generate custom analysis. Please try again.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorDetail = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'object') {
            errorDetail = JSON.stringify(error.response.data.detail, null, 2);
          } else {
            errorDetail = error.response.data.detail;
          }
        } else if (error.response.data.message) {
          errorDetail = error.response.data.message;
        }
      } else if (error.message) {
        errorDetail = error.message;
      }
      
      setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: errorDetail }));
    } finally {
      setGeneratingCustomReport(false);
    }
  };

  const fetchExisting = async (background = false) => {
    // Coalesce automatic refreshes. Explicit post-mutation reads supersede an
    // older read so a response started before a save cannot restore old data.
    if (background && reportReadRef.current) return reportReadRef.current.promise;
    reportReadRef.current?.controller.abort();
    const read = { controller: new AbortController() };
    reportReadRef.current = read;
    setReportLoadState('loading');
    setReportLoadError('');
    read.promise = (async () => {
    const ticket = nextStepsState.beginRead();
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/phase5`, {
        signal: read.controller.signal, timeout: 30000, skipGlobalLoading: true,
      });
      if (!reportMountedRef.current || reportReadRef.current !== read || currentNextStepsState.current !== nextStepsState) return;
      const safeSynthesisPoints = normalizeSynthesisPoints(res.data?.synthesis_points);
      
      // Add derived fields for backward compatibility
      // Check if AI Summary and Metaphorical Portrait exist in ai_reports array
      const aiSummaryReport = findClientSummaryReport(res.data.ai_reports || [])?.report;
      const metaphoricalReport = res.data.ai_reports?.find(r => r.report_type === 'metaphorical_portrait');
      
      const enrichedData = {
        ...res.data,
        next_steps: nextStepsState.accept(res.data?.next_steps, ticket, res.data?.synthesis_reviewed === false),
        synthesis_points: safeSynthesisPoints,
        ai_summary: aiSummaryReport?.content || res.data.ai_summary, // Derived from ai_reports or legacy field
        metaphorical_narrative: metaphoricalReport?.content || res.data.metaphorical_narrative // Derived from ai_reports or legacy field
      };
      
      setReportLoadState(safeSynthesisPoints.length > 0 ? 'loaded' : 'empty');
      if (safeSynthesisPoints.length > 0) {
        setExistingData(enrichedData);
        setSynthesisPoints(safeSynthesisPoints);
        // Initialize editable points
        if (!editingPointsRef.current) setEditablePoints(safeSynthesisPoints.map(p => ({
          point_number: p.point_number,
          title: p.title,
          content: p.content
        })));
      } else {
        setExistingData(enrichedData);
        setSynthesisPoints([]);
      }
      
      // Fetch book recommendations if available
      if (res.data.book_recommendations) {
        setBookRecommendations(res.data.book_recommendations);
        setBookRecsShared(res.data.book_recommendations_shared_with_client || false);
      }
      
      // Fetch movie recommendations if available
      if (res.data.movie_recommendations) {
        setMovieRecommendations(res.data.movie_recommendations);
        setMovieRecsShared(res.data.movie_recommendations_shared_with_client || false);
      }
      if (res.data.music_recommendations) {
        setMusicRecsShared(res.data.music_recommendations_shared_with_client || false);
      }

      return enrichedData;
    } catch (error) {
      if (!reportMountedRef.current || reportReadRef.current !== read || read.controller.signal.aborted) return;
      setReportLoadState('error');
      setReportLoadError(error.response?.status === 401
        ? 'Your session has expired. Sign in again to load Analysis.'
        : error.response?.status === 403
          ? 'You do not have access to this Analysis. Check your account or retry.'
          : 'Analysis could not be loaded. Check your connection and retry.');
    } finally {
      if (reportReadRef.current === read) reportReadRef.current = null;
    }
    })();
    return read.promise;
  };

  useEffect(() => subscribeInsightInvalidation((change) => {
    if (change.sessionId && change.sessionId !== String(sessionId)) return;
    if (change.type === 'report_visibility_changed') fetchReportVisibility();
    if (change.type !== 'report_visibility_changed') fetchExisting(true);
  }), [sessionId, fetchReportVisibility]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      fetchReportVisibility();
      fetchExisting(true);
    };
    const interval = window.setInterval(refresh, 45000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', refresh); };
  }, [sessionId, fetchReportVisibility]);

  const handleGenerateBookRecommendations = async () => {
    if (bookGenerationInFlightRef.current) return;
    if (!isReportEnabled('book_recommendations')) {
      showError('"Book Reflections" is currently disabled by the administrator.');
      return;
    }
    const isRegen = !!existingData?.book_recommendations;
    setProgressModal({ open: true, kind: 'books', label: 'Book Reflections', phase: 'generating', isRegenerate: isRegen, errorMessage: '' });
    setGeneratingBooks(true);
    bookGenerationInFlightRef.current = true;
    setShowBooksTimer(true);
    try {
      // Book generation started (no notification)
      const response = await axios.post(
        `${API}/sessions/${sessionId}/phase5/generate-book-recommendations`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      
      console.log('[BOOK RECS] Generation response:', response.data);
      // Handle new response format with metadata
      const recsData = response.data.recommendations || response.data;
      const metadata = response.data.metadata || {};
      
      console.log('[BOOK RECS] Extracted recommendations data:', recsData);
      console.log('[BOOK RECS] Metadata:', metadata);
      
      setBookRecommendations(recsData);
      setExistingData(prev => ({
        ...prev, 
        book_recommendations: recsData, 
        recommendations_generated_by_name: metadata.generated_by_name,
        recommendations_generated_at: metadata.generated_at
      }));
      
      setProgressModal((m) => ({ ...m, phase: 'done' }));
      
      console.log('[BOOK RECS] Refreshing data from server...');
      // Refresh data from server instead of page reload
      await fetchExisting();
    } catch (error) {
      console.error('Error generating book recommendations:', error);
      const detail = error.response?.data?.detail || 'Failed to generate book recommendations';
      setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: detail }));
    } finally {
      bookGenerationInFlightRef.current = false;
      setGeneratingBooks(false);
    }
  };

  const handleToggleShareBooks = async () => {
    try {
      const newShareState = !bookRecsShared;
      await axios.patch(
        `${API}/sessions/${sessionId}/phase5/book-recommendations/share?share=${newShareState}`,
        {},
        { 
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        }
      );
      setBookRecsShared(newShareState);
      setExistingData(prev => ({...prev, book_recommendations_shared_with_client: newShareState}));
      // Book recommendations sharing updated (silent)
    } catch (error) {
      console.error('Failed to update sharing status:', error);
      // Silent error handling
    }
  };


  // Movie Recommendations Handlers
  const handleGenerateMovieRecommendations = async () => {
    if (movieGenerationInFlightRef.current) return;
    if (!isReportEnabled('movie_recommendations')) {
      showError('"Movie Reflections" is currently disabled by the administrator.');
      return;
    }
    const isRegen = !!existingData?.movie_recommendations;
    setProgressModal({ open: true, kind: 'movies', label: 'Movie Reflections', phase: 'generating', isRegenerate: isRegen, errorMessage: '' });
    setGeneratingMovies(true);
    movieGenerationInFlightRef.current = true;
    setShowMoviesTimer(true);
    try {
      // Movie generation started (no notification)
      const response = await axios.post(
        `${API}/sessions/${sessionId}/phase5/generate-movie-recommendations`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      
      console.log('[MOVIE RECS] Generation response:', response.data);
      // Handle response format
      const recsData = response.data.recommendations || response.data;
      
      console.log('[MOVIE RECS] Extracted recommendations data:', recsData);
      
      setMovieRecommendations(recsData);
      setExistingData(prev => ({
        ...prev, 
        movie_recommendations: recsData, 
        movie_recommendations_generated_by_name: response.data.generated_by,
        movie_recommendations_generated_at: response.data.generated_at
      }));
      
      console.log('[MOVIE RECS] State updated, recommendations should now display');
      setProgressModal((m) => ({ ...m, phase: 'done' }));
    } catch (error) {
      console.error('Error generating movie recommendations:', error);
      const detail = error.response?.data?.detail || 'Failed to generate movie recommendations';
      setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: detail }));
    } finally {
      movieGenerationInFlightRef.current = false;
      setGeneratingMovies(false);
    }
  };

  const downloadRecommendationPDF = async (kind) => {
    const isBook = kind === 'book';
    const isMovie = kind === 'movie';
    const label = isBook ? 'Book Recommendations' : isMovie ? 'Movie Recommendations' : 'Music Recommendations';
    const report = isBook ? existingData?.book_recommendations : isMovie ? existingData?.movie_recommendations : existingData?.music_recommendations;
    if (!report) return;
    try {
      const { generateStandardPDF } = await import('../utils/pdfGenerator');
      const generatedAt = report.generated_at || (isBook ? existingData?.recommendations_generated_at : isMovie ? existingData?.movie_recommendations_generated_at : existingData?.music_recommendations_generated_at);
      const doc = await generateStandardPDF({
        title: `INSIGHT\n${label}`,
        content: recommendationReportPdfContent(report),
        metadata: { date: generatedAt ? new Date(generatedAt).toLocaleString() : undefined },
        footerText: `INSIGHT Platform - ${label}`,
        preserveText: true,
      });
      doc.save(`INSIGHT_${isBook ? 'Book' : isMovie ? 'Movie' : 'Music'}_Recommendations.pdf`);
      showSuccess('PDF downloaded');
    } catch (error) {
      console.error('[Recommendations PDF] Download failed:', error);
      showError('Failed to generate PDF');
    }
  };

  const handleToggleShareMovies = async () => {
    try {
      const currentSharedState = movieRecsShared || (existingData && existingData.movie_recommendations_shared_with_client) || false;
      const newShareState = !currentSharedState;
      await axios.patch(
        `${API}/sessions/${sessionId}/phase5/movie-recommendations/share?share=${newShareState}`,
        {},
        { 
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        }
      );
      setMovieRecsShared(newShareState);
      setExistingData(prev => ({...prev, movie_recommendations_shared_with_client: newShareState}));
      // Movie recommendations sharing updated (silent)
      
      // Refresh phase5 data to ensure client sees the update
      await fetchPhase5Data();
    } catch (error) {
      console.error('Failed to update sharing status:', error);
      // Silent error handling
    }
  };

  const handleGenerateMusicRecommendations = async () => {
    if (musicGenerationInFlightRef.current) return;
    musicGenerationInFlightRef.current = true;
    setGeneratingMusic(true);
    setProgressModal({ open: true, kind: 'music', label: 'Music Reflections', phase: 'generating', isRegenerate: !!existingData?.music_recommendations, errorMessage: '' });
    try {
      const response = await axios.post(`${API}/sessions/${sessionId}/phase5/generate-music-recommendations`);
      setExistingData(prev => ({ ...prev, music_recommendations: response.data.recommendations,
        music_recommendations_generated_at: response.data.generated_at }));
      setProgressModal(m => ({ ...m, phase: 'done' }));
      await fetchExisting();
    } catch (error) {
      setProgressModal(m => ({ ...m, phase: 'error', errorMessage: error.response?.data?.detail || 'Failed to generate music recommendations' }));
    } finally {
      musicGenerationInFlightRef.current = false;
      setGeneratingMusic(false);
    }
  };

  const applyResourceShare = async () => {
    const { resource, share } = shareConfirmation;
    if (shareMutationPending || !resource || !['book', 'movie', 'music'].includes(resource)) return;
    console.info('[HELPFUL RESOURCE SHARE] confirm', { resource, sessionId, role: userRole, share });
    setShareMutationPending(true);
    try {
      const endpoint = `${API}/sessions/${sessionId}/phase5/${resource}-recommendations/share`;
      console.info('[HELPFUL RESOURCE SHARE] request', { resource, sessionId, endpoint, share });
      const response = await axios.patch(endpoint, {}, { params: { share } });
      console.info('[HELPFUL RESOURCE SHARE] response', { resource, status: response.status, shared: response.data?.shared });
      if (response.status < 200 || response.status >= 300 || response.data?.shared !== share) {
        throw new Error('SHARE_CONFIRMATION_INVALID');
      }
      const field = `${resource}_recommendations_shared_with_client`;
      const snapshotField = `${resource}_recommendations_shared_snapshot`;
      setExistingData(prev => ({
        ...prev,
        [field]: share,
        ...(share ? { [snapshotField]: prev?.[`${resource}_recommendations`] } : {}),
      }));
      if (resource === 'book') setBookRecsShared(share);
      if (resource === 'movie') setMovieRecsShared(share);
      if (resource === 'music') setMusicRecsShared(share);
      console.info('[HELPFUL RESOURCE SHARE] state updated', { resource, share });
      setShareConfirmation({ open: false, resource: null, share: true });
      showSuccess(share ? 'Shared with client successfully.' : 'Client access removed successfully.');
    } catch (error) {
      const reason = error.response?.data?.detail || 'Please try again.';
      console.error('[HELPFUL RESOURCE SHARE] failed', { resource, status: error.response?.status, reason });
      showError(`Unable to ${share ? 'share with client' : 'remove client access'}: ${reason}`);
    } finally {
      setShareMutationPending(false);
    }
  };

  const shareControl = (resource) => {
    const current = existingData?.[`${resource}_recommendations`];
    const snapshot = existingData?.[`${resource}_recommendations_shared_snapshot`];
    const hasSharedSnapshot = !!existingData?.[`${resource}_recommendations_shared_with_client`];
    const currentVersionShared = hasSharedSnapshot && !!snapshot && (
      (current?.generated_at && current.generated_at === snapshot.generated_at) ||
      (!current?.generated_at && JSON.stringify(current) === JSON.stringify(snapshot))
    );
    return currentVersionShared ? (
      <><span className="text-sm font-semibold text-green-700" data-testid={`status-${resource}-shared`}>Shared with Client ✓</span><Button type="button" variant="outline" onClick={() => { console.info('[HELPFUL RESOURCE SHARE] modal open', { resource, sessionId, role: userRole, share: false }); setShareConfirmation({ open: true, resource, share: false }); }} data-testid={`btn-unshare-${resource}`}>Unshare</Button></>
    ) : <>{hasSharedSnapshot && <span className="text-xs font-medium text-amber-700" data-testid={`status-${resource}-previous-version-shared`}>Previous version remains shared</span>}<Button type="button" variant="outline" onClick={() => { console.info('[HELPFUL RESOURCE SHARE] modal open', { resource, sessionId, role: userRole, share: true }); setShareConfirmation({ open: true, resource, share: true }); }} data-testid={`btn-share-${resource}`}>Share with Client</Button></>;
  };


  // Transcription fetch functionality removed


  const canGenerateSqis = userRole === 'psychologist' && !isViewOnlyMode;
  const requestGeneratePoints = () => {
    if (!canGenerateSqis || !['loaded', 'empty'].includes(reportLoadState) || generationPointsRef.current || isEditing16Points || updating16Points) return;
    if (reportLoadState === 'loaded') setPhase5RegenerationKind('sqis');
    else generatePoints();
  };

  const generatePoints = async () => {
    if (generationPointsRef.current) return;
    if (!canGenerateSqis || !['loaded', 'empty'].includes(reportLoadState) || isEditing16Points || updating16Points) return;
    generationPointsRef.current = true;
    setGeneratingPoints(true);
    setProgressModal({ open: true, kind: 'sqis', label: '14-Point SQIS', phase: 'generating', isRegenerate: synthesisPoints.length > 0, errorMessage: '' });
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/phase5/generate-points`);
      // The authorized read supplies the persisted points AND reset review/dependency metadata.
      const saved = await fetchExisting();
      if (!saved) {
        setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: 'SQIS was saved, but the saved report could not be reloaded. Use Retry to load it before generating again.' }));
        return;
      }
      setIsSynthesisVisible(true);
      setSqisTab('sqis');
      setProgressModal((m) => ({ ...m, phase: 'done' }));
    } catch (error) {
      console.error('❌ SQIS generation failed:', error);
      const errorMsg = sqisGenerationErrorMessage(error);
      setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: errorMsg }));
    } finally {
      setGeneratingPoints(false);
      generationPointsRef.current = false;
    }
  };

  const handleUpdatePoint = async (pointNumber, newContent) => {
    try {
      await axios.put(`${API}/sessions/${sessionId}/phase5/update-point?point_number=${pointNumber}&content=${encodeURIComponent(newContent)}`);
      
      // Update local state
      setSynthesisPoints(prev => 
        prev.map(p => p.point_number === pointNumber ? {...p, content: newContent} : p)
      );
      
      showSuccess('Point updated!');
    } catch (error) {
      showError('Failed to update point');
    }
  };

  const handleGenerateReports = async () => {
    const isRegen = !!(existingData?.ai_reports && existingData.ai_reports.length > 0);
    setProgressModal({ open: true, kind: 'report', label: 'All Standard Analysis', phase: 'generating', isRegenerate: isRegen, errorMessage: '' });
    setGeneratingReports(true);
    // Generating AI reports (no notification)
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/phase5/generate-reports`);
      setProgressModal((m) => ({ ...m, phase: 'done' }));
      fetchExisting();
      onComplete();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to generate Analysis';
      setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: detail }));
    } finally {
      setGeneratingReports(false);
    }
  };

  const handleStopReportGeneration = () => {
    if (reportAbortControllerRef.current) {
      reportAbortControllerRef.current.abort();
      setGeneratingIndividualReport(null);
      setReportProgressMessage('');
      setShowIndividualTimer(false);
      showModalNotification('info', 'Analysis generation cancelled by user');
    }
  };

  const handleGenerateIndividualReport = async (reportType, reportName) => {
    if (reportType === 'ai_summary' && reportAbortControllerRef.current) return;
    if (!isReportEnabled(reportType)) {
      showError(`"${reportName}" is currently disabled by the administrator. Please contact your admin to enable it.`);
      return;
    }
    // Detect whether this report already exists (regeneration vs first-time generation)
    const isRegen = !!(existingData?.ai_reports || []).find(
      (r) => r && (r.report_type === reportType || r.type === reportType)
    );
    setProgressModal({ open: true, kind: 'report', label: reportName || 'Analysis', phase: 'generating', isRegenerate: isRegen, errorMessage: '' });
    setGeneratingIndividualReport(reportType);
    setShowIndividualTimer(true); // Show timer when generation starts
    setReportProgressMessage('Initializing Analysis generation...'); // Set initial message
    let accumulatedContent = '';
    const abortController = new AbortController();
    
    // Store abort controller in ref for cleanup on unmount
    reportAbortControllerRef.current = abortController;
    
    try {
      console.log(`🚀 Starting report generation: ${reportName} (${reportType})`);
      console.log(`📡 API endpoint: ${API}/sessions/${sessionId}/phase5/generate-individual-report`);
      
      // Use native fetch API for streaming support with abort signal
      // Use window.fetch directly to bypass rrweb recorder wrapper that causes clone errors
      const nativeFetch = window.fetch.bind(window);
      const response = await nativeFetch(`${API}/sessions/${sessionId}/phase5/generate-individual-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          ...(reportType === 'ai_summary' ? documentHeaders() : {})
        },
        body: JSON.stringify({ report_type: reportType }),
        signal: abortController.signal
      });

      console.log(`📥 Response status: ${response.status}`);
      console.log(`📥 Response headers:`, response.headers);

      if (reportType === 'ai_summary') {
        await readClientSynthesisStream(response, setReportProgressMessage);
        const saved = await fetchExisting();
        if (!findClientSummaryReport(saved?.ai_reports || [])) {
          throw new Error('Generation finished, but the saved Client Synthesis could not be loaded. Please refresh before retrying.');
        }
        setProgressModal((m) => ({ ...m, phase: 'done' }));
        return;
      }

      if (!response.ok) {
        // Handle non-OK responses properly - read body only ONCE
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          // Check content-type to determine how to read the body
          const contentType = response.headers.get('content-type');
          
          if (contentType && contentType.includes('application/json')) {
            // Response is JSON
            const errorData = await response.json();
            if (errorData.detail) {
              errorMessage = errorData.detail;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            } else {
              errorMessage = JSON.stringify(errorData);
            }
          } else {
            // Response is text or unknown
            const errorText = await response.text();
            if (errorText) {
              // Try to parse as JSON in case content-type header is wrong
              try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.detail || errorData.message || errorText;
              } catch {
                errorMessage = errorText;
              }
            }
          }
        } catch (readError) {
          console.error('Could not read error response:', readError);
          errorMessage = `HTTP ${response.status}: Unable to read error details`;
        }
        
        console.error(`❌ HTTP error! status: ${response.status}, message:`, errorMessage);
        
        // Clear progress message and show error to user
        setReportProgressMessage('');
        showModalNotification('error', errorMessage);
        throw new Error(errorMessage);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Process stream in a non-blocking way
      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.substring(6));
                  
                  if (data.type === 'metadata') {
                    console.log('✅ Streaming started for:', data.report_name);
                    const msg = 'Starting AI generation...';
                    console.log('🎯 SETTING PROGRESS MESSAGE:', msg);
                    setReportProgressMessage(msg);
                    console.log('🔍 Current reportProgressMessage state after set:', msg);
                  } else if (data.type === 'progress') {
                    console.log('⏳ Progress:', data.message);
                    console.log('🎯 SETTING PROGRESS MESSAGE:', data.message);
                    console.log('🔴🔴🔴 PROGRESS UPDATE RECEIVED:', data.message);
                    setReportProgressMessage(data.message); // Show progress to user
                    console.log('🔍 Current reportProgressMessage state after set:', data.message);
                  } else if (data.type === 'chunk') {
                    accumulatedContent += data.content;
                    console.log('📦 Received chunk, total length:', accumulatedContent.length);
                  } else if (data.type === 'complete') {
                    console.log('✅ Stream complete:', data.message);
                    setReportProgressMessage(''); // Clear progress message
                    setProgressModal((m) => ({ ...m, phase: 'done' }));
                    await fetchExisting(); // Refresh data from server instead of page reload
                  } else if (data.type === 'error') {
                    console.error('❌ Error:', data.message);
                    setReportProgressMessage(''); // Clear progress message
                    setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: data.message }));
                    throw new Error(data.message);
                  }
                } catch (parseError) {
                  console.error('Error parsing chunk:', parseError);
                }
              }
            }
            
            // Yield control back to the browser to prevent UI blocking
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        } catch (streamError) {
          // Only throw if it's not an abort error
          if (streamError.name !== 'AbortError') {
            throw streamError;
          }
          console.log('Stream aborted by user navigation');
        }
      };

      await processStream();
    } catch (error) {
      // Handle cancellation gracefully
      if (error.name === 'AbortError') {
        console.log('Analysis generation cancelled by user');
        setReportProgressMessage(''); // Clear progress message
        setProgressModal((m) => ({ ...m, open: false }));
        showModalNotification('info', `${reportName} generation was cancelled`);
        return;
      }
      
      // Handle rrweb recorder clone errors (these indicate a backend error occurred)
      if (error.message && error.message.includes("Failed to execute 'clone' on 'Response'")) {
        console.error('Response clone error (backend returned error):', error);
        let errorMessage = `Analysis generation failed. `;
        
        // Provide context-specific guidance based on report type
        if (reportType === 'ai_summary') {
          errorMessage += `This could mean: (1) The Client Synthesis prompt is not configured in Admin Dashboard → Analysis Prompts, OR (2) All 4 standard Analysis items need to be generated first (Exploring Client Feelings, Emotional Balance Snapshot, Strengths and Coping Guide, Deep Dive into Inner World).`;
        } else {
          errorMessage += `The Analysis prompt may be missing or empty. Please check Admin Dashboard → Analysis Prompts to ensure the prompt for "${reportName}" has content.`;
        }
        
        setReportProgressMessage('');
        setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: errorMessage }));
        return;
      }
      
      console.error('Streaming error:', error);
      
      // Better error messages for different error types
      let errorMessage = `Failed to generate ${reportName}`;
      if (error.message.includes('502') || error.message.includes('Bad Gateway')) {
        errorMessage = `Server timeout while generating ${reportName}. The Analysis generation is taking longer than expected. Please try again or contact support if this persists.`;
      } else if (error.message.includes('500')) {
        errorMessage = `Server error while generating ${reportName}: ${error.message}`;
      } else if (error.message.includes('budget') || error.message.includes('insufficient funds')) {
        errorMessage = `Insufficient budget in your Universal LLM Key. Please add balance and try again.`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setReportProgressMessage(''); // Clear progress message
      setProgressModal((m) => ({ ...m, phase: 'error', errorMessage: errorMessage }));
    } finally {
      setGeneratingIndividualReport(null);
      setReportProgressMessage(''); // Clear progress message on completion
      reportAbortControllerRef.current = null; // Clear ref after completion
    }
  };

  const handleGenerateResearchReport = async (reportType, reportName) => {
    if (!isReportEnabled(reportType)) {
      showWarning('This Research Resource is currently disabled by the administrator.');
      return;
    }
    const endpoints = {
      apa_case_parallels: `/sessions/${sessionId}/phase5/apa-case-parallels`,
      research_framework_synthesis: `/sessions/${sessionId}/phase5/research-framework-synthesis`,
      developmental_ecological_cultural: `/sessions/${sessionId}/reports/developmental-ecological-cultural`,
    };
    const existing = (existingData?.ai_reports || []).some((report) => report?.report_type === reportType);
    setProgressModal({ open: true, kind: 'report', label: reportName, phase: 'generating', isRegenerate: existing, errorMessage: '' });
    setGeneratingIndividualReport(reportType);
    try {
      await axios.post(`${API}${endpoints[reportType]}`);
      await fetchExisting();
      setProgressModal((state) => ({ ...state, phase: 'done' }));
    } catch (error) {
      setProgressModal((state) => ({ ...state, phase: 'error', errorMessage: apiErrorMessage(error, 'Research Analysis generation failed.') }));
    } finally {
      setGeneratingIndividualReport(null);
    }
  };

  const downloadReportPDF = async (report) => {
    try {
      // Use unified PDF generator
      const { generateStandardPDF } = await import('../utils/pdfGenerator');
      
      // Get psychologist name from report or from existingData or from current user
      const psychologistName = report.psychologist_name || 
                               existingData?.psychologist_name || 
                               user?.name || 
                               'Unknown';
      
      const isClientSynthesis = report.report_type === 'ai_summary';
      const pdfTitle = isClientSynthesis ? 'INSIGHT Client Synthesis' : (report.report_name || 'INSIGHT Report');
      const pdfFilename = isClientSynthesis
        ? clientSynthesisFileName
        : `INSIGHT_${report.report_name?.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
      const generatePDF = isClientSynthesis
        ? (await import('../utils/clientSynthesisPdf')).generateClientSynthesisPDF
        : generateStandardPDF;
      const doc = await generatePDF({
        title: pdfTitle,
        branding: isClientSynthesis ? await loadSynthesisBranding(sessionId) : undefined,
        content: report.report_type === 'ai_summary'
          ? (report.content ?? report.report_content ?? '')
          : (report.report_content || report.content || 'No content available'),
        metadata: {
          date: isClientSynthesis && report.generated_at ? new Date(report.generated_at).toLocaleDateString() : new Date().toLocaleDateString(),
          clientName: clientDisplayName,
          psychologistName: psychologistName
        },
        footerText: "INSIGHT Platform - Therapeutic Report",
        filename: pdfFilename,
        preserveText: report.report_type === 'ai_summary'
      });
      
      // Save PDF
      doc.save(pdfFilename);
      showSuccess('Analysis downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      showError('Failed to download PDF. Please try again.');
    }
  };

  const download16PointSynthesisPDF = async () => {
    try {
      if (!existingData?.synthesis_points || existingData.synthesis_points.length === 0) {
        showError('No 14 Points SQIS data available');
        return;
      }

      // Build PDF with table rendering for Points 11 and 14 (matching UI)
      const {
        initializePDF, addLogo, addHeader, addMetadata,
        addFormattedContent, addFooter, applyHeadingStyle, checkNewPage
      } = await import('../utils/pdfGenerator');
      const autoTableModule = await import('jspdf-autotable');
      const autoTable = autoTableModule.default || autoTableModule;

      const { doc, pageWidth, pageHeight, margin, maxWidth } = await initializePDF();
      let yPosition = await addLogo(doc, pageWidth, margin);
      yPosition = addHeader(doc, '14 Points Structured Qualitative Interpretative Summary', pageWidth, yPosition);
      yPosition = addMetadata(doc, {
        date: new Date().toLocaleDateString(),
        clientName: existingData?.client_name || existingData?.name || 'Client',
        psychologistName: existingData?.psychologist_name || user?.name || 'Psychologist'
      }, margin, yPosition);

      // Separator line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;

      // Review-status banner: communicates whether the profile has been reviewed
      // by the clinician. Shown immediately under the header for visibility.
      const isReviewed = !!existingData?.synthesis_reviewed;
      const reviewedAt = existingData?.synthesis_reviewed_at
        ? new Date(existingData.synthesis_reviewed_at).toLocaleString()
        : '';
      const reviewMessage = isReviewed
        ? `This profile has been reviewed by the clinician${reviewedAt ? ` on ${reviewedAt}` : ''}.`
        : 'This profile has NOT yet been reviewed by the clinician. Please ensure clinician review before relying on it.';
      const bannerColor = isReviewed
        ? { fill: [220, 252, 231], border: [22, 163, 74], text: [22, 101, 52] }   // green
        : { fill: [254, 243, 199], border: [217, 119, 6], text: [120, 53, 15] }; // amber
      // Render banner box
      const bannerLines = doc.splitTextToSize(reviewMessage, maxWidth - 8);
      const bannerHeight = bannerLines.length * 5 + 8;
      doc.setFillColor(...bannerColor.fill);
      doc.setDrawColor(...bannerColor.border);
      doc.setLineWidth(0.8);
      doc.roundedRect(margin, yPosition, maxWidth, bannerHeight, 1, 1, 'FD');
      doc.setTextColor(...bannerColor.text);
      doc.setFont('times', 'bold');
      doc.setFontSize(10);
      let bannerTextY = yPosition + 6;
      bannerLines.forEach(bl => {
        doc.text(bl, margin + 4, bannerTextY);
        bannerTextY += 5;
      });
      // Reset text styles for subsequent content
      doc.setTextColor(0, 0, 0);
      doc.setFont('times', 'normal');
      yPosition += bannerHeight + 6;

      // Helper: ASCII-only replacements for glyphs that the default Times font cannot render.
      const cleanForPdf = (s) => (s || '')
        .replace(/→/g, '->')
        .replace(/←/g, '<-')
        .replace(/–/g, '-')
        .replace(/—/g, '-')
        .replace(/“|”/g, '"')
        .replace(/‘|’/g, "'")
        .replace(/•/g, '-')
        .replace(/…/g, '...');

      // Helper: strip HTML tags & decode common entities for PDF rendering.
      // NOTE: keeps Unicode glyphs intact so downstream parsers can still match
      // markers like the `•` bullet. cleanForPdf is applied only on final cell values.
      const stripHtml = (s) => {
        if (!s) return '';
        return String(s)
          .replace(/<br\s*\/?>(\s*)/gi, '\n')
          .replace(/<\/p>/gi, '\n')
          .replace(/<\/li>/gi, '\n')
          .replace(/<li[^>]*>/gi, '• ')
          .replace(/<\/?(strong|b|em|i|u|span|div|p|ul|ol)[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/[ \t]+\n/g, '\n')
          .trim();
      };

      // Parser for Point 11 content (Theme / Theme Connotations / bullets / Time)
      // Time Taken value may span multiple lines (one entry per line, plus an Avg line).
      const parsePoint11 = (content) => {
        const rows = [];
        // Normalize HTML structure to plain text BEFORE line-splitting
        const normalized = stripHtml(content || '');
        const lines = normalized.split('\n');
        let theme = '', connotations = '', responses = [], time = '', inTime = false;
        const flush = () => {
          if (theme) {
            rows.push([
              `${theme}\n${connotations}`.trim(),
              responses.join('\n'),
              time.trim()
            ]);
          }
          theme = ''; connotations = ''; responses = []; time = ''; inTime = false;
        };
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          if (line.startsWith('Theme Connotations:')) {
            inTime = false;
            connotations = line.replace('Theme Connotations:', '').trim();
          } else if (line.startsWith('Theme:')) {
            // Starting a new theme — flush any pending one first
            flush();
            theme = line.replace('Theme:', '').trim();
          } else if (line.startsWith('•')) {
            inTime = false;
            responses.push(line);
          } else if (line.startsWith('Average Time Taken:') || line.startsWith('Time Taken:')) {
            time = line.replace('Average Time Taken:', '').replace('Time Taken:', '').trim();
            inTime = true;
          } else if (inTime && !line.startsWith('---') && !line.startsWith('Client Responses')) {
            // Continuation line of multi-line Time Taken value
            time = time ? `${time}\n${line}` : line;
          }
        }
        flush();
        return rows;
      };

      // Parser for Point 14 content (Response / Themes / Repeated Times)
      const parsePoint14 = (content) => {
        const rows = [];
        const normalized = stripHtml(content || '');
        const lines = normalized.split('\n');
        let response = '', themes = '', count = '';
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          if (line.startsWith('Response:')) {
            response = line.replace('Response:', '').trim();
          } else if (line.startsWith('Themes:')) {
            themes = line.replace('Themes:', '').trim();
          } else if (line.startsWith('Repeated Times:')) {
            count = line.replace('Repeated Times:', '').trim();
            if (response) {
              rows.push([response, themes, count]);
              response = ''; themes = ''; count = '';
            }
          }
        }
        return rows;
      };

      const visiblePoints = existingData.synthesis_points.filter(
        point => point.point_number !== 15 && point.point_number !== 16
      );

      visiblePoints.forEach((point) => {
        // Sanitize once before every PDF branch, including raw-content fallbacks.
        point = { ...point, content: pointAnalysisDisplay(point) };
        // Point heading
        yPosition = checkNewPage(doc, yPosition, pageHeight, margin, 20);
        applyHeadingStyle(doc, 1);
        const headingLines = doc.splitTextToSize(
          `Point ${point.point_number}: ${getDisplayTitle(point.point_number, point.title)}`,
          maxWidth
        );
        headingLines.forEach(hLine => {
          doc.text(hLine, margin, yPosition);
          yPosition += 7;
        });
        yPosition += 3;

        if (point.point_number === 11) {
          const { tableContent, findings } = splitPoint11Content(point.content);
          const rows = parsePoint11(tableContent).map(r => r.map(cleanForPdf));
          if (rows.length > 0) {
            autoTable(doc, {
              startY: yPosition,
              head: [['Theme Connotations', 'Client Responses', 'Time Taken']],
              body: rows,
              theme: 'grid',
              styles: { font: 'times', fontSize: 9, cellPadding: 3, valign: 'top' },
              headStyles: { fillColor: [219, 234, 254], textColor: [55, 65, 81], fontStyle: 'bold' },
              columnStyles: {
                0: { cellWidth: maxWidth * 0.35 },
                1: { cellWidth: maxWidth * 0.45 },
                2: { cellWidth: maxWidth * 0.20, halign: 'center' }
              },
              margin: { left: margin, right: margin }
            });
            yPosition = doc.lastAutoTable.finalY + 6;
          } else {
            yPosition = addFormattedContent(doc, tableContent || 'No content available', {
              pageWidth, pageHeight, margin, maxWidth, yPosition
            });
          }
          if (findings) {
            yPosition = addFormattedContent(doc, findings, {
              pageWidth, pageHeight, margin, maxWidth, yPosition
            });
          }
        } else if (point.point_number === 14) {
          const rows = parsePoint14(point.content).map(r => r.map(cleanForPdf));
          if (rows.length > 0) {
            autoTable(doc, {
              startY: yPosition,
              head: [['Client Responses', 'Themes', 'Repeated Times']],
              body: rows,
              theme: 'grid',
              styles: { font: 'times', fontSize: 9, cellPadding: 3, valign: 'top' },
              headStyles: { fillColor: [220, 252, 231], textColor: [55, 65, 81], fontStyle: 'bold' },
              columnStyles: {
                0: { cellWidth: maxWidth * 0.45 },
                1: { cellWidth: maxWidth * 0.35 },
                2: { cellWidth: maxWidth * 0.20, halign: 'center' }
              },
              margin: { left: margin, right: margin }
            });
            yPosition = doc.lastAutoTable.finalY + 6;
          } else {
            yPosition = addFormattedContent(doc, point.content || 'No content available', {
              pageWidth, pageHeight, margin, maxWidth, yPosition
            });
          }
        } else {
          // Generic path: detect markdown tables in the point's content and render them
          // as proper tables via autoTable; render surrounding text via formatted content.
          const blocks = parseMarkdownBlocks(point.content || '');
          if (blocks.length === 0) {
            yPosition = addFormattedContent(doc, point.content || 'No content available', {
              pageWidth, pageHeight, margin, maxWidth, yPosition
            });
          } else {
            blocks.forEach(b => {
              if (b.type === 'text') {
                yPosition = addFormattedContent(doc, b.text, {
                  pageWidth, pageHeight, margin, maxWidth, yPosition
                });
              } else {
                // Strip markdown bold and replace Unicode glyphs with ASCII equivalents.
                const cleanCell = (s) => cleanForPdf(
                  (s || '')
                    .replace(/\*\*(.+?)\*\*/g, '$1')
                    .replace(/\*\*/g, '')
                ).trim();
                const head = [b.headers.map(cleanCell)];
                const body = b.rows.map(r => r.map(cleanCell));
                const colCount = head[0].length || 1;
                const colWidth = maxWidth / colCount;
                const columnStyles = {};
                for (let c = 0; c < colCount; c++) {
                  columnStyles[c] = { cellWidth: colWidth };
                }
                yPosition = checkNewPage(doc, yPosition, pageHeight, margin, 30);
                autoTable(doc, {
                  startY: yPosition,
                  head,
                  body,
                  theme: 'grid',
                  styles: { font: 'times', fontSize: 9, cellPadding: 3, valign: 'top' },
                  headStyles: { fillColor: [219, 234, 254], textColor: [55, 65, 81], fontStyle: 'bold' },
                  columnStyles,
                  margin: { left: margin, right: margin }
                });
                yPosition = doc.lastAutoTable.finalY + 4;
              }
            });
          }
        }
        yPosition += 4;
      });

      // Append standardized disclaimer note at the end of the report.
      yPosition = checkNewPage(doc, yPosition, pageHeight, margin, 30);
      yPosition += 6;
      doc.setDrawColor(245, 158, 11); // amber-500
      doc.setLineWidth(1);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;
      if (SYNTHESIS_DISCLAIMER_NOTE) {
        yPosition = addFormattedContent(doc, SYNTHESIS_DISCLAIMER_NOTE, {
          pageWidth, pageHeight, margin, maxWidth, yPosition
        });
      }

      addFooter(doc, 'INSIGHT Platform - 14 Points Structured Qualitative Interpretative Summary', pageWidth);

      const fileName = `INSIGHT_14_Point_Synthesis_${Date.now()}.pdf`;
      doc.save(fileName);
      showSuccess('14 Points SQIS downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      showError('Failed to download PDF. Please try again.');
    }
  };
  
  // DEPRECATED: Old downloadReportPDF function (kept for reference, will be removed after verification)
  const downloadReportPDF_OLD = async (report) => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add Times New Roman font support (jsPDF uses times by default)
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14.4; // 0.2 inches (1 inch = 72 points, so 0.2 inch = 14.4 points)
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = 5; // Start at absolute top - minimal space (5pt from edge)

      // Load and add INSIGHT logo
      try {
        const logoImg = new Image();
        logoImg.src = '/insight-logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(reject, 2000); // Timeout after 2 seconds
        });
        
        // Add smaller logo at top RIGHT
        const logoWidth = 25; // Reduced from 40 to 25 (37.5% smaller)
        const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        const logoX = pageWidth - logoWidth - margin; // Position at right side
        doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 8; // Added space between logo and main heading (8pt)
      } catch (logoError) {
        console.log('Logo not loaded, continuing without logo');
        // Continue without logo if it fails to load
        yPosition = margin; // If logo fails, start at margin
      }

      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
      };

      // Helper: remove unsupported characters while preserving hyphens,
      // comparison operators (>=, <=, <, >) and possessive apostrophes.
      const cleanText = (text) => {
        if (!text) return '';
        let cleaned = text;
        // Normalize em/en dashes to standard hyphen-minus (preserve as hyphens)
        cleaned = cleaned.replace(/[\u2014\u2013]/g, '-');
        // Collapse triple-dash separators into a comma pause
        cleaned = cleaned.replace(/-{3,}/g, ', ');
        // Remove stray ")}" that some AI outputs append
        cleaned = cleaned.replace(/\)\}/g, '');
        // Repair possessive spacing: "name 's" -> "name's"
        cleaned = cleaned.replace(/(\w)\s+'s\b/g, "$1's");
        // Strip underscores per product requirement
        cleaned = cleaned.replace(/_/g, '');
        // Allow-list: word chars, whitespace, common punctuation, hyphens,
        // and comparison operators (< > and Unicode >=/<=).
        cleaned = cleaned.replace(/[^\w\s.,!?:;'"()\[\]\/\n\-<>\u2265\u2264%&@#$*+=]/g, '');
        // Underscore is inside \w — strip again after allow-list to enforce removal
        cleaned = cleaned.replace(/_/g, '');
        return cleaned.trim();
      };

      // Helper function to detect heading level and return {level, isHeading}
      // Level 1 (RED): Main headings - ALL CAPS, numbered sections
      // Level 2 (GREEN): Sub-headings - Title case, standalone phrases
      // Level 3 (BLACK): Third-level - Ends with ":" (like "Evidence:", "Purpose:")
      const getHeadingLevel = (line) => {
        const trimmedLine = line.trim();
        const words = trimmedLine.split(/\s+/);
        
        // Level 3: Third-level headings (BLACK) - Ends with colon like "Evidence:", "Purpose:", "Key Points:"
        // Should be: starts with capital, 1-5 words, ends with colon
        if (/^[A-Z][^:]*:$/.test(trimmedLine) && words.length >= 1 && words.length <= 5) {
          return { level: 3, isHeading: true };
        }
        
        // Level 1: Main headings (RED) - ALL CAPS, numbered sections
        const mainHeadingPatterns = [
          /^[A-Z\s]{5,}$/,  // ALL CAPS with at least 5 characters
          /^\d+\./,          // Starts with number and dot (1., 2., etc.)
          /^[IVX]+\./,       // Roman numerals
          /^(CHAPTER|SECTION|PART|SUMMARY|INTRODUCTION|CONCLUSION|OVERVIEW)/i,
          /^(Step|Phase|Point)\s*\d+/i
        ];
        
        if (mainHeadingPatterns.some(pattern => pattern.test(trimmedLine)) || 
            (trimmedLine.length < 50 && trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length > 3)) {
          return { level: 1, isHeading: true };
        }
        
        // Level 2: Sub-headings (GREEN) - Title case, 3-8 words, no ending punctuation
        const isCapitalized = /^[A-Z]/.test(trimmedLine);
        const noEndPunctuation = !/[.!?;,:]$/.test(trimmedLine); // Also exclude colon to avoid conflict with level 3
        const notAllCaps = trimmedLine !== trimmedLine.toUpperCase();
        const hasSomeCapitals = /[A-Z].*[A-Z]/.test(trimmedLine);
        
        if (isCapitalized && noEndPunctuation && notAllCaps && words.length >= 2 && words.length <= 8 && hasSomeCapitals) {
          return { level: 2, isHeading: true };
        }
        
        return { level: 0, isHeading: false };
      };

      // Split content into lines and process
      const rawContent = cleanText(report.report_content || report.content);
      const contentBlocks = rawContent.split('\n\n'); // Split by double newlines for paragraphs
      
      // Title/Report Name - RED, BOLD, Times New Roman, Size 16
      doc.setFont('times', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 0, 0); // RED
      const cleanedTitle = cleanText(report.report_name);
      doc.text(cleanedTitle, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 9; // 1.5 line spacing after title (16pt × 1.5 ≈ 9pt)
      
      // Line separator removed - no visual break
      // Content starts immediately after title

      // Process content blocks
      contentBlocks.forEach((block) => {
        if (!block.trim()) return;
        
        const lines = block.split('\n').filter(line => line.trim());
        
        lines.forEach((line) => {
          const cleanedLine = line.trim();
          if (!cleanedLine) return;
          
          const headingInfo = getHeadingLevel(cleanedLine);
          
          if (headingInfo.isHeading) {
            // HEADING - Color based on level, same font size as paragraph (12pt)
            doc.setFont('times', 'bold');
            doc.setFontSize(12); // Same as paragraph font size
            
            if (headingInfo.level === 1) {
              // Level 1: Main Heading - RED, 12pt
              doc.setTextColor(255, 0, 0); // RED
            } else if (headingInfo.level === 2) {
              // Level 2: Sub-heading - GREEN, 12pt
              doc.setTextColor(0, 128, 0); // GREEN
            } else if (headingInfo.level === 3) {
              // Level 3: Third-level - BLACK, 12pt
              doc.setTextColor(0, 0, 0); // BLACK
            }
            
            const headingLines = doc.splitTextToSize(cleanedLine, maxWidth);
            headingLines.forEach(hLine => {
              checkNewPage();
              doc.text(hLine, margin, yPosition, { align: 'justify' });
              yPosition += 6.75; // 1.5 line spacing for 12pt font (same as paragraph)
            });
            // NO space after heading - continues directly to next line
            
          } else {
            // PARAGRAPH: BLUE, BOLD, Times New Roman, Size 12
            // NO space before paragraph
            checkNewPage(5);
            doc.setFont('times', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 255); // BLUE
            
            const paragraphLines = doc.splitTextToSize(cleanedLine, maxWidth);
            paragraphLines.forEach((pLine, idx) => {
              checkNewPage();
              // FULLY JUSTIFY all paragraph text (including last line)
              doc.text(pLine, margin, yPosition, { 
                align: 'justify',
                maxWidth: maxWidth
              });
              yPosition += 6.75; // 1.5 line spacing for 12pt font (12pt × 1.5 = 6.75pt)
            });
            // NO space after paragraph - continues directly to next line
          }
        });
        
        // NO extra space between any blocks - absolute zero spacing
      });

      // Footer with page numbers - Always show pagination (minimum "Page 1")
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setFont('times', 'normal');
        doc.setTextColor(100, 100, 100);
        
        // Always show page number (e.g., "Page 1 of 1" for single page, "Page 1 of 5" for multi-page)
        const pageText = `Page ${i} of ${totalPages}`;
        const reportNameClean = cleanText(report.report_name).substring(0, 40); // Limit name length
        
        doc.text(
          `${reportNameClean} | ${pageText}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      // Save PDF with cleaned filename
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const cleanFileName = cleanText(report.report_name).replace(/\s+/g, '_').substring(0, 50);
      const fileName = `${cleanFileName}_${timestamp}.pdf`;
      doc.save(fileName);
      showSuccess('Professional Analysis PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF download error:', error);
      showError('Failed to download PDF. Please try again.');
    }
  };

  const handleDeleteReport = async (reportIndex) => {
    // Open a modal confirmation dialog (instead of native window.confirm)
    const reportName = existingData?.ai_reports?.[reportIndex]?.report_name || 'this Analysis';
    setDeleteConfirm({
      open: true,
      kind: 'ai_report',
      index: reportIndex,
      reportName,
    });
  };

  const confirmDeleteAiReport = async (reportIndex) => {
    try {
      console.log('[DELETE] Attempting to delete report:', reportIndex);
      const token = sessionStorage.getItem('token');
      const response = await axios.delete(
        `${API}/sessions/${sessionId}/phase5/report/${reportIndex}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('[DELETE] Success response:', response.data);
      showSuccess('Analysis deleted successfully');
      await fetchExisting();
    } catch (error) {
      console.error('[DELETE] Full error object:', error);
      const detail = error.response?.data?.detail || error.message || 'Failed to delete Analysis';
      showError(`Delete failed: ${detail}`);
    }
  };


  const handleDeleteCustomReport = async (reportOrIndex, originalIndex) => {
    const report = typeof reportOrIndex === 'object' ? reportOrIndex : existingData?.custom_reports?.[reportOrIndex];
    const reportReference = report?.id || report?._id || (originalIndex ?? reportOrIndex);
    const reportName = report?.report_name || 'this custom analysis';
    setDeleteConfirm({
      open: true,
      kind: 'custom_report',
      index: reportReference,
      reportName,
    });
  };

  const confirmDeleteCustomReport = async (reportReference) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(
        typeof reportReference === 'string' ? `${API}/sessions/${sessionId}/phase5/custom-report-history/${reportReference}` : `${API}/sessions/${sessionId}/phase5/custom-reports/${reportReference}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      showSuccess('Custom analysis deleted successfully');
      setExistingData((current) => current ? {
        ...current,
        custom_reports: (current.custom_reports || []).filter((report, index) => (
          typeof reportReference === 'string'
            ? (report.id || report._id) !== reportReference
            : index !== reportReference
        )),
      } : current);
    } catch (error) {
      console.error('Failed to delete custom report:', error);
      const detail = error.response?.data?.detail || error.message || 'Failed to delete custom analysis';
      showError(`Delete failed: ${detail}`);
    }
  };

  const confirmDeleteQuery = async (queryReference) => {
    try {
      await axios.delete(typeof queryReference === 'string' ? `${API}/sessions/${sessionId}/phase5/query-history/${queryReference}` : `${API}/sessions/${sessionId}/phase5/query/${queryReference}`);
      showSuccess('Ask Buddy item deleted successfully');
      setExistingData((current) => current ? {
        ...current,
        ai_queries: (current.ai_queries || []).filter((item, index) => (
          typeof queryReference === 'string'
            ? (item.id || item._id) !== queryReference
            : index !== queryReference
        )),
      } : current);
    } catch (error) {
      console.error('Failed to delete Ask Buddy item:', error);
      const detail = error.response?.data?.detail || error.message || 'Failed to delete Ask Buddy item';
      showError(`Delete failed: ${detail}`);
    }
  };

  const handleDeleteSpecialReport = async (reportType) => {
    try {
      const fieldName = reportType === 'ai_summary' ? 'ai_summary' : 'metaphorical_narrative';
      console.log('[DELETE SPECIAL] Deleting:', fieldName);
      
      const token = sessionStorage.getItem('token');
      const response = await axios.delete(
        `${API}/sessions/${sessionId}/phase5/special-report/${fieldName}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[DELETE SPECIAL] Success:', response.data);
      showSuccess('Analysis deleted successfully');
      await fetchExisting();
    } catch (error) {
      console.error('[DELETE SPECIAL] Error:', error.response?.data || error.message);
      showError(error.response?.data?.detail || 'Failed to delete Analysis');
    }
  };


  const handleReviewReport = async (reportIndex) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/phase5/reports/${reportIndex}/review`);
      showSuccess('Analysis marked as reviewed. Now visible to client.');
      fetchExisting();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to mark Analysis as reviewed');
    }
  };

  const clientSummaryHasUnsavedChanges = editingClientSummary && clientSummaryDraft !== clientSummaryOriginal;

  function requestClientSummaryAction(action) {
    if (clientSummaryHasUnsavedChanges) {
      setClientSummaryDiscardAction(() => action);
      return;
    }
    action();
  }

  const keepEditingClientSummary = () => setClientSummaryDiscardAction(null);

  const discardClientSummaryChanges = () => {
    const pendingAction = clientSummaryDiscardAction;
    setClientSummaryDiscardAction(null);
    setClientSummaryDraft(clientSummaryOriginal);
    setEditingClientSummary(false);
    pendingAction?.();
  };

  useEffect(() => {
    if (!registerClientSummaryNavigationGuard) return undefined;
    registerClientSummaryNavigationGuard(requestClientSummaryAction);
    return () => registerClientSummaryNavigationGuard(null);
  }, [registerClientSummaryNavigationGuard, clientSummaryHasUnsavedChanges]);

  useEffect(() => {
    const warnBeforeUnload = (event) => {
      if (!clientSummaryHasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [clientSummaryHasUnsavedChanges]);

  const startClientSummaryEdit = (report) => {
    const editable = plainTextToClientSummaryHtml(report?.content || '');
    setClientSummaryOriginal(editable);
    setClientSummaryDraft(editable);
    setEditingClientSummary(true);
  };

  const cancelClientSummaryEdit = () => {
    requestClientSummaryAction(() => {
      setClientSummaryDraft(clientSummaryOriginal);
      setEditingClientSummary(false);
    });
  };

  const saveClientSummaryEdit = async (reportIndex) => {
    const content = sanitizeClientSummaryHtml(clientSummaryDraft);
    if (!clientSummaryHtmlToText(content).trim()) return showError(`${clientSynthesisName} content cannot be empty`);
    setSavingClientSummary(true);
    try {
      const response = await axios.put(`${API}/sessions/${sessionId}/phase5/reports/${reportIndex}/client-summary`, { content });
      const saved = response.data?.report?.content || content;
      setExistingData((previous) => ({
        ...previous,
        ai_summary: saved,
        ai_reports: (previous?.ai_reports || []).map((report, index) => index === reportIndex ? response.data.report : report),
      }));
      setClientSummaryOriginal(saved);
      setClientSummaryDraft(saved);
      setEditingClientSummary(false);
      showSuccess(`${clientSynthesisName} saved. Review status reset to Pending Review.`);
    } catch (error) {
      showError(error.response?.data?.detail || `Failed to save ${clientSynthesisName}`);
    } finally {
      setSavingClientSummary(false);
    }
  };

  const handleReviewCustomReport = async (reportIndex) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/phase5/custom-reports/${reportIndex}/review`);
      showSuccess('Custom analysis marked as reviewed. Now visible to client.');
      fetchExisting();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to mark custom analysis as reviewed');
    }
  };

  const handleUpdate16Points = async () => {
    console.log('🎉 [UPDATE] Button clicked!');
    try {
      setUpdating16Points(true);
      console.log('🎉 [UPDATE] Sending API request...');
      await axios.put(`${API}/sessions/${sessionId}/phase5/synthesis-points`, {
        synthesis_points: editablePoints
      });
      console.log('🎉 [UPDATE] API success! Setting modal to true');
      setIsEditing16Points(false);
      setShow16PointsUpdateSuccessModal(true);
      console.log('🎉 [UPDATE] Modal should appear now!');
      // Don't call fetchExisting here - it triggers re-render and closes modal
      // Will be called when modal closes
    } catch (error) {
      console.error('❌ [UPDATE] Error:', error);
      showError(error.response?.data?.detail || 'Failed to update 14 Points');
    } finally {
      setUpdating16Points(false);
    }
  };

  const handleReview16Points = async () => {
    console.log('🎉 [REVIEW] Button clicked!');
    try {
      console.log('🎉 [REVIEW] Sending API request...');
      await axios.post(`${API}/sessions/${sessionId}/phase5/synthesis-points/review`);
      console.log('🎉 [REVIEW] API success! Setting modal to true');
      setShow16PointsReviewSuccessModal(true);
      console.log('🎉 [REVIEW] Modal state:', true);
      
      // Refresh Phase5 data
      await fetchExisting();
      
      // CRITICAL: Refresh parent session data to update phase5_synthesis_complete flag
      // This enables Phase 1-4 tab locking for psychologists
      if (refreshSession) {
        console.log('🔒 [REVIEW] Refreshing parent session to lock Phase 1-4 tabs...');
        await refreshSession();
        console.log('🔒 [REVIEW] Session refreshed successfully');
        console.log('🔒 [REVIEW] Session data after refresh:', session);
      } else {
        console.error('❌ [REVIEW] refreshSession callback not provided!');
      }
    } catch (error) {
      console.error('❌ [REVIEW] Error:', error);
      showError(error.response?.data?.detail || 'Failed to mark 14 Points as reviewed');
    }
  };


  const handleToggleReportVisibility = async (reportIndex, reportType, currentVisibility) => {
    try {
      console.log('🔄 TOGGLE VISIBILITY CALLED:', { reportIndex, reportType, currentVisibility });
      const newVisibility = !currentVisibility;
      console.log('🔄 New visibility will be:', newVisibility);
      
      // Build the visibility key based on report type and index
      let visibilityKey;
      if (reportType === 'ai') {
        visibilityKey = `ai_report_${reportIndex}`;
      } else if (reportType === 'custom') {
        visibilityKey = `custom_report_${reportIndex}`;
      } else if (reportType === '16points') {
        visibilityKey = 'show_16_points';
      }
      
      console.log('🔑 Visibility key:', visibilityKey);
      
      // Get current visibility settings
      const currentSettings = existingData?.report_visibility || {};
      console.log('📋 Current settings:', currentSettings);
      
      // Update with new setting
      const updatedSettings = {
        ...currentSettings,
        [visibilityKey]: newVisibility
      };
      
      console.log('📋 Updated settings:', updatedSettings);
      
      // Send to backend
      await axios.patch(`${API}/sessions/${sessionId}/report-visibility`, updatedSettings);
      console.log('✅ Backend updated successfully');
      
      console.log('Report visibility updated:', newVisibility ? 'visible' : 'hidden');
      // Refresh data without showing notification
      fetchExisting();
    } catch (error) {
      console.error('Failed to update report visibility:', error);
      showModalNotification('error', 'Failed to update Analysis visibility');
    }
  };


  // Unified function to mark reports as reviewed by ID and type
  const handleMarkReportReviewed = async (reportIdOrIndex, reportType) => {
    console.log('handleMarkReportReviewed called:', { reportIdOrIndex, reportType });
    try {
      if (reportType === 'custom') {
        let reportIndex = reportIdOrIndex;
        
        // If it looks like an ID string, try to find the index
        if (typeof reportIdOrIndex === 'string' && reportIdOrIndex.includes('-')) {
          console.log('Custom reports:', existingData.custom_reports);
          reportIndex = existingData.custom_reports?.findIndex(r => r.id === reportIdOrIndex);
          console.log('Found report index:', reportIndex);
        }
        
        if (reportIndex === -1 || reportIndex === undefined) {
          showModalNotification('error', 'Analysis not found');
          return;
        }
        
        console.log('Calling API to mark report as reviewed with index:', reportIndex);
        await axios.post(`${API}/sessions/${sessionId}/phase5/custom-reports/${reportIndex}/review`);
        showModalNotification('success', 'Custom analysis marked as reviewed!');
      } else if (reportType === 'special') {
        // Special reports: AI Summary or Metaphorical Portrait
        const fieldName = reportIdOrIndex; // 'ai_summary' or 'metaphorical_narrative'
        await axios.post(`${API}/sessions/${sessionId}/phase5/special-reports/${fieldName}/review`);
        showModalNotification('success', 'Analysis marked as reviewed!');
      } else {
        // AI report
        let reportIndex = reportIdOrIndex;
        if (typeof reportIdOrIndex === 'string' && reportIdOrIndex.includes('-')) {
          reportIndex = existingData.ai_reports?.findIndex(r => r.id === reportIdOrIndex);
        }
        
        if (reportIndex === -1 || reportIndex === undefined) {
          showModalNotification('error', 'Analysis not found');
          return;
        }
        await axios.post(`${API}/sessions/${sessionId}/phase5/reports/${reportIndex}/review`);
        showModalNotification('success', 'Analysis marked as reviewed!');
      }
      await fetchExisting();
    } catch (error) {
      console.error('Error marking report as reviewed:', error);
      showModalNotification('error', error.response?.data?.detail || 'Failed to mark Analysis as reviewed');
    }
  };

  const handleDeleteAISummary = async () => {
    try {
      await axios.delete(`${API}/sessions/${sessionId}/phase5/ai-summary`);
      showSuccess(`${clientSynthesisName} deleted successfully`);
      fetchExisting();
    } catch (error) {
      showError(`Failed to delete ${clientSynthesisName}`);
    }
  };

  const handleDeleteMetaphor = async () => {
    try {
      console.log('[DELETE METAPHOR] Deleting metaphorical narrative');
      
      const token = sessionStorage.getItem('token');
      const response = await axios.delete(
        `${API}/sessions/${sessionId}/phase5/metaphorical-narrative`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[DELETE METAPHOR] Success:', response.data);
      showSuccess('Metaphorical Client Portrait deleted successfully');
      await fetchExisting();
    } catch (error) {
      console.error('[DELETE METAPHOR] Error:', error.response?.data || error.message);
      showError(error.response?.data?.detail || 'Failed to delete Metaphorical Client Portrait');
    }
  };

  const handleDeleteClientReport = async () => {
    try {
      console.log('[DELETE CLIENT] Deleting client wellness report');
      
      const token = sessionStorage.getItem('token');
      const response = await axios.delete(
        `${API}/sessions/${sessionId}/phase5/client-report`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[DELETE CLIENT] Success:', response.data);
      showSuccess('Wellness Analysis deleted successfully');
      await fetchExisting();
    } catch (error) {
      console.error('[DELETE CLIENT] Error:', error.response?.data || error.message);
      showError(error.response?.data?.detail || 'Failed to delete wellness Analysis');
    }
  };

  // Client view - Show only "Strengths and Coping Guide" report
  if (userRole === 'client') {
    
    // If reports not generated yet
    if (!existingData || !existingData.ai_reports) {
      const allPhasesActuallyDone = !!(session && (
        session.phase1_complete && session.phase2_complete && session.phase3_complete && session.phase4_complete
      ) || (session && session.current_phase >= 5));
      return (
        <Card className="mt-6" data-testid="phase5-client-restricted">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {allPhasesActuallyDone ? 'Session Complete' : 'Analysis Not Yet Available'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: allPhasesActuallyDone ? '#2563eb' : '#9CA3AF' }} />
              {allPhasesActuallyDone ? (
                <>
                  <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    All Phases Completed!
                  </h3>
                  <p className="text-gray-600 mb-4">
                    You have completed all 4 phases of the exploration. Your psychologist will review your responses and prepare comprehensive Analysis.
                  </p>
                  <p className="text-sm text-gray-500">Phase 5 (Analysis) is handled by your psychologist.</p>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Analysis Will Be Available After You Complete All Phases
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Please finish Phases 1 – 4 first. Once all phases are completed, your psychologist can prepare and share the Analysis here.
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }

    // Get all available reports (already filtered by backend based on visibility settings)
    const availableReports = existingData.ai_reports || [];
    const availableCustomReports = existingData.custom_reports?.filter(report => report.reviewed) || [];
    const totalReports = availableReports.length + availableCustomReports.length;

    return (
      <>
        <Card className="mt-6" data-testid="phase5-client-view">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Your Analysis</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              {totalReports > 0 
                ? `${totalReports} Analysis item${totalReports > 1 ? 's' : ''} available`
                : 'Analysis is being prepared by your psychologist'
              }
            </p>
          </CardHeader>
          <CardContent>
            {/* Text-to-Speech Controls for Clients */}
            {totalReports > 0 && (
              <details className="phase5-tts-panel phase5-tts-client mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg" open={ttsExpanded} onToggle={(event) => setTtsExpanded(event.currentTarget.open)}>
                <summary className="phase5-tts-summary flex items-center justify-between gap-3 cursor-pointer list-none">
                  <span className="flex items-center gap-3"><Volume2 className={`w-5 h-5 ${isSpeaking ? 'text-blue-600 animate-pulse' : 'text-blue-600'}`} /><span className="font-semibold text-gray-700">{isSpeaking ? 'Reading Analysis...' : 'Listen to Analysis'}</span></span>
                  <ChevronDown className="phase5-tts-chevron w-5 h-5" aria-hidden="true" />
                </summary>
                <div className="phase5-tts-body">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    {isSpeaking ? (
                      <>
                        <Volume2 className="w-5 h-5 text-blue-600 animate-pulse" />
                        <span className="font-semibold text-blue-900">🔊 Reading Analysis...</span>
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-gray-700">Listen to Your Analysis</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    {/* Voice Selection */}
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-gray-700 font-medium">Voice:</Label>
                      <select
                        value={selectedVoice ? availableVoices.indexOf(selectedVoice) : 0}
                        onChange={(e) => {
                          const voice = availableVoices[parseInt(e.target.value)];
                          setSelectedVoice(voice);
                          // Auto-restart if currently speaking
                          if (isSpeaking) {
                            restartSpeechWithNewSettings();
                          }
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white max-w-[200px]"
                      >
                        {availableVoices.map((voice, idx) => (
                          <option key={idx} value={idx}>
                            {voice.name} ({voice.lang})
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Speed Control */}
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-gray-700 font-medium">Speed:</Label>
                      <select
                        value={speechRate}
                        onChange={(e) => {
                          const newRate = parseFloat(e.target.value);
                          setSpeechRate(newRate);
                          // Auto-restart if currently speaking
                          if (isSpeaking) {
                            restartSpeechWithNewSettings();
                          }
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="0.5">0.5x (Slow)</option>
                        <option value="0.75">0.75x</option>
                        <option value="1.0">1.0x (Normal)</option>
                        <option value="1.25">1.25x</option>
                        <option value="1.5">1.5x (Fast)</option>
                        <option value="2.0">2.0x</option>
                      </select>
                    </div>
                    {isSpeaking && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={stopSpeaking}
                        className="bg-red-50 border-red-300 hover:bg-red-100 text-red-700 font-semibold"
                      >
                        <StopCircle className="w-4 h-4 mr-1" />
                        Stop
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  💡 Click the 🔊 "Listen" button on any Analysis card below to hear it read aloud. Choose from {availableVoices.length}+ voices in multiple languages!
                </p>
                </div>
              </details>
            )}
            
            {totalReports > 0 ? (
              <div className="grid gap-4">
                {/* Standard AI Reports */}
                {availableReports.map((report, idx) => (
                  <div 
                    key={`ai-${idx}`}
                    className="flex items-center justify-between p-4 bg-white rounded-lg border hover:shadow-md transition-all"
                    data-testid={`client-report-row-${idx}`}
                  >
                    <div className="flex-1">
                      <button
                        onClick={() => {
                          setSelectedReport(report);
                          setShowReportModal(true);
                        }}
                        className="text-left hover:underline"
                        style={{ color: '#2563EB' }}
                        data-testid={`client-report-link-${idx}`}
                      >
                        <h4 className="font-semibold text-lg">{analysisDisplayText(report.report_name)}</h4>
                      </button>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <p className="text-sm text-gray-500">Click to view Analysis</p>
                        {report.psychologist_name && (
                          <>
                            <span className="text-gray-300">•</span>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">By:</span> {withDoctorTitle(report.psychologist_name)}
                            </p>
                          </>
                        )}
                        {(report.generated_at || report.created_at) && (
                          <>
                            <span className="text-gray-300">•</span>
                            <p className="text-sm text-gray-500">
                              <span className="font-medium">Created:</span> {new Date(report.generated_at || report.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const reportId = `client-report-${idx}`;
                          if (currentSpeakingReport === reportId && isSpeaking) {
                            stopSpeaking();
                          } else {
                            speakReport(report.report_content || report.content, reportId);
                          }
                        }}
                        data-testid={`listen-client-report-${idx}`}
                        className={currentSpeakingReport === `client-report-${idx}` && isSpeaking ? "bg-orange-100 border-orange-500" : ""}
                      >
                        {currentSpeakingReport === `client-report-${idx}` && isSpeaking ? (
                          <><StopCircle className="w-4 h-4 mr-2 text-blue-600" /> Stop</>
                        ) : (
                          <><Volume2 className="w-4 h-4 mr-2" /> Listen</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadReportPDF(report)}
                        data-testid={`download-client-report-${idx}`}
                      >
                        <FileText className="w-4 h-4 mr-2" /> Download PDF
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Custom Reports */}
                {availableCustomReports.map((report, idx) => (
                  <div 
                    key={`custom-${idx}`}
                    className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-purple-200 hover:shadow-md transition-all"
                    data-testid={`client-custom-report-row-${idx}`}
                  >
                    <div className="flex-1">
                      <button
                        onClick={() => {
                          setSelectedReport(report);
                          setShowReportModal(true);
                        }}
                        className="text-left hover:underline"
                        style={{ color: '#2563eb' }}
                        data-testid={`client-custom-report-link-${idx}`}
                      >
                        <h4 className="font-semibold text-lg">{analysisDisplayText(report.report_name)}</h4>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-200 text-blue-800 mt-1">
                          Custom Analysis
                        </span>
                      </button>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <p className="text-sm text-gray-500">Click to view Analysis</p>
                        {report.psychologist_name && (
                          <>
                            <span className="text-gray-300">•</span>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">By:</span> {withDoctorTitle(report.psychologist_name)}
                            </p>
                          </>
                        )}
                        {(report.generated_at || report.created_at) && (
                          <>
                            <span className="text-gray-300">•</span>
                            <p className="text-sm text-gray-500">
                              <span className="font-medium">Created:</span> {new Date(report.generated_at || report.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const reportId = `client-custom-${idx}`;
                          if (currentSpeakingReport === reportId && isSpeaking) {
                            stopSpeaking();
                          } else {
                            speakReport(report.report_content || report.content, reportId);
                          }
                        }}
                        data-testid={`listen-client-custom-report-${idx}`}
                        className={currentSpeakingReport === `client-custom-${idx}` && isSpeaking ? "bg-orange-100 border-orange-500" : ""}
                      >
                        {currentSpeakingReport === `client-custom-${idx}` && isSpeaking ? (
                          <><StopCircle className="w-4 h-4 mr-2 text-blue-600" /> Stop</>
                        ) : (
                          <><Volume2 className="w-4 h-4 mr-2" /> Listen</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadReportPDF(report)}
                        data-testid={`download-client-custom-report-${idx}`}
                      >
                        <FileText className="w-4 h-4 mr-2" /> Download PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">Your Analysis is being prepared by your psychologist.</p>
              </div>
            )}
            
            {/* Show 14 Points if available and visible - Same layout as Psychologist view */}
            {(existingData.synthesis_points || existingData.points) && ((existingData.synthesis_points?.length || 0) + (existingData.points?.length || 0)) > 0 && (
              <div className="sqis-report phase5-report sqis-report--standalone sqis-report--client mb-8 mt-6">
                <div className="sqis-header flex justify-between items-center mb-4 flex-wrap">
                  <h3 className="sqis-title">14 Points SQIS</h3>
                  <div className="sqis-header-actions flex items-center gap-3 flex-wrap">
                    {existingData.psychologist_name && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">By:</span> {withDoctorTitle(existingData.psychologist_name)}
                      </p>
                    )}
                    {existingData.synthesis_generated_at && (
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Created:</span> {new Date(existingData.synthesis_generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    )}
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                      ✓ Reviewed
                    </span>
                  </div>
                </div>
                <div className="sqis-content space-y-4">
                  <div className="sqis-accordion-toolbar phase5-shared-toolbar flex items-center gap-3 text-xs">
                    <div className="phase5-report-toolbar" role="group" aria-label="14-Point SQIS section controls">
                      <button type="button" onClick={() => setCollapsedPoints(new Set())} className="sqis-accordion-action phase5-report-toolbar-button">Expand All</button>
                      <span className="phase5-report-toolbar-separator" aria-hidden="true">|</span>
                      <button type="button" onClick={() => setCollapsedPoints(new Set((existingData.synthesis_points || existingData.points || []).filter(point => point.point_number !== 15 && point.point_number !== 16).map(point => point.point_number)))} className="sqis-accordion-action phase5-report-toolbar-button">Collapse All</button>
                    </div>
                    <ReportFontSelector />
                    <ReportTextSizeSelector />
                  </div>
                  {(existingData.synthesis_points || existingData.points || []).filter(point => point.point_number !== 15 && point.point_number !== 16).map((point, idx) => {
                    const isCollapsed = collapsedPoints.has(point.point_number);
                    return (
                    <div key={idx} className={`sqis-point-card phase5-report-section ${phase5Tone(idx)}`}>
                      <div className="sqis-point-header phase5-section-header">
                        <h4 className="sqis-point-title">
                          <span className="sqis-point-number phase5-section-badge" data-testid={`client-sqis-point-number-${point.point_number}`} aria-hidden="true">{point.point_number}</span>
                          <span className="sqis-point-title-text phase5-section-title">{getDisplayTitle(point.point_number, point.title)}</span>
                          <button type="button" onClick={() => toggleCollapsedPoint(point.point_number)} aria-expanded={!isCollapsed} aria-label={isCollapsed ? `Expand point ${point.point_number}` : `Collapse point ${point.point_number}`} className="sqis-point-toggle phase5-section-chevron" data-testid={`client-sqis-point-toggle-${point.point_number}`}>
                            <ChevronDown className={`w-4 h-4${isCollapsed ? '' : ' is-expanded'}`} />
                          </button>
                        </h4>
                      </div>
                      {!isCollapsed && (
                      <div className="sqis-point-body phase5-section-body">
                      {point.point_number === 11 ? (
                        /* Point 11: Grid View - Theme Connotations, Client Responses, Time Taken */
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-gray-300">
                            <thead className="bg-blue-50">
                              <tr>
                                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Theme Connotations</th>
                                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Client Responses</th>
                                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Time Taken</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const rows = [];
                                const lines = splitPoint11Content(point.content).tableContent.split('\n');
                                let currentTheme = '';
                                let currentConnotations = '';
                                let currentResponses = [];
                                let currentTime = '';
                                let inTime = false;
                                const flush = () => {
                                  if (currentTheme) {
                                    rows.push({
                                      theme: currentTheme,
                                      connotations: currentConnotations,
                                      responses: currentResponses.join('\n'),
                                      time: currentTime.trim()
                                    });
                                  }
                                  currentTheme = '';
                                  currentConnotations = '';
                                  currentResponses = [];
                                  currentTime = '';
                                  inTime = false;
                                };
                                for (let i = 0; i < lines.length; i++) {
                                  const line = lines[i].trim();
                                  if (!line) continue;
                                  if (line.startsWith('Theme Connotations:')) {
                                    inTime = false;
                                    currentConnotations = line.replace('Theme Connotations:', '').trim();
                                  } else if (line.startsWith('Theme:')) {
                                    flush();
                                    currentTheme = line.replace('Theme:', '').trim();
                                  } else if (line.startsWith('•')) {
                                    inTime = false;
                                    currentResponses.push(line);
                                  } else if (line.startsWith('Average Time Taken:') || line.startsWith('Time Taken:')) {
                                    currentTime = line.replace('Average Time Taken:', '').replace('Time Taken:', '').trim();
                                    inTime = true;
                                  } else if (inTime && !line.startsWith('---') && !line.startsWith('Client Responses')) {
                                    currentTime = currentTime ? `${currentTime}\n${line}` : line;
                                  }
                                }
                                flush();
                                return rows.map((row, ridx) => (
                                  <tr key={ridx} className="hover:bg-gray-50">
                                    <td className="border border-gray-300 px-4 py-3 align-top">
                                      <div className="font-semibold text-blue-800 mb-1">{row.theme}</div>
                                      <div className="text-sm text-gray-700">{row.connotations}</div>
                                    </td>
                                    <td className="border border-gray-300 px-4 py-3 align-top">
                                      <div className="text-sm whitespace-pre-line" dangerouslySetInnerHTML={{ __html: row.responses }}></div>
                                    </td>
                                    <td className="border border-gray-300 px-4 py-3 text-center align-top">
                                      <div className="flex flex-col items-center gap-1">
                                        {row.time.split('\n').filter(t => t).map((t, ti) => (
                                          <span
                                            key={ti}
                                            className={`inline-block px-2 py-0.5 rounded font-mono text-xs ${t.toLowerCase().startsWith('avg') ? 'bg-green-200 text-green-900 font-semibold' : 'bg-green-100 text-green-800'}`}
                                          >
                                            {t}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                          <Point11Findings content={point.content} />
                        </div>
                      ) : point.point_number === 14 ? (
                        /* Point 14: Grid View - Client Responses, Themes, Repeated Times */
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-gray-300">
                            <thead className="bg-green-50">
                              <tr>
                                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Client Responses</th>
                                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Themes</th>
                                <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-700">Repeated Times</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const rows = [];
                                const lines = point.content.split('\n');
                                let currentResponse = '';
                                let currentThemes = '';
                                let currentCount = '';
                                for (let i = 0; i < lines.length; i++) {
                                  const line = lines[i].trim();
                                  if (line.startsWith('Response:')) {
                                    currentResponse = line.replace('Response:', '').trim();
                                  } else if (line.startsWith('Themes:')) {
                                    currentThemes = line.replace('Themes:', '').trim();
                                  } else if (line.startsWith('Repeated Times:')) {
                                    currentCount = line.replace('Repeated Times:', '').trim();
                                    if (currentResponse) {
                                      rows.push({
                                        response: currentResponse,
                                        themes: currentThemes,
                                        count: currentCount
                                      });
                                      currentResponse = '';
                                      currentThemes = '';
                                      currentCount = '';
                                    }
                                  }
                                }
                                return rows.map((row, ridx) => (
                                  <tr key={ridx} className="hover:bg-gray-50">
                                    <td className="border border-gray-300 px-4 py-3 align-top">
                                      <div className="font-medium text-gray-800">{row.response}</div>
                                    </td>
                                    <td className="border border-gray-300 px-4 py-3 align-top">
                                      <div className="text-sm text-gray-700">{row.themes}</div>
                                    </td>
                                    <td className="border border-gray-300 px-4 py-3 text-center align-middle">
                                      <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 rounded font-bold text-lg">
                                        {row.count}
                                      </span>
                                    </td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <PointContentRenderer content={pointAnalysisDisplay(point)} />
                      )}
                      {/* SQIS is READ-ONLY for the client (no per-point actions). Feedback is captured in a single form below the entire SQIS. */}
                      </div>
                      )}
                    </div>
                    );
                  })}
                  {userRole === 'client' && (() => {
                    const submittedFeedback = existingData.client_feedback;
                    if (submittedFeedback && submittedFeedback.submitted_at) {
                      return (
                        <div className="sqis-feedback-panel sqis-feedback-panel--received mt-6" data-testid="client-feedback-submitted-banner">
                          <div className="font-semibold text-green-900 mb-2 text-lg">✓ Feedback Submitted</div>
                          <div className="space-y-3 text-sm">
                            <div>
                              <b>Overall Resonance:</b>
                              <div className="flex flex-wrap gap-3 mt-2">
                                {['Accurate', 'Partially Accurate', 'Inaccurate', 'Surprising'].map(opt => {
                                  const isSelected = submittedFeedback.overall_resonance === opt;
                                  return (
                                    <label key={opt} className={`flex items-center gap-2 px-3 py-2 rounded border ${isSelected ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200' : 'bg-white border-gray-300 opacity-60'}`}>
                                      <input
                                        type="radio"
                                        checked={isSelected}
                                        disabled
                                        readOnly
                                        data-testid={`submitted-resonance-${opt.toLowerCase().replace(' ', '-')}`}
                                      />
                                      <span className={`text-sm ${isSelected ? 'text-blue-800 font-semibold' : 'text-gray-600'}`}>{opt}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            <div>
                              <b>Your Context & Clarification:</b>
                              <div className="mt-1 p-3 bg-white border border-green-200 rounded whitespace-pre-wrap" data-testid="client-feedback-context-display">{submittedFeedback.context_clarification}</div>
                            </div>
                            <p className="text-xs text-green-700">Submitted on {new Date(submittedFeedback.submitted_at).toLocaleString()}. No further feedback can be submitted for this SQIS.</p>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="sqis-feedback-panel sqis-feedback-panel--awaiting mt-6" data-testid="client-sqis-feedback-form">
                        <div className="mb-3">
                          <h4 className="text-lg font-semibold text-gray-900 mb-1">Share Your Feedback on the SQIS</h4>
                          <p className="text-sm text-gray-600">Please respond to the questions below. You will only be able to submit this once.</p>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Overall Resonance</label>
                          <div className="flex flex-wrap gap-3">
                            {['Accurate', 'Partially Accurate', 'Inaccurate', 'Surprising'].map(opt => (
                              <label key={opt} className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-all ${feedbackDraft.overall_resonance === opt ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200' : 'bg-white border-gray-300 hover:border-blue-300'}`}>
                                <input
                                  type="radio"
                                  name="overall_resonance"
                                  value={opt}
                                  checked={feedbackDraft.overall_resonance === opt}
                                  onChange={() => setFeedbackDraft(d => ({ ...d, overall_resonance: opt }))}
                                  data-testid={`overall-resonance-${opt.toLowerCase().replace(' ', '-')}`}
                                />
                                <span className="text-sm text-gray-800">{opt}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-gray-800 mb-2">Your Context & Clarification</label>
                          <textarea
                            value={feedbackDraft.context_clarification}
                            onChange={(e) => setFeedbackDraft(d => ({ ...d, context_clarification: e.target.value }))}
                            rows={5}
                            placeholder="Why does this feel accurate or inaccurate? What is missing from this picture?"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                            data-testid="context-clarification-textarea"
                          />
                        </div>
                        {feedbackInlineMessage && (
                          <div className={`mb-3 p-3 rounded font-semibold text-sm ${feedbackInlineMessageType === 'success' ? 'bg-green-100 text-green-900 border border-green-400' : 'bg-red-100 text-red-900 border border-red-400'}`} data-testid="feedback-inline-message">
                            {feedbackInlineMessage}
                          </div>
                        )}
                        <div className="flex justify-end">
                          <Button
                            onClick={handleSubmitClientSQISFeedback}
                            disabled={submittingFeedback}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="feedback-given-btn"
                          >
                            {submittingFeedback ? 'Submitting…' : 'Feedback Given'}
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                  {SYNTHESIS_DISCLAIMER_NOTE && (
                    <div className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded text-xs text-amber-900 italic" data-testid="client-synthesis-disclaimer-note">
                      {SYNTHESIS_DISCLAIMER_NOTE}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Self-Help Resources Card for Clients (if shared) */}
        {(existingData?.book_recommendations_shared_with_client || existingData?.movie_recommendations_shared_with_client || existingData?.music_recommendations_shared_with_client) && (
          <div className="mt-6" data-testid="client-helpful-resources-heading">
            <h2 className="text-xl font-bold">Reflection Resources</h2>
            <p className="text-sm text-gray-600">Resources shared with you by your psychologist for reflection.</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {existingData?.book_recommendations_shared_with_client && existingData?.book_recommendations && <Card data-testid="client-resource-card-book"><CardContent className="p-4"><h3 className="font-semibold">📚 Book Recommendation</h3><div className="mt-3 flex gap-2"><Button onClick={() => setClientResourceView('book')} data-testid="client-view-book">View</Button><Button variant="outline" onClick={() => downloadRecommendationPDF('book')} data-testid="client-download-book-pdf"><Download className="w-4 h-4 mr-1" />Download PDF</Button></div></CardContent></Card>}
              {existingData?.movie_recommendations_shared_with_client && existingData?.movie_recommendations && <Card data-testid="client-resource-card-movie"><CardContent className="p-4"><h3 className="font-semibold">🎬 Movie Recommendation</h3><div className="mt-3 flex gap-2"><Button onClick={() => setClientResourceView('movie')} data-testid="client-view-movie">View</Button><Button variant="outline" onClick={() => downloadRecommendationPDF('movie')} data-testid="client-download-movie-pdf"><Download className="w-4 h-4 mr-1" />Download PDF</Button></div></CardContent></Card>}
              {existingData?.music_recommendations_shared_with_client && existingData?.music_recommendations && <Card data-testid="client-resource-card-music"><CardContent className="p-4"><h3 className="font-semibold">🎵 Music Reflections</h3><div className="mt-3 flex gap-2"><Button onClick={() => setClientResourceView('music')} data-testid="client-view-music">View</Button><Button variant="outline" onClick={() => downloadRecommendationPDF('music')} data-testid="client-download-music-pdf"><Download className="w-4 h-4 mr-1" />Download PDF</Button></div></CardContent></Card>}
            </div>
          </div>
        )}
        {clientResourceView === 'book' && existingData?.book_recommendations_shared_with_client && existingData?.book_recommendations && (
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between gap-3"><CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>📚 Book Recommendation</CardTitle><Button variant="outline" onClick={() => setClientResourceView(null)}>Close</Button></div>
              <p className="text-sm text-gray-600 mt-2">
                Your psychologist has recommended these evidence-based books to support your therapeutic journey. All books are free to download.
              </p>
            </CardHeader>
            <CardContent>
              {existingData.book_recommendations.schema_version === 2 && <BookReport report={existingData.book_recommendations} sessionId={sessionId} />}
              {existingData.book_recommendations.schema_version !== 2 && (() => {
                const recs = existingData.book_recommendations;
                
                return (
                  <div className="space-y-6">
                    {/* Summary */}
                    {recs.client_challenges_summary && (
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2">About Your Recommendations</h4>
                        <p className="text-sm text-blue-800 whitespace-pre-line">{recs.client_challenges_summary}</p>
                      </div>
                    )}

                    {/* Recommended By Info - Shows who GENERATED these specific recommendations */}
                    {existingData?.recommendations_generated_by_name && (
                      <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-green-900">
                          <span className="font-semibold">📚 Recommended by:</span>
                          <span>{existingData.recommendations_generated_by_name}</span>
                          {existingData?.recommendations_generated_at && (
                            <>
                              <span className="text-green-700">•</span>
                              <span className="text-green-700">
                                {new Date(existingData.recommendations_generated_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Books */}
                    {recs.book_recommendations && recs.book_recommendations.slice(0, 1).map((book, idx) => (
                      <div key={idx} className="border-2 border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xl font-bold text-green-600">#{book.reading_sequence || idx + 1}</span>
                            <h5 className="text-lg font-bold text-gray-900">{book.title}</h5>
                          </div>
                          <p className="text-sm text-gray-700">by {book.author} ({book.year})</p>
                        </div>
                        
                        <div className="p-4 space-y-3">
                          {/* Psychological Theme */}
                          {book.psychological_theme && (
                            <div className="bg-indigo-50 p-3 rounded-lg">
                              <p className="font-medium text-indigo-900">🧠 Psychological Theme:</p>
                              <p className="text-gray-700 mt-1 whitespace-pre-line">{book.psychological_theme}</p>
                            </div>
                          )}
                          
                          {/* Why Recommended */}
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="font-medium text-green-900">💡 Why This Book?</p>
                            <p className="text-gray-700 mt-1 whitespace-pre-line">{book.why_recommended}</p>
                          </div>
                          
                          {/* Therapeutic Value */}
                          {book.therapeutic_value && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <p className="font-medium text-blue-900">🎯 Therapeutic Value:</p>
                              <p className="text-gray-700 mt-1 whitespace-pre-line">{book.therapeutic_value}</p>
                            </div>
                          )}
                          
                          {/* Specific Fit for Client */}
                          {book.specific_fit_for_client && (
                            <div className="bg-teal-50 p-3 rounded-lg">
                              <p className="font-medium text-teal-900">👤 Specific Fit for You:</p>
                              <p className="text-gray-700 mt-1 whitespace-pre-line">{book.specific_fit_for_client}</p>
                            </div>
                          )}
                          
                          {/* Themes */}
                          {((book.themes && book.themes.length > 0) || (book.relevant_themes && book.relevant_themes.length > 0)) && (
                            <div>
                              <p className="font-medium text-green-900">🎭 Key Themes:</p>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {(book.themes || book.relevant_themes || []).map((theme, tIdx) => (
                                  <span key={tIdx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    {theme}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {book.expected_outcomes && Array.isArray(book.expected_outcomes) && book.expected_outcomes.length > 0 && (
                            <div>
                              <h6 className="font-semibold text-sm mb-2 text-purple-900">What you'll gain:</h6>
                              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                {book.expected_outcomes.map((outcome, oIdx) => (
                                  <li key={oIdx}>{outcome}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          <div className="pt-3 border-t">
                            <h6 className="font-semibold text-sm mb-2">Download:</h6>
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-2">
                                {book.gutenberg_url && !book.gutenberg_url.toLowerCase().includes('not available') && !book.gutenberg_url.toLowerCase().includes('search on') ? (
                                  <a href={book.gutenberg_url} target="_blank" rel="noopener noreferrer"
                                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                                    View on Gutenberg →
                                  </a>
                                ) : null}
                                {book.epub_download && !book.epub_download.toLowerCase().includes('not available') ? (
                                  <a href={book.epub_download} target="_blank" rel="noopener noreferrer"
                                    className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700">
                                    📱 EPUB
                                  </a>
                                ) : null}
                                {book.txt_download && !book.txt_download.toLowerCase().includes('not available') ? (
                                  <a href={book.txt_download} target="_blank" rel="noopener noreferrer"
                                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                                    📝 TXT
                                  </a>
                                ) : null}
                              </div>
                              {(!book.gutenberg_url || book.gutenberg_url.toLowerCase().includes('not available')) && (
                                <p className="text-xs text-gray-600 bg-gray-100 p-2 rounded">
                                  📖 <strong>Link not available.</strong> Search for "<em>{book.title}</em>" on{' '}
                                  <a href="https://www.gutenberg.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                    www.gutenberg.org
                                  </a>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Reading Plan */}
                    {recs.reading_plan && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-3">Reading Plan</h4>
                        <div className="space-y-2 text-sm">
                          {Object.entries(recs.reading_plan).map(([period, plan], idx) => (
                            <div key={idx}>
                              <p className="font-semibold text-yellow-900 capitalize">{period.replace(/_/g, ' ')}:</p>
                              <p className="text-yellow-800 ml-4">{plan}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Movie Recommendations for Client (if shared) */}
        {clientResourceView === 'movie' && existingData?.movie_recommendations?.schema_version === 2 && existingData?.movie_recommendations_shared_with_client && (
          <Card className="mt-6" data-testid="client-movie-recommendations">
            <CardHeader><div className="flex items-center justify-between gap-3"><CardTitle>🎬 Movie Recommendation</CardTitle><Button variant="outline" onClick={() => setClientResourceView(null)}>Close</Button></div></CardHeader>
            <CardContent><MovieReport report={existingData.movie_recommendations} sessionId={sessionId} /></CardContent>
          </Card>
        )}
        {clientResourceView === 'movie' && existingData?.movie_recommendations?.schema_version !== 2 && existingData?.movie_recommendations && existingData?.movie_recommendations_shared_with_client && (() => {
          // Extract movie array - handle both nested and flat structures
          const movieArray = existingData.movie_recommendations.movie_recommendations || 
                            existingData.movie_recommendations || 
                            [];
          
          console.log('[CLIENT MOVIES] Total movies to display:', movieArray.length);
          console.log('[CLIENT MOVIES] Movie data structure:', existingData.movie_recommendations);
          
          if (!Array.isArray(movieArray) || movieArray.length === 0) {
            console.warn('[CLIENT MOVIES] No movies found or invalid format');
            return null;
          }
          
          return (
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between gap-3"><CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>🎬 Movie Recommendation</CardTitle><Button variant="outline" onClick={() => setClientResourceView(null)}>Close</Button></div>
                <p className="text-sm text-gray-600 mt-2">
                  Your psychologist has recommended these therapeutic movies to support your journey. Each film has been carefully selected based on your unique situation and goals.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {movieArray.map((movie, idx) => {
                    console.log(`[CLIENT MOVIES] Rendering movie ${idx + 1}:`, movie.title);
                    return (
                      <div key={idx} className="p-4 border rounded-lg bg-gradient-to-r from-purple-50 to-pink-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg text-purple-900">{movie.title}</h4>
                            <div className="flex gap-2 mt-1 text-sm text-purple-700">
                              {movie.year && <span>{movie.year}</span>}
                              {movie.director && <span>• Directed by {movie.director}</span>}
                              {movie.genre && <span>• {movie.genre}</span>}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-3 space-y-3">
                          {/* Psychological Theme */}
                          {movie.psychological_theme && (
                            <div className="bg-indigo-50 p-3 rounded-lg">
                              <p className="font-medium text-indigo-900">🧠 Psychological Theme:</p>
                              <p className="text-gray-700 mt-1 whitespace-pre-line">{movie.psychological_theme}</p>
                            </div>
                          )}
                          
                          {/* Why This Movie */}
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <p className="font-medium text-purple-900">💡 Why This Movie?</p>
                            <p className="text-gray-700 mt-1 whitespace-pre-line">{movie.why_recommended}</p>
                          </div>
                          
                          {/* Key Themes - supports both 'themes' and 'relevant_themes' */}
                          {((movie.themes && movie.themes.length > 0) || (movie.relevant_themes && movie.relevant_themes.length > 0)) && (
                            <div>
                              <p className="font-medium text-purple-900">🎭 Key Themes:</p>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {(movie.themes || movie.relevant_themes || []).map((theme, i) => (
                                  <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                    {theme}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Therapeutic Value */}
                          {movie.therapeutic_value && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <p className="font-medium text-blue-900">🎯 Therapeutic Value:</p>
                              <p className="text-gray-700 mt-1 whitespace-pre-line">{movie.therapeutic_value}</p>
                            </div>
                          )}
                          
                          {/* Specific Fit for Client */}
                          {movie.specific_fit_for_client && (
                            <div className="bg-green-50 p-3 rounded-lg">
                              <p className="font-medium text-green-900">👤 Specific Fit for You:</p>
                              <p className="text-gray-700 mt-1 whitespace-pre-line">{movie.specific_fit_for_client}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {clientResourceView === 'music' && existingData?.music_recommendations_shared_with_client && existingData?.music_recommendations && (
          <Card className="mt-6" data-testid="client-music-recommendations">
            <CardHeader><div className="flex items-center justify-between gap-3"><CardTitle>🎵 Music Reflections</CardTitle><Button variant="outline" onClick={() => setClientResourceView(null)}>Close</Button></div></CardHeader>
            <CardContent>
              <MusicReport report={existingData.music_recommendations} sessionId={sessionId} />
            </CardContent>
          </Card>
        )}

        {/* Report Modal for Client */}
        <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
          <DialogContent className="max-w-[90vw] w-full max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {analysisDisplayText(selectedReport?.report_name)}
              </DialogTitle>
            </DialogHeader>
            <div>
              <p className="narrative-text whitespace-pre-line">{selectedReport?.report_content || selectedReport?.content}</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowReportModal(false)}>
                Close
              </Button>
              <Button onClick={() => downloadReportPDF(selectedReport)}>
                <FileText className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const handleQuerySubmit = async (savedQuery = null) => {
    const question = savedQuery?.query || aiQuery.trim();
    if (!question) {
      showModalNotification('error', 'Please enter a question');
      return;
    }
    
    setQueryLoading(true);
    try {
      let fileContent = null;
      let fileName = null;
      
      // If file is selected and should be included
      if (queryFile && includeFileInPrompt) {
        const reader = new FileReader();
        fileContent = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(queryFile.name.toLowerCase().endsWith('.pdf')
            ? String(e.target.result || '').split(',', 2)[1]
            : e.target.result);
          reader.onerror = reject;
          if (queryFile.name.toLowerCase().endsWith('.pdf')) reader.readAsDataURL(queryFile);
          else reader.readAsText(queryFile);
        });
        fileName = queryFile.name;
      }
      
      const res = await axios.post(`${API}/sessions/${sessionId}/phase5/query`, {
        query: question,
        file_content: savedQuery ? null : fileContent,
        file_name: savedQuery ? null : fileName,
        file_encoding: savedQuery ? null : (fileName?.toLowerCase().endsWith('.pdf') ? 'base64' : 'text'),
        include_file: savedQuery ? false : includeFileInPrompt
      });
      
      console.log('[ASK BUDDY] Query response:', res.data);
      setAiQuery('');
      setQueryFile(null);
      setIncludeFileInPrompt(false);
      showModalNotification('success', 'AI responded to your query!');
      console.log('[ASK BUDDY] Fetching updated data...');
      // Refresh to get updated query history
      fetchExisting();
    } catch (error) {
      showModalNotification('error', error.response?.data?.detail || 'Query failed');
    } finally {
      setQueryLoading(false);
    }
  };

  const exportToPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      await import('jspdf-autotable');
      
      const doc = new jsPDF();
      
      // Use same formatting as downloadReportPDF - Times New Roman, logo, proper headings
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14.4; // 0.2 inches - same as downloadReportPDF
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = 5; // Start at absolute top

      // Load and add INSIGHT logo (same as downloadReportPDF)
      try {
        const logoImg = new Image();
        logoImg.src = '/insight-logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(reject, 2000);
        });
        
        const logoWidth = 25;
        const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        const logoX = pageWidth - logoWidth - margin;
        doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 8;
      } catch (logoError) {
        console.log('Logo not loaded, continuing without logo');
        yPosition = margin;
      }

      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
      };

      // Title - RED, BOLD, Times New Roman, Size 16 (same as downloadReportPDF)
      doc.setFont('times', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 0, 0); // RED
      const titleLines = doc.splitTextToSize('16-POINT STRUCTURED SYNTHESIS REPORT', pageWidth - 40);
      titleLines.forEach(line => {
        doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 7;
      });
      yPosition += 5;

      // SECTION: 14 Points Structured Qualitative Interpretative Summary - REMOVED FROM PDF EXPORT
      // (Commented out as per user request)
      /*
      if (existingData.synthesis_points) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('16-POINT STRUCTURED SYNTHESIS', margin, yPosition);
        yPosition += 10;

        existingData.synthesis_points.forEach((point, idx) => {
          checkNewPage(40);

          // Point header with number
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          if (point.auto_generated) {
            doc.setFillColor(74, 144, 226);
          } else {
            doc.setFillColor(106, 166, 113);
          }
          doc.circle(margin + 5, yPosition - 3, 5, 'F');
          doc.setTextColor(255, 255, 255);
          doc.text(point.point_number.toString(), margin + 5, yPosition, { align: 'center' });
          
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'bold');
          doc.text(`${getDisplayTitle(point.point_number, point.title)}`, margin + 12, yPosition);
          yPosition += 7;

          // Point content
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          
          // Strip HTML tags and format content
          let cleanContent = point.content;
          
          // Convert HTML to plain text
          if (cleanContent.includes('<') && cleanContent.includes('>')) {
            // Remove HTML tags but preserve structure
            cleanContent = cleanContent
              .replace(/<br\s*\/?>/gi, '\n')
              .replace(/<\/p>/gi, '\n\n')
              .replace(/<\/li>/gi, '\n')
              .replace(/<strong>(.*?)<\/strong>/gi, '$1')
              .replace(/<b>(.*?)<\/b>/gi, '$1')
              .replace(/<em>(.*?)<\/em>/gi, '$1')
              .replace(/<i>(.*?)<\/i>/gi, '$1')
              .replace(/<ul>/gi, '')
              .replace(/<\/ul>/gi, '\n')
              .replace(/<ol>/gi, '')
              .replace(/<\/ol>/gi, '\n')
              .replace(/<li>/gi, '• ')
              .replace(/<[^>]+>/g, '') // Remove any remaining tags
              .replace(/&nbsp;/g, ' ')
              .replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .trim();
          }
          
          const lines = doc.splitTextToSize(cleanContent, maxWidth - 12);
          lines.forEach(line => {
            checkNewPage();
            doc.text(line, margin + 12, yPosition);
            yPosition += 5;
          });
          yPosition += 3;
        });

        checkNewPage(30);
        doc.addPage();
        yPosition = margin;
      }
      */

      // SECTION: AI Generated Reports
      if (existingData.ai_reports) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('AI-GENERATED COMPREHENSIVE REPORTS', margin, yPosition);
        yPosition += 10;

        existingData.ai_reports.forEach((report, i) => {
          checkNewPage(30);

          // Report Title
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(41, 128, 185);
          doc.text(report.report_name.toUpperCase(), margin, yPosition);
          yPosition += 7;

          // Report Content
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);

          // Clean HTML from report content
          let cleanReportContent = report.report_content || report.content;
          if (cleanReportContent.includes('<') && cleanReportContent.includes('>')) {
            cleanReportContent = cleanReportContent
              .replace(/<br\s*\/?>/gi, '\n')
              .replace(/<\/p>/gi, '\n\n')
              .replace(/<\/li>/gi, '\n')
              .replace(/<strong>(.*?)<\/strong>/gi, '$1')
              .replace(/<b>(.*?)<\/b>/gi, '$1')
              .replace(/<em>(.*?)<\/em>/gi, '$1')
              .replace(/<i>(.*?)<\/i>/gi, '$1')
              .replace(/<ul>/gi, '')
              .replace(/<\/ul>/gi, '\n')
              .replace(/<ol>/gi, '')
              .replace(/<\/ol>/gi, '\n')
              .replace(/<li>/gi, '• ')
              .replace(/<[^>]+>/g, '')
              .replace(/&nbsp;/g, ' ')
              .replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .trim();
          }

          const contentLines = cleanReportContent.split('\n');
          contentLines.forEach(line => {
            const trimmedLine = line.trim();
            if (trimmedLine) {
              checkNewPage();
              
              // Handle bullet points
              if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
                doc.circle(margin + 2, yPosition - 2, 1, 'F');
                const bulletText = doc.splitTextToSize(trimmedLine.substring(1).trim(), maxWidth - 8);
                bulletText.forEach(bt => {
                  doc.text(bt, margin + 5, yPosition);
                  yPosition += 5;
                });
              } else {
                const paraLines = doc.splitTextToSize(trimmedLine, maxWidth);
                paraLines.forEach(pl => {
                  doc.text(pl, margin, yPosition);
                  yPosition += 5;
                });
              }
            }
          });
          yPosition += 8;
        });

        checkNewPage(30);
        doc.addPage();
        yPosition = margin;
      }

      // SECTION: INSIGHT Client Synthesis
      if (existingData.ai_summary) {
        checkNewPage(20);
        // Section heading - RED, Bold, Size 12 (matching downloadReportPDF Level 1 headings)
        doc.setFont('times', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(255, 0, 0); // RED
        doc.text('CLIENT SUMMARY REPORT', margin, yPosition);
        yPosition += 10;

        // Body text - Times, Normal, Black, Size 10
        doc.setTextColor(0, 0, 0);
        doc.setFont('times', 'normal');
        doc.setFontSize(10);
        
        // Clean HTML from summary
        let cleanSummary = existingData.ai_summary;
        if (cleanSummary.includes('<') && cleanSummary.includes('>')) {
          cleanSummary = cleanSummary
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n\n')
            .replace(/<\/li>/gi, '\n')
            .replace(/<strong>(.*?)<\/strong>/gi, '$1')
            .replace(/<b>(.*?)<\/b>/gi, '$1')
            .replace(/<em>(.*?)<\/em>/gi, '$1')
            .replace(/<i>(.*?)<\/i>/gi, '$1')
            .replace(/<ul>/gi, '')
            .replace(/<\/ul>/gi, '\n')
            .replace(/<ol>/gi, '')
            .replace(/<\/ol>/gi, '\n')
            .replace(/<li>/gi, '• ')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .trim();
        }
        
        const summaryLines = doc.splitTextToSize(cleanSummary, maxWidth);
        summaryLines.forEach(line => {
          checkNewPage();
          doc.text(line, margin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }

      // SECTION: Metaphorical Client Portrait
      if (existingData.metaphorical_narrative) {
        checkNewPage(20);

        // Section heading - RED, Bold, Size 12 (matching downloadReportPDF)
        doc.setFont('times', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(255, 0, 0); // RED
        doc.text('METAPHORICAL CLIENT PORTRAIT', margin, yPosition);
        yPosition += 10;

        // Body text - Times, Normal (not italic for consistency), Black, Size 10
        doc.setTextColor(0, 0, 0);
        doc.setFont('times', 'normal');
        doc.setFontSize(10);
        
        // Clean HTML from narrative
        let cleanNarrative = existingData.metaphorical_narrative;
        if (cleanNarrative.includes('<') && cleanNarrative.includes('>')) {
          cleanNarrative = cleanNarrative
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n\n')
            .replace(/<\/li>/gi, '\n')
            .replace(/<strong>(.*?)<\/strong>/gi, '$1')
            .replace(/<b>(.*?)<\/b>/gi, '$1')
            .replace(/<em>(.*?)<\/em>/gi, '$1')
            .replace(/<i>(.*?)<\/i>/gi, '$1')
            .replace(/<ul>/gi, '')
            .replace(/<\/ul>/gi, '\n')
            .replace(/<ol>/gi, '')
            .replace(/<\/ol>/gi, '\n')
            .replace(/<li>/gi, '• ')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .trim();
        }
        
        const narrativeLines = doc.splitTextToSize(cleanNarrative, maxWidth);
        narrativeLines.forEach(line => {
          checkNewPage();
          doc.text(line, margin, yPosition);
          yPosition += 5;
        });
      }

      // Footer - matching downloadReportPDF style (Times, Italic, Gray)
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('times', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `INSIGHT Platform - 14 Points Report | Page ${i} of ${totalPages}`,
          pageWidth / 2,
          285, // Same position as downloadReportPDF
          { align: 'center' }
        );
      }

      // Save PDF
      const clientName = existingData.synthesis_points?.[0]?.content?.split(',')[0] || 'Client';
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      doc.save(`${clientName}_16_Points_INSIGHT_REPORT_${timestamp}.pdf`);
      showSuccess('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      showError('Failed to export PDF');
    }
  };

  const downloadQueryAsPDF = async (query, response, timestamp) => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Use same formatting as downloadReportPDF - Times New Roman, logo, proper headings
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14.4; // 0.2 inches - same as downloadReportPDF
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = 5; // Start at absolute top

      // Load and add INSIGHT logo (same as downloadReportPDF)
      try {
        const logoImg = new Image();
        logoImg.src = '/insight-logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(reject, 2000);
        });
        
        const logoWidth = 25;
        const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        const logoX = pageWidth - logoWidth - margin;
        doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 8;
      } catch (logoError) {
        console.log('Logo not loaded, continuing without logo');
        yPosition = margin;
      }

      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
      };

      // Title - RED, BOLD, Times New Roman, Size 16 (same as downloadReportPDF)
      doc.setFont('times', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 0, 0); // RED
      doc.text('Ask Buddy Query & Response', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Timestamp - Gray, Normal, Size 10
      doc.setFont('times', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Date: ${new Date(timestamp).toLocaleString()}`, margin, yPosition);
      yPosition += 10;

      // Question heading - RED, Bold, Size 12 (same as paragraph font size)
      checkNewPage(15);
      doc.setFont('times', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(255, 0, 0);
      doc.text('QUESTION:', margin, yPosition);
      yPosition += 6.75; // Same line spacing as paragraphs, no extra space

      // Question content - BLUE, Bold, Size 12 (matching AI report paragraphs)
      doc.setFont('times', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 255); // BLUE
      const questionLines = doc.splitTextToSize(query, maxWidth);
      questionLines.forEach(line => {
        checkNewPage(10);
        doc.text(line, margin, yPosition);
        yPosition += 6.75; // 1.5 line spacing for 12pt
      });
      // NO extra space after question

      // Answer heading - RED, Bold, Size 12 (same as paragraph font size)
      checkNewPage(15);
      doc.setFont('times', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(255, 0, 0);
      doc.text('ANSWER:', margin, yPosition);
      yPosition += 6.75; // Same line spacing as paragraphs, no extra space

      // Answer content - BLUE, Bold, Size 12 (matching AI report paragraphs)
      doc.setFont('times', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 255); // BLUE
      const responseLines = doc.splitTextToSize(response, maxWidth);
      responseLines.forEach(line => {
        checkNewPage(10);
        doc.text(line, margin, yPosition);
        yPosition += 6.75; // 1.5 line spacing for 12pt
      });

      // Footer - matching downloadReportPDF style (Times, Italic, Gray)
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('times', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(`INSIGHT Platform - Ask Buddy Query | Page ${i} of ${totalPages}`, pageWidth / 2, 285, { align: 'center' });
      }

      doc.save(`Ask_Buddy_Query_${new Date(timestamp).toISOString().split('T')[0]}.pdf`);
      showSuccess('Query downloaded as PDF!');
    } catch (error) {
      console.error('PDF download error:', error);
      showError('Failed to download PDF');
    }
  };

  // Both the populated and empty report screens need regeneration confirmation.
  const regenerationConfirmationDialog = (
      <Phase5RegenerationDialog
        open={phase5RegenerationKind !== null}
        kind={phase5RegenerationKind}
        onCancel={() => setPhase5RegenerationKind(null)}
        onConfirm={() => {
          const kind = phase5RegenerationKind;
          setPhase5RegenerationKind(null);
          if (kind === 'sqis') generatePoints();
          if (kind === 'why') handleGenerateWhyPatterns(true);
          if (kind === 'next') handleGenerateNextSteps(true);
          if (kind === 'nexus') handleGenerateNexus(true);
        }}
      />
  );

  const shareConfirmationDialog = (
    <AlertDialog open={shareConfirmation.open} onOpenChange={(open) => { if (!open && !shareMutationPending) setShareConfirmation({ open: false, resource: null, share: true }); }}>
      <AlertDialogContent data-testid="share-resource-confirmation">
        <AlertDialogHeader>
          <AlertDialogTitle>{shareConfirmation.share ? 'SHARE WITH CLIENT' : 'REMOVE CLIENT ACCESS?'}</AlertDialogTitle>
          <AlertDialogDescription>
            {shareConfirmation.share ? "You are about to make this resource available in the client's INSIGHT account." : <><span>This will remove this shared resource from the client's INSIGHT account.</span><span className="block mt-2">The psychologist's original generated Analysis will remain unchanged.</span></>}
            <span className="block mt-3 font-semibold text-gray-900">Resource: {shareConfirmation.resource === 'book' ? 'Book Recommendation' : shareConfirmation.resource === 'movie' ? 'Movie Recommendation' : shareConfirmation.resource === 'music' ? 'Music Reflections' : ''}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel disabled={shareMutationPending}>Cancel</AlertDialogCancel><AlertDialogAction disabled={shareMutationPending} onClick={(event) => { event.preventDefault(); applyResourceShare(); }}>{shareMutationPending ? 'Saving…' : (shareConfirmation.share ? 'Share' : 'Unshare')}</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const sqisGenerationButton = canGenerateSqis && ['loaded', 'empty'].includes(reportLoadState) ? (
    <Button onClick={requestGeneratePoints} disabled={generatingPoints || isEditing16Points || updating16Points}
      size="sm" style={{ background: '#2563eb' }} data-testid="generate-regenerate-sqis">
      {generatingPoints ? 'Generating 14 Points…' : reportLoadState === 'loaded' ? 'Regenerate 14 Points SQIS' : 'Generate 14 Points SQIS'}
    </Button>
  ) : null;

  const loadingFeedback = reportLoadState === 'loading'
    ? <Phase5Loading message={existingData ? 'Refreshing saved analysis reports…' : 'Checking session and existing SQIS…'} />
    : reportLoadState === 'error'
      ? <div role="alert" className="my-4 rounded-lg border border-red-200 bg-red-50 p-4" data-testid="phase5-load-error">
          <p>{reportLoadError}</p>
          <Button className="mt-3" variant="outline" onClick={() => fetchExisting()} data-testid="phase5-load-retry">Retry</Button>
        </div>
      : null;

  // The report endpoint returns one authorized snapshot. Until it arrives,
  // show navigation and local loading feedback without guessing eligibility.
  if (!existingData) return (
    <Card className="mt-6" data-testid="phase5-loading-shell">
      <CardHeader><CardTitle>INSIGHT Analysis</CardTitle></CardHeader>
      <CardContent>
        {loadingFeedback}
        {userRole === 'psychologist' && <div role="tablist" aria-label="Analysis reports" className="phase5-report-navigation mb-4">
          {visiblePhase5ReportTabs(reportVisibility).map(({ key, label }) => (
            <button key={key} role="tab" aria-selected={sqisTab === key} onClick={() => selectSqisTab(key)}
              className="rounded-lg border px-3 py-2 text-sm">{label}</button>
          ))}
        </div>}
      </CardContent>
    </Card>
  );

  // Show Phase 5 content if ai_summary exists OR if movie/book recommendations exist OR custom reports OR ask buddy queries
  // This allows clients to see shared recommendations even before synthesis is complete
  if (existingData?.ai_summary || existingData?.ai_reports?.length > 0 || existingData?.movie_recommendations || existingData?.book_recommendations || existingData?.custom_reports?.length > 0 || existingData?.ai_queries?.length > 0 || existingData?.synthesis_points?.length > 0) {
    // Calculate pagination - REVERSE ORDER (latest first)
    const queries = existingData.ai_queries || [];
    const reversedQueries = [...queries].reverse(); // Latest first
    const totalPages = Math.ceil(reversedQueries.length / queriesPerPage);
    const startIndex = (currentQueryPage - 1) * queriesPerPage;
    const endIndex = startIndex + queriesPerPage;
    const currentQueries = reversedQueries.slice(startIndex, endIndex);
    const clientSummaryEntry = findClientSummaryReport(existingData.ai_reports || []);
    const clientSummaryReport = clientSummaryEntry?.report || null;
    const clientSummaryReportIndex = clientSummaryEntry?.index ?? -1;
    const tableAiReports = indexedAiReportsWithoutClientSummary(existingData.ai_reports || []);
    // Custom Analysis has its own history UI; never route Custom Reports through
    // the review/share-oriented AI Generated Reports table.
    const tableCustomReports = [];

    const requestDeleteQuery = (queryIndex, query) => setDeleteConfirm({
      open: true,
      kind: 'ask_buddy',
      index: queryIndex,
      reportName: query || 'this Ask Buddy item',
    });

    const handleReviewQuery = async (queryIndex) => {
      try {
        await axios.post(`${API}/sessions/${sessionId}/phase5/queries/${queryIndex}/review`);
        // Refresh data without showing toast
        fetchExisting();
      } catch (error) {
        console.error('Failed to mark query as reviewed:', error);
        // Don't show error toast, just log it
      }
    };

    return (
      <>
        {regenerationConfirmationDialog}
        <WhyNextProgressDialog
          open={whyNextModal.open}
          phase={whyNextModal.phase}
          kind={whyNextModal.kind}
          isRegenerate={whyNextModal.isRegenerate}
          errorMessage={whyNextModal.errorMessage}
          attempts={whyNextModal.attempts}
          maxAttempts={whyNextModal.maxAttempts}
          onClose={closeWhyNextModal}
          onCancel={cancelProgressGeneration}
        />
        <WhyNextProgressDialog
          open={progressModal.open}
          phase={progressModal.phase}
          kind={progressModal.kind}
          label={progressModal.label}
          isRegenerate={progressModal.isRegenerate}
          errorMessage={progressModal.errorMessage}
          onClose={closeProgressModal}
          onCancel={cancelProgressGeneration}
        />
        {shareConfirmationDialog}
        {/* Delete confirmation modal — shared render for both return paths */}
        <AlertDialog open={deleteConfirm.open} onOpenChange={(o) => { if (!o) closeDeleteConfirm(); }}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Analysis?</AlertDialogTitle>
              <AlertDialogDescription>
                You're about to delete{' '}
                <span className="font-semibold text-gray-900">"{analysisDisplayText(deleteConfirm.reportName)}"</span>.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting} data-testid="delete-confirm-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={isDeleting}
                onClick={runDeleteConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-confirm-accept"
              >
                {isDeleting ? 'Deleting...' : 'Yes, delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {loadingFeedback}
        <Card className="mt-6" data-testid="phase5-reports-view">
          <CardHeader>
            <div>
              <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Phase 5 Complete - Comprehensive Analysis</CardTitle>
              
              {/* Clean Professional Subtitle */}
              {userRole === 'psychologist' && (
                <p className="mt-2 text-sm text-gray-600">
                  {existingData?.synthesis_reviewed
                    ? '14 Points SQIS is Reviewed'
                    : '14 Points SQIS for Psychologist Review'}
                </p>
              )}

              {/* Text-to-Speech Controls - Positioned above the SQIS tabs */}
              {sqisTab !== 'research' && <details className="phase5-tts-panel mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg" open={ttsExpanded} onToggle={(event) => setTtsExpanded(event.currentTarget.open)}>
                <summary className="phase5-tts-summary flex items-center justify-between gap-3 cursor-pointer list-none">
                  <span className="flex items-center gap-3"><Volume2 className={`w-5 h-5 ${isSpeaking ? 'text-blue-600 animate-pulse' : 'text-blue-600'}`} /><span className="font-semibold text-gray-700">{isSpeaking ? 'Reading Analysis...' : 'Listen to Analysis'}</span></span>
                  <ChevronDown className="phase5-tts-chevron w-5 h-5" aria-hidden="true" />
                </summary>
                <div className="phase5-tts-body">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    {isSpeaking ? (
                      <>
                        <Volume2 className="w-5 h-5 text-blue-600 animate-pulse" />
                        <span className="font-semibold text-blue-900">🔊 Reading Analysis...</span>
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-gray-700">Text-to-Speech Controls</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    {/* Voice Selection */}
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-gray-700 font-medium">Voice:</Label>
                      <select
                        value={selectedVoice ? availableVoices.indexOf(selectedVoice) : 0}
                        onChange={(e) => {
                          const voice = availableVoices[parseInt(e.target.value)];
                          setSelectedVoice(voice);
                          if (isSpeaking) {
                            restartSpeechWithNewSettings();
                          }
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white max-w-[200px]"
                      >
                        {availableVoices.map((voice, idx) => (
                          <option key={idx} value={idx}>
                            {voice.name} ({voice.lang})
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Speed Control */}
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-gray-700 font-medium">Speed:</Label>
                      <select
                        value={speechRate}
                        onChange={(e) => {
                          const newRate = parseFloat(e.target.value);
                          setSpeechRate(newRate);
                          if (isSpeaking) {
                            restartSpeechWithNewSettings();
                          }
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="0.5">0.5x (Slow)</option>
                        <option value="0.75">0.75x</option>
                        <option value="1.0">1.0x (Normal)</option>
                        <option value="1.25">1.25x</option>
                        <option value="1.5">1.5x (Fast)</option>
                        <option value="2.0">2.0x (Very Fast)</option>
                      </select>
                    </div>
                    {isSpeaking && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={stopSpeaking}
                        className="bg-red-50 border-red-300 hover:bg-red-100 text-red-700 font-semibold"
                      >
                        <StopCircle className="w-4 h-4 mr-1" />
                        Stop Reading
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  💡 Click the <Volume2 className="w-3 h-3 inline mx-1" /> "Listen" button on any Analysis below to hear it read aloud. Choose from {availableVoices.length}+ voices in multiple languages!
                </p>
                </div>
              </details>}

              {/* "Why" (Patterns & Contributing Themes) + "Next Steps" Tabs
                  Visible to psychologists once the SQIS has been reviewed. */}
              {userRole === 'psychologist' && (existingData?.synthesis_points || []).length > 0 && (
                <div className="mt-4" data-testid="sqis-extension-tabs">
                  <div
                    role="tablist"
                    className="phase5-report-navigation"
                  >
                    {(() => {
                      const tabs = visiblePhase5ReportTabs(reportVisibility).filter(tab => existingData?.synthesis_reviewed || ['data-quality', 'sqis'].includes(tab.key));
                      return tabs.map(({ key, label, testId }) => {
                        const displayLabel = key === 'client-summary' ? clientSynthesisName : label;
                        const isHelpfulResources = key === 'helpful-resources';
                        const isResearchResources = key === 'research';
                        const active = sqisTab === key || (isHelpfulResources && ['books', 'movies', 'music'].includes(sqisTab));
                        if (isHelpfulResources) return (
                          <div key={key} className="relative" ref={helpfulResourcesRef}>
                            <button type="button" role="tab" aria-selected={active} aria-expanded={helpfulResourcesOpen} onClick={() => { selectSqisTab('helpful-resources'); setHelpfulResourcesOpen((value) => !value); }} className={`inline-flex items-center gap-1 px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${active ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-black/5' : 'text-gray-600 hover:text-gray-900'}`} data-testid={testId}>
                              {label}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${helpfulResourcesOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {helpfulResourcesOpen && <div className="absolute z-50 right-0 sm:left-0 sm:right-auto mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-2 shadow-xl" role="menu" data-testid="helpful-resources-menu">
                              {isReportEnabled('book_recommendations') && <button type="button" role="menuitem" onClick={() => { selectSqisTab('books'); setHelpfulResourcesOpen(false); }} className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none" data-testid="menu-book-recommendations">📚 Book Reflections</button>}
                              {isReportEnabled('movie_recommendations') && <button type="button" role="menuitem" onClick={() => { selectSqisTab('movies'); setHelpfulResourcesOpen(false); }} className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none" data-testid="menu-movie-recommendations">🎬 Movie Reflections</button>}
                              {isReportEnabled('music_recommendations') && <button type="button" role="menuitem" onClick={() => { selectSqisTab('music'); setHelpfulResourcesOpen(false); }} className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none" data-testid="menu-music-recommendations">🎵 Music Reflections</button>}
                            </div>}
                          </div>
                        );
                        if (isResearchResources) return (
                          <div key={key} className="relative" ref={researchResourcesRef}>
                            <button type="button" role="tab" aria-selected={active} aria-expanded={researchResourcesOpen} onClick={() => { selectSqisTab('research'); setResearchResourcesOpen((value) => !value); }} className={`inline-flex items-center gap-1 px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${active ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-black/5' : 'text-gray-600 hover:text-gray-900'}`} data-testid={testId}>
                              {label}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${researchResourcesOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {researchResourcesOpen && <div className="absolute z-50 right-0 sm:left-0 sm:right-auto mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-2 shadow-xl" role="menu" data-testid="research-resources-menu">
                              {isReportEnabled('apa_case_parallels') && <button type="button" role="menuitem" onClick={() => { selectSqisTab('research'); setSelectedResearchReport('apa_case_parallels'); setResearchResourcesOpen(false); }} className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none" data-testid="menu-apa-case-parallels">APA Case Parallels</button>}
                              {isReportEnabled('research_framework_synthesis') && <button type="button" role="menuitem" onClick={() => { selectSqisTab('research'); setSelectedResearchReport('research_framework_synthesis'); setResearchResourcesOpen(false); }} className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none" data-testid="menu-research-framework-synthesis">Research Framework Synthesis</button>}
                              {isReportEnabled('developmental_ecological_cultural') && <button type="button" role="menuitem" onClick={() => { selectSqisTab('research'); setSelectedResearchReport('developmental_ecological_cultural'); setResearchResourcesOpen(false); }} className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none" data-testid="menu-developmental-ecological-cultural">Developmental–Ecological–Cultural Integration Analysis</button>}
                            </div>}
                          </div>
                        );
                        return (
                          <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => {
                              selectSqisTab(key);
                              if (key === 'sqis') {
                                setTimeout(() => {
                                  const el = document.querySelector('[data-testid="sqis-full-section"]')
                                    || document.getElementById('sqis-full-section');
                                  if (el && el.scrollIntoView) {
                                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }, 50);
                              }
                            }}
                            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
                              active
                                ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-black/5'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                            data-testid={testId}
                          >
                            {displayLabel}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  {sqisTab === 'data-quality' && (
                    <div className="phase5-report p-4" data-testid="tab-panel-data-quality">
                      <Phase5ReportTitle title="Data Quality Note" />
                      <PointContentRenderer content={phase5DataQualityContent(existingData, true)} />
                    </div>
                  )}

                  {existingData?.synthesis_reviewed && <>
                  {sqisTab === 'intro' && (
                    <div data-testid="tab-panel-intro">
                      <ProcessIntro synthesisPoints={existingData?.synthesis_points || existingData?.points || []} />
                    </div>
                  )}

                  {sqisTab === 'sqis' && (
                    <div className="hidden" data-testid="tab-panel-sqis" />
                  )}

                  {sqisTab === 'why' && (
                    <div className="phase5-report p-4" data-testid="tab-panel-why-patterns">
                      <Phase5ReportTitle title={'"Why" (Patterns & Contributing Themes)'} generatedBy={existingData?.why_patterns?.generated_by_model} generatedAt={existingData?.why_patterns?.generated_at} actions={<>
                          <Button
                            onClick={() => downloadSqisExtensionPDF('why')}
                            disabled={!existingData?.why_patterns?.content}
                            variant="outline"
                            className="text-sm border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            data-testid="btn-download-why-patterns"
                          >
                            <Download className="w-4 h-4 mr-1" /> Download PDF
                          </Button>
                          <Button
                            onClick={handleGenerateWhyPatterns}
                            disabled={generatingWhy}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                            data-testid="btn-generate-why-patterns"
                          >
                            {generatingWhy
                              ? 'Generating...'
                              : (existingData?.why_patterns?.content ? 'Regenerate' : 'Generate')}
                          </Button>
                      </>} />
                      {existingData?.why_patterns?.content ? (
                        <>
                          {existingData.why_patterns.completion_status === 'partial' && (
                            <div
                              className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                              role="alert"
                              data-testid="why-partial-warning"
                            >
                              Why Analysis is incomplete. Missing sections: {(existingData.why_patterns.missing_sections || []).join(', ')}
                            </div>
                          )}
                          <Phase5ReportView sessionId={sessionId} reportName="Why" reportKey="why">
                            {({ expandedSectionIds, onExpandedSectionIdsChange, onSectionIdsChange }) => <RichReportRenderer sourceResponses={reportSourceResponses(existingData.synthesis_points)} content={existingData.why_patterns.content} testId="why-patterns-rich" sessionId={sessionId} reportKey="why" expandedSectionIds={expandedSectionIds} onExpandedSectionIdsChange={onExpandedSectionIdsChange} onSectionIdsChange={onSectionIdsChange} />}
                          </Phase5ReportView>
                          <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                            {existingData.why_patterns.attempts > 1 && (
                              <span
                                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 cursor-help"
                                data-testid="why-patterns-retry-badge"
                                title={`Retried ${existingData.why_patterns.attempts - 1}× · last at ${formatRetryTime(existingData.why_patterns.generated_at)}`}
                              >
                                Retried {existingData.why_patterns.attempts - 1}&times;
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          Click "Generate" to produce the Patterns & Contributing Themes narrative based on the reviewed 14-point SQIS.
                        </p>
                      )}
                    </div>
                  )}

                  {sqisTab === 'next' && (
                    <div className="phase5-report p-4" data-testid="tab-panel-next-steps">
                      <Phase5ReportTitle title="Next Steps" generatedBy={existingData?.next_steps?.generated_by_model} generatedAt={existingData?.next_steps?.generated_at} actions={<>
                          <Button
                            onClick={() => downloadSqisExtensionPDF('next')}
                            disabled={!existingData?.next_steps?.content}
                            variant="outline"
                            className="text-sm border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            data-testid="btn-download-next-steps"
                          >
                            <Download className="w-4 h-4 mr-1" /> Download PDF
                          </Button>
                          <Button
                            onClick={handleGenerateNextSteps}
                            disabled={generatingNext || !existingData?.why_patterns?.content}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                            data-testid="btn-generate-next-steps"
                            title={!existingData?.why_patterns?.content ? 'Generate "Why" (Patterns & Contributing Themes) first' : undefined}
                          >
                            {generatingNext
                              ? 'Generating...'
                              : (existingData?.next_steps?.content ? 'Regenerate' : 'Generate')}
                          </Button>
                      </>} />
                      {!existingData?.why_patterns?.content && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 mb-3">
                          Generate the "Why" (Patterns & Contributing Themes) tab first — the Next Steps prompt uses that section as input.
                        </p>
                      )}
                      {existingData?.next_steps?.content ? (
                        <>
                          <Phase5ReportView sessionId={sessionId} reportName="Next Steps" reportKey="next_steps">
                            {({ expandedSectionIds, onExpandedSectionIdsChange, onSectionIdsChange }) => <FormattedReport sourceResponses={reportSourceResponses(existingData.synthesis_points)} sessionId={sessionId} reportType="next_steps" reportVersion={existingData.next_steps.generated_at} generationRequestId={existingData.next_steps.generation_request_id} content={existingData.next_steps.content} testId="next-steps-rich" expandedSectionIds={expandedSectionIds} onExpandedSectionIdsChange={onExpandedSectionIdsChange} onSectionIdsChange={onSectionIdsChange} />}
                          </Phase5ReportView>
                          <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                            {existingData.next_steps.attempts > 1 && (
                              <span
                                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 cursor-help"
                                data-testid="next-steps-retry-badge"
                                title={`Retried ${existingData.next_steps.attempts - 1}× · last at ${formatRetryTime(existingData.next_steps.generated_at)}`}
                              >
                                Retried {existingData.next_steps.attempts - 1}&times;
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          Click "Generate" to produce 3-5 actionable next steps grounded in the SQIS and the Patterns & Contributing Themes.
                        </p>
                      )}
                    </div>
                  )}
                  {sqisTab === 'nexus' && (
                    <div className="phase5-report p-4" data-testid="tab-panel-nexus">
                      <Phase5ReportTitle title="Nexus" subtitle="Conversational Playbook & Resistance Simulator" generatedBy={existingData?.nexus?.generated_by_model} generatedAt={existingData?.nexus?.generated_at} actions={<>
                          <Button
                            onClick={() => openRoleplay()}
                            disabled={!existingData?.nexus?.content}
                            variant="outline"
                            className="text-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid="btn-start-roleplay"
                            title={!existingData?.nexus?.content ? 'Generate Nexus first' : 'Open the client roleplay simulator'}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" /> Start Roleplay
                          </Button>
                          <Button
                            onClick={() => downloadSqisExtensionPDF('nexus')}
                            disabled={!existingData?.nexus?.content}
                            variant="outline"
                            className="text-sm border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            data-testid="btn-download-nexus"
                          >
                            <Download className="w-4 h-4 mr-1" /> Download PDF
                          </Button>
                          <Button
                            onClick={handleGenerateNexus}
                            disabled={generatingNexus || !existingData?.why_patterns?.content || !existingData?.next_steps?.content}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                            data-testid="btn-generate-nexus"
                            title={
                              !existingData?.why_patterns?.content
                                ? 'Generate "Why" (Patterns & Contributing Themes) first'
                                : !existingData?.next_steps?.content
                                  ? 'Generate Next Steps first'
                                  : undefined
                            }
                          >
                            {generatingNexus
                              ? 'Generating...'
                              : (existingData?.nexus?.content ? 'Regenerate' : 'Generate')}
                          </Button>
                      </>} />
                      {(!existingData?.why_patterns?.content || !existingData?.next_steps?.content) && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 mb-3" data-testid="nexus-prerequisites-warning">
                          Generate the "Why" (Patterns & Contributing Themes) and "Next Steps" tabs first — the Nexus prompt uses both sections as input.
                        </p>
                      )}
                      {existingData?.nexus?.content ? (
                        <>
                          <Phase5ReportView sessionId={sessionId} reportName="Nexus" reportKey="nexus">
                            {({ expandedSectionIds, onExpandedSectionIdsChange, onSectionIdsChange }) => <FormattedReport sourceResponses={reportSourceResponses(existingData.synthesis_points)} sessionId={sessionId} reportType="nexus" content={existingData.nexus.content} testId="nexus-rich" expandedSectionIds={expandedSectionIds} onExpandedSectionIdsChange={onExpandedSectionIdsChange} onSectionIdsChange={onSectionIdsChange} />}
                          </Phase5ReportView>
                          <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                            {existingData.nexus.attempts > 1 && (
                              <span
                                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 cursor-help"
                                data-testid="nexus-retry-badge"
                                title={`Retried ${existingData.nexus.attempts - 1}× · last at ${formatRetryTime(existingData.nexus.generated_at)}`}
                              >
                                Retried {existingData.nexus.attempts - 1}&times;
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          Click "Generate" to produce a Conversational Playbook & Resistance Simulator grounded in the SQIS, Patterns, and Next Steps.
                        </p>
                      )}
                    </div>
                  )}
                  {sqisTab === 'client-summary' && (
                    <div className="phase5-report p-4" data-testid="tab-panel-client-summary">
                      <Phase5ReportTitle
                        title={clientSynthesisName}
                        generatedBy={clientSummaryReport?.psychologist_name || clientSummaryReport?.generated_by_model}
                        generatedAt={clientSummaryReport?.generated_at}
                        actions={<>
                          {userRole === 'psychologist' && clientSummaryReport && !editingClientSummary && <Button
                            variant="outline"
                            onClick={() => startClientSummaryEdit(clientSummaryReport)}
                            disabled={isViewOnlyMode || existingData?.report_visibility?.[`ai_report_${clientSummaryReportIndex}`] === true}
                            data-testid="edit-client-summary"
                            title={existingData?.report_visibility?.[`ai_report_${clientSummaryReportIndex}`] === true ? 'Unshare this Analysis before editing' : `Edit ${clientSynthesisName}`}
                          ><Edit className="w-4 h-4 mr-1" /> Edit</Button>}
                          {editingClientSummary && <><Button onClick={() => saveClientSummaryEdit(clientSummaryReportIndex)} disabled={savingClientSummary} data-testid="save-client-summary"><Save className="w-4 h-4 mr-1" />{savingClientSummary ? 'Saving…' : 'Save'}</Button><Button variant="outline" onClick={cancelClientSummaryEdit} disabled={savingClientSummary} data-testid="cancel-client-summary"><X className="w-4 h-4 mr-1" />Cancel</Button></>}
                          <Button
                            variant="outline"
                            className="text-sm border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            onClick={() => downloadReportPDF(clientSummaryReport)}
                            disabled={!clientSummaryReport}
                            data-testid="download-client-summary-pdf"
                          ><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
                          <Button
                            variant="outline"
                            className={currentSpeakingReport === 'client-summary' && isSpeaking ? 'bg-orange-100 border-orange-500' : 'text-sm'}
                            onClick={() => {
                              if (currentSpeakingReport === 'client-summary' && isSpeaking) stopSpeaking();
                              else speakReport(clientSummaryHtmlToText(clientSummaryReport?.report_content || clientSummaryReport?.content), 'client-summary');
                            }}
                            disabled={!clientSummaryReport}
                            data-testid="listen-client-summary"
                          >{currentSpeakingReport === 'client-summary' && isSpeaking ? <StopCircle className="w-4 h-4 mr-1" /> : <Volume2 className="w-4 h-4 mr-1" />} Listen</Button>
                          {userRole === 'psychologist' && clientSummaryReport && !clientSummaryReport.reviewed && (!isViewOnlyMode || !clientSummaryReport.psychologist_id || clientSummaryReport.psychologist_id === currentUser?.id) && <Button
                            onClick={() => handleReviewReport(clientSummaryReportIndex)}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                            data-testid="review-client-summary"
                          ><CheckCircle className="w-4 h-4 mr-1" /> Mark as Reviewed</Button>}
                          {userRole === 'psychologist' && clientSummaryReport && <Button
                            variant="outline"
                            onClick={() => handleToggleReportVisibility(clientSummaryReportIndex, 'ai', existingData?.report_visibility?.[`ai_report_${clientSummaryReportIndex}`] === true)}
                            disabled={isViewOnlyMode || !clientSummaryReport.reviewed}
                            className="text-sm"
                            data-testid="share-client-summary"
                          >{existingData?.report_visibility?.[`ai_report_${clientSummaryReportIndex}`] === true ? 'Unshare' : 'Share with Client'}</Button>}
                          {userRole === 'psychologist' && !isViewOnlyMode && isReportEnabled('ai_summary') && <Button
                            onClick={() => handleGenerateIndividualReport('ai_summary', clientSynthesisName)}
                            disabled={generatingIndividualReport !== null || !existingData?.why_patterns?.content || !existingData?.next_steps?.content}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                            data-testid="generate-client-summary"
                          >{generatingIndividualReport === 'ai_summary' ? 'Generating...' : (clientSummaryReport ? 'Regenerate' : 'Generate')}</Button>}
                          {userRole === 'psychologist' && clientSummaryReport && <Button
                            variant="destructive"
                            onClick={() => handleDeleteReport(clientSummaryReportIndex)}
                            data-testid="delete-client-summary"
                          ><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
                        </>}
                      />
                      <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="client-summary-status">
                        {clientSummaryReport ? <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${clientSummaryReport.reviewed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {clientSummaryReport.reviewed ? <><CheckCircle className="w-3 h-3 mr-1" /> Reviewed</> : <><Clock className="w-3 h-3 mr-1" /> Pending Review</>}
                        </span> : <span className="text-sm text-gray-500">Not generated</span>}
                        {clientSummaryReport?.reviewed && <span className="text-xs text-gray-600">{existingData?.report_visibility?.[`ai_report_${clientSummaryReportIndex}`] === true ? 'Shared with Client' : 'Not shared with Client'}</span>}
                        <ReportFontSelector />
                        <ReportTextSizeSelector />
                      </div>
                      {clientSummaryReport ? (editingClientSummary ? (
                        <ClientSummaryEditor value={clientSummaryDraft} onChange={setClientSummaryDraft} />
                      ) : (
                        <div data-testid="client-summary-content">
                          <PremiumClientSynthesis sessionId={sessionId} metadata={{clientName:clientDisplayName, date:clientSummaryReport.generated_at ? new Date(clientSummaryReport.generated_at).toLocaleDateString() : undefined, psychologistName:clientSummaryReport.psychologist_name||existingData?.psychologist_name}} content={clientSummaryReport.content} testId="client-synthesis-rich" />
                        </div>
                      )) : (
                        <div className="rounded border border-indigo-200 bg-indigo-50 p-4" data-testid="client-summary-empty-state">
                          <p className="text-sm text-gray-700">Generate the Client Synthesis from the reviewed 14-point SQIS, Why Analysis, and Next Steps Analysis when you are ready.</p>
                          {(!existingData?.why_patterns?.content || !existingData?.next_steps?.content) && <p className="mt-2 text-sm text-amber-700" data-testid="client-summary-prerequisites">The reviewed 14-Point SQIS, Why Analysis, and Next Steps Analysis are all required.</p>}
                          {!isReportEnabled('ai_summary') && <p className="mt-2 text-sm text-amber-700">Client Synthesis generation is currently disabled by the administrator.</p>}
                        </div>
                      )}
                    </div>
                  )}
                  {sqisTab === 'helpful-resources' && (
                    <div className="p-4" data-testid="tab-panel-helpful-resources">
                      <h4 className="text-lg font-semibold text-gray-900">Reflection Resources</h4>
                      <p className="mt-1 text-sm text-gray-600">Supporting materials selected from the themes and directions already identified in INSIGHT.</p>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {isReportEnabled('book_recommendations') && <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
                          <h5 className="font-bold text-emerald-900">Book Reflections</h5>
                          <p className="mt-2 text-sm text-gray-700">Reading suggestions connected to themes and next-step directions identified in INSIGHT.</p>
                          <Button className="mt-4" variant="outline" onClick={() => selectSqisTab('books')} data-testid="open-book-recommendations">Open Book Reflections</Button>
                        </div>}
                        {isReportEnabled('movie_recommendations') && <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
                          <h5 className="font-bold text-indigo-900">Movie Reflections</h5>
                          <p className="mt-2 text-sm text-gray-700">Film suggestions that may provide relatable stories, themes and opportunities for reflection.</p>
                          <Button className="mt-4" variant="outline" onClick={() => selectSqisTab('movies')} data-testid="open-movie-recommendations">Open Movie Reflections</Button>
                        </div>}
                        {isReportEnabled('music_recommendations') && <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4">
                          <h5 className="font-bold text-violet-900">Music Reflections</h5>
                          <p className="mt-2 text-sm text-gray-700">Reflective listening suggestions connected to Why and Next Steps.</p>
                          <Button className="mt-4" variant="outline" onClick={() => selectSqisTab('music')} data-testid="open-music-recommendations">Open Music Reflections</Button>
                        </div>}
                      </div>
                    </div>
                  )}
                  {sqisTab === 'custom_reports' && (() => {
                    if (!isReportEnabled('custom_reports')) return <div className="m-4 rounded border border-amber-300 bg-amber-50 p-4 text-amber-900" role="alert" data-testid="custom-analysis-disabled">Custom Analysis is currently disabled by the administrator.</div>;
                    const missing = [
                      !existingData?.synthesis_reviewed || !(existingData?.synthesis_points || []).length ? 'reviewed 14-Point SQIS' : null,
                      !existingData?.why_patterns?.content?.trim() ? 'Why' : null,
                      !existingData?.next_steps?.content?.trim() ? 'Next Steps' : null,
                    ].filter(Boolean);
                    return <div className="phase5-report p-4" data-testid="assistant-custom-reports">
                      <Phase5ReportTitle title="Custom Analysis" />
                      {missing.length > 0 && <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="custom-report-prerequisites">Missing prerequisite{missing.length > 1 ? 's' : ''}: {missing.join(', ')}.</div>}
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
                        <Label>Analysis Name</Label><input className="w-full rounded border p-2" value={customReportName} onChange={(e) => setCustomReportName(e.target.value)} disabled={generatingCustomReport} />
                        <Label>Expert request / purpose</Label><Textarea className="min-h-[120px]" value={customReportFocus} onChange={(e) => setCustomReportFocus(e.target.value)} disabled={generatingCustomReport} />
                        <Button onClick={() => handleGenerateCustomReport()} disabled={generatingCustomReport || missing.length > 0 || !customReportName.trim() || !customReportFocus.trim()} data-testid="generate-custom-report">{generatingCustomReport ? 'Generating…' : 'Perform Custom Analysis'}</Button>
                      </div>
                      <CustomReportHistory key={sessionId} reports={existingData?.custom_reports || []} sessionId={sessionId} currentUser={currentUser}
                        onDownload={downloadReportPDF} onDelete={handleDeleteCustomReport} />
                    </div>;
                  })()}
                  {sqisTab === 'research' && (() => {
                    const definitions = [
                      ['apa_case_parallels', 'APA Case Parallels', 'Research-oriented case parallels derived from reviewed INSIGHT findings.', 'Open APA Case Parallels', 'rounded-lg border border-emerald-200 bg-emerald-50/40 p-4', 'font-bold text-emerald-900'],
                      ['research_framework_synthesis', 'Research Framework Synthesis', 'Research framework analysis based on reviewed INSIGHT findings.', 'Open Research Framework Synthesis', 'rounded-lg border border-indigo-200 bg-indigo-50/40 p-4', 'font-bold text-indigo-900'],
                      ['developmental_ecological_cultural', 'Developmental–Ecological–Cultural Integration Analysis', 'Integrated developmental, ecological, and cultural research interpretation.', 'Open Integration Analysis', 'rounded-lg border border-violet-200 bg-violet-50/40 p-4', 'font-bold text-violet-900'],
                    ];
                    const selectedDefinition = definitions.find(([reportType]) => reportType === selectedResearchReport);
                    if (!selectedDefinition) return <div className="p-4" data-testid="tab-panel-research">
                        <h4 className="text-lg font-semibold text-gray-900">Research Resources</h4>
                        <p className="mt-1 text-sm text-gray-600">Private psychologist/domain-expert Analysis based on the reviewed SQIS, Why, and Next Steps.</p>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                          {definitions.filter(([reportType]) => isReportEnabled(reportType)).map(([reportType, reportName, description, openLabel, cardClass, titleClass]) => <div key={reportType} className={cardClass} data-testid={`research-card-${reportType}`}>
                            <h5 className={titleClass}>{reportName.toUpperCase()}</h5>
                            <p className="mt-2 text-sm text-gray-700">{description}</p>
                            <Button className="mt-4" variant="outline" onClick={() => setSelectedResearchReport(reportType)} data-testid={`open-${reportType}`}>{openLabel}</Button>
                          </div>)}
                        </div>
                    </div>;

                    const [reportType, reportName] = selectedDefinition;
                    if (!isReportEnabled(reportType)) return <div className="p-4 rounded border border-amber-200 bg-amber-50" data-testid={`research-disabled-${reportType}`}>
                      <Button variant="outline" onClick={() => setSelectedResearchReport(null)} data-testid="back-to-research">Back to Research Resources</Button>
                      <p className="mt-4 text-sm text-amber-700">This Research Resource is currently disabled by the administrator.</p>
                    </div>;
                    const historicalReports = researchReportsForType(existingData || {}, reportType);
                    const report = [...historicalReports].reverse()[0];
                    const generating = generatingIndividualReport === reportType;
                    return <div className="phase5-report p-4 space-y-4" data-testid={`research-report-${reportType}`}>
                      <Button variant="outline" onClick={() => setSelectedResearchReport(null)} data-testid="back-to-research">Back to Research Resources</Button>
                      <Phase5ReportTitle title={reportName} generatedBy={report?.psychologist_name || report?.generated_by} generatedAt={report?.generated_at} actions={<>
                        {report && <Button variant="outline" onClick={() => downloadReportPDF(report)} data-testid={`btn-download-${reportType}`}><Download className="w-4 h-4 mr-1" />Download PDF</Button>}
                        <Button onClick={() => handleGenerateResearchReport(reportType, reportName)} disabled={generating || !isReportEnabled(reportType)} data-testid={`btn-generate-${reportType}`}>{generating ? 'Generating...' : (report ? 'Regenerate' : 'Generate')}</Button>
                      </>} />
                      {report ? <Phase5ReportView sessionId={sessionId} reportName={reportName} reportKey={reportType}>
                        {({ expandedSectionIds, onExpandedSectionIdsChange, onSectionIdsChange }) => <RichReportRenderer content={report.content || report.report_content} testId={`research-content-${reportType}`} sessionId={sessionId} reportKey={reportType} expandedSectionIds={expandedSectionIds} onExpandedSectionIdsChange={onExpandedSectionIdsChange} onSectionIdsChange={onSectionIdsChange} />}
                      </Phase5ReportView> : <p className="text-sm text-gray-600" data-testid={`research-empty-${reportType}`}>Not generated. Reviewed SQIS, Why, and Next Steps are required.</p>}
                    </div>;
                  })()}
                  {sqisTab === 'books' && (
                    <div className="phase5-report p-4" data-testid="tab-panel-book-recommendations">
                      <Phase5ReportTitle title="Book Reflections" subtitle="Uses Why + Next Steps"
                        generatedBy={existingData?.book_recommendations ? recommendationGeneratedBy(existingData.book_recommendations) : undefined}
                        generatedAt={existingData?.book_recommendations?.generated_at || existingData?.recommendations_generated_at}
                        icon={BookOpen} actions={<>
                          {existingData?.book_recommendations && <Button onClick={() => downloadRecommendationPDF('book')} variant="outline" data-testid="btn-download-book-recommendations"><Download className="w-4 h-4 mr-1" />Download PDF</Button>}
                          {existingData?.book_recommendations && canManageHelpfulResources && shareControl('book')}
                          {canManageHelpfulResources && <Button onClick={handleGenerateBookRecommendations} disabled={generatingBooks || !recommendationDependenciesReady(existingData)} data-testid="btn-generate-book-recommendations">
                            {generatingBooks ? 'Generating...' : (existingData?.book_recommendations ? 'Regenerate' : 'Generate Book Reflections')}
                          </Button>}
                        </>} />
                      {!recommendationDependenciesReady(existingData) && <p className="mt-3 text-sm text-amber-700">Generate Why and Next Steps first.</p>}
                      {existingData?.book_recommendations ? (
                        existingData.book_recommendations.schema_version === 2 ? (
                          <BookReport report={existingData.book_recommendations} sessionId={sessionId} />
                        ) : <div data-testid="historical-book-recommendations"><LegacyRecommendationReport report={existingData.book_recommendations} type="book" sessionId={sessionId} /></div>
                      ) : <p className="mt-3 text-sm text-gray-600">Not generated</p>}
                    </div>
                  )}
                  {sqisTab === 'movies' && (
                    <div className="phase5-report p-4" data-testid="tab-panel-movie-recommendations">
                      <Phase5ReportTitle title="Movie Reflections" subtitle="Uses Why + Next Steps"
                        generatedBy={existingData?.movie_recommendations ? recommendationGeneratedBy(existingData.movie_recommendations) : undefined}
                        generatedAt={existingData?.movie_recommendations?.generated_at || existingData?.movie_recommendations_generated_at}
                        actions={<>
                          {existingData?.movie_recommendations && <Button onClick={() => downloadRecommendationPDF('movie')} variant="outline" data-testid="btn-download-movie-recommendations"><Download className="w-4 h-4 mr-1" />Download PDF</Button>}
                          {existingData?.movie_recommendations && canManageHelpfulResources && shareControl('movie')}
                          {canManageHelpfulResources && <Button onClick={handleGenerateMovieRecommendations} disabled={generatingMovies || !recommendationDependenciesReady(existingData)} data-testid="btn-generate-movie-recommendations">
                            {generatingMovies ? 'Generating...' : (existingData?.movie_recommendations ? 'Regenerate' : 'Generate Movie Reflections')}
                          </Button>}
                        </>} />
                      {!recommendationDependenciesReady(existingData) && <p className="mt-3 text-sm text-amber-700">Generate Why and Next Steps first.</p>}
                      {existingData?.movie_recommendations ? (
                        existingData.movie_recommendations.schema_version === 2 ? (
                          <MovieReport report={existingData.movie_recommendations} sessionId={sessionId} />
                        ) : <div data-testid="historical-movie-recommendations"><LegacyRecommendationReport report={existingData.movie_recommendations} type="movie" sessionId={sessionId} /></div>
                      ) : <p className="mt-3 text-sm text-gray-600">Not generated</p>}
                    </div>
                  )}
                  {sqisTab === 'music' && (
                    <div className="phase5-report p-4" data-testid="tab-panel-music-recommendations">
                      <Phase5ReportTitle title="Music Reflections" subtitle="Reflective Listening Suggestions"
                        generatedBy={existingData?.music_recommendations ? recommendationGeneratedBy(existingData.music_recommendations) : undefined}
                        generatedAt={existingData?.music_recommendations?.generated_at || existingData?.music_recommendations_generated_at}
                        actions={<>
                          {existingData?.music_recommendations && <Button onClick={() => downloadRecommendationPDF('music')} variant="outline" data-testid="btn-download-music-recommendations"><Download className="w-4 h-4 mr-1" />Download PDF</Button>}
                          {existingData?.music_recommendations && canManageHelpfulResources && shareControl('music')}
                          {canManageHelpfulResources && <Button onClick={handleGenerateMusicRecommendations} disabled={generatingMusic || !recommendationDependenciesReady(existingData)} data-testid="btn-generate-music-recommendations">{generatingMusic ? 'Generating...' : (existingData?.music_recommendations ? 'Regenerate' : 'Generate Music Reflections')}</Button>}
                        </>} />
                      {!recommendationDependenciesReady(existingData) && <p className="mt-3 text-sm text-amber-700">Generate Why and Next Steps first.</p>}
                      {existingData?.music_recommendations ? <MusicReport report={existingData.music_recommendations} sessionId={sessionId} /> : <p className="mt-3 text-sm text-gray-600">Not generated</p>}
                    </div>
                  )}
                  </>}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* All Phase 5 Sections - Sequential Display (No Tabs) */}
            <div className="space-y-8 mt-8">
              
              {/* SECTION 1: 14 Points Structured Qualitative Interpretative Summary - Hidden when psychologist has switched to Refs / Why / Next Steps tab */}
              {existingData.synthesis_points && !(userRole === 'psychologist' && (existingData?.synthesis_reviewed || sqisTab === 'data-quality') && sqisTab !== 'sqis') && (() => {
                const isMergedWithTabs = userRole === 'psychologist' && existingData?.synthesis_reviewed;
                return (
                <div
                  id="sqis-full-section"
                  data-testid="sqis-full-section"
                  className={`sqis-report phase5-report ${isMergedWithTabs
                    ? "sqis-report--merged -mt-8 mb-8 bg-white"
                    : "sqis-report--standalone mb-8"}`}
                >
                  {!isMergedWithTabs && (
                  <div className="sqis-header flex justify-between items-center mb-4 flex-wrap gap-3">
                    <h3 className="sqis-title">14 Points SQIS</h3>
                    <div className="sqis-header-actions flex items-center gap-2 flex-wrap">
                      {userRole === 'psychologist' && (
                        <>
                          {sqisGenerationButton}
                          <Button
                            onClick={() => setIsSynthesisVisible(!isSynthesisVisible)}
                            variant="outline"
                            size="sm"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          >
                            {isSynthesisVisible ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Hide
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Show
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => download16PointSynthesisPDF()}
                            variant="outline"
                            size="sm"
                            className="border-purple-500 text-purple-600 hover:bg-purple-50"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                        </>
                      )}
                      {existingData.synthesis_reviewed ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                          ✓ Reviewed
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">
                          ⚠ Not Reviewed
                        </span>
                      )}
                    </div>
                  </div>
                  )}
                  <div className={isMergedWithTabs ? "px-4 pb-4" : ""}>
                  {/* Header row (Why-tab style): title + timestamp on the left, actions on the right */}
                  {isMergedWithTabs && (() => {
                    const sqisGeneratedAt = existingData?.synthesis_reviewed_at
                      || existingData?.synthesis_generated_at
                      || existingData?.synthesis_updated_at
                      || existingData?.updated_at;
                    return (
                      <div className="sqis-header sqis-header--merged flex items-center justify-between mb-3 flex-wrap gap-2" data-testid="sqis-header-row">
                        <div>
                          <h4 className="sqis-title sqis-title--merged">
                            14 Points SQIS
                          </h4>
                          {sqisGeneratedAt && (
                            <p className="text-xs text-gray-500 mt-1">
                              Generated: {new Date(sqisGeneratedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <div className="sqis-header-actions flex items-center gap-2 flex-wrap">
                          {sqisGenerationButton}
                          <Button
                            onClick={() => setIsSynthesisVisible(!isSynthesisVisible)}
                            variant="outline"
                            size="sm"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                            data-testid="btn-toggle-sqis-visibility"
                          >
                            {isSynthesisVisible ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Hide
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Show
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => download16PointSynthesisPDF()}
                            variant="outline"
                            size="sm"
                            className="border-purple-500 text-purple-600 hover:bg-purple-50"
                            data-testid="btn-download-sqis-pdf"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                          <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                            ✓ Reviewed
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Client feedback status banner for psychologist (status-only, no header) */}
                  {userRole === 'psychologist' && existingData.synthesis_reviewed && hasAnyReportEnabled && sqisTab === 'sqis' && (() => {
                    const feedback = existingData.client_feedback;
                    const hasFeedback = feedback && feedback.submitted_at;
                    return (
                      <div className={`sqis-feedback-panel mb-4 ${hasFeedback ? 'sqis-feedback-panel--received' : 'sqis-feedback-panel--awaiting'}`} data-testid="psychologist-client-feedback-status">
                        {hasFeedback ? (
                          <div>
                            <div className="font-semibold mb-1">✓ Client feedback received • AI Analysis enabled</div>
                            <p className="text-sm text-gray-700">The client has submitted their SQIS feedback. AI Analysis generation is now available. See the feedback table below the SQIS.</p>
                          </div>
                        ) : (
                          <div>
                            <div className="font-semibold mb-1">Awaiting client SQIS feedback</div>
                            <p className="text-sm text-gray-700">The client may optionally review the SQIS and provide Overall Resonance and Context & Clarification.</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {isSynthesisVisible && (
                  <div className="sqis-content space-y-4">
                    {!isEditing16Points && (
                      <div className="sqis-accordion-toolbar phase5-shared-toolbar flex items-center justify-end gap-3 text-xs">
                        <div className="phase5-report-toolbar" role="group" aria-label="14-Point SQIS section controls">
                        <button
                          type="button"
                          onClick={() => setCollapsedPoints(new Set())}
                          className="sqis-accordion-action phase5-report-toolbar-button"
                          data-testid="sqis-expand-all-btn"
                        >
                          Expand All
                        </button>
                        <span className="phase5-report-toolbar-separator" aria-hidden="true">|</span>
                        <button
                          type="button"
                          onClick={() => setCollapsedPoints(new Set(
                            (existingData.synthesis_points || [])
                              .filter(p => p.point_number !== 15 && p.point_number !== 16)
                              .map(p => p.point_number)
                          ))}
                          className="sqis-accordion-action phase5-report-toolbar-button"
                          data-testid="sqis-collapse-all-btn"
                        >
                          Collapse All
                        </button>
                        </div>
                        <ReportFontSelector />
                        <ReportTextSizeSelector />
                      </div>
                    )}
                    {(isEditing16Points ? editablePoints : existingData.synthesis_points).filter(point => point.point_number !== 15 && point.point_number !== 16).map((point, idx) => {
                      const clientNote = ((existingData.client_validations || {})[point.point_number] || (existingData.client_validations || {})[String(point.point_number)])?.feedback;
                      const isConditionTriggered = typeof point.content === 'string' && point.content.includes('This is a Condition-Triggered Observation');
                      const isPsychologistEditableInEditMode =
                        isEditing16Points &&
                        point.point_number !== 11 &&
                        point.point_number !== 14 &&
                        !isConditionTriggered &&
                        (!existingData.synthesis_reviewed || Boolean(clientNote && clientNote.trim()));
                      // Force-expand while editing so the psychologist can always see/modify content.
                      const isCollapsed = !isEditing16Points && collapsedPoints.has(point.point_number);
                      return (
                      <div key={idx} className={`sqis-point-card phase5-report-section ${phase5Tone(idx)}`} data-testid={`sqis-point-card-${point.point_number}`}>
                        <div className={`sqis-point-header ${isEditing16Points ? 'phase5-section-header' : 'phase5-section-button'} flex items-center justify-between gap-2`}>
                          <div className="flex-1 min-w-0">
                        {isPsychologistEditableInEditMode ? (
                          <div className="mb-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Point {point.point_number} Title:</label>
                            <input
                              type="text"
                              value={point.title}
                              onChange={(e) => {
                                const updated = [...editablePoints];
                                updated[idx].title = e.target.value;
                                setEditablePoints(updated);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        ) : (
                          <h4 className="sqis-point-title">
                            <span className="sqis-point-number phase5-section-badge" data-testid={`sqis-point-number-${point.point_number}`} aria-hidden="true">{point.point_number}</span>
                            <span className="sqis-point-title-text phase5-section-title">{getDisplayTitle(point.point_number, point.title)}</span>
                            {isEditing16Points && (point.point_number === 11 || point.point_number === 14) && (
                              <span className="ml-2 text-xs font-normal text-gray-500 italic">(auto-generated, not editable)</span>
                            )}
                            {isEditing16Points && isConditionTriggered && point.point_number !== 11 && point.point_number !== 14 && (
                              <span className="ml-2 text-xs font-normal text-gray-500 italic">(condition-triggered observation, not editable)</span>
                            )}
                            {isEditing16Points && existingData.synthesis_reviewed && !isConditionTriggered && point.point_number !== 11 && point.point_number !== 14 && !clientNote && (
                              <span className="ml-2 text-xs font-normal text-gray-500 italic">(locked — editable only if client provides a note)</span>
                            )}
                          </h4>
                        )}
                          </div>
                          {!isEditing16Points && (
                            <button
                              type="button"
                              onClick={() => toggleCollapsedPoint(point.point_number)}
                              aria-expanded={!isCollapsed}
                              aria-label={isCollapsed ? `Expand point ${point.point_number}` : `Collapse point ${point.point_number}`}
                              className="sqis-point-toggle phase5-section-chevron shrink-0 inline-flex items-center justify-center"
                              data-testid={`sqis-point-toggle-${point.point_number}`}
                            >
                              <ChevronDown className={`w-4 h-4${isCollapsed ? '' : ' is-expanded'}`} />
                            </button>
                          )}
                        </div>
                        {!isCollapsed && (
                        <div className="sqis-point-body phase5-section-body">
                        {point.point_number === 11 ? (
                          /* Point 11: Grid View - Theme Connotations, Client Responses, Time Taken */
                          <div className="sqis-table-wrap overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300">
                              <thead className="bg-blue-50">
                                <tr>
                                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Theme Connotations</th>
                                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Client Responses</th>
                                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Time Taken</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const rows = [];
                                  const lines = splitPoint11Content(point.content).tableContent.split('\n');
                                  let currentTheme = '';
                                  let currentConnotations = '';
                                  let currentResponses = [];
                                  let currentTime = '';
                                  let inTime = false;
                                  const flush = () => {
                                    if (currentTheme) {
                                      rows.push({
                                        theme: currentTheme,
                                        connotations: currentConnotations,
                                        responses: currentResponses.join('\n'),
                                        time: currentTime.trim()
                                      });
                                    }
                                    currentTheme = '';
                                    currentConnotations = '';
                                    currentResponses = [];
                                    currentTime = '';
                                    inTime = false;
                                  };
                                  
                                  for (let i = 0; i < lines.length; i++) {
                                    const line = lines[i].trim();
                                    if (!line) continue;
                                    if (line.startsWith('Theme Connotations:')) {
                                      inTime = false;
                                      currentConnotations = line.replace('Theme Connotations:', '').trim();
                                    } else if (line.startsWith('Theme:')) {
                                      flush();
                                      currentTheme = line.replace('Theme:', '').trim();
                                    } else if (line.startsWith('•')) {
                                      inTime = false;
                                      currentResponses.push(line);
                                    } else if (line.startsWith('Average Time Taken:') || line.startsWith('Time Taken:')) {
                                      currentTime = line.replace('Average Time Taken:', '').replace('Time Taken:', '').trim();
                                      inTime = true;
                                    } else if (inTime && !line.startsWith('---') && !line.startsWith('Client Responses')) {
                                      currentTime = currentTime ? `${currentTime}\n${line}` : line;
                                    }
                                  }
                                  flush();
                                  
                                  return rows.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="border border-gray-300 px-4 py-3 align-top">
                                        <div className="font-semibold text-blue-800 mb-1">{row.theme}</div>
                                        <div className="text-sm text-gray-700">{row.connotations}</div>
                                      </td>
                                      <td className="border border-gray-300 px-4 py-3 align-top">
                                        <div className="text-sm whitespace-pre-line" dangerouslySetInnerHTML={{ __html: row.responses }}></div>
                                      </td>
                                      <td className="border border-gray-300 px-4 py-3 text-center align-top">
                                        <div className="flex flex-col items-center gap-1">
                                          {row.time.split('\n').filter(t => t).map((t, ti) => (
                                            <span
                                              key={ti}
                                              className={`inline-block px-2 py-0.5 rounded font-mono text-xs ${t.toLowerCase().startsWith('avg') ? 'bg-green-200 text-green-900 font-semibold' : 'bg-green-100 text-green-800'}`}
                                            >
                                              {t}
                                            </span>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  ));
                                })()}
                              </tbody>
                            </table>
                            <Point11Findings content={point.content} />
                          </div>
                        ) : point.point_number === 14 ? (
                          /* Point 14: Grid View - Client Responses, Themes, Repeated Times */
                          <div className="sqis-table-wrap overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300">
                              <thead className="bg-green-50">
                                <tr>
                                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Client Responses</th>
                                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Themes</th>
                                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-700">Repeated Times</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const rows = [];
                                  const lines = point.content.split('\n');
                                  let currentResponse = '';
                                  let currentThemes = '';
                                  let currentCount = '';
                                  
                                  for (let i = 0; i < lines.length; i++) {
                                    const line = lines[i].trim();
                                    if (line.startsWith('Response:')) {
                                      currentResponse = line.replace('Response:', '').trim();
                                    } else if (line.startsWith('Themes:')) {
                                      currentThemes = line.replace('Themes:', '').trim();
                                    } else if (line.startsWith('Repeated Times:')) {
                                      currentCount = line.replace('Repeated Times:', '').trim();
                                      // Save the row
                                      if (currentResponse) {
                                        rows.push({
                                          response: currentResponse,
                                          themes: currentThemes,
                                          count: currentCount
                                        });
                                        // Reset
                                        currentResponse = '';
                                        currentThemes = '';
                                        currentCount = '';
                                      }
                                    }
                                  }
                                  
                                  return rows.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="border border-gray-300 px-4 py-3 align-top">
                                        <div className="font-medium text-gray-800">{row.response}</div>
                                      </td>
                                      <td className="border border-gray-300 px-4 py-3 align-top">
                                        <div className="text-sm text-gray-700">{row.themes}</div>
                                      </td>
                                      <td className="border border-gray-300 px-4 py-3 text-center align-middle">
                                        <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 rounded font-bold text-lg">
                                          {row.count}
                                        </span>
                                      </td>
                                    </tr>
                                  ));
                                })()}
                              </tbody>
                            </table>
                          </div>
                        ) : isPsychologistEditableInEditMode ? (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Content:</label>
                            <textarea
                              value={pointAnalysisDisplay(point)}
                              onChange={(e) => {
                                const updated = [...editablePoints];
                                updated[idx].content = replacePointAnalysis(point, e.target.value);
                                setEditablePoints(updated);
                              }}
                              rows={8}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                        ) : (
                          <PointContentRenderer content={pointAnalysisDisplay(point)} />
                        )}
                        </div>
                        )}
                      </div>
                      );
                    })}
                    {/* Client SQIS Feedback table (psychologist view only) */}
                    {userRole === 'psychologist' && existingData.client_feedback && existingData.client_feedback.submitted_at && (
                      <div className="sqis-feedback-panel sqis-feedback-panel--received mt-6" data-testid="psychologist-client-feedback-table">
                        <h4 className="text-lg font-semibold text-indigo-900 mb-3">Client Feedback on SQIS</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-indigo-300 bg-white">
                            <tbody>
                              <tr>
                                <td className="border border-indigo-300 px-4 py-3 w-1/3 font-semibold text-indigo-900 bg-indigo-100 align-top">Overall Resonance</td>
                                <td className="border border-indigo-300 px-4 py-3" data-testid="psychologist-feedback-resonance">{existingData.client_feedback.overall_resonance}</td>
                              </tr>
                              <tr>
                                <td className="border border-indigo-300 px-4 py-3 font-semibold text-indigo-900 bg-indigo-100 align-top">Your Context & Clarification</td>
                                <td className="border border-indigo-300 px-4 py-3 whitespace-pre-wrap" data-testid="psychologist-feedback-context">{existingData.client_feedback.context_clarification}</td>
                              </tr>
                              <tr>
                                <td className="border border-indigo-300 px-4 py-3 font-semibold text-indigo-900 bg-indigo-100 align-top">Submitted</td>
                                <td className="border border-indigo-300 px-4 py-3 text-sm text-gray-700">{new Date(existingData.client_feedback.submitted_at).toLocaleString()}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {SYNTHESIS_DISCLAIMER_NOTE && (
                      <div className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded text-xs text-amber-900 italic" data-testid="synthesis-disclaimer-note">
                        {SYNTHESIS_DISCLAIMER_NOTE}
                      </div>
                    )}
                  </div>
                  )}
                  </div>
                </div>
              );
              })()}

              {/* Edit / Update / Mark-as-Reviewed buttons:
                  Available ONLY before the psychologist marks the 14 Points as reviewed.
                  Once reviewed, no more edits allowed. */}
              {sqisTab === 'sqis' && existingData?.synthesis_points && userRole === 'psychologist' && !isViewOnlyMode && !existingData?.synthesis_reviewed && (
                <div className="mt-6 flex gap-3 justify-end border-t-2 border-gray-300 pt-6 pb-2">
                  {!isEditing16Points ? (
                    <Button
                      onClick={() => setIsEditing16Points(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="edit-14-points-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit 14 Points
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => {
                          setIsEditing16Points(false);
                          setEditablePoints(existingData.synthesis_points.map(p => ({
                            point_number: p.point_number,
                            title: p.title,
                            content: p.content
                          })));
                        }}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleUpdate16Points}
                        disabled={updating16Points}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {updating16Points ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Update 14 Points
                          </>
                        )}
                      </Button>
                    </>
                  )}
                  {!existingData.synthesis_reviewed && (
                    <Button
                      onClick={handleReview16Points}
                      disabled={isEditing16Points}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark 14 Points as Reviewed
                    </Button>
                  )}
                </div>
              )}
              {/* Green "reviewed and shared" line removed — the client-feedback status banner above already conveys this. */}

              {/* AI Generated Reports GridView - Display Only - Only after 14 Points reviewed */}
              {sqisTab !== 'research' && existingData?.synthesis_reviewed && (tableAiReports.length > 0 || tableCustomReports.length > 0) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>AI Generated Analysis</h3>
                  
                  {/* Table View */}
                  <div className="phase5-report-table-wrap">
                    <table className="phase5-ai-reports-table w-full border-collapse border border-gray-300 table-fixed">
                      <colgroup>
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '28%' }} />
                      </colgroup>
                      <thead className="bg-blue-50">
                        <tr>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 text-sm">Analysis Name</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 text-sm">Type</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 text-sm">Psychologist</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 text-sm">Status</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 text-sm">Generated Date</th>
                          <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Client Summary is presented in its dedicated Phase 5 tab. */}
                        {tableAiReports.map(({ report, index: i }) => (
                          <tr key={`ai-${i}`} className="hover:bg-gray-50" data-testid={`report-row-${i}`}>
                            <td className="border border-gray-300 px-3 py-2 text-sm font-medium break-words">
                              {analysisDisplayText(report.report_type === 'ai_summary' ? clientSynthesisName : report.report_name)}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-sm">
                              {report.report_type === 'ask_buddy' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Ask Buddy
                                </span>
                              ) : report.report_type === 'custom_report' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Custom Analysis
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Standard AI Analysis
                                </span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700 break-words">
                              {report.psychologist_name || currentUser?.name || 'Psychologist'}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-sm">
                              {isDeepDiveReport(report) ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  Psychologist Only
                                </span>
                              ) : report.reviewed ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Reviewed
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending Review
                                </span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-xs text-gray-600">
                              <div className="break-words">{report.generated_at ? new Date(report.generated_at).toLocaleString() : 'N/A'}</div>
                            </td>
                            <td className="border border-gray-300 px-2 py-2">
                              <div className="flex gap-1 justify-center items-center flex-wrap">
                                {/* Visibility Checkbox - Only for psychologists */}
                                {userRole === 'psychologist' && !isDeepDiveReport(report) && (
                                  <div className="flex flex-col items-center">
                                    <input 
                                      type="checkbox"
                                      checked={existingData?.report_visibility?.[`ai_report_${i}`] === true}
                                      onChange={() => handleToggleReportVisibility(i, 'ai', existingData?.report_visibility?.[`ai_report_${i}`] === true)}
                                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                      title={existingData?.report_visibility?.[`ai_report_${i}`] === true ? 'Visible to client - Click to hide' : 'Hidden from client - Click to show'}
                                      disabled={isViewOnlyMode || !report.reviewed}
                                    />
                                    {!report.reviewed && (
                                      <span className="text-[10px] text-gray-400 mt-0.5">Review</span>
                                    )}
                                  </div>
                                )}
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedReport(report);
                                    setShowReportModal(true);
                                  }}
                                  data-testid={`open-report-${i}`}
                                  title="Open full Analysis"
                                  disabled={false}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => downloadReportPDF(report)}
                                  data-testid={`download-report-${i}`}
                                  title="Download as PDF"
                                  disabled={false}
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    const reportId = `ai-report-${i}`;
                                    if (currentSpeakingReport === reportId && isSpeaking) {
                                      stopSpeaking();
                                    } else {
                                      speakReport(report.report_content || report.content, reportId);
                                    }
                                  }}
                                  data-testid={`listen-report-${i}`}
                                  title={currentSpeakingReport === `ai-report-${i}` && isSpeaking ? "Stop reading" : "Listen to Analysis"}
                                  className={currentSpeakingReport === `ai-report-${i}` && isSpeaking ? "bg-orange-100 border-orange-500" : ""}
                                  disabled={false}
                                >
                                  {currentSpeakingReport === `ai-report-${i}` && isSpeaking ? (
                                    <StopCircle className="w-4 h-4 text-blue-600" />
                                  ) : (
                                    <Volume2 className="w-4 h-4" />
                                  )}
                                </Button>
                                {/* Allow review/delete if: psychologist AND (not view-only OR created by current psychologist OR no owner set) */}
                                {userRole === 'psychologist' && !isDeepDiveReport(report) && !report.reviewed && (!isViewOnlyMode || !report.psychologist_id || report.psychologist_id === currentUser?.id) && (
                                  <Button 
                                    variant="default" 
                                    size="sm"
                                    onClick={() => handleReviewReport(i)}
                                    data-testid={`review-report-${i}`}
                                    title="Mark as reviewed"
                                    className="bg-blue-600 hover:bg-blue-700"
                                    disabled={false}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                {userRole === 'psychologist' && (
                                  <Button 
                                    variant="destructive" 
                                    size="sm"
                                    onClick={() => {
                                      console.log('[DELETE BUTTON] Clicked delete for report:', i);
                                      handleDeleteReport(i);
                                    }}
                                    data-testid={`delete-report-${i}`}
                                    title="Delete Analysis"
                                    disabled={false}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        
                        {/* Custom Reports */}
                        {tableCustomReports.map(({ report, index: i }) => (
                          <tr key={`custom-${i}`} className="hover:bg-gray-50 bg-blue-50" data-testid={`custom-report-row-${i}`}>
                            <td className="border border-gray-300 px-3 py-2 text-sm font-medium break-words">
                              {analysisDisplayText(report.report_name)}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-sm">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Custom Analysis
                              </span>
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700 break-words">
                              {report.psychologist_name || currentUser?.name || 'Psychologist'}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-sm">
                              {report.reviewed ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Reviewed
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending Review
                                </span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-xs text-gray-600">
                              <div className="break-words">{report.generated_at ? new Date(report.generated_at).toLocaleString() : 'N/A'}</div>
                            </td>
                            <td className="border border-gray-300 px-2 py-2">
                              <div className="flex gap-1 justify-center items-center flex-wrap">
                                {/* Visibility Checkbox - Only for psychologists */}
                                {userRole === 'psychologist' && (
                                  <div className="flex flex-col items-center">
                                    <input 
                                      type="checkbox"
                                      checked={existingData?.report_visibility?.[`custom_report_${i}`] === true}
                                      onChange={() => handleToggleReportVisibility(i, 'custom', existingData?.report_visibility?.[`custom_report_${i}`] === true)}
                                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                      title={existingData?.report_visibility?.[`custom_report_${i}`] === true ? 'Visible to client - Click to hide' : 'Hidden from client - Click to show'}
                                      disabled={isViewOnlyMode || !report.reviewed}
                                    />
                                    {!report.reviewed && (
                                      <span className="text-[10px] text-gray-400 mt-0.5">Review</span>
                                    )}
                                  </div>
                                )}
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedReport(report);
                                    setShowReportModal(true);
                                  }}
                                  data-testid={`open-custom-report-${i}`}
                                  title="Open full Analysis"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => downloadReportPDF(report)}
                                  data-testid={`download-custom-report-${i}`}
                                  title="Download as PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    const reportId = `custom-report-${i}`;
                                    if (currentSpeakingReport === reportId && isSpeaking) {
                                      stopSpeaking();
                                    } else {
                                      speakReport(report.report_content || report.content, reportId);
                                    }
                                  }}
                                  data-testid={`listen-custom-report-${i}`}
                                  title={currentSpeakingReport === `custom-report-${i}` && isSpeaking ? "Stop reading" : "Listen to Analysis"}
                                  className={currentSpeakingReport === `custom-report-${i}` && isSpeaking ? "bg-orange-100 border-orange-500" : ""}
                                >
                                  {currentSpeakingReport === `custom-report-${i}` && isSpeaking ? (
                                    <StopCircle className="w-4 h-4 text-blue-600" />
                                  ) : (
                                    <Volume2 className="w-4 h-4" />
                                  )}
                                </Button>
                                {/* Allow review/delete if: psychologist AND (not view-only OR created by current psychologist OR no owner set) */}
                                {userRole === 'psychologist' && !report.reviewed && (!isViewOnlyMode || !report.psychologist_id || report.psychologist_id === currentUser?.id) && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handleReviewCustomReport(i)}
                                    data-testid={`review-custom-report-${i}`}
                                    title="Mark as reviewed"
                                    style={{ borderColor: '#10b981', color: '#10b981' }}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                {userRole === 'psychologist' && (!isViewOnlyMode || !report.psychologist_id || report.psychologist_id === currentUser?.id) && (
                                  <Button 
                                    variant="destructive" 
                                    size="sm"
                                    onClick={() => handleDeleteCustomReport(i)}
                                    data-testid={`delete-custom-report-${i}`}
                                    title="Delete custom analysis"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* SECTION 2: AI Report Generation Buttons - Only after 14 Points reviewed */}
              {(() => {
                return null;
              })()}
              {userRole === 'psychologist' && !isViewOnlyMode && existingData?.synthesis_reviewed && (() => {
                // Hide the AI report panel entirely when the admin has disabled every predefined report.
                const predefinedReportKeys = ['report1', 'report2', 'report3', 'report4', 'metaphorical_portrait'];
                const anyPredefinedReportEnabled = predefinedReportKeys.some((k) => isReportEnabled(k));
                if (!anyPredefinedReportEnabled) {
                  return null;
                }

                // Check if all 6 reports are generated
                const allReportsComplete = (
                  existingData?.ai_reports?.some(r => r.report_name === 'Exploring Client Feelings') &&
                  existingData?.ai_reports?.some(r => r.report_name === 'Emotional Balance Snapshot') &&
                  existingData?.ai_reports?.some(r => r.report_name === 'Deep Dive into Inner World') &&
                  existingData?.ai_reports?.some(r => r.report_name === 'Strengths and Coping Guide') &&
                  existingData?.ai_summary &&
                  existingData?.metaphorical_narrative
                );

                return (
                  <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg">
                    {allReportsComplete && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-300 rounded-lg flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-green-800 font-semibold">All AI Analysis has been generated!</span>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {isReportEnabled('report1') && (
                    <Button 
                      onClick={() => handleGenerateIndividualReport('report1', 'Exploring Client Feelings')} 
                      disabled={generatingIndividualReport === 'report1'}
                      className="w-full text-white font-semibold py-3 rounded-lg text-sm"
                      style={{ 
                        background: existingData?.ai_reports?.some(r => r.report_name === 'Exploring Client Feelings') ? '#10b981' : '#3b82f6',
                        cursor: 'pointer'
                      }}
                    >
                      {existingData?.ai_reports?.some(r => r.report_name === 'Exploring Client Feelings') ? '✓ ' : ''}
                      {generatingIndividualReport === 'report1' ? 'Generating...' : 'Exploring Client Feelings'}
                    </Button>
                    )}
                    {isReportEnabled('report2') && (
                    <Button 
                      onClick={() => handleGenerateIndividualReport('report2', 'Emotional Balance Snapshot')} 
                      disabled={generatingIndividualReport === 'report2'}
                      className="w-full text-white font-semibold py-3 rounded-lg text-sm"
                      style={{ 
                        background: existingData?.ai_reports?.some(r => r.report_name === 'Emotional Balance Snapshot') ? '#10b981' : '#8b5cf6',
                        cursor: 'pointer'
                      }}
                    >
                      {existingData?.ai_reports?.some(r => r.report_name === 'Emotional Balance Snapshot') ? '✓ ' : ''}
                      {generatingIndividualReport === 'report2' ? 'Generating...' : 'Emotional Balance Snapshot'}
                    </Button>
                    )}
                    {isReportEnabled('report3') && (
                    <Button 
                      onClick={() => handleGenerateIndividualReport('report3', 'Deep Dive into Inner World')} 
                      disabled={generatingIndividualReport === 'report3'}
                      className="w-full text-white font-semibold py-3 rounded-lg text-sm"
                      style={{ 
                        background: existingData?.ai_reports?.some(r => r.report_name === 'Deep Dive into Inner World') ? '#10b981' : '#f59e0b',
                        cursor: 'pointer'
                      }}
                    >
                      {existingData?.ai_reports?.some(r => r.report_name === 'Deep Dive into Inner World') ? '✓ ' : ''}
                      {generatingIndividualReport === 'report3' ? 'Generating...' : 'Deep Dive into Inner World'}
                    </Button>
                    )}
                    {isReportEnabled('report4') && (
                    <Button 
                      onClick={() => handleGenerateIndividualReport('report4', 'Strengths and Coping Guide')} 
                      disabled={generatingIndividualReport === 'report4'}
                      className="w-full text-white font-semibold py-3 rounded-lg text-sm"
                      style={{ 
                        background: existingData?.ai_reports?.some(r => r.report_name === 'Strengths and Coping Guide') ? '#10b981' : '#2563eb',
                        cursor: 'pointer'
                      }}
                    >
                      {existingData?.ai_reports?.some(r => r.report_name === 'Strengths and Coping Guide') ? '✓ ' : ''}
                      {generatingIndividualReport === 'report4' ? 'Generating...' : 'Strengths and Coping Guide'}
                    </Button>
                    )}
                    {isReportEnabled('metaphorical_portrait') && (
                    <Button 
                      onClick={() => handleGenerateIndividualReport('metaphorical_portrait', 'Metaphorical Client Portrait')} 
                      disabled={generatingIndividualReport === 'metaphorical_portrait'}
                      className="w-full text-white font-semibold py-3 rounded-lg text-sm"
                      style={{ 
                        background: existingData?.metaphorical_narrative ? '#10b981' : '#3b82f6',
                        cursor: 'pointer'
                      }}
                    >
                      {existingData?.metaphorical_narrative ? '✓ ' : ''}
                      {generatingIndividualReport === 'metaphorical_portrait' ? 'Generating...' : 'Metaphorical Client Portrait'}
                    </Button>
                    )}
                  </div>
                  {(generatingIndividualReport || showIndividualTimer) && (
                    <div className="mt-3 flex flex-col items-center justify-center gap-3">
                      {generatingIndividualReport && (
                        <div className="flex flex-col items-center gap-3 w-full">
                          {/* Progress message hidden as per user request */}
                          <Button
                            onClick={handleStopReportGeneration}
                            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-bold shadow-lg transition-all text-base"
                          >
                            <StopCircle className="mr-2 h-5 w-5" />
                            Stop Generation
                          </Button>
                        </div>
                      )}
                      <GenerationTimer 
                        isGenerating={!!generatingIndividualReport} 
                        isVisible={showIndividualTimer}
                        label="Elapsed" 
                      />
                    </div>
                  )}
                </div>
                );
              })()}

              {/* SECTION 3: Custom Reports - REMOVED */}

              {/* SECTION 4: Ask Buddy - REMOVED */}

            </div>
          </CardContent>
        </Card>

        {/* Report Modal */}
        <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
          <DialogContent className="max-w-[90vw] w-full max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {analysisDisplayText(selectedReport?.report_type === 'ai_summary' ? clientSynthesisName : selectedReport?.report_name)}
              </DialogTitle>
            </DialogHeader>
            <div>
              {selectedReport?.report_type === 'ai_summary'
                ? <PremiumClientSynthesis sessionId={sessionId} metadata={{clientName:clientDisplayName, date:selectedReport?.generated_at ? new Date(selectedReport.generated_at).toLocaleDateString() : undefined, psychologistName:selectedReport?.psychologist_name||existingData?.psychologist_name}} content={selectedReport?.content ?? selectedReport?.report_content} testId="client-synthesis-modal-rich" />
                : <p className="narrative-text whitespace-pre-line">{selectedReport?.report_content || selectedReport?.content}</p>}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowReportModal(false)}>
                Close
              </Button>
              <Button onClick={() => downloadReportPDF(selectedReport)}>
                <FileText className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (userRole !== 'psychologist') {
    return null;
  }

  if (generatingPoints) {
    return (
      <Card className="mt-6">
        <CardContent className="text-center py-12">
          <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: '#2563eb' }} />
          <p className="text-lg font-semibold">Generating 14 Synthesis Points...</p>
          <p className="text-sm text-gray-600 mt-2">Analyzing data from all phases...</p>
          <Phase5Loading title="Generating 14-Point SQIS" message="Waiting for the generated report to be saved…" />
        </CardContent>
      </Card>
    );
  }

  // Derive a human-readable label of the active AI generation for the progress overlay
  const activeGenerationLabel = (() => {
    if (generatingIndividualReport) {
      const map = {
        aptitude: 'Aptitude Analysis',
        career: 'Career Analysis',
        education: 'Education Analysis',
        relationship: 'Relationship Analysis',
        marital: 'Marital Analysis',
        health: 'Health Analysis',
        parenting: 'Parenting Analysis',
        financial: 'Financial Analysis',
        social: 'Social Analysis',
        emotional: 'Emotional Analysis',
        spiritual: 'Spiritual Analysis',
        legal: 'Legal Analysis',
        grief: 'Grief Analysis',
        trauma: 'Trauma Analysis',
        anxiety: 'Anxiety Analysis',
        depression: 'Depression Analysis',
        addiction: 'Addiction Analysis',
        apa_case_parallels: 'APA Case Parallels',
      };
      return map[generatingIndividualReport] || `${String(generatingIndividualReport).replace(/_/g, ' ')} Analysis`;
    }
    if (generatingReports) return 'All Standard Analysis';
    if (generatingPoints) return '14-Point SQIS Analysis';
    if (generatingWhy) return 'Patterns & Contributing Themes';
    if (generatingNext) return 'Next Steps';
    if (generatingBooks) return 'Book Reflections';
    if (generatingMovies) return 'Movie Reflections';
    if (generatingMusic) return 'Music Reflections';
    if (generatingCustomReport) return 'Custom Analysis';
    return 'Analysis';
  })();

  const isAnyGenerating = !!(
    generatingIndividualReport ||
    generatingReports ||
    generatingPoints ||
    generatingWhy ||
    generatingNext ||
    generatingBooks ||
    generatingMovies ||
    generatingMusic ||
    generatingCustomReport
  );

  return (
    <>
      {/* Diagnostic: log roleplay state on every render */}
      <GenerationProgressOverlay isGenerating={isAnyGenerating} label={activeGenerationLabel} />
      {regenerationConfirmationDialog}
      {shareConfirmationDialog}
      <WhyNextProgressDialog
        open={whyNextModal.open}
        phase={whyNextModal.phase}
        kind={whyNextModal.kind}
        isRegenerate={whyNextModal.isRegenerate}
        errorMessage={whyNextModal.errorMessage}
        attempts={whyNextModal.attempts}
        maxAttempts={whyNextModal.maxAttempts}
        onClose={closeWhyNextModal}
        onCancel={cancelProgressGeneration}
      />
      <WhyNextProgressDialog
        open={progressModal.open}
        phase={progressModal.phase}
        kind={progressModal.kind}
        label={progressModal.label}
        isRegenerate={progressModal.isRegenerate}
        errorMessage={progressModal.errorMessage}
        onClose={closeProgressModal}
        onCancel={cancelProgressGeneration}
      />
      {/* Nexus Roleplay Simulator is mounted globally in App.js — opened via window event */}
      {/* Delete confirmation modal — replaces the native window.confirm() popup */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(o) => { if (!o) closeDeleteConfirm(); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Analysis?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to delete{' '}
              <span className="font-semibold text-gray-900">"{analysisDisplayText(deleteConfirm.reportName)}"</span>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} data-testid="delete-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={runDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-accept"
            >
              {isDeleting ? 'Deleting...' : 'Yes, delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Individual AI Report Generation Buttons - ABOVE 14 Points */}
      {existingData?.synthesis_reviewed && (
        <div className="mb-6 space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
            <h4 className="font-semibold text-blue-900 mb-2">📊 Generate AI Analysis Individually</h4>
            <p className="text-sm text-blue-800 mb-2">
              Generate each Analysis separately to avoid token limits and get focused analysis. Click any button below to generate that specific Analysis.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Report 1: Exploring Client Feelings */}
            {isReportEnabled('report1') && (
            <Button 
              onClick={() => handleGenerateIndividualReport('report1', 'Exploring Client Feelings')} 
              disabled={generatingIndividualReport !== null}
              className="w-full text-white font-semibold py-4 rounded-lg shadow-lg transition-all hover:shadow-xl"
              style={{ 
                background: (existingData?.ai_reports?.some(r => r.report_name === 'Exploring Client Feelings') 
                  ? 'linear-gradient(to right, #10b981, #059669)' 
                  : 'linear-gradient(to right, #3b82f6, #2563eb)'),
                cursor: 'pointer'
              }}
            >
              {generatingIndividualReport === 'report1' ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <>
                  {existingData?.ai_reports?.some(r => r.report_name === 'Exploring Client Feelings') ? '✓ ' : ''}
                  Analysis 1: Exploring Client Feelings
                </>
              )}
            </Button>
            )}

            {/* Report 2: Emotional Balance Snapshot */}
            {isReportEnabled('report2') && (
            <Button 
              onClick={() => handleGenerateIndividualReport('report2', 'Emotional Balance Snapshot')} 
              disabled={generatingIndividualReport !== null}
              className="w-full text-white font-semibold py-4 rounded-lg shadow-lg transition-all hover:shadow-xl"
              style={{ 
                background: (existingData?.ai_reports?.some(r => r.report_name === 'Emotional Balance Snapshot') 
                  ? 'linear-gradient(to right, #10b981, #059669)' 
                  : 'linear-gradient(to right, #8b5cf6, #7c3aed)'),
                cursor: 'pointer'
              }}
            >
              {generatingIndividualReport === 'report2' ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <>
                  {existingData?.ai_reports?.some(r => r.report_name === 'Emotional Balance Snapshot') ? '✓ ' : ''}
                  Analysis 2: Emotional Balance Snapshot
                </>
              )}
            </Button>
            )}

            {/* Report 3: Deep Dive into Inner World */}
            {isReportEnabled('report3') && (
            <Button 
              onClick={() => handleGenerateIndividualReport('report3', 'Deep Dive into Inner World')} 
              disabled={generatingIndividualReport !== null}
              className="w-full text-white font-semibold py-4 rounded-lg shadow-lg transition-all hover:shadow-xl"
              style={{ 
                background: (existingData?.ai_reports?.some(r => r.report_name === 'Deep Dive into Inner World') 
                  ? 'linear-gradient(to right, #10b981, #059669)' 
                  : 'linear-gradient(to right, #f59e0b, #d97706)'),
                cursor: 'pointer'
              }}
            >
              {generatingIndividualReport === 'report3' ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <>
                  {existingData?.ai_reports?.some(r => r.report_name === 'Deep Dive into Inner World') ? '✓ ' : ''}
                  Analysis 3: Deep Dive into Inner World
                </>
              )}
            </Button>
            )}

            {/* Report 4: Strengths and Coping Guide */}
            {isReportEnabled('report4') && (
            <Button 
              onClick={() => handleGenerateIndividualReport('report4', 'Strengths and Coping Guide')} 
              disabled={generatingIndividualReport !== null}
              className="w-full text-white font-semibold py-4 rounded-lg shadow-lg transition-all hover:shadow-xl"
              style={{ 
                background: (existingData?.ai_reports?.some(r => r.report_name === 'Strengths and Coping Guide') 
                  ? 'linear-gradient(to right, #10b981, #059669)' 
                  : 'linear-gradient(to right, #2563eb, #db2777)'),
                cursor: 'pointer'
              }}
            >
              {generatingIndividualReport === 'report4' ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <>
                  {existingData?.ai_reports?.some(r => r.report_name === 'Strengths and Coping Guide') ? '✓ ' : ''}
                  Analysis 4: Strengths and Coping Guide
                </>
              )}
            </Button>
            )}

            {/* Metaphorical Client Portrait - Enabled once the client validation is finalized/approved (based on 14 Points only) */}
            {isReportEnabled('metaphorical_portrait') && (
            <Button 
              onClick={() => handleGenerateIndividualReport('metaphorical_portrait', 'Metaphorical Client Portrait')} 
              disabled={generatingIndividualReport !== null || !(existingData?.psychologist_confirmed_client_validation || existingData?.validation_finalized)}
              className="w-full text-white font-semibold py-4 rounded-lg shadow-lg transition-all hover:shadow-xl"
              style={{ 
                background: existingData?.metaphorical_narrative
                  ? 'linear-gradient(to right, #10b981, #059669)' 
                  : ((existingData?.psychologist_confirmed_client_validation || existingData?.validation_finalized)
                    ? 'linear-gradient(to right, #3b82f6, #2563eb)' 
                    : '#9CA3AF'),
                cursor: ((existingData?.psychologist_confirmed_client_validation || existingData?.validation_finalized) && !generatingIndividualReport) ? 'pointer' : 'not-allowed'
              }}
            >
              {generatingIndividualReport === 'metaphorical_portrait' ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <>
                  {existingData?.metaphorical_narrative ? '✓ ' : ''}
                  Metaphorical Client Portrait
                  {!(existingData?.psychologist_confirmed_client_validation || existingData?.validation_finalized) && ' (Requires Client Validation)'}
                </>
              )}
            </Button>
            )}

            {/* Legacy: Generate All Reports Button (for backward compatibility) */}
            <Button 
              onClick={handleGenerateReports} 
              disabled={generatingReports || generatingIndividualReport !== null || existingData?.standard_report_generated}
              className="w-full text-white font-semibold py-4 rounded-lg shadow-lg transition-all hover:shadow-xl"
              style={{ 
                background: !existingData?.standard_report_generated
                  ? 'linear-gradient(to right, #2563eb, #2563eb)' 
                  : '#9CA3AF',
                cursor: !existingData?.standard_report_generated ? 'pointer' : 'not-allowed'
              }}
            >
              {generatingReports ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating All...</>
              ) : (
                <>
                  {existingData?.standard_report_generated ? '✓ ' : ''}
                  Generate All (Legacy)
                </>
              )}
            </Button>
          </div>
          
          {/* Progress message hidden - Stop button moved to individual report cards */}
          {generatingIndividualReport && (
            <div className="mt-6 flex justify-center">
              <Button
                onClick={handleStopReportGeneration}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-bold shadow-lg transition-all flex items-center text-base"
              >
                <StopCircle className="mr-2 h-5 w-5" />
                Stop Generation
              </Button>
            </div>
          )}
        </div>
      )}

      {loadingFeedback}
      <Card className="mt-6" data-testid="phase5-synthesis-points">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Phase 5 - 14 Point Structured Synthesis</CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Auto-generated and manual synthesis points based on comprehensive structured qualitative interpretative summary
              </p>
            </div>
            {userRole === 'psychologist' && existingData?.points_reviewed && (
              <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-lg border">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={existingData?.report_visibility?.show_16_points !== false}
                    onChange={() => handleToggleReportVisibility(null, '16points', existingData?.report_visibility?.show_16_points !== false)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-orange-500 cursor-pointer"
                    disabled={isViewOnlyMode}
                  />
                  <span className="text-sm font-medium text-gray-700">Visible to Client</span>
                </label>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {synthesisPoints.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">Generate the 14 synthesis points when you are ready</p>
              <p className="text-sm text-gray-600 mb-4">Complete and validate Phases 1–4 before generating. Generation checks the saved source data.</p>
              {sqisGenerationButton}
            </div>
          ) : (
            <div className="space-y-6">
              {synthesisPoints.filter(point => point.point_number !== 15 && point.point_number !== 16).map((point, idx) => (
                <div key={idx} className="border rounded-lg p-5 bg-gray-50" data-testid={`synthesis-point-${point.point_number}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div 
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ background: point.auto_generated ? '#2563EB' : '#2563eb' }}
                    >
                      {point.point_number}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {getDisplayTitle(point.point_number, point.title)}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded ${point.auto_generated ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {point.auto_generated ? 'Auto-generated' : 'Manual Input Required'}
                      </span>
                    </div>
                  </div>
                  
                  {/* All points are editable for psychologists, except Points 11, 13, and 14 which have custom rendering */}
                  <div className="ml-13">
                    {point.point_number === 11 ? (
                      /* Point 11: Grid View - Theme Connotations, Client Responses, Time Taken */
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                          <thead className="bg-blue-50">
                            <tr>
                              <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Theme Connotations</th>
                              <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Client Responses</th>
                              <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Time Taken</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rows = [];
                              const lines = splitPoint11Content(point.content).tableContent.split('\n');
                              let currentTheme = '';
                              let currentConnotations = '';
                              let currentResponses = [];
                              let currentTime = '';
                              let inTime = false;
                              const flush = () => {
                                if (currentTheme) {
                                  rows.push({
                                    theme: currentTheme,
                                    connotations: currentConnotations,
                                    responses: currentResponses.join('\n'),
                                    time: currentTime.trim()
                                  });
                                }
                                currentTheme = '';
                                currentConnotations = '';
                                currentResponses = [];
                                currentTime = '';
                                inTime = false;
                              };
                              
                              for (let i = 0; i < lines.length; i++) {
                                const line = lines[i].trim();
                                if (!line) continue;
                                if (line.startsWith('Theme Connotations:')) {
                                  inTime = false;
                                  currentConnotations = line.replace('Theme Connotations:', '').trim();
                                } else if (line.startsWith('Theme:')) {
                                  flush();
                                  currentTheme = line.replace('Theme:', '').trim();
                                } else if (line.startsWith('•')) {
                                  inTime = false;
                                  currentResponses.push(line);
                                } else if (line.startsWith('Average Time Taken:') || line.startsWith('Time Taken:')) {
                                  currentTime = line.replace('Average Time Taken:', '').replace('Time Taken:', '').trim();
                                  inTime = true;
                                } else if (inTime && !line.startsWith('---') && !line.startsWith('Client Responses')) {
                                  currentTime = currentTime ? `${currentTime}\n${line}` : line;
                                }
                              }
                              flush();
                              
                              return rows.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="border border-gray-300 px-4 py-3 align-top">
                                    <div className="font-semibold text-blue-800 mb-1">{row.theme}</div>
                                    <div className="text-sm text-gray-700">{row.connotations}</div>
                                  </td>
                                  <td className="border border-gray-300 px-4 py-3 align-top">
                                    <div className="text-sm whitespace-pre-line" dangerouslySetInnerHTML={{ __html: row.responses }}></div>
                                  </td>
                                  <td className="border border-gray-300 px-4 py-3 text-center align-top">
                                    <div className="flex flex-col items-center gap-1">
                                      {row.time.split('\n').filter(t => t).map((t, ti) => (
                                        <span
                                          key={ti}
                                          className={`inline-block px-2 py-0.5 rounded font-mono text-xs ${t.toLowerCase().startsWith('avg') ? 'bg-green-200 text-green-900 font-semibold' : 'bg-green-100 text-green-800'}`}
                                        >
                                          {t}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                        <Point11Findings content={point.content} />
                      </div>
                    ) : point.point_number === 13 ? (
                      /* Point 13: Moral Values Analysis - Frequency + Editable Sections */
                      (() => {
                        let point13Data = { frequency_analysis: "", psychologist_conclusion: "", clinical_implications: "" };
                        try {
                          point13Data = JSON.parse(point.content);
                        } catch (e) {
                          point13Data = { frequency_analysis: point.content, psychologist_conclusion: "", clinical_implications: "" };
                        }
                        
                        return (
                          <div className="space-y-4">
                            {/* Frequency Analysis (Read-Only) */}
                            <div className="border rounded-lg p-4 bg-blue-50">
                              <h4 className="font-semibold text-blue-900 mb-2">Value Frequency Analysis</h4>
                              <div className="bg-white p-3 rounded border border-blue-200">
                                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">{point13Data.frequency_analysis}</pre>
                              </div>
                            </div>
                            
                            {/* Psychologist's Conclusion (Editable) */}
                            <div className="border rounded-lg p-4 bg-green-50">
                              <h4 className="font-semibold text-green-900 mb-2">Psychologist's Conclusion</h4>
                              <Textarea
                                value={point13Data.psychologist_conclusion}
                                onChange={(e) => {
                                  const updated = { ...point13Data, psychologist_conclusion: e.target.value };
                                  setSynthesisPoints(prev => 
                                    prev.map(p => p.point_number === 13 ? {...p, content: JSON.stringify(updated)} : p)
                                  );
                                }}
                                onBlur={async () => {
                                  try {
                                    const current = JSON.parse(synthesisPoints.find(p => p.point_number === 13).content);
                                    await axios.put(`${API}/sessions/${sessionId}/phase5/update-point13`, {
                                      psychologist_conclusion: current.psychologist_conclusion,
                                      clinical_implications: current.clinical_implications
                                    });
                                    showSuccess('Psychologist conclusion updated');
                                  } catch (err) {
                                    showError('Failed to update');
                                  }
                                }}
                                className="min-h-[120px] bg-white"
                                placeholder="Enter professional conclusion..."
                              />
                            </div>
                            
                            {/* Clinical Implications (Editable) */}
                            <div className="border rounded-lg p-4 bg-blue-50">
                              <h4 className="font-semibold text-blue-900 mb-2">Clinical Implications</h4>
                              <Textarea
                                value={point13Data.clinical_implications}
                                onChange={(e) => {
                                  const updated = { ...point13Data, clinical_implications: e.target.value };
                                  setSynthesisPoints(prev => 
                                    prev.map(p => p.point_number === 13 ? {...p, content: JSON.stringify(updated)} : p)
                                  );
                                }}
                                onBlur={async () => {
                                  try {
                                    const current = JSON.parse(synthesisPoints.find(p => p.point_number === 13).content);
                                    await axios.put(`${API}/sessions/${sessionId}/phase5/update-point13`, {
                                      psychologist_conclusion: current.psychologist_conclusion,
                                      clinical_implications: current.clinical_implications
                                    });
                                    showSuccess('Clinical implications updated');
                                  } catch (err) {
                                    showError('Failed to update');
                                  }
                                }}
                                className="min-h-[120px] bg-white"
                                placeholder="Enter clinical implications..."
                              />
                            </div>
                          </div>
                        );
                      })()
                    ) : point.point_number === 14 ? (
                      /* Point 14: Grid View - Client Responses, Themes, Repeated Times */
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                          <thead className="bg-green-50">
                            <tr>
                              <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Client Responses</th>
                              <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Themes</th>
                              <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-700">Repeated Times</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rows = [];
                              const lines = point.content.split('\n');
                              let currentResponse = '';
                              let currentThemes = '';
                              let currentCount = '';
                              
                              for (let i = 0; i < lines.length; i++) {
                                const line = lines[i].trim();
                                if (line.startsWith('Response:')) {
                                  currentResponse = line.replace('Response:', '').trim();
                                } else if (line.startsWith('Themes:')) {
                                  currentThemes = line.replace('Themes:', '').trim();
                                } else if (line.startsWith('Repeated Times:')) {
                                  currentCount = line.replace('Repeated Times:', '').trim();
                                  // Save the row
                                  if (currentResponse) {
                                    rows.push({
                                      response: currentResponse,
                                      themes: currentThemes,
                                      count: currentCount
                                    });
                                    // Reset
                                    currentResponse = '';
                                    currentThemes = '';
                                    currentCount = '';
                                  }
                                }
                              }
                              
                              return rows.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="border border-gray-300 px-4 py-3 align-top">
                                    <div className="font-medium text-gray-800">{row.response}</div>
                                  </td>
                                  <td className="border border-gray-300 px-4 py-3 align-top">
                                    <div className="text-sm text-gray-700">{row.themes}</div>
                                  </td>
                                  <td className="border border-gray-300 px-4 py-3 text-center align-middle">
                                    <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 rounded font-bold text-lg">
                                      {row.count}
                                    </span>
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <Textarea
                        value={pointAnalysisDisplay(point)}
                        onChange={(e) => {
                          const newContent = replacePointAnalysis(point, e.target.value);
                          setSynthesisPoints(prev => 
                            prev.map(p => p.point_number === point.point_number ? {...p, content: newContent} : p)
                          );
                        }}
                        onBlur={(e) => handleUpdatePoint(point.point_number, replacePointAnalysis(point, e.target.value))}
                        placeholder={`Edit ${getDisplayTitle(point.point_number, point.title).toLowerCase()}...`}
                        className="min-h-[120px]"
                        data-testid={`point-input-${point.point_number}`}
                      />
                    )}
                    {(point.point_number === 2 || point.point_number === 12 || point.point_number === 15) && (
                      <p className="text-xs text-gray-500 mt-2">
                        {point.point_number === 2 && "Analyze all phases to determine: Ambivert, Introvert, or Extrovert"}
                        {point.point_number === 12 && "Identify consistent response patterns under emotional elicitation"}
                        {point.point_number === 15 && "Add relevant APA citations to support clinical interpretations"}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {SYNTHESIS_DISCLAIMER_NOTE && (
                <div className="mt-2 p-4 bg-amber-50 border-l-4 border-amber-400 rounded text-xs text-amber-900 italic" data-testid="edit-synthesis-disclaimer-note">
                  {SYNTHESIS_DISCLAIMER_NOTE}
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-4 mt-8 pt-6 border-t">
                {/* Review 14 Points Button */}
                {userRole === 'psychologist' && !existingData?.synthesis_reviewed && !isViewOnlyMode && (
                  <div className="space-y-2">
                    <Button 
                      onClick={handleReview16Points} 
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3"
                      data-testid="review-16-points-btn"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" /> Mark 14 Points as Reviewed
                    </Button>
                    <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded border border-yellow-200">
                      ⚠️ Please review all 14 points before marking as reviewed. Once reviewed, you can generate AI Analysis.
                    </p>
                  </div>
                )}
                
                {/* Review Status Indicator */}
                {userRole === 'psychologist' && existingData?.synthesis_reviewed && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">14 Points Reviewed</p>
                      <p className="text-sm text-green-700">
                        Reviewed on {new Date(existingData.synthesis_reviewed_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
                
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
      
      {/* Phase 5 Modals - Book & Movie Recommendations */}
      <AlertDialog
        open={Boolean(clientSummaryDiscardAction)}
        onOpenChange={(open) => { if (!open) keepEditingClientSummary(); }}
      >
        <AlertDialogContent
          className="sm:max-w-md"
          data-testid="client-summary-unsaved-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes in the {clientSynthesisName}. Do you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="client-summary-keep-editing">
              Keep Editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={discardClientSummaryChanges}
              className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600"
              data-testid="client-summary-discard-changes"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BooksConfirmModal 
        open={showBooksConfirmModal} 
        onClose={() => setShowBooksConfirmModal(false)}
        onConfirm={() => {
          setShowBooksConfirmModal(false);
          handleGenerateBookRecommendations();
        }}
        loading={generatingBooks}
        showTimer={showBooksTimer}
        onTimerDismiss={() => setShowBooksTimer(false)}
      />
      <BooksSuccessModal 
        open={showBooksSuccessModal} 
        onClose={() => {
          setShowBooksSuccessModal(false);
          setShowBooksTimer(false); // Hide timer when success modal is dismissed
        }}
      />
      
      <MoviesConfirmModal 
        open={showMoviesConfirmModal} 
        onClose={() => setShowMoviesConfirmModal(false)}
        onConfirm={() => {
          setShowMoviesConfirmModal(false);
          handleGenerateMovieRecommendations();
        }}
        loading={generatingMovies}
        showTimer={showMoviesTimer}
        onTimerDismiss={() => setShowMoviesTimer(false)}
      />
      <MoviesSuccessModal 
        open={showMoviesSuccessModal} 
        onClose={() => {
          setShowMoviesSuccessModal(false);
          setShowMoviesTimer(false); // Hide timer when success modal is dismissed
        }}
      />
      
      {/* Modal Notification System */}
      {notificationModal.show && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" onClick={(e) => {
          if (e.target === e.currentTarget) closeModalNotification();
        }}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center mb-4">
              {notificationModal.type === 'success' && (
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              )}
              {notificationModal.type === 'error' && (
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              )}
              {notificationModal.type === 'info' && (
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <Info className="w-6 h-6 text-blue-600" />
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {notificationModal.type === 'success' && 'Success'}
                {notificationModal.type === 'error' && 'Error'}
                {notificationModal.type === 'info' && 'Information'}
              </h3>
            </div>
            <p className="text-gray-600 mb-6">{notificationModal.message}</p>
            <div className="flex justify-end">
              <button
                onClick={closeModalNotification}
                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showGeneratingModal} onOpenChange={setShowGeneratingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <Brain className="w-6 h-6 text-blue-600 animate-pulse" />
              Generating Synthesis Points
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            <p className="text-center text-gray-700">
              Generating 14 synthesis points, please wait...
            </p>
            <p className="text-center text-sm text-gray-500">
              This may take a few moments while we analyze the thematic data.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* 14 Points Generated Success Modal */}
      <Dialog open={showGeneratedModal} onOpenChange={setShowGeneratedModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <CheckCircle className="w-6 h-6 text-green-600" />
              Synthesis Complete
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <p className="text-center text-lg font-semibold text-gray-900">
              14 synthesis points generated successfully!
            </p>
            <p className="text-center text-sm text-gray-600">
              The synthesis points are now available in Phase 5.
            </p>
            <Button 
              onClick={() => setShowGeneratedModal(false)}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 14 Points Update Success Modal */}
      <Dialog open={show16PointsUpdateSuccessModal} onOpenChange={setShow16PointsUpdateSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <CheckCircle className="w-6 h-6 text-green-600" />
              14 Points Updated
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <p className="text-center text-lg font-semibold text-gray-900">
              14 Points updated successfully!
            </p>
            <p className="text-center text-sm text-gray-600">
              Your changes have been saved.
            </p>
            <Button 
              onClick={() => setShow16PointsUpdateSuccessModal(false)}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 14 Points Review Success Modal */}
      <Dialog open={show16PointsReviewSuccessModal} onOpenChange={setShow16PointsReviewSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <CheckCircle className="w-6 h-6 text-green-600" />
              14 Points Reviewed
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <p className="text-center text-lg font-semibold text-gray-900">
              14 Points reviewed & shared with client
            </p>
            <p className="text-center text-sm text-gray-600">
              The 14 Points SQIS is now visible to the client in their Phase 5 view. You can also generate AI Analysis.
            </p>
            <Button 
              onClick={() => setShow16PointsReviewSuccessModal(false)}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Finalize Validation Confirmation Modal */}
      <Dialog open={showFinalizeValidationModal} onOpenChange={setShowFinalizeValidationModal}>
        <DialogContent className="sm:max-w-md" data-testid="finalize-validation-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <AlertCircle className="w-6 h-6 text-amber-600" />
              Finalize Validation?
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <Lock className="w-10 h-10 text-amber-600" />
            </div>
            <p className="text-center text-base font-semibold text-gray-900">
              Are you sure you want to mark your validation as DONE?
            </p>
            <p className="text-center text-sm text-gray-600">
              Once finalized, <b>no further changes</b> can be made by you or your psychologist, and <b>AI Analysis will be enabled immediately</b>.
            </p>
            <div className="flex w-full gap-2 pt-2">
              <Button
                onClick={() => setShowFinalizeValidationModal(false)}
                variant="outline"
                className="flex-1"
                data-testid="finalize-validation-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFinalizeClientValidation}
                disabled={validationSubmitting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="finalize-validation-confirm-btn"
              >
                {validationSubmitting ? 'Finalizing…' : 'Yes, Finalize'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default SessionPhases;
